import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import axios from "axios";
import {
  getActiveDestinations,
  getLdsDates,
  searchFlights,
  searchHotels,
  searchHotelsComposite,
  searchLds,
} from "../services/fhpService";
import { isHubwayzHotelsTabEnabled } from "../features/hubwayzHotelsTab/hubwayzHotelsTabConfig";
import { searchHotelsViaHubwayz } from "../features/hubwayzHotelsTab/searchHotelsViaHubwayz";
import { getCurrentDate, getNextMonth } from "../utils/DatesHelpers";
import { isLdsResponseEmpty } from "../utils/LDSHelpers";
import {
  HR_VACATION_PACKAGE_NEW_SEARCH_EVENT,
  buildPackageOccupancySignature,
  logPackageOccupancyTrace,
} from "../utils/passengers/packageOccupancySignature";
import {
  buildActiveSearchIdentity,
  destinationKeyFromLocation,
  destinationKeyFromPhrase,
  phraseBelongsToTab,
  shouldCommitSearchResponse,
  stampSearchRows,
} from "../features/searchOffer/activeSearchIdentity";
import {
  logOrgTourSearchPerf,
  orgTourPerfNow,
  pushOrgTourTracePanelSample,
} from "../utils/orgTourPerf";
import {
  dealsFromSearchFlightsSettled,
  dealsListFromSearchFlightsResponseBody,
  isSearchFlightsAxiosResponseOk,
  parallelRoundTripSettledTransportOk,
  pickAvailableFlightsDealFromParallelSettled,
  sortFlightDealsByPerPersonAscending,
} from "../utils/flightParallelSearchHelpers";
import {
  probeOneWayFlightAlternatives,
  probeRoundTripFlightAlternatives,
} from "../services/flightAlternativeDatesProbe";
import {
  HOTEL_SEARCH_CURRENCY_STORAGE_KEY,
  DEFAULT_HOTEL_SEARCH_CURRENCY,
  normalizeHotelSearchCurrencyCode,
} from "../constants/hotelCurrencies";
import {
  detectSearchIntent,
  INTENT_HOTEL_SEARCH,
  INTENT_DESTINATION_SEARCH,
  normalizeHotelName,
  getHotelMatchNames,
  calculateTextMatchScore,
  calculateRelevanceScore,
  getDeduplicationKey,
  pickBetterHotelRowForListDedup,
} from "../utils/searchRelevance";
import {
  buildHotelNameIndex,
  searchHotelNameIndex,
  getDedupKey,
  INDEX_MATCH_BOOST,
} from "../utils/hotelNameIndex";
import {
  getBestFuzzyHotelMatch,
  isAutocorrectConfidenceHigh,
  AUTOCORRECT_BOOST,
} from "../utils/hotelNameAutocorrect";
import {
  trackSearchMatch,
  trackDidYouMeanShown,
  trackDidYouMeanClicked,
} from "../utils/searchAnalytics";
import { trackMetaSearch } from "../utils/metaPixel/metaPixelAnalytics";
import { resolveMonthlyDisplayPrice } from "../utils/monthlyHotelPrice";
import { isSearchResultStaleForDates } from "../utils/searchResultsStaleGuard";
import {
  getFallbackLdsDatesForAllDestinations,
  getOrganizedTourPrimaryLocation,
  getPackageMonthDateCombinations,
  isOrganizedTourAllDestinationsLocation,
  isOrganizedTourSearchReady,
  isPackageMonthSearchReady,
  resolveLdsSearchCityFromLocation,
  resolveLdsSearchPayloadFilters,
} from "./SearchContextHelpers";
import { getUserInfo } from "../services/localStorage";
import isArray from "lodash/isArray";
import useTimer from "./TimerContext";
import {
  isNumber,
  round2,
  useAppContext,
  FX_SOURCE_ADMIN_CONFIG,
  FX_SOURCE_FALLBACK_DEFAULT,
} from "./AppContext";
import {
  resolveWalletCommissionPercentage,
  resolveLdsHrMarginFromFeatures,
  inferLdsHrProductType,
} from "../features/agentEntitlements/selectors";
import { AGENT_FEATURE_KEYS } from "../features/agentEntitlements/constants";
import {
  applyDevPackageTierToEntitlementsPayload,
  useDevViewModeOptional,
} from "../dev/viewMode";
import { runLdsVacationPackageFareFromEngine } from "../pricing/vacationPackageLdsAdapter";
import { resolveFlightHrDirectionCount } from "../pricing/flightHrCommission";
import { getLdsPackagePricingChannelType } from "../stores/customerVacationChatPricingChannel";
import { getUserDetails, isAdminUser } from "../services/authService";
import { isTripMasterSurface } from "../surfaces/tripMaster/tripMasterEnv";
import { recordTripMasterPublicSearch } from "../surfaces/tripMaster/recordTripMasterPublicSearch";
import {
  isPublicConsumerSearchSurface,
  shouldNotifyAgentSystemToasts,
} from "../features/vacationPackagesPublic/publicSiteIdentity";
import { resolveLdsSupplierCodeForPricing } from "../utils/resolveLdsSupplierCodeForPricing";
import {
  isDynamicPackagesTemporarilyDisabled,
  reportHrDpTempDisabledTrace,
  resolveDynamicPackagesUserRoleForTrace,
} from "../utils/dynamicPackage/dynamicPackagesMaintenanceGate.js";
import { normalizePackageTierCode } from "../utils/packageTier";
import { LDS_AUTOCOMPLETE_MORE } from "../assets/Data/LdsAirportsAutocomplete";
import { parseLocationToString } from "../Components/Agent/SearchBars/SubComponents/AutoCompleteInput/autocompleteinputLDS";
import dayjs from "dayjs";
import { App } from "antd";
import {
  GoogleMapSecretKey,
  isSearchResultsAttractionsEnabled,
  ApiReqBaseUrl,
} from "../assets/GlobalVariables";
import { runParallelAttractionsSearch } from "../utils/parallelAttractionsSearch";
import { fetchAttractionRulesForPackage } from "../services/attractionsApi";
import { getDestinationResolveHints } from "../utils/resolveAttractionsDestinationId";
import { resolveCanonicalDestinationIdForHotelSearch } from "../utils/resolveCanonicalDestinationForHotelSearch";
import { buildHotelSearchRankingFields } from "../utils/buildHotelSearchRankingFields";
import {
  HOTEL_FLOW_STEP,
  createHotelFlowPerfMark,
  traceHotelFlowStep,
} from "../features/hotelFlowTrace/hotelFlowTrace";
import { sortByServerHrOrder } from "../utils/hotelListPrioritySort";
import {
  getGeocodingByAddress,
  getGeocodingByCoordinates,
} from "../services/geocodingService";
import { logGeocodingMap, isGeocodingMapDebugEnabled } from "../utils/geocodingMapDebug";
import { pickCityAndCountryFromAddressComponents } from "../utils/nominatimGoogleAddressFormat";
import { smartTransliterate, containsHebrew } from "../services/transliterationService.js";
import translateLDSDestinations from "../services/translateLDSDestinations";
import i18n from "i18next";
import {
  resolveOriginTokenForSearchFlights,
  resolveDestinationTokenForSearchFlights,
} from "../utils/flightSearchPayloadHelpers";
import {
  isShowAllHotelResultsDebug,
  isMultiRoomSplitSearchEnabled,
  shouldLogHotelSearchPipeline,
  logDroppedHotel,
  shouldSkipCompositeHotelSearchForWizard,
  isHotelCardPriceDebugEnabled,
  isLdsSupplierMatchDebugEnabled,
  isLdsCommissionTypeDebugEnabled,
  isLdsRuleMatchDebugEnabled,
} from "../utils/hotelSearchDebugFlags";
import {
  selectLdsSupplierCommissionRow,
  extractLdsDealReferenceDate,
  extractLdsFlightDealDestination,
  normalizeDealTypeForLdsSupplierRow,
  coerceToYmd,
} from "../utils/ldsSupplierRuleMatch";
import { mapCompositeHotelResultsToLegacyRows } from "../utils/mapCompositeHotelSearchToVM";
import { logHotelCardPriceDebug } from "../utils/hotelCardPriceAuditLog";
import { logHotelPriceTrace, isHotelPriceTraceEnabled } from "../utils/hotelPriceTrace";
import { agentHotelSearchLowestPackagePrice } from "../utils/agentHotelSearchApiPrice";
import { computeNightsFromSearchPhrase } from "../utils/hotelSearchListDisplay";
import { toLdsDdMmYyyy } from "../utils/ldsDealHelpers";
import {
  mergeOwDealsToLinkedFlightsSlice,
  firstLegFromLdsDeal,
} from "../utils/dynamicPackage/mergeSelfComposeDeals";
import {
  filterDynamicPackageOneWayDeals,
  resolveDynamicPackageParallelFlightPick,
} from "../utils/dynamicPackage/dynamicPackageFlightDateGuard";
import { computeSelfComposePricingSsot } from "../Components/FlightBookingFlowMockup/flightBookingMockup/selfCompose/selfComposeSummaryUtils";
import {
  isDynamicPackagePricingNormalizedEnabled,
  getDynamicPackagePricingSource,
  sealLdsDealPriceBranchesForReadOnly,
  DYNAMIC_PACKAGE_PRICING_SOURCE_STORAGE_KEY,
  DYNAMIC_PACKAGE_PRICING_SOURCE_NORMALIZED,
} from "../utils/dynamicPackage/dynamicPackagePricingFeatureFlag";
import { buildNormalizedDynamicPackagePricing } from "../utils/dynamicPackage/dynamicPackageNormalizedPricing";
import { attachDynamicPackageFutureCxlToHotelSearchResults } from "../utils/dynamicPackage/attachDynamicPackageFutureCxlToHotelSearchResults";
import { HOTELSRIVER_REHYDRATION_SNAPSHOT_KEY } from "../utils/dynamicPackage/hotelsRiverResolvedPricingVm";
import { isHrDpLeanProfileEnabled } from "../utils/dynamicPackage/hrDpLeanProfileFlag";
import {
  HR_DP_FLIGHTS_LIST_SNAPSHOT_KEY,
  rehydrateAvailableFlightsFromCompactSnapshot,
  rehydrateFlightSelectionFromCompactSnapshot,
} from "../utils/dynamicPackage/dynamicPackageFlightsListSnapshot";
import { clearDynamicPackageCheapestFlightSsotCache } from "../utils/dynamicPackage/buildDynamicPackageSearchResultSsotPricing";
import {
  buildAgentRecentSearchPayload,
  buildMonthlyHotelPricingRecentPayload,
} from "../utils/agentRecentSearchPayload";
import { persistAgentRecentSearch } from "../services/agentRecentSearchesApi";
import { resolveAgentRecentSearchRestorePlan } from "../utils/restoreAgentRecentSearch";
import { shouldRunHotelSearch } from "../utils/search/shouldRunHotelSearch";

/** When set, full pipeline debug is logged for this hotel (development). */
const TARGET_DEBUG_HOTEL_NAME = "AC Hotel by Marriott Miami Aventura";

/**
 * מלון נבחר בשדה היעד (Google lodging / מזהה מלון) — מותר לחפש בלי city/country מהיעד הכללי.
 */
function hasHotelSpecificSelection(loc) {
  if (!loc || typeof loc !== "object") return false;
  if (typeof loc.hotelserched === "string" && loc.hotelserched.trim() !== "")
    return true;
  if (loc.HotelId != null && String(loc.HotelId).trim() !== "") return true;
  if (loc.hotelId != null && String(loc.hotelId).trim() !== "") return true;
  if (loc.place_id != null && String(loc.place_id).trim() !== "") return true;
  if (loc.placeId != null && String(loc.placeId).trim() !== "") return true;
  if (Array.isArray(loc.types) && loc.types.includes("lodging")) return true;
  return false;
}

function logHotelSearchEligibilityBlocked(reasonBlocked, ctx) {
  if (!import.meta.env?.DEV && !isShowAllHotelResultsDebug()) return;
  try {
    console.warn("[HOTEL_SEARCH_ELIGIBILITY_BLOCKED]", {
      reasonBlocked,
      searchMode: ctx.searchMode ?? null,
      selectedHotel: ctx.selectedHotel ?? null,
      hotelId: ctx.hotelId ?? null,
      destination: ctx.destination ?? null,
      city: ctx.city ?? null,
      country: ctx.country ?? null,
      ...ctx,
    });
  } catch (_) {
    /* ignore */
  }
}

/** BE internal merge rows may omit GeoLocation / DefaultImage; skip Gemonix-only guards. */
function isInternalInventorySearchHotel(hotel) {
  if (!hotel || typeof hotel !== "object") return false;
  if (hotel.internal_inventory === true) return true;
  if (hotel.has_internal_inventory === true) return true;
  if (hotel.is_internal === true) return true;
  if (hotel.is_internal_hotel === true) return true;
  const src = hotel.inventory_source ?? hotel.inventory_backend;
  if (src != null) {
    const u = String(src).trim().toLowerCase();
    if (u === "internal" || u === "mixed") return true;
  }
  return false;
}

/** B2C חבילות — גם /vacation-packages וגם כינוי שגוי /vacation_packages לפני redirect בראוטר. */
function isVacationPackagesB2CPathname() {
  if (typeof window === "undefined") return false;
  const p = window.location.pathname;
  return p.includes("/vacation-packages") || p.includes("/vacation_packages");
}

function normalizePathname(pathname) {
  if (typeof pathname !== "string") return "";
  return pathname.toLowerCase().replace(/\/+$/, "");
}

function isVacationPackageViewRoute(pathname) {
  const p = normalizePathname(pathname);
  if (!p) return false;
  return (
    /^\/vacation-packages\/package\/[^/]+$/.test(p) ||
    /^\/vacation_packages\/package\/[^/]+$/.test(p)
  );
}

function isDynamicPackageSearchRoute(pathname) {
  const p = normalizePathname(pathname);
  if (!p) return false;
  if (isVacationPackageViewRoute(p)) return false;
  return true;
}

const SearchContext = createContext();

const language = localStorage.getItem("i18nextLng")
  ? localStorage.getItem("i18nextLng")
  : "he";

export const PLANES = "planes";
export const HOTELS = "hotels";
export const PACKAGES = "FlightsHotel";
export const ORGANIZED_TOURS = "OrganizedTour";
export const DYNAMIC_PACKAGES = "DynamicPackages";
export const CRUISE_PAYMENT = "CruisePayment";
export const CAMPAIGNS = "Campaigns";
const SEARCH_CATEGORY_VALUES = new Set([
  PLANES,
  HOTELS,
  PACKAGES,
  ORGANIZED_TOURS,
  DYNAMIC_PACKAGES,
  CRUISE_PAYMENT,
  CAMPAIGNS,
]);

function isSearchCategoryTraceEnabled() {
  try {
    return Boolean(import.meta?.env?.DEV);
  } catch (_) {
    return false;
  }
}

function emitSearchCategoryTrace(payload = {}) {
  if (!isSearchCategoryTraceEnabled() || typeof window === "undefined") return;
  const {
    previousCategory = null,
    nextCategory = null,
    source = "unknown",
    phase = "write",
    note = null,
  } = payload;
  console.log("[SEARCH_CATEGORY_CHANGE_TRACE]", {
    previousCategory,
    nextCategory,
    source,
    phase,
    note,
    pathname: window.location.pathname || "",
    timestamp: new Date().toISOString(),
  });
}

function safeJsonSizeBytes(value) {
  try {
    return new TextEncoder().encode(JSON.stringify(value)).length;
  } catch (_) {
    return null;
  }
}

function isPublicFlightsVacationPackagesRoute() {
  if (typeof window === "undefined") return false;
  if (isTripMasterSurface()) return true;
  try {
    const url = new URL(window.location.href);
    return (
      url.pathname === "/vacation-packages" &&
      url.searchParams.get("category") === "flights"
    );
  } catch {
    return false;
  }
}

/**
 * Public campaign pages (Belvedere booking/landing, Batumi, etc.) do not use
 * SearchContext's LDS date calendar. Prefetching 12× getLdsDates there saturates
 * the browser connection pool and freezes the UI while unrelated campaign APIs wait.
 */
function isCampaignPublicPathname(pathname) {
  return String(pathname || "").includes("/campaigns/");
}

function shouldPrefetchLdsDatesOnCurrentRoute() {
  if (typeof window === "undefined") return true;
  try {
    return !isCampaignPublicPathname(window.location.pathname);
  } catch {
    return true;
  }
}



export const ADULTS = "adults";
export const KIDS = "kids";
export const INFANTS = "infants";
export const KIDS_AGES = "kidsAges";

const defaultRoomInfo = {
  rooms: [{ [ADULTS]: 2, [KIDS]: 0, [INFANTS]: 0, [KIDS_AGES]: [] }],
};
const TWENTY_MIN = 1800;

const FLIGHT_AGENT_PLAN_ID_TO_CODE = {
  1: "BASIC",
  2: "PRO",
  3: "AGENCY",
};

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value == null) continue;
    const raw = String(value).trim();
    if (raw !== "") return raw;
  }
  return "";
}

function normalizeFlightAgentPlanCode(value) {
  const raw = firstNonEmpty(value).toUpperCase();
  if (!raw) return "";
  if (/^\d+$/.test(raw)) {
    return FLIGHT_AGENT_PLAN_ID_TO_CODE[Number(raw)] || "";
  }
  return normalizePackageTierCode(raw);
}

function resolveFlightAgentPlanAuditMeta({ agentEntitlements, userDetails }) {
  const packageObj =
    agentEntitlements?.package && typeof agentEntitlements.package === "object"
      ? agentEntitlements.package
      : null;
  const rawPackageId = firstNonEmpty(
    packageObj?.id,
    packageObj?.package_id,
    packageObj?.packageId,
    agentEntitlements?.subscription?.package_id,
    agentEntitlements?.subscription?.packageId,
    agentEntitlements?.package_id,
    agentEntitlements?.packageId,
    userDetails?.package_id,
    userDetails?.packageId
  );
  const rawPackageCode = firstNonEmpty(
    packageObj?.code,
    packageObj?.package_code,
    packageObj?.packageCode,
    packageObj?.name,
    agentEntitlements?.subscription?.effectivePackage,
    agentEntitlements?.subscription?.effective_package,
    agentEntitlements?.subscription?.package_code,
    agentEntitlements?.subscription?.packageCode,
    agentEntitlements?.package_code,
    agentEntitlements?.packageCode,
    agentEntitlements?.package,
    userDetails?.package_code,
    userDetails?.packageCode,
    userDetails?.package
  );
  const resolvedFromCode = normalizeFlightAgentPlanCode(rawPackageCode);
  const resolvedFromId = normalizeFlightAgentPlanCode(rawPackageId);
  let resolvedPackageCode = resolvedFromCode || resolvedFromId || "";
  if (
    (!resolvedPackageCode || resolvedPackageCode === "BASIC") &&
    isAdminUser()
  ) {
    resolvedPackageCode = "PRO";
  }
  return {
    rawPackageId: rawPackageId || null,
    rawPackageCode: rawPackageCode || null,
    packageId: rawPackageId || null,
    packageCode: rawPackageCode || null,
    resolvedPackageCode: resolvedPackageCode || null,
    missingPackage: !resolvedPackageCode,
    fallbackUsed: !resolvedFromCode && Boolean(resolvedFromId),
  };
}

// Geocoding is now handled by geocodingService.js with centralized caching

export function hotelSearchPhraseCacheIdentity(location) {
  const hubwayzLocationId =
    location?.hubwayzLocationId != null &&
    String(location.hubwayzLocationId).trim() !== ""
      ? String(location.hubwayzLocationId).trim()
      : null;
  const countryCodeRaw = location?.countryCode || location?.iso2 || null;
  const countryCode =
    countryCodeRaw != null && String(countryCodeRaw).trim() !== ""
      ? String(countryCodeRaw).trim()
      : null;
  return { hubwayzLocationId, countryCode };
}

export function cachedHotelSearchPhraseDropsSelection(location, cachedPhrase) {
  const selected = hotelSearchPhraseCacheIdentity(location);
  const cachedId =
    cachedPhrase?.hubwayzLocationId != null &&
    String(cachedPhrase.hubwayzLocationId).trim() !== ""
      ? String(cachedPhrase.hubwayzLocationId).trim()
      : null;
  const cachedCountry =
    cachedPhrase?.countryCode != null && String(cachedPhrase.countryCode).trim() !== ""
      ? String(cachedPhrase.countryCode).trim()
      : null;
  return (
    (selected.hubwayzLocationId != null && cachedId !== selected.hubwayzLocationId) ||
    (selected.countryCode != null && cachedCountry !== selected.countryCode)
  );
}

function withCorporateSearchChannel(payload) {
  try {
    const user = getUserInfo();
    const usertype = String(user?.usertype || user?.userType || "").toLowerCase();
    if (usertype === "corporate") {
      return { ...payload, search_context: "corporate", channel: "corporate" };
    }
  } catch (_) {
    /* ignore */
  }
  return payload;
}

export const SearchProvider = ({ totalPrice, children }) => {
  const { message, notification } = App.useApp();
  const notifyAgentSearch = (type, config) => {
    if (!shouldNotifyAgentSystemToasts()) return;
    const fn = notification?.[type];
    if (typeof fn === "function") fn(config);
  };
  const {
    currencyRate,
    currencyRateSource,
    ldsSuppliers,
    getCreditCardFeePercent,
    isB2CMode,
    getHrCommissionPercent,
    getHrCommissionPercentHotels,
    getAgentCommissionPercent,
    agentEntitlements,
    agentEntitlementsStatus,
  } = useAppContext();

  /** DEV package-tier preview for display pricing (does not change real subscription). */
  const devViewMode = useDevViewModeOptional();
  const packageTierOverride =
    devViewMode?.isPackageOverrideActive && devViewMode.packageTier
      ? devViewMode.packageTier
      : null;
  const pricingEntitlements = useMemo(
    () =>
      applyDevPackageTierToEntitlementsPayload(
        agentEntitlements,
        packageTierOverride,
      ),
    [agentEntitlements, packageTierOverride],
  );

  const [userTouchedCategory, _setUserTouchedCategory] = useState(false);
  const userTouchedCategoryRef = useRef(false);
  const markUserTouchedCategory = useCallback((nextTouched) => {
    const normalized = Boolean(nextTouched);
    userTouchedCategoryRef.current = normalized;
    _setUserTouchedCategory(normalized);
  }, []);
  const resolveInitialSearchTab = useCallback(() => {
    if (isPublicFlightsVacationPackagesRoute()) {
      emitSearchCategoryTrace({
        previousCategory: null,
        nextCategory: PLANES,
        source: "url_restore",
        phase: "read",
      });
      return PLANES;
    }
    const saved = localStorage.getItem("searchTab");
    if (saved && SEARCH_CATEGORY_VALUES.has(saved)) {
      emitSearchCategoryTrace({
        previousCategory: null,
        nextCategory: saved,
        source: "storage_restore",
        phase: "read",
      });
      return saved;
    }
    emitSearchCategoryTrace({
      previousCategory: null,
      nextCategory: PACKAGES,
      source: "default_init",
      phase: "read",
    });
    return PACKAGES;
  }, []);
  const [searchTab, _setSearchTab] = useState(() => resolveInitialSearchTab());

  const SEARCH_PARAMS_LOCKED_KEY = "searchParamsLocked";
  const [searchParamsLocked, _setSearchParamsLocked] = useState(() => {
    try {
      return localStorage.getItem(SEARCH_PARAMS_LOCKED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const searchParamsLockedRef = useRef(searchParamsLocked);
  useEffect(() => {
    searchParamsLockedRef.current = searchParamsLocked;
  }, [searchParamsLocked]);

  const setSearchParamsLocked = useCallback((locked) => {
    const next = Boolean(locked);
    _setSearchParamsLocked(next);
    try {
      localStorage.setItem(SEARCH_PARAMS_LOCKED_KEY, next ? "true" : "false");
    } catch {
      /* ignore */
    }
  }, []);

  const toggleSearchParamsLock = useCallback(() => {
    const next = !searchParamsLockedRef.current;
    setSearchParamsLocked(next);
    if (next) {
      syncLockedSearchDatesForCategoryChange(searchTabRef.current);
    }
  }, [setSearchParamsLocked]);

  const [searchPhrase, setSearchPhrase] = useState(null);

  const { setTimeLeft } = useTimer();

  const getInitRoomInfo = (fresh = false) => {
    if (!fresh) {
      if (sessionStorage.getItem("roomData")) {
        if (sessionStorage.getItem("roomData").length > 0) {
          const savedData = JSON.parse(sessionStorage.getItem("roomData"));
          return { rooms: savedData };
        }
      }
    }
    return JSON.parse(JSON.stringify(defaultRoomInfo));
  };

  // Search params
  const [roomInfo, _setRoomInfo] = useState(getInitRoomInfo());
  const [tourMonth, setTourMonth] = useState("");
  /** Packages: "exact" = dep/ret dates; "month" = search whole month (MM/YYYY). */
  const [packageDateMode, _setPackageDateMode] = useState("exact");
  const [packageMonth, _setPackageMonth] = useState("");
  const setPackageDateMode = useCallback((mode) => {
    const next = mode === "month" ? "month" : "exact";
    _setPackageDateMode(next);
    if (next === "month") {
      setCalendarDateArrival("");
      setCalendarDateLeave("");
    } else {
      _setPackageMonth("");
    }
  }, []);
  const setPackageMonth = useCallback((month) => {
    _setPackageMonth(String(month || "").trim());
  }, []);
  const [calendarDateArrival, setCalendarDateArrival] = useState("");
  const [calendarDateLeave, setCalendarDateLeave] = useState("");
  /** Flight search only (PLANES / dynamic packages): RoundTrip = dep + return; OneWay = dep only (no ret_date to API). */
  const [flightTripType, _setFlightTripType] = useState("RoundTrip");
  const setFlightTripType = useCallback((value) => {
    _setFlightTripType(value);
    if (value === "OneWay") {
      setCalendarDateLeave("");
    }
  }, []);
  const [dateRange, setDateRange] = useState(0);
  const [selectedHotelCurrency, _setSelectedHotelCurrency] = useState(() => {
    try {
      const raw = localStorage.getItem(HOTEL_SEARCH_CURRENCY_STORAGE_KEY);
      return normalizeHotelSearchCurrencyCode(raw);
    } catch {
      return DEFAULT_HOTEL_SEARCH_CURRENCY;
    }
  });
  const fetchSearchDataRef = useRef(() => {});
  const pendingHotelCurrencyRefetchRef = useRef(false);
  const setSelectedHotelCurrency = useCallback((code) => {
    const next = normalizeHotelSearchCurrencyCode(code);
    if (next !== selectedHotelCurrency) {
      pendingHotelCurrencyRefetchRef.current = true;
    }
    _setSelectedHotelCurrency(next);
    try {
      localStorage.setItem(HOTEL_SEARCH_CURRENCY_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, [selectedHotelCurrency]);
  const [origin, _setOrigin] = useState({
    COUNTRYNAME_HEB: "ישראל",
    CITYNAME_HEB: "תל אביב",
    AIRPORTCODE: "TLV",
    AIRPORTNAME: 'נתב"ג',
    CITYCODE: "TLV",
    CITYNAME: "TEL AVIV",
    COUNTRYCODE: "IL",
    COUNTRYNAME: "ISRAEL",
  });
  // Load default destination if no location is saved
  // Priority: defaultDestinationLDS > defaultDestination (legacy) > saved location > fallback
  const getInitialLocation = () => {
    // First, check if there's a default destination for LDS (highest priority)
    const defaultDestinationLDS = localStorage.getItem("defaultDestinationLDS");
    if (defaultDestinationLDS && defaultDestinationLDS !== "undefined") {
      try {
        return JSON.parse(defaultDestinationLDS);
      } catch (e) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error parsing default destination LDS:", e);
        }
      }
    }
    // Check legacy key for backward compatibility
    const defaultDestination = localStorage.getItem("defaultDestination");
    if (defaultDestination && defaultDestination !== "undefined") {
      try {
        const parsed = JSON.parse(defaultDestination);
        // Only use if it's LDS format (has CITYCODE or AIRPORTCODE)
        if (parsed.CITYCODE || parsed.AIRPORTCODE) {
          return parsed;
        }
      } catch (e) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error parsing default destination:", e);
        }
      }
    }
    // If no default destination, try saved location
    const savedLocation = localStorage.getItem("location");
    if (savedLocation && savedLocation !== "undefined") {
      try {
        return JSON.parse(savedLocation);
      } catch (e) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error parsing saved location:", e);
        }
      }
    }
    // Fallback to default
    return LDS_AUTOCOMPLETE_MORE[0];
  };

  const [location, _setLocation] = useState(getInitialLocation());
  const [retOrigin, _setRetOrigin] = useState(null);
  const [retDest, _setRetDest] = useState(null);
  const [categories, _setCategories] = useState(["ALL"]);
  const [isSearched, setIsSearched] = useState(false);
  /** Monotonic bridge signal: organized-tour LDS pipeline finished so /searchoffer can navigate to results. */
  const [organizedTourSearchHandshake, setOrganizedTourSearchHandshake] =
    useState(0);
  const [destinations, setDestinations] = useState([]);
  const [subjects, setSubjects] = useState({
    FlightsHotel: [],
    OrganizedTours: [],
  });

  // Responses
  const [sessionId, setSessionId] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [currentSearchRequestId, setCurrentSearchRequestId] = useState(0);
  const [lastSearchPayload, setLastSearchPayload] = useState(null);
  /** תוצאות חיפוש אטרקציות מקביל ל־searchLds / searchHotels (מקור: SearchContext). */
  const [attractionsSearchResults, setAttractionsSearchResults] = useState([]);
  const [attractionsSearchLoading, setAttractionsSearchLoading] = useState(false);
  const [attractionsSearchError, setAttractionsSearchError] = useState(null);
  const [attractionsSearchMeta, setAttractionsSearchMeta] = useState(null);
  /** חוקי GET /api/attractions/rules ליעד הנוכחי — מקור אמת לבאדג' מול בלוק אטרקציות בפרופיל (אחרי חיפוש מקביל). */
  const [attractionsDestinationRules, setAttractionsDestinationRules] = useState([]);
  /** Last /searchHotelsComposite envelope (HOTELS tab, multi-room split only). */
  const [compositeHotelSearchMeta, setCompositeHotelSearchMeta] = useState(null);
  /**
   * מטבע ה-dataset של תוצאות המלונות הנוכחיות (כפי שנשלח בבקשה שחזרה עם השורות האלה).
   * מנוע תצוגה: לא להציג סמל מטבע אחר על אותם מספרים.
   */
  const [hotelSearchResultsCurrency, setHotelSearchResultsCurrency] = useState(null);
  const [targetHotelDedupKey, setTargetHotelDedupKey] = useState(null);
  const [availableFlights, _setAvailableFlights] = useState(null);
  /** After RoundTrip parallel search only; null = not run or cleared (OneWay tab / new search). */
  const [roundTripFlightResults, setRoundTripFlightResults] = useState(null);
  const [outboundOneWayFlightResults, setOutboundOneWayFlightResults] =
    useState(null);
  const [inboundOneWayFlightResults, setInboundOneWayFlightResults] =
    useState(null);
  /** True after a RoundTrip parallel triple-search finished with ≥1 deal (RT or either OW). */
  const [
    parallelRoundTripFlightSearchCompleted,
    setParallelRoundTripFlightSearchCompleted,
  ] = useState(false);
  /** UI: classic RT deals vs self-compose from two OneWay legs (booking not wired yet). */
  const [flightResultsBrowseMode, setFlightResultsBrowseMode] = useState(
    "roundTrip"
  );
  const [selectedSelfComposeOutboundDeal, setSelectedSelfComposeOutboundDeal] =
    useState(null);
  const [selectedSelfComposeInboundDeal, setSelectedSelfComposeInboundDeal] =
    useState(null);
  /** null | "empty" | "error" — רק לטאב טיסות; מבדיל תשובה ריקה תקינה משגיאת רשת/שרת */
  const [planesFlightSearchOutcome, setPlanesFlightSearchOutcome] =
    useState(null);
  const [flightAlternativeDateSuggestions, setFlightAlternativeDateSuggestions] =
    useState([]);
  const [flightAlternativesProbeLoading, setFlightAlternativesProbeLoading] =
    useState(false);

  useEffect(() => {
    if (flightTripType !== "RoundTrip") {
      setRoundTripFlightResults(null);
      setOutboundOneWayFlightResults(null);
      setInboundOneWayFlightResults(null);
      setParallelRoundTripFlightSearchCompleted(false);
      setFlightResultsBrowseMode("roundTrip");
      setSelectedSelfComposeOutboundDeal(null);
      setSelectedSelfComposeInboundDeal(null);
    }
  }, [flightTripType]);

  useEffect(() => {
    if (
      !selectedSelfComposeOutboundDeal &&
      !selectedSelfComposeInboundDeal
    ) {
      return;
    }
    console.log("[self-compose flights] selected deal ids:", {
      outbound:
        selectedSelfComposeOutboundDeal?.dealId ??
        selectedSelfComposeOutboundDeal?.ID,
      inbound:
        selectedSelfComposeInboundDeal?.dealId ??
        selectedSelfComposeInboundDeal?.ID,
    });
  }, [selectedSelfComposeOutboundDeal, selectedSelfComposeInboundDeal]);

  // Use refs to prevent race conditions and track active requests
  const currentSearchRequestIdRef = useRef(0);
  const searchCommitSnapshotRef = useRef(null);
  /** Same-search duplicate submit guard (organized tours on searchoffer). */
  const lastOrganizedTourSubmitFingerprintRef = useRef(null);
  const hotelSearchInFlightRef = useRef(new Map());
  const hotelSearchPerfLastLogAtRef = useRef(new Map());
  const lastFlightSearchFingerprintRef = useRef(null);
  /** מיני־סייט סוכן: נשלח ב־POST /searchFlights כ־agent_id */
  const flightSearchAttributionAgentIdRef = useRef(null);
  const setFlightSearchAttributionAgentId = useCallback((id) => {
    flightSearchAttributionAgentIdRef.current =
      id != null && String(id).trim() !== "" ? String(id).trim() : null;
  }, []);
  const flightSearchDateOverridesRef = useRef(null);
  /** One-shot location/dates for submitSearch when caller passes overrides (banner tiles). */
  const submitSearchOverridesRef = useRef(null);
  const pendingRecentSearchRestoreRef = useRef(null);
  const [pendingRecentSearchRestoreTick, setPendingRecentSearchRestoreTick] =
    useState(0);
  const abortControllerRef = useRef(null);
  /** חיפוש מחירים חודשי למלון: session + Abort נפרדים מחיפוש החבילה הראשי */
  const searchHotelForMonthSessionRef = useRef(0);
  const searchHotelForMonthAbortRef = useRef(null);
  const lastSetSearchResultsRef = useRef(null);
  
  // Debug flag - set to true to enable detailed logging
  const DEBUG_SEARCH = process.env.NODE_ENV === "development" || localStorage.getItem("DEBUG_SEARCH") === "true";
  const [savedDeal, _setSavedDeal] = useState(JSON.parse(null));
  const [linkedFlights, setLinkedFlights] = useState(null);
  const [supplements, setSupplements] = useState(null);
  const [initSupplements, setInitSupplements] = useState(true);
  const [linkedFlightsDealPrice, setLinkedFlightsDealPrice] = useState(0);
  /** Dynamic package flight modal: full rows from search vs independent OW legs. */
  const [dynamicPackageFlightPickMode, setDynamicPackageFlightPickMode] = useState("readyMade");
  /** חבילות דינמיות: סינון תוצאות + פרופיל לפי ביטול עם deadline עתידי אמיתי */
  const [dynamicPackageFutureCxlOnly, setDynamicPackageFutureCxlOnly] = useState(false);
  const [selectedDepart, setSelectedDepart] = useState(null);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const lastHotelsRiverPricingSnapshotRef = useRef("");
  const [rangeSearchResults, setRangeSearchResults] = useState(null);
  const [ldsDates, setLdsDates] = useState(null);
  const [passengerInfo, setPassengerInfo] = useState(null);
  const [loading, setLoadingBase] = useState(false);
  const loadingRef = useRef(loading);
  const lastSetSearchLoadingSourceRef = useRef("(init)");
  const lastSetSearchLoadingMetaRef = useRef({ source: "(init)", at: 0 });
  /** _initBeforeSearch מגדיל; כל קריאה ל-_initAfterSearch (כולל stale) מקטין — מאזן סשנים כשמבטלים חיפוש קודם */
  const pendingMainSearchLoadsRef = useRef(0);
  /** Blocks remount/auto submitSearch from bumping RID while searchByCategory is the only in-flight search */
  const categorySearchInFlightRef = useRef(false);
  /** RID of the last finished searchByCategory — blocks post-results auto submitSearch until the user clicks Search */
  const categorySearchResultsRidRef = useRef(null);
  /** Skip the [categories] fetchLdsDates effect when restoring the same category snapshot */
  const suppressLdsDatesPrefetchRef = useRef(false);
  const searchTabRef = useRef(searchTab);
  const tourMonthRef = useRef(tourMonth);
  const calendarDateArrivalRef = useRef(calendarDateArrival);
  const calendarDateLeaveRef = useRef(calendarDateLeave);
  const packageMonthRef = useRef(packageMonth);
  const packageDateModeRef = useRef(packageDateMode);
  const flightTripTypeRef = useRef(flightTripType);
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);
  const setSearchLoading = useCallback((next, meta = {}) => {
    const prev = loadingRef.current;
    const m = meta || {};
    const tracePayload = {
      prev,
      next,
      source: m.source || "SearchContext.unattributed",
      requestId:
        m.requestId != null
          ? m.requestId
          : next
            ? currentSearchRequestIdRef.current
            : null,
      sessionId:
        m.sessionId != null
          ? m.sessionId
          : m.requestId != null
            ? m.requestId
            : next
              ? currentSearchRequestIdRef.current
              : null,
      endpoint: m.endpoint ?? null,
      reason: m.reason || (next ? "loading_on" : "loading_off"),
      stack: import.meta.env.DEV ? new Error().stack : undefined,
    };
    if (import.meta.env.DEV) {
      console.info("[SEARCH_LOADING_STATE_TRACE]", tracePayload);
    }
    lastSetSearchLoadingSourceRef.current = tracePayload.source;
    lastSetSearchLoadingMetaRef.current = { ...tracePayload, at: Date.now() };
    loadingRef.current = next;
    setLoadingBase(next);
  }, []);

  useEffect(() => {
    if (!loading) return;
    const tab = searchTabRef.current;
    const isHotelOrPackages =
      tab === PACKAGES || tab === HOTELS || tab === ORGANIZED_TOURS;
    if (!import.meta.env.DEV && !isHotelOrPackages) return;
    const timeoutMs = import.meta.env.DEV ? 45000 : 90000;
    const timer = window.setTimeout(() => {
      if (!loadingRef.current) return;
      if (import.meta.env.DEV) {
        console.warn("[SEARCH_LOADING_STUCK_TRACE]", {
          activeRequestId: currentSearchRequestIdRef.current,
          activeSessionId: searchHotelForMonthSessionRef.current,
          lastSetLoadingSource: lastSetSearchLoadingSourceRef.current,
          lastSetLoadingMeta: lastSetSearchLoadingMetaRef.current,
          pendingSearches: pendingMainSearchLoadsRef.current,
          currentPath:
            typeof window !== "undefined" ? window.location.pathname || "" : "",
        });
      }
      pendingMainSearchLoadsRef.current = 0;
      categorySearchInFlightRef.current = false;
      setSearchLoading(false, {
        source: import.meta.env.DEV
          ? "SearchContext.dev_watchdog"
          : "SearchContext.packages_watchdog",
        reason: "loading_watchdog_unstuck",
      });
    }, timeoutMs);
    return () => window.clearTimeout(timer);
  }, [loading, setSearchLoading]);

  useEffect(() => {
    console.log("[FLIGHT_SEARCH_DEBUG] loading", loading);
  }, [loading]);
  useEffect(() => {
    searchTabRef.current = searchTab;
    tourMonthRef.current = tourMonth;
    calendarDateArrivalRef.current = calendarDateArrival;
    calendarDateLeaveRef.current = calendarDateLeave;
    packageMonthRef.current = packageMonth;
    packageDateModeRef.current = packageDateMode;
    flightTripTypeRef.current = flightTripType;
  }, [
    searchTab,
    tourMonth,
    calendarDateArrival,
    calendarDateLeave,
    packageMonth,
    packageDateMode,
    flightTripType,
  ]);

  const bumpOrganizedTourSearchHandshake = useCallback(() => {
    if (typeof window === "undefined") return;
    if (searchTabRef.current !== ORGANIZED_TOURS) return;
    const path = String(window.location.pathname || "");
    if (!path.includes("/searchoffer")) return;
    setOrganizedTourSearchHandshake((n) => {
      const next = n + 1;
      if (import.meta.env?.DEV) {
        console.log("[ORG_TOUR_SEARCH_NAVIGATE_START]", { handshake: next });
      }
      return next;
    });
  }, []);

  /**
   * OrganizedTourSearchToResultsBridge remounts with routing/layout; a local useRef would reset
   * and re-fire navigate for the same handshake (logs: repeated H1 with identical handshake).
   */
  const organizedTourResultsBridgeHandledHandshakeRef = useRef(-1);

  useEffect(() => {
    if (searchTab !== ORGANIZED_TOURS) {
      organizedTourResultsBridgeHandledHandshakeRef.current = -1;
    }
  }, [searchTab]);

  const shouldSkipOrganizedTourResultsBridgeHandshake = useCallback((h) => {
    const n = Number(h) || 0;
    if (n <= 0) return true;
    return organizedTourResultsBridgeHandledHandshakeRef.current === n;
  }, []);

  const markOrganizedTourResultsBridgeHandshakeHandled = useCallback((h) => {
    const n = Number(h) || 0;
    if (n > 0) organizedTourResultsBridgeHandledHandshakeRef.current = n;
  }, []);

  /** Monotonic guard so stale parallel getLdsDates batches cannot overwrite newer results. */
  const fetchLdsDatesGenerationRef = useRef(0);
  const [error, setError] = useState(false);
  const [loadingDates, setLoadingDates] = useState(false);
  const [datesError, setDatesError] = useState(false);
  /** When set, "Did you mean" was applied; next getSearchPhrase uses this as hotelName. Cleared in fetchSearchData after use. */
  const [pendingAutocorrectApply, setPendingAutocorrectApply] = useState(null);
  /** Shown in UI when fuzzy match applied and confidence high; { originalQuery, suggestedQuery, confidence, candidateSource }. */
  const [autocorrectSuggestion, setAutocorrectSuggestion] = useState(null);
  /** When true, user dismissed the "Did you mean" banner for this search; reset in _initBeforeSearch. */
  const [autocorrectDismissed, setAutocorrectDismissed] = useState(false);
  /** Last autocorrect suggestion shown (for analytics on Apply click). */
  const lastAutocorrectSuggestionRef = useRef(null);

  React.useEffect(() => {
    getActiveDestinations().then((res) => {
      if (!res) return;
      setDestinations(res);
      setSubjects({
        FlightsHotel: res
          .filter((dest) => dest.dealType === "FlightsHotel")
          .map((dest) => dest.subject)
          .filter((value) => !!value)
          .reduce(
            (acc, curr) => {
              if (!acc.includes(curr)) acc.push(curr);
              return acc;
            },
            ["ALL"]
          ),
        OrganizedTours: res
          .filter((dest) => dest.dealType === "OrganizedTour")
          .map((dest) => dest.subject)
          .filter((value) => !!value)
          .reduce(
            (acc, curr) => {
              if (!acc.includes(curr)) acc.push(curr);
              return acc;
            },
            ["ALL"]
          ),
      });
    });
    // Suppliers load once in AppContext startup — avoid duplicate GET /loadLDSSuppliers (see runtime observability).
  }, []);

  // Use ref to prevent infinite loops when updating sessionStorage
  const lastRoomInfoRef = React.useRef(JSON.stringify(roomInfo));
  
  React.useEffect(() => {
    const currentRoomInfoStr = JSON.stringify(roomInfo);
    // Only update if roomInfo actually changed
    if (lastRoomInfoRef.current !== currentRoomInfoStr) {
      lastRoomInfoRef.current = currentRoomInfoStr;
      if (roomInfo.rooms.length > 0) {
        sessionStorage.setItem("roomData", JSON.stringify(roomInfo.rooms));
      }
    }
  }, [roomInfo]);

  // Use ref to prevent infinite loops when loading default destination
  const defaultDestinationLoadedRef = useRef(false);
  const lastLocationRef = useRef(null);
  
  // Load default destination when component mounts or when returning to page
  // This ensures that if user has set a default destination, it will be loaded
  // even if they navigate away and come back
  React.useEffect(() => {
    // Guard: Only load default destination once on mount, or when searchTab changes
    // Don't reload when location changes to prevent infinite loops
    const loadDefaultDestination = () => {
      // Only load default destination for LDS tabs (packages/organized tours/dynamic packages)
      // NOT for hotels - hotels use Google Places which has different format
      if (searchTab !== PACKAGES && searchTab !== ORGANIZED_TOURS && searchTab !== DYNAMIC_PACKAGES) {
        return; // Don't load default destination for hotels
      }
      
      // Check for LDS default destination first (for packages/organized tours)
      const defaultDestinationLDS = localStorage.getItem("defaultDestinationLDS");
      const defaultDestination = defaultDestinationLDS || localStorage.getItem("defaultDestination");
      
      // If there's a default destination, always load it (only for LDS format)
      if (defaultDestination) {
        try {
          const defaultDest = JSON.parse(defaultDestination);
          // Only use if it's LDS format (has CITYCODE or AIRPORTCODE)
          if (defaultDest.CITYCODE || defaultDest.AIRPORTCODE) {
            const currentLoc = location;
            
            // Use string comparison to avoid reference issues
            const currentLocStr = currentLoc ? JSON.stringify({ CITYCODE: currentLoc.CITYCODE, AIRPORTCODE: currentLoc.AIRPORTCODE }) : null;
            const defaultDestStr = JSON.stringify({ CITYCODE: defaultDest.CITYCODE, AIRPORTCODE: defaultDest.AIRPORTCODE });
            
            // Check if current location is different from default
            const isDifferent = currentLocStr !== defaultDestStr;
            
            // Only update if different AND we haven't already loaded this exact location
            // This prevents infinite loops
            if (isDifferent && lastLocationRef.current !== defaultDestStr) {
              lastLocationRef.current = defaultDestStr;
              _setLocation(defaultDest);
              localStorage.setItem("location", JSON.stringify(defaultDest));
              
              // For packages/organized tours/dynamic packages, fetch dates automatically
              if (searchTab === PACKAGES || searchTab === ORGANIZED_TOURS || searchTab === DYNAMIC_PACKAGES) {
                const primaryLocation = getPrimaryLocation(defaultDest);
                if (primaryLocation) {
                  fetchLdsDates(
                    searchTab === DYNAMIC_PACKAGES ? "Flights" : searchTab,
                    origin,
                    primaryLocation,
                    categories,
                    roomInfo
                  );
                }
              }
            }
          }
        } catch (e) {
          if (process.env.NODE_ENV === "development") {
            console.error("Error loading default destination:", e);
          }
        }
      }
    };

    // Load on mount only (not when location changes)
    if (!defaultDestinationLoadedRef.current) {
      loadDefaultDestination();
      defaultDestinationLoadedRef.current = true;
    }

    // Also listen for focus events (when user returns to tab/window)
    // But only reload if searchTab changed (not on every focus)
    const handleFocus = () => {
      // Small delay to ensure page is fully loaded
      // Only reload if searchTab is for packages (to avoid unnecessary calls)
      if (searchTab === PACKAGES || searchTab === ORGANIZED_TOURS || searchTab === DYNAMIC_PACKAGES) {
        setTimeout(() => {
          // Check again if location changed while away
          const defaultDestinationLDS = localStorage.getItem("defaultDestinationLDS");
          const defaultDestination = defaultDestinationLDS || localStorage.getItem("defaultDestination");
          if (defaultDestination) {
            try {
              const defaultDest = JSON.parse(defaultDestination);
              const defaultDestStr = JSON.stringify({ CITYCODE: defaultDest.CITYCODE, AIRPORTCODE: defaultDest.AIRPORTCODE });
              if (lastLocationRef.current !== defaultDestStr) {
                loadDefaultDestination();
              }
            } catch (e) {
              // Ignore errors on focus
            }
          }
        }, 100);
      }
    };

    // Listen for visibility change (when tab becomes visible)
    const handleVisibilityChange = () => {
      if (!document.hidden && (searchTab === PACKAGES || searchTab === ORGANIZED_TOURS || searchTab === DYNAMIC_PACKAGES)) {
        setTimeout(() => {
          const defaultDestinationLDS = localStorage.getItem("defaultDestinationLDS");
          const defaultDestination = defaultDestinationLDS || localStorage.getItem("defaultDestination");
          if (defaultDestination) {
            try {
              const defaultDest = JSON.parse(defaultDestination);
              const defaultDestStr = JSON.stringify({ CITYCODE: defaultDest.CITYCODE, AIRPORTCODE: defaultDest.AIRPORTCODE });
              if (lastLocationRef.current !== defaultDestStr) {
                loadDefaultDestination();
              }
            } catch (e) {
              // Ignore errors on visibility change
            }
          }
        }, 100);
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [searchTab, origin, categories, roomInfo]); // Removed location from dependencies to prevent infinite loops

  // Load searchPhrase and availableFlights from localStorage on mount (for new tabs)
  React.useEffect(() => {
    if (!searchPhrase) {
      try {
        const savedSearchPhrase = localStorage.getItem("currentSearchPhrase");
        if (savedSearchPhrase) {
          const parsed = JSON.parse(savedSearchPhrase);
          if (phraseBelongsToTab(parsed, searchTabRef.current)) {
            setSearchPhrase(parsed);
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error loading searchPhrase from localStorage:", error);
        }
      }
    }

    /**
     * Lean profile may skip the legacy full availableFlights blob only.
     * Compact flight snapshot + pricing selection must always rehydrate —
     * otherwise DP profile loses flights after refresh.
     */
    const skipLegacyFlightBlob = isHrDpLeanProfileEnabled();

    // Load availableFlights for dynamic packages: compact snapshot first, then legacy full blob.
    try {
      const savedSearchTab = localStorage.getItem("searchTab");
      const savedSearchPhraseHydrate = localStorage.getItem("currentSearchPhrase");
      let parsedSearchPhraseHydrate = null;
      if (savedSearchPhraseHydrate) {
        try {
          parsedSearchPhraseHydrate = JSON.parse(savedSearchPhraseHydrate);
        } catch {
          parsedSearchPhraseHydrate = null;
        }
      }
      const isDynamicPhrase =
        parsedSearchPhraseHydrate?.dynamic === true ||
        String(parsedSearchPhraseHydrate?.searchType || "")
          .toLowerCase()
          .includes("dynamic");
      const compactRaw =
        typeof sessionStorage !== "undefined"
          ? sessionStorage.getItem(HR_DP_FLIGHTS_LIST_SNAPSHOT_KEY)
          : null;

      let hydratedFromCompact = false;
      let compactSelection = null;
      if (
        compactRaw &&
        (savedSearchTab === DYNAMIC_PACKAGES || isDynamicPhrase)
      ) {
        try {
          const parsedCompact = JSON.parse(compactRaw);
          compactSelection = rehydrateFlightSelectionFromCompactSnapshot(parsedCompact);
          const rebuilt =
            compactSelection?.availableFlights ??
            rehydrateAvailableFlightsFromCompactSnapshot(parsedCompact);
          if (Array.isArray(rebuilt) && rebuilt.length > 0) {
            _setAvailableFlights(rebuilt);
            hydratedFromCompact = true;
          }
        } catch {
          /* legacy fallback below */
        }
      }

      if (!hydratedFromCompact && !skipLegacyFlightBlob) {
        const ssFlights =
          typeof sessionStorage !== "undefined"
            ? sessionStorage.getItem("availableFlights")
            : null;
        const lsFlights = localStorage.getItem("availableFlights");
        const savedFlights = ssFlights || lsFlights;
        if (savedFlights && (savedSearchTab === DYNAMIC_PACKAGES || isDynamicPhrase)) {
          const parsed = JSON.parse(savedFlights);
          if (parsed && (Array.isArray(parsed) ? parsed.length > 0 : parsed)) {
            _setAvailableFlights(parsed);
          }
        }
      }

      // Rehydrate dynamic-package pricing contract (flight selection + deal references).
      let pricingHadDepart = false;
      let pricingHadReturn = false;
      let pricingHadLinked = false;
      let pricingHadDealPrice = false;
      try {
        const rawSnapshot = sessionStorage.getItem(HOTELSRIVER_REHYDRATION_SNAPSHOT_KEY);
        if (rawSnapshot) {
          const snapshot = JSON.parse(rawSnapshot);
          if (snapshot && typeof snapshot === "object") {
            if (snapshot.selectedDepart) {
              pricingHadDepart = true;
              setSelectedDepart(snapshot.selectedDepart);
            }
            if (snapshot.selectedReturn) {
              pricingHadReturn = true;
              setSelectedReturn(snapshot.selectedReturn);
            }
            if (snapshot.dynamicPackageFlightPickMode) {
              setDynamicPackageFlightPickMode(snapshot.dynamicPackageFlightPickMode);
            }
            if (snapshot.savedDeal && typeof snapshot.savedDeal === "object") {
              _setSavedDeal(snapshot.savedDeal);
            }
            if (snapshot.linkedFlights && typeof snapshot.linkedFlights === "object") {
              pricingHadLinked = true;
              setLinkedFlights(snapshot.linkedFlights);
            }
            if (
              snapshot.linkedFlightsDealPrice != null &&
              Number.isFinite(Number(snapshot.linkedFlightsDealPrice))
            ) {
              pricingHadDealPrice = true;
              setLinkedFlightsDealPrice(Number(snapshot.linkedFlightsDealPrice));
            }
            if (process.env.NODE_ENV === "development") {
              console.info("[HOTELSRIVER_PACKAGE_REHYDRATION_PRICE_TRACE]", {
                phase: "after_rehydrate",
                dealId: snapshot?.dealId ?? null,
                packageId: snapshot?.packageId ?? null,
                selectedResultExists: false,
                currentHotelOrderExists: false,
                hasFlightPricing: Boolean(snapshot?.linkedFlightsDealPrice != null),
                hasHotelPricing: false,
                sourceCurrency: snapshot?.sourceCurrency ?? null,
                targetCurrency: "ILS",
                flightTotalRaw: snapshot?.linkedFlightsDealPrice ?? null,
                hotelTotalRaw: null,
                handlingFee: null,
                finalTotalRaw: snapshot?.finalTotalRaw ?? null,
                finalTotalIls: snapshot?.finalTotalIls ?? null,
                sourceOfPricing: snapshot?.sourceOfPricing ?? "search_context_rehydration_snapshot",
              });
            }
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.warn("[HOTELSRIVER_PACKAGE_REHYDRATION_PRICE_TRACE]", {
            phase: "after_rehydrate",
            error: error?.message || String(error),
          });
        }
      }

      // Fill gaps from compact snapshot (lean-safe; keeps flights bound after refresh).
      if (compactSelection) {
        if (!pricingHadDepart && compactSelection.selectedDepart) {
          setSelectedDepart(compactSelection.selectedDepart);
        }
        if (!pricingHadReturn && compactSelection.selectedReturn) {
          setSelectedReturn(compactSelection.selectedReturn);
        }
        if (!pricingHadLinked && compactSelection.linkedFlights) {
          setLinkedFlights(compactSelection.linkedFlights);
        }
        if (
          !pricingHadDealPrice &&
          compactSelection.linkedFlightsDealPrice != null &&
          Number.isFinite(Number(compactSelection.linkedFlightsDealPrice))
        ) {
          setLinkedFlightsDealPrice(Number(compactSelection.linkedFlightsDealPrice));
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error loading dynamic package flights from storage:", error);
      }
    }
  }, []); // Only run on mount

  React.useEffect(() => {
    if (searchTab !== DYNAMIC_PACKAGES) return;
    const snapshot = {
      phase: "before_refresh_source",
      searchTab,
      dealId:
        selectedDepart?.dealId ??
        selectedDepart?.ID ??
        selectedDepart?.id ??
        searchPhrase?.dealId ??
        null,
      packageId: searchPhrase?.hotelID ?? null,
      selectedDepart: selectedDepart || null,
      selectedReturn: selectedReturn || null,
      dynamicPackageFlightPickMode,
      savedDeal: savedDeal || null,
      linkedFlights: linkedFlights || null,
      linkedFlightsDealPrice: Number(linkedFlightsDealPrice || 0),
      sourceCurrency: searchPhrase?.currency || null,
      finalTotalRaw: null,
      finalTotalIls: null,
      sourceOfPricing: "SearchContext.dynamic_package_selection_snapshot",
      createdAt: Date.now(),
    };
    const serialized = JSON.stringify(snapshot);
    if (serialized === lastHotelsRiverPricingSnapshotRef.current) return;
    lastHotelsRiverPricingSnapshotRef.current = serialized;
    try {
      sessionStorage.setItem(HOTELSRIVER_REHYDRATION_SNAPSHOT_KEY, serialized);
    } catch {
      /* ignore */
    }
  }, [
    searchTab,
    selectedDepart,
    selectedReturn,
    dynamicPackageFlightPickMode,
    savedDeal,
    linkedFlights,
    linkedFlightsDealPrice,
    searchPhrase?.dealId,
    searchPhrase?.hotelID,
    searchPhrase?.currency,
  ]);

  React.useEffect(() => {
    if (error) {
      const hasResults = Array.isArray(searchResults) && searchResults.length > 0;
      if (!hasResults) {
        setSearchResults(null);
      }
      setError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, searchResults]); // Guard against clearing valid results when an error arrives late

  const setRoomInfo = useCallback((newRoomInfo) => {
    _setRoomInfo(newRoomInfo);
  }, []);

  function updateDates(newRoomInfo = roomInfo) {
    setSearchResults(null);
    if (
      searchTab === PLANES ||
      searchTab === PACKAGES ||
      searchTab === ORGANIZED_TOURS ||
      searchTab === DYNAMIC_PACKAGES
    ) {
      fetchLdsDates(
        searchTab === PLANES || searchTab === DYNAMIC_PACKAGES
          ? "Flights"
          : searchTab,
        origin,
        getPrimaryLocation(location),
        categories,
        newRoomInfo
      );
    }
  }

  function setOrigin(newOrigin) {
    setError(false);
    _setOrigin(newOrigin);
    setSearchResults(null);
    // Clear ALL data when origin changes for dynamic packages
    // to prevent showing data from the previous origin
    if (searchTab === DYNAMIC_PACKAGES || searchTab === PLANES) {
      // Clear localStorage items
      localStorage.removeItem("currentHotelOrder");
      localStorage.removeItem("availableFlights");
      try {
        sessionStorage.removeItem("availableFlights");
        sessionStorage.removeItem(HR_DP_FLIGHTS_LIST_SNAPSHOT_KEY);
      } catch {
        /* ignore */
      }
      localStorage.removeItem("currentSearchPhrase");
      
      // Dispatch custom events to notify components to clear state
      window.dispatchEvent(new CustomEvent('clearCurrentHotelOrder'));
      window.dispatchEvent(new CustomEvent('clearSelectedResult'));
      
      // Clear all state related to previous origin
      _setAvailableFlights(null);
      setSavedDeal(null);
      setSearchPhrase(null);
      setLinkedFlights(null);
      setSupplements(null);
      setLinkedFlightsDealPrice(0);
      setSelectedDepart(null);
      setSelectedReturn(null);
      setDynamicPackageFlightPickMode("readyMade");
      setRangeSearchResults(null);
      setPassengerInfo(null);
      setSessionId(null);
      setLdsDates(null); // Will be fetched again for new origin
      
      fetchLdsDates(
        searchTab === PLANES || searchTab === DYNAMIC_PACKAGES
          ? "Flights"
          : searchTab,
        newOrigin,
        getPrimaryLocation(location),
        categories,
        roomInfo
      );
    }
  }

  // Helper function to get the primary location (first location if array, or location itself)
  function getPrimaryLocation(loc) {
    if (Array.isArray(loc)) {
      if (loc.length > 0) return loc[0];
      return null;
    }
    if (loc == null) return null;
    return loc;
  }

  function recordAgentRecentSearchSnapshot(extraRaw, flightsDateOverride) {
    try {
      const dep =
        flightsDateOverride && Object.prototype.hasOwnProperty.call(flightsDateOverride, "dep")
          ? flightsDateOverride.dep
          : calendarDateArrival;
      const ret =
        flightsDateOverride && Object.prototype.hasOwnProperty.call(flightsDateOverride, "ret")
          ? flightsDateOverride.ret
          : calendarDateLeave;
      const recentPayload = buildAgentRecentSearchPayload({
        searchTab,
        primaryLocation: getPrimaryLocation(location),
        location,
        origin,
        flightTripType,
        calendarDateArrival: dep,
        calendarDateLeave: ret,
        roomInfo,
        extraRaw,
        tourMonth: searchTab === ORGANIZED_TOURS ? tourMonth : null,
      });
      if (isTripMasterSurface()) {
        recordTripMasterPublicSearch(recentPayload);
      } else {
        void persistAgentRecentSearch(recentPayload);
      }
    } catch (_) {
      /* ignore */
    }
  }

  // Helper function to get all locations (array of locations)
  function getAllLocations(loc) {
    if (Array.isArray(loc)) {
      return loc;
    }
    return loc ? [loc] : [];
  }

  /**
   * @param {object} newLocation
   * @param {{ preserveSearchState?: boolean }} [options] — כש־true: לא לאפס תאריכים/תוצאות חיפוש (סנכרון URL חבילות נופש / חזרה מתוצאות)
   */
  function setLocation(newLocation, options = {}) {
    const preserveSearchState = options?.preserveSearchState === true;
    // For dynamic packages, clear ALL data related to previous destination BEFORE changing location
    // to prevent showing old destination in hotel block
    if (searchTab === DYNAMIC_PACKAGES) {
      // Clear localStorage items
      localStorage.removeItem("currentHotelOrder");
      localStorage.removeItem("availableFlights");
      try {
        sessionStorage.removeItem("availableFlights");
        sessionStorage.removeItem(HR_DP_FLIGHTS_LIST_SNAPSHOT_KEY);
      } catch {
        /* ignore */
      }
      localStorage.removeItem("currentSearchPhrase");
      
      // Dispatch custom events to notify components to clear state
      window.dispatchEvent(new CustomEvent('clearCurrentHotelOrder'));
      window.dispatchEvent(new CustomEvent('clearSelectedResult'));
      
      // Clear all state related to previous destination
      _setAvailableFlights(null);
      setSavedDeal(null);
      setSearchPhrase(null);
      setLinkedFlights(null);
      setSupplements(null);
      setLinkedFlightsDealPrice(0);
      setSelectedDepart(null);
      setSelectedReturn(null);
      setDynamicPackageFlightPickMode("readyMade");
      setRangeSearchResults(null);
      setPassengerInfo(null);
      setSessionId(null);
      setLdsDates(null); // Will be fetched again for new location
    }
    
    // Save location to localStorage (but don't override defaultDestination)
    if (process.env.NODE_ENV === "development") {
      console.log("SearchContext setLocation called with:", newLocation);
      if (newLocation && newLocation.fullname) {
        console.log("SearchContext: Location has fullname:", newLocation.fullname);
      } else {
        console.log("SearchContext: Location missing fullname!", newLocation);
      }
    }
    localStorage.setItem("location", JSON.stringify(newLocation));
    setError(false);
    _setLocation(newLocation);
    if (
      import.meta.env?.DEV &&
      searchTab === ORGANIZED_TOURS &&
      isOrganizedTourAllDestinationsLocation(newLocation)
    ) {
      console.log("[ORG_TOUR_ALL_DESTINATIONS_SELECTED]", {
        location: newLocation,
      });
    }
    if (process.env.NODE_ENV === "development") {
      console.log("SearchContext: Location state updated");
    }
    if (!preserveSearchState) {
      setCalendarDateArrival("");
      setCalendarDateLeave("");
      setSearchResults(null);
    }
    if (
      searchTab === PLANES ||
      searchTab === PACKAGES ||
      searchTab === ORGANIZED_TOURS ||
      searchTab === DYNAMIC_PACKAGES
    ) {
      // Use primary location for fetching dates
      const primaryLocation = getPrimaryLocation(newLocation);
      if (primaryLocation) {
        fetchLdsDates(
          searchTab === PLANES || searchTab === DYNAMIC_PACKAGES
            ? "Flights"
            : searchTab,
          origin,
          primaryLocation,
          categories,
          roomInfo
        );
      }
    }
  }

  /** Updates only location display (fullname/CITYNAME) for "Did you mean" apply; does not clear calendar or results. */
  function updateLocationDisplayOnly(suggestedQuery) {
    if (!suggestedQuery || typeof suggestedQuery !== 'string') return;
    const trimmed = suggestedQuery.trim();
    const newLocation =
      location && typeof location === 'object'
        ? { ...location, fullname: trimmed, CITYNAME: trimmed }
        : { fullname: trimmed, CITYNAME: trimmed };
    try {
      localStorage.setItem("location", JSON.stringify(newLocation));
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.warn("updateLocationDisplayOnly: localStorage failed", e);
    }
    _setLocation(newLocation);
  }

  function setCategories(newCategories) {
    setError(false);
    _setLocation(LDS_AUTOCOMPLETE_MORE[0]);
    localStorage.setItem("location", JSON.stringify(LDS_AUTOCOMPLETE_MORE[0]));
    _setCategories(newCategories);
    setCalendarDateArrival("");
    setCalendarDateLeave("");
    setSearchResults(null);
    if (searchTab === PACKAGES || searchTab === ORGANIZED_TOURS) {
      fetchLdsDates(
        searchTab,
        origin,
        LDS_AUTOCOMPLETE_MORE[0],
        newCategories,
        roomInfo
      );
    }
  }

  function calculatFlightLDSPrice(deal) {
    if (!deal) {
      return 0;
    }
    /* מדלג על מנוע LDS בפרונט; 0 = אין סכום מחושב ב-FE (לא null כדי לא לשבור מיונים בלי שינוי בכל הקוראים) */
    if (isDynamicPackagePricingNormalizedEnabled() && searchTab === DYNAMIC_PACKAGES) {
      return 0;
    }
    // Extract deal properties and pass category
    const dealCategory = deal?.category || (categories && categories.length > 0 && !categories.includes("ALL") ? categories[0] : null);
    const priceResult = calculateLDSDealFare({
      ...deal,
      dealCategory: dealCategory,
      dealReferenceDate: extractLdsDealReferenceDate(deal),
    });
    const price = priceResult?.ourTotalFare?.priceWithCommissionAndCreditCardFee || 0;
    return price;
  }

  function selfComposeSsotFlightsTotalToIls(ssot, usdToIlsFn) {
    if (!ssot || !Number.isFinite(ssot.flightsTotal)) return null;
    const c = String(ssot.currency || "").trim().toUpperCase();
    if (c === "ILS" || c === "NIS" || c === "₪") return ssot.flightsTotal;
    if (c === "USD" || c === "US$" || c === "") {
      return typeof usdToIlsFn === "function" ? usdToIlsFn(ssot.flightsTotal, false) : null;
    }
    return null;
  }

  /**
   * Persist dynamic-package flight choice built from parallel OW results (no packaged pair / savedDeal).
   * @param {{ outboundDeal: object, inboundDeal?: object|null, usdToIls?: (n: number, fixed?: boolean) => number }} payload
   */
  function applyDynamicPackageSelfComposeSelection({ outboundDeal, inboundDeal = null, usdToIls: usdToIlsFn }) {
    if (!outboundDeal) return;
    const depLeg = firstLegFromLdsDeal(outboundDeal);
    const retLeg = inboundDeal ? firstLegFromLdsDeal(inboundDeal) : null;
    setSelectedDepart(depLeg || null);
    setSelectedReturn(inboundDeal ? retLeg || null : null);
    setDynamicPackageFlightPickMode("selfCompose");
    _setSavedDeal(null);
    const merged = mergeOwDealsToLinkedFlightsSlice(outboundDeal, inboundDeal || null);
    setLinkedFlights(merged);
    setSupplements(null);
    setInitSupplements(true);

    let flightIls = 0;
    if (
      isDynamicPackagePricingNormalizedEnabled() &&
      searchTab === DYNAMIC_PACKAGES
    ) {
      flightIls = 0;
    } else if (inboundDeal) {
      const ssot = computeSelfComposePricingSsot(outboundDeal, inboundDeal, roomInfo);
      const converted = selfComposeSsotFlightsTotalToIls(ssot, usdToIlsFn);
      if (converted != null && Number.isFinite(converted)) {
        flightIls = converted;
      } else {
        const a = calculatFlightLDSPrice(outboundDeal);
        const b = calculatFlightLDSPrice(inboundDeal);
        flightIls = (Number(a) || 0) + (Number(b) || 0);
      }
    } else {
      const a = calculatFlightLDSPrice(outboundDeal);
      flightIls = Number(a) || 0;
    }
    setLinkedFlightsDealPrice(flightIls);
  }

  function setSavedDeal(deal, savedSupplements = undefined) {
    const dpNormLocked =
      isDynamicPackagePricingNormalizedEnabled() && searchTab === DYNAMIC_PACKAGES;
    const storedDeal =
      dpNormLocked && deal
        ? sealLdsDealPriceBranchesForReadOnly(deal)
        : deal;
    _setSavedDeal(storedDeal);
    if (deal && searchTab === DYNAMIC_PACKAGES) {
      setDynamicPackageFlightPickMode("readyMade");
    }
    // Check for both dealId and ID (for dynamic packages)
    const hasDealId = deal && (deal["dealId"] || deal["ID"]);
    
    // Normalize linkedFlights structure - handle both array and object formats
    let normalizedLinkedFlights = null;
    if (deal?.linkedFlights) {
      // If it's an array, use it as is (for dynamic packages with multiple flight options)
      if (Array.isArray(deal.linkedFlights)) {
        normalizedLinkedFlights = deal.linkedFlights;
      } 
      // If it's an object with flight property, use it as is
      else if (deal.linkedFlights.flight) {
        normalizedLinkedFlights = deal.linkedFlights;
      }
      // If it's just an object, wrap it in the expected structure
      else {
        normalizedLinkedFlights = deal.linkedFlights;
      }
    }
    
    if (deal && (hasDealId || normalizedLinkedFlights)) {
      const supplements = savedSupplements
        ? savedSupplements
        : deal?.supplement
          ? deal.supplement
          : null;
      if (savedSupplements) {
        setInitSupplements(false);
      } else {
        setInitSupplements(true);
      }
      setSupplements(supplements);
      setLinkedFlights(normalizedLinkedFlights);

      if (dpNormLocked) {
        setLinkedFlightsDealPrice(0);
        return;
      }

      try {
        const linkedFlightsDealPrice = normalizedLinkedFlights
          ? calculatFlightLDSPrice(deal)
          : 0;
        const finalPrice = normalizedLinkedFlights
          ? Number(linkedFlightsDealPrice) || 0
          : 0;
        setLinkedFlightsDealPrice(finalPrice);
      } catch (e) {
        // Error calculating price - try to set linkedFlights anyway if it exists
        if (normalizedLinkedFlights) {
          setLinkedFlights(normalizedLinkedFlights);
          // Try to get price from deal
          try {
            const price = deal?.dealFare?.totalFare || deal?.price || 0;
            setLinkedFlightsDealPrice(price);
          } catch (e2) {
            setLinkedFlightsDealPrice(0);
          }
        } else {
          setLinkedFlights(null);
          setLinkedFlightsDealPrice(0);
        }
      }
    } else {
      setLinkedFlights(null);
      setSupplements(null);
      setLinkedFlightsDealPrice(0);
    }
  }

  function setAvailableFlights(
    newAvailableFlights,
    linkedFlights = undefined,
    supplements = undefined
  ) {
    if (!newAvailableFlights) {
      _setAvailableFlights(null);
      setSavedDeal(null);
      return;
    }
    if (!Array.isArray(newAvailableFlights))
      newAvailableFlights = [newAvailableFlights];
    _setAvailableFlights(newAvailableFlights);
    let deal = null;
    if (linkedFlights) {
      const deals = newAvailableFlights.filter((deal) => {
        // Check if all flights in deal.flight match those in linkedFlights.flight by leg.supplierId and leg.supplierFlightId
        if (
          !deal.linkedFlights ||
          !deal.linkedFlights.flight ||
          !linkedFlights.flight
        )
          return false;
        if (deal.linkedFlights.flight.length !== linkedFlights.flight.length)
          return false;
        return deal.linkedFlights.flight.every((flight, idx) => {
          const dealLeg = flight.leg;
          const linkedLeg = linkedFlights.flight[idx]?.leg;
          return (
            dealLeg &&
            linkedLeg &&
            dealLeg.supplierId === linkedLeg.supplierId &&
            dealLeg.arrDate === linkedLeg.arrDate &&
            dealLeg.depDate === linkedLeg.depDate &&
            dealLeg.airlineId === linkedLeg.airlineId
          );
        });
      });
      if (deals.length > 0) {
        deal = deals.find((foundDeal) => {
          // const foundDealPrice = calculatFlightLDSPrice(foundDeal);
          try {
            return (
              parseInt(foundDeal.dealFare.totalFare) ===
              parseInt(linkedFlights.ldsPrice)
            );
          } catch (e) {
            return false;
          }
        });
        if (!deal) {
          // If no deal found with exact price, take the first one
          deal = deals.reduce((minDeal, currentDeal) => {
            return currentDeal.dealFare.totalFare < minDeal.dealFare.totalFare
              ? currentDeal
              : minDeal;
          }, deals[0]);
        }
      }
    }
    if (deal == null) {
      if (linkedFlights) {
        notifyAgentSearch("error", {
          message: i18n.t("notifications.flightsNotAvailable"),
          description: i18n.t("notifications.flightsNotAvailableDescription"),
          duration: 5,
          placement: "bottomRight",
        });
      }
      if (Array.isArray(newAvailableFlights)) {
        // Filter out deals with missing pricing data before sorting
        const validDeals = newAvailableFlights.filter((d) => {
          if (!d) return false;
          if (!d.dealFare || !d.fareDetails || !d.supplierDetails) {
            return false;
          }
          return true;
        });
        
        if (validDeals.length === 0) {
          // Show notification to user
          notifyAgentSearch("warning", {
            message: i18n.t("notifications.pricingIssue"),
            description: i18n.t("notifications.pricingIssueDescription"),
            duration: 5,
            placement: "bottomRight",
          });
          // Still set the first deal
          deal = newAvailableFlights[0];
        } else {
          deal = validDeals.sort((a, b) => {
            const aPrice = Number(calculatFlightLDSPrice(a)) || 0;
            const bPrice = Number(calculatFlightLDSPrice(b)) || 0;
            return aPrice - bPrice;
          })[0];
        }
      } else {
        deal = newAvailableFlights;
      }
    }
    setSavedDeal(deal, supplements);
  }

  function reRunSearch() {
    categorySearchResultsRidRef.current = null;
    setTimeLeft(TWENTY_MIN);
    setSearchResults(null);
    setRangeSearchResults(null);
    _initAfterSearch(currentSearchRequestIdRef.current);
    setDatesError(false);
    setLoadingDates(false);
    setError(false);
    submitSearch();
  }

  function restoreAgentRecentSearchFromRow(row) {
    const plan = resolveAgentRecentSearchRestorePlan(row);
    if (!plan.ok) {
      if (import.meta.env?.DEV) {
        console.warn("[RECENT_SEARCH_RESTORE_REJECT]", {
          reason: plan.reason,
          rowId: row?.id ?? null,
          category: row?.category ?? null,
        });
      }
      return plan;
    }

    const s = plan.state;

    if (s.variant === "belvedere_campaign") {
      if (typeof window !== "undefined" && s.url) {
        window.open(s.url, "_blank", "noopener,noreferrer");
      }
      return { ok: true };
    }

    if (s.variant === "monthly_hotel_lds_grid") {
      if (s.roomInfo) _setRoomInfo(s.roomInfo);
      void searchHotelForMonth(
        s.hotelId,
        s.hotelName,
        s.month,
        s.year,
        s.monthsCount,
      );
      return { ok: true };
    }

    if (s.origin) _setOrigin(s.origin);
    if (s.location != null) {
      _setLocation(s.location);
      try {
        localStorage.setItem("location", JSON.stringify(s.location));
      } catch (_) {
        /* ignore */
      }
    }
    if (s.roomInfo) _setRoomInfo(s.roomInfo);
    if (s.flightTripType) _setFlightTripType(s.flightTripType);
    if (s.calendarDateArrival != null) {
      setCalendarDateArrival(s.calendarDateArrival);
    }
    if (s.calendarDateLeave != null) {
      setCalendarDateLeave(s.calendarDateLeave);
    }
    if (s.tourMonth != null) setTourMonth(s.tourMonth);
    if (s.packageDateMode) _setPackageDateMode(s.packageDateMode);
    if (s.packageMonth != null) _setPackageMonth(s.packageMonth);

    setSearchTab(s.searchTab, { source: "recent_search_restore" });

    setSearchResults(null);
    setRangeSearchResults(null);
    setIsSearched(false);
    setError(false);
    pendingRecentSearchRestoreRef.current = {
      rowId: row?.id ?? null,
      at: Date.now(),
    };
    setPendingRecentSearchRestoreTick((tick) => tick + 1);
    return { ok: true };
  }

  /** Apply "Did you mean" suggestion: update search field, then re-run search (via useEffect when pendingAutocorrectApply is set). */
  function applyAutocorrectSuggestion(suggestedQuery) {
    if (!suggestedQuery || typeof suggestedQuery !== 'string') return;
    const trimmed = suggestedQuery.trim();
    const lastSuggestion = lastAutocorrectSuggestionRef.current;
    if (lastSuggestion) {
      trackDidYouMeanClicked({
        originalQuery: lastSuggestion.originalQuery ?? trimmed,
        suggestedQuery: trimmed,
      });
      lastAutocorrectSuggestionRef.current = null;
    }
    updateLocationDisplayOnly(trimmed);
    setAutocorrectSuggestion(null);
    setAutocorrectDismissed(true);
    setPendingAutocorrectApply(trimmed);
  }

  /** Run search when user applied "Did you mean" (pendingAutocorrectApply set); no setTimeout, deterministic. */
  useEffect(() => {
    if (!pendingAutocorrectApply) return;
    submitSearch();
  }, [pendingAutocorrectApply]);

  /** Run search after recent-search row restore rehydrates form state. */
  useEffect(() => {
    if (!pendingRecentSearchRestoreRef.current) return;
    pendingRecentSearchRestoreRef.current = null;
    void submitSearch({ source: "recent_search_restore" });
  }, [pendingRecentSearchRestoreTick]);

  /** Dismiss "Did you mean" banner for this search so it does not show again until next search. */
  function dismissAutocorrectSuggestion() {
    setAutocorrectDismissed(true);
  }

  function parseSearchDateToMonthKey(dateStr) {
    const s = String(dateStr || "").trim();
    if (!s) return "";
    const strict = dayjs(s, "DD/MM/YYYY", true);
    if (strict.isValid()) return strict.format("MM/YYYY");
    const loose = dayjs(s);
    return loose.isValid() ? loose.format("MM/YYYY") : "";
  }

  /** When params are locked, map exact dates ↔ month fields across product tabs. */
  function syncLockedSearchDatesForCategoryChange(nextCategory) {
    const arrival = String(calendarDateArrivalRef.current || "").trim();
    const leave = String(calendarDateLeaveRef.current || "").trim();
    const tourMonthValue = String(tourMonthRef.current || "").trim();
    const packageMonthValue = String(packageMonthRef.current || "").trim();

    if (nextCategory === ORGANIZED_TOURS) {
      if (!tourMonthValue) {
        const derivedMonth =
          packageMonthValue || parseSearchDateToMonthKey(arrival);
        if (derivedMonth) setTourMonth(derivedMonth);
      }
      return;
    }

    const usesExactDates =
      nextCategory === HOTELS ||
      nextCategory === PACKAGES ||
      nextCategory === PLANES ||
      nextCategory === DYNAMIC_PACKAGES;
    if (!usesExactDates) return;

    if (nextCategory === PACKAGES && packageDateModeRef.current === "month") {
      if (!packageMonthValue) {
        const derivedMonth = tourMonthValue || parseSearchDateToMonthKey(arrival);
        if (derivedMonth) _setPackageMonth(derivedMonth);
      }
      return;
    }

    if (arrival) return;

    const sourceMonth = tourMonthValue || packageMonthValue;
    if (!sourceMonth) return;

    const monthParsed = dayjs(sourceMonth, "MM/YYYY", true);
    if (!monthParsed.isValid()) return;

    setCalendarDateArrival(monthParsed.startOf("month").format("DD/MM/YYYY"));
    const needsReturn =
      nextCategory !== PLANES || flightTripTypeRef.current !== "OneWay";
    if (needsReturn && !leave) {
      setCalendarDateLeave(monthParsed.endOf("month").format("DD/MM/YYYY"));
    }
  }

  const setSearchTab = useCallback((newSearchTab, options = {}) => {
    const source =
      typeof options === "string"
        ? options
        : String(options?.source || "context_sync");
    const previousCategory = searchTabRef.current;
    const nextCategory = SEARCH_CATEGORY_VALUES.has(newSearchTab)
      ? newSearchTab
      : previousCategory;

    // G.4E — Dynamic Package not an active product; refuse tab activation.
    if (
      nextCategory === DYNAMIC_PACKAGES &&
      isDynamicPackagesTemporarilyDisabled()
    ) {
      reportHrDpTempDisabledTrace({
        userRole: resolveDynamicPackagesUserRoleForTrace(),
        route:
          typeof window !== "undefined" ? window.location.pathname || "" : "",
        gateTriggered: "search_tab_set_dynamic",
        bypassAdmin: false,
        blockedReason: "product_not_active_g4e_tab",
      });
      return;
    }

    if (source === "public_engine_tab") {
      if (previousCategory !== nextCategory) {
        searchTabRef.current = nextCategory;
        try {
          localStorage.setItem("searchTab", nextCategory);
        } catch (_) {
          /* ignore */
        }
        emitSearchCategoryTrace({
          previousCategory,
          nextCategory,
          source,
          note: "public_engine_tab_ref_only",
        });
      }
      return;
    }

    if (source === "recent_search_restore" || source === "public_home_search") {
      if (previousCategory !== nextCategory) {
        searchTabRef.current = nextCategory;
        _setSearchTab(nextCategory);
        try {
          localStorage.setItem("searchTab", nextCategory);
        } catch (_) {
          /* ignore */
        }
        emitSearchCategoryTrace({
          previousCategory,
          nextCategory,
          source,
          note:
            source === "public_home_search"
              ? "public_home_search_tab_only"
              : "recent_restore_tab_only",
        });
      }
      return;
    }

    if (previousCategory === nextCategory) {
      emitSearchCategoryTrace({
        previousCategory,
        nextCategory,
        source,
        note: "skip_same_value",
      });
      return;
    }

    if (source === "user_click" || source === "form_change") {
      markUserTouchedCategory(true);
    }

    emitSearchCategoryTrace({
      previousCategory,
      nextCategory,
      source,
    });

    if (previousCategory === ORGANIZED_TOURS && nextCategory !== ORGANIZED_TOURS) {
      setOrganizedTourSearchHandshake(0);
    }

    searchTabRef.current = nextCategory;
    _setSearchTab(nextCategory);
    localStorage.setItem("searchTab", nextCategory);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (searchHotelForMonthAbortRef.current) {
      searchHotelForMonthAbortRef.current.abort();
      searchHotelForMonthAbortRef.current = null;
    }
    currentSearchRequestIdRef.current += 1;
    setCurrentSearchRequestId(currentSearchRequestIdRef.current);
    searchCommitSnapshotRef.current = {
      requestId: currentSearchRequestIdRef.current,
      product: nextCategory,
      destinationKey: "",
      checkIn: null,
      checkOut: null,
      occupancySignature: null,
    };
    setSearchPhrase((prev) => (phraseBelongsToTab(prev, nextCategory) ? prev : null));
    try {
      const rawPhrase = localStorage.getItem("currentSearchPhrase");
      if (rawPhrase && !phraseBelongsToTab(JSON.parse(rawPhrase), nextCategory)) {
        localStorage.removeItem("currentSearchPhrase");
        sessionStorage.removeItem("currentSearchPhrase");
      }
    } catch (_) {
      /* phrase binding only — price and profile caches stay */
    }
    setSearchResults(null);
    setRangeSearchResults(null);
    setError(false);
    pendingMainSearchLoadsRef.current = 0;
    categorySearchInFlightRef.current = false;
    categorySearchResultsRidRef.current = null;
    setSearchLoading(false, {
      source: "SearchContext.setSearchTab",
      reason: "category_change_clear",
    });
    setPassengerInfo(null);
    setIsSearched(false);
    const preserveSearchParams =
      searchParamsLockedRef.current ||
      (typeof options === "object" && options?.preserveSearchParams === true);
    if (!preserveSearchParams) {
      _setLocation("");
      _setOrigin({
        COUNTRYNAME_HEB: "ישראל",
        CITYNAME_HEB: "תל אביב",
        AIRPORTCODE: "TLV",
        AIRPORTNAME: 'נתב"ג',
        CITYCODE: "TLV",
        CITYNAME: "TEL AVIV",
        COUNTRYCODE: "IL",
        COUNTRYNAME: "ISRAEL",
      });
      _setRoomInfo(getInitRoomInfo(true));
      setCategories(["ALL"]);
      setLdsDates(null);
    } else {
      _setCategories(["ALL"]);
    }
    if (nextCategory === DYNAMIC_PACKAGES) {
      _setFlightTripType("RoundTrip");
    }
    if (nextCategory !== DYNAMIC_PACKAGES) {
      _setAvailableFlights(null);
      setDynamicPackageFutureCxlOnly(false);
    }
    setRoundTripFlightResults(null);
    setOutboundOneWayFlightResults(null);
    setInboundOneWayFlightResults(null);
    setParallelRoundTripFlightSearchCompleted(false);
    setFlightResultsBrowseMode("roundTrip");
    setSelectedSelfComposeOutboundDeal(null);
    setSelectedSelfComposeInboundDeal(null);
    if (searchParamsLockedRef.current) {
      syncLockedSearchDatesForCategoryChange(nextCategory);
    }
  }, [markUserTouchedCategory]);

  const switchFromOrganizedToursIfNeeded = useCallback((nextSearchTab) => {
    if (searchTabRef.current !== ORGANIZED_TOURS) return;
    setSearchTab(nextSearchTab, { source: "leave_organized_module" });
  }, [setSearchTab]);

  const clearPublicEmptySearch = useCallback(() => {
    setSearchResults(null);
    setError(false);
    setIsSearched(false);
  }, []);

  useEffect(() => {
    // Campaign booking/landing mounts SearchProvider globally — never blast getLdsDates there.
    if (!shouldPrefetchLdsDatesOnCurrentRoute()) {
      fetchLdsDatesGenerationRef.current += 1;
      setLoadingDates(false);
      return;
    }
    if (
      searchTab !== PLANES &&
      searchTab !== PACKAGES &&
      searchTab !== ORGANIZED_TOURS &&
      searchTab !== DYNAMIC_PACKAGES
    ) {
      return;
    }
    if (suppressLdsDatesPrefetchRef.current) {
      suppressLdsDatesPrefetchRef.current = false;
      return;
    }
    const primaryLocation = getPrimaryLocation(location);
    if (!primaryLocation) return;
    fetchLdsDates(
      searchTab === PLANES || searchTab === DYNAMIC_PACKAGES
        ? "Flights"
        : searchTab,
      origin,
      primaryLocation,
      categories,
      roomInfo
    );
  }, [searchTab, origin, location, categories, roomInfo]);

  // Invalidate in-flight 12-month LDS date batches when entering a campaign route
  // (SearchProvider sits outside <Router>, so we patch history).
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const invalidateIfCampaign = () => {
      if (!isCampaignPublicPathname(window.location.pathname)) return;
      fetchLdsDatesGenerationRef.current += 1;
      setLoadingDates(false);
    };
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    window.history.pushState = function hrCampaignAwarePushState(...args) {
      const ret = origPush.apply(this, args);
      invalidateIfCampaign();
      return ret;
    };
    window.history.replaceState = function hrCampaignAwareReplaceState(...args) {
      const ret = origReplace.apply(this, args);
      invalidateIfCampaign();
      return ret;
    };
    window.addEventListener("popstate", invalidateIfCampaign);
    return () => {
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
      window.removeEventListener("popstate", invalidateIfCampaign);
    };
  }, []);

  const forceStopLoading = useCallback(() => {
    pendingMainSearchLoadsRef.current = 0;
    categorySearchInFlightRef.current = false;
    categorySearchResultsRidRef.current = null;
    setSearchLoading(false, {
      source: "SearchContext.forceStopLoading",
      reason: "force_stop",
    });
  }, [setSearchLoading]);

  function logFlightsLoadingStateAudit(payload) {
    if (typeof import.meta === "undefined" || !import.meta.env?.DEV) return;
    console.log("[FLIGHTS_LOADING_STATE_AUDIT]", payload);
  }

  function _initBeforeSearch() {
    setAutocorrectSuggestion(null);
    setAutocorrectDismissed(false);
    // Cancel any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      if (DEBUG_SEARCH) {
        console.log('[SearchContext] Cancelled previous search request');
      }
    }
    if (searchHotelForMonthAbortRef.current) {
      searchHotelForMonthAbortRef.current.abort();
      searchHotelForMonthAbortRef.current = null;
    }
    searchHotelForMonthSessionRef.current += 1;

    // Create new AbortController for this search
    abortControllerRef.current = new AbortController();
    
    // Increment request ID to track this search
    currentSearchRequestIdRef.current += 1;
    const requestId = currentSearchRequestIdRef.current;
    setCurrentSearchRequestId(requestId);
    pendingMainSearchLoadsRef.current += 1;

    if (DEBUG_SEARCH) {
      console.log(`[SearchContext] Starting search #${requestId}`);
    }
    console.log("[FLIGHT_SEARCH_DEBUG] searchStarted", {
      requestId,
      searchTab,
      flightTripType,
    });
    
    setError(false);
    setSearchLoading(true, {
      source: "SearchContext._initBeforeSearch",
      requestId,
      reason: "search_request_started",
    });
    setSearchResults(null);
    if (searchTabRef.current === PACKAGES && typeof window !== "undefined") {
      const occupancySignature = buildPackageOccupancySignature({
        rooms: roomInfo?.rooms,
        destination:
          getPrimaryLocation(location)?.CITYCODE ||
          getPrimaryLocation(location)?.AIRPORTCODE ||
          null,
        dates: {
          dep: calendarDateArrival || packageMonth || null,
          ret: calendarDateLeave || null,
        },
      });
      window.dispatchEvent(
        new CustomEvent(HR_VACATION_PACKAGE_NEW_SEARCH_EVENT, {
          detail: { searchId: requestId, occupancySignature },
        }),
      );
      logPackageOccupancyTrace("search_submit", {
        searchId: requestId,
        roomsComposition: roomInfo?.rooms,
        currentOccupancySignature: occupancySignature,
      });
    }
    setTargetHotelDedupKey(null);
    setAvailableFlights(null);
    setPassengerInfo(null);
    setRoundTripFlightResults(null);
    setOutboundOneWayFlightResults(null);
    setInboundOneWayFlightResults(null);
    setParallelRoundTripFlightSearchCompleted(false);
    setFlightResultsBrowseMode("roundTrip");
    setSelectedSelfComposeOutboundDeal(null);
    setSelectedSelfComposeInboundDeal(null);
    setDynamicPackageFlightPickMode("readyMade");
    setPlanesFlightSearchOutcome(null);
    setFlightAlternativeDateSuggestions([]);
    setFlightAlternativesProbeLoading(false);
    setAttractionsSearchResults([]);
    setAttractionsSearchLoading(false);
    setAttractionsSearchError(null);
    setAttractionsSearchMeta(null);
    console.log("[FLIGHT_SEARCH_DEBUG] loading", true);
    logFlightsLoadingStateAudit({
      source: "SearchContext",
      phase: "search_request_started",
      requestId,
      searchTab,
      loading: true,
      pathname:
        typeof window !== "undefined" ? window.location.pathname || "" : "",
    });

    searchCommitSnapshotRef.current = {
      requestId,
      product: searchTabRef.current,
      destinationKey: destinationKeyFromLocation(location),
      checkIn: calendarDateArrivalRef.current || null,
      checkOut: calendarDateLeaveRef.current || null,
      occupancySignature: buildPackageOccupancySignature({ rooms: roomInfo?.rooms }),
    };

    return requestId;
  }

  const logHotelPerfAudit = useCallback(
    (tag, bucket, payload, minIntervalMs = 8000) => {
      if (!import.meta.env.DEV) return;
      const now = Date.now();
      const last = hotelSearchPerfLastLogAtRef.current.get(bucket) || 0;
      if (now - last < minIntervalMs) return;
      hotelSearchPerfLastLogAtRef.current.set(bucket, now);
      console.log(tag, payload);
    },
    []
  );

  function _logLdsFlightDealsFromSearch(dealNode) {
    const list = Array.isArray(dealNode)
      ? dealNode
      : dealNode
        ? [dealNode]
        : [];
    list.forEach((deal) => {
      console.log("RAW LDS DEAL", deal);
      console.log("[searchFlights] displayed price source (supplier)", {
        dealId: deal?.dealId ?? deal?.ID,
        totalFare: deal?.dealFare?.totalFare,
        currency: deal?.fareDetails?.currency,
        supplementFare: deal?.supplement?.fare,
      });
    });
  }

  function isSearchLdsAbortError(err) {
    return (
      err?.name === "AbortError" ||
      err?.name === "CanceledError" ||
      err?.code === "ERR_CANCELED" ||
      axios.isCancel?.(err) === true
    );
  }

  /**
   * @param {string} [source]
   * @param {object} [extra]
   */
  function logSearchFlowTrace(source, extra = {}) {
    if (!import.meta.env?.DEV) return;
    console.info("[SEARCH_FLOW_TRACE]", {
      source: source || "SearchContext",
      ...extra,
    });
  }

  async function searchLdsRequestTracked(payload, meta = {}) {
    const { requestId, source, signal } = meta;
    const endpoint = "/searchLds";
    const latest =
      requestId == null || requestId === currentSearchRequestIdRef.current;
    logSearchFlowTrace(source || "searchLds", {
      requestId: requestId ?? null,
      endpoint,
      started: true,
      aborted: !!signal?.aborted,
      finished: false,
      ignoredStaleResponse: false,
      isLatestRequest: latest,
      setLoading: null,
      reason: "request_start",
    });
    try {
      const res = await searchLds(payload, { signal });
      logSearchFlowTrace(source || "searchLds", {
        requestId: requestId ?? null,
        endpoint,
        started: true,
        aborted: !!signal?.aborted,
        finished: true,
        ignoredStaleResponse: false,
        isLatestRequest:
          requestId == null ||
          requestId === currentSearchRequestIdRef.current,
        setLoading: null,
        reason: "request_success",
      });
      return res;
    } catch (err) {
      const aborted = isSearchLdsAbortError(err);
      logSearchFlowTrace(source || "searchLds", {
        requestId: requestId ?? null,
        endpoint,
        started: true,
        aborted,
        finished: true,
        ignoredStaleResponse: false,
        isLatestRequest:
          requestId == null ||
          requestId === currentSearchRequestIdRef.current,
        setLoading: null,
        reason: aborted ? "aborted_or_canceled" : "request_error",
      });
      throw err;
    }
  }

  /** Pass finishedRequestId so stale completions from a previous AbortController do not clear the main search loader. */
  function _initAfterSearch(finishedRequestId) {
    pendingMainSearchLoadsRef.current = Math.max(
      0,
      pendingMainSearchLoadsRef.current - 1
    );
    const isLatest =
      finishedRequestId == null ||
      finishedRequestId === currentSearchRequestIdRef.current;
    const noPending = pendingMainSearchLoadsRef.current === 0;
    if (!isLatest && !noPending) {
      logSearchFlowTrace("SearchContext._initAfterSearch", {
        requestId: finishedRequestId,
        endpoint: null,
        started: false,
        aborted: false,
        finished: true,
        ignoredStaleResponse: true,
        isLatestRequest: false,
        setLoading: false,
        reason: "skip_clear_loading_stale_session",
      });
      return;
    }
    setSearchLoading(false, {
      source: "SearchContext._initAfterSearch",
      requestId: finishedRequestId ?? null,
      reason: isLatest
        ? "clear_loading_latest_session"
        : "clear_loading_no_pending_after_stale_session",
    });
    if (import.meta.env?.DEV && searchTabRef.current === ORGANIZED_TOURS) {
      console.log("[ORG_TOUR_SEARCH_LOADING_OFF]");
    }
    console.log("[FLIGHT_SEARCH_DEBUG] loading", false);
    logFlightsLoadingStateAudit({
      source: "SearchContext",
      phase: "search_request_finished",
      requestId: finishedRequestId ?? null,
      loading: false,
      pathname:
        typeof window !== "undefined" ? window.location.pathname || "" : "",
    });
    logSearchFlowTrace("SearchContext._initAfterSearch", {
      requestId: finishedRequestId ?? null,
      endpoint: null,
      started: false,
      aborted: false,
      finished: true,
      ignoredStaleResponse: false,
      isLatestRequest: true,
      setLoadingApplied: true,
      reason: "clear_loading_latest_session",
    });
  }

  function commitActiveSearchRows(rows, requestId, flow) {
    const snap = searchCommitSnapshotRef.current;
    const responseProduct = snap?.requestId === requestId ? snap.product : null;
    const decision = shouldCommitSearchResponse({
      responseSearchId: requestId,
      activeSearchId: currentSearchRequestIdRef.current,
      responseProduct,
      activeProduct: searchTabRef.current,
    });
    if (!decision.accept) {
      if (import.meta.env?.DEV) {
        console.warn("[SEARCH_RESULT_REJECTED]", { ...decision, flow, requestId });
      }
      return false;
    }
    const identity = buildActiveSearchIdentity({
      searchId: requestId,
      productType: responseProduct || searchTabRef.current,
      destinationKey:
        snap?.requestId === requestId
          ? snap.destinationKey
          : destinationKeyFromLocation(location),
      checkIn: snap?.requestId === requestId ? snap.checkIn : calendarDateArrivalRef.current,
      checkOut: snap?.requestId === requestId ? snap.checkOut : calendarDateLeaveRef.current,
      occupancySignature: snap?.requestId === requestId ? snap.occupancySignature : null,
    });
    lastSetSearchResultsRef.current = requestId;
    setSearchResults(stampSearchRows(rows, identity));
    return true;
  }

  function scheduleFlightAlternativesProbe({
    flightTripType: tripForProbe,
    originToken,
    destToken,
    dep,
    ret,
    rooms,
    requestId: probeRequestId,
  }) {
    setFlightAlternativeDateSuggestions([]);
    setFlightAlternativesProbeLoading(true);
    void (async () => {
      try {
        let found = [];
        const probeAgentId = flightSearchAttributionAgentIdRef.current;
        if (tripForProbe === "RoundTrip") {
          found = await probeRoundTripFlightAlternatives({
            originToken,
            destToken,
            depDate: dep,
            retDate: ret,
            roomCompositions: rooms,
            agentId: probeAgentId || undefined,
            isStale: () =>
              currentSearchRequestIdRef.current !== probeRequestId,
          });
        } else {
          found = await probeOneWayFlightAlternatives({
            originToken,
            destToken,
            depDate: dep,
            roomCompositions: rooms,
            agentId: probeAgentId || undefined,
            isStale: () =>
              currentSearchRequestIdRef.current !== probeRequestId,
          });
        }
        if (currentSearchRequestIdRef.current !== probeRequestId) return;
        setFlightAlternativeDateSuggestions(found);
      } finally {
        if (currentSearchRequestIdRef.current === probeRequestId) {
          setFlightAlternativesProbeLoading(false);
        }
      }
    })();
  }

  async function submitSearch(options = {}) {
    const source = options?.source || "unknown";
    if (SEARCH_CATEGORY_VALUES.has(options?.searchTabOverride)) {
      searchTabRef.current = options.searchTabOverride;
    }
    const tab = searchTabRef.current;
    const pathname =
      typeof window !== "undefined" ? window.location.pathname || "" : "";
    if (
      tab === DYNAMIC_PACKAGES &&
      isDynamicPackagesTemporarilyDisabled()
    ) {
      reportHrDpTempDisabledTrace({
        userRole: resolveDynamicPackagesUserRoleForTrace(),
        route: pathname,
        gateTriggered: "search_submit",
        bypassAdmin: false,
        blockedReason: "maintenance_blocked_non_admin_dp_search_submit",
      });
      return;
    }
    if (
      tab === PLANES &&
      pathname.includes("/flights/results") &&
      source !== "button"
    ) {
      return;
    }
    if (source === "button" || source === "recent_search_restore") {
      categorySearchResultsRidRef.current = null;
    }
    if (
      (categorySearchInFlightRef.current ||
        categorySearchResultsRidRef.current != null) &&
      source !== "button" &&
      source !== "recent_search_restore"
    ) {
      logSearchFlowTrace("SearchContext.submitSearch", {
        requestId: currentSearchRequestIdRef.current,
        endpoint: null,
        started: false,
        aborted: false,
        finished: false,
        ignoredStaleResponse: false,
        isLatestRequest: true,
        setLoading: null,
        reason: categorySearchInFlightRef.current
          ? "skip_auto_submit_during_category_search"
          : "skip_auto_submit_after_category_results",
        source,
      });
      return;
    }
    if (tab === PLANES) {
      const dep = flightSearchDateOverridesRef.current?.dep ?? calendarDateArrival;
      const ret =
        flightSearchDateOverridesRef.current?.ret !== undefined
          ? flightSearchDateOverridesRef.current.ret
          : calendarDateLeave;
      const fingerprint = JSON.stringify({
        searchTab: tab,
        flightTripType,
        originToken: resolveOriginTokenForSearchFlights(origin),
        destinationToken: resolveDestinationTokenForSearchFlights(location),
        dep: dep || "",
        ret: ret || "",
        rooms: roomInfo?.rooms || [],
      });
      if (loadingRef.current && lastFlightSearchFingerprintRef.current === fingerprint) {
        return;
      }
      lastFlightSearchFingerprintRef.current = fingerprint;
    }
    if (
      tab === ORGANIZED_TOURS &&
      isOrganizedTourSearchReady(tourMonth, location)
    ) {
      const primaryTourFp = getPrimaryLocation(location);
      const locAttrTourFp = primaryTourFp || location;
      const hintsTourFp = getDestinationResolveHints(
        locAttrTourFp,
        searchPhrase,
      );
      const citiesFp = getAllLocations(location).map((loc) =>
        String(resolveLdsSearchCityFromLocation(loc).city || "").trim(),
      );
      const orgSubmitFp = JSON.stringify({
        tourMonth: String(tourMonth || "").trim(),
        ldsHints: hintsTourFp,
        categories: [...(categories || [])].slice().sort().join("|"),
        cities: [...citiesFp].slice().sort().join("|"),
        roomsFingerprint: JSON.stringify(roomInfo?.rooms || []),
        pathnameScope: pathname.includes("/searchoffer") ? "searchoffer" : "",
      });
      if (
        loadingRef.current &&
        lastOrganizedTourSubmitFingerprintRef.current === orgSubmitFp
      ) {
        if (import.meta.env?.DEV) {
          console.log("[ORG_TOUR_SEARCH_SKIP_DUPLICATE]");
        }
        return;
      }
      lastOrganizedTourSubmitFingerprintRef.current = orgSubmitFp;
    }
    setIsSearched(true);
    const overrides = flightSearchDateOverridesRef.current;
    flightSearchDateOverridesRef.current = null;
    const depForFlightSearch = overrides?.dep ?? calendarDateArrival;
    const retForFlightSearch =
      overrides?.ret !== undefined ? overrides.ret : calendarDateLeave;

    const requestId = _initBeforeSearch();
    const occ = {};
    trackMetaSearch(
      {
        searchTab: tab,
        searchPhrase,
        location,
        origin,
        roomInfo,
        departure_date: calendarDateArrival,
        return_date: calendarDateLeave,
        adults: occ.adults,
        children: occ.children,
        rooms: occ.rooms,
        currency: "ILS",
      },
      { requestId }
    );
    const withFlightAgent = (p) => {
      const aid = flightSearchAttributionAgentIdRef.current;
      return aid ? { ...p, agent_id: aid } : p;
    };
    switch (tab) {
      case PLANES: {
        setTimeLeft(TWENTY_MIN);
        const originToken = resolveOriginTokenForSearchFlights(origin);
        const destToken = resolveDestinationTokenForSearchFlights(location);
        if (!destToken) {
          message.warning(
            "נא לבחור יעד לטיסה (קוד שדה או עיר) — היעד חסר בבקשה לשרת."
          );
          _initAfterSearch(requestId);
          return;
        }
        if (!String(depForFlightSearch || "").trim()) {
          message.warning("נא לבחור תאריך יציאה.");
          _initAfterSearch(requestId);
          return;
        }
        if (
          flightTripType === "RoundTrip" &&
          !String(retForFlightSearch || "").trim()
        ) {
          message.warning("נא לבחור תאריך חזרה.");
          _initAfterSearch(requestId);
          return;
        }
        const myRid = requestId;
        recordAgentRecentSearchSnapshot(
          { planesResolvedOriginToken: originToken, planesResolvedDestToken: destToken },
          { dep: depForFlightSearch, ret: retForFlightSearch },
        );
        scheduleParallelAttractionsSearch(myRid, {});

        if (flightTripType === "RoundTrip") {
          const rtPayload = {
            dealType: "RoundTrip",
            origin: originToken,
            destination: destToken,
            dep_date: depForFlightSearch,
            ret_date: retForFlightSearch,
            room_compositions: roomInfo.rooms,
          };
          const owOutPayload = {
            dealType: "OneWay",
            origin: originToken,
            destination: destToken,
            dep_date: depForFlightSearch,
            room_compositions: roomInfo.rooms,
          };
          const owInPayload = {
            dealType: "OneWay",
            origin: destToken,
            destination: originToken,
            dep_date: retForFlightSearch,
            room_compositions: roomInfo.rooms,
          };
          console.log(
            "[SearchContext submitSearch] selected tripType:",
            flightTripType
          );
          console.log("[parallel flight search] roundTrip payload:", rtPayload);
          console.log(
            "[parallel flight search] outbound OneWay payload:",
            owOutPayload,
            {
              note: "same route as RT outbound: origin → destination, dep_date = departure",
            }
          );
          console.log(
            "[parallel flight search] inbound OneWay payload:",
            owInPayload,
            {
              note: "reversed route: destination → origin (was outbound dest→origin), dep_date = return date (ret_date)",
              expectOriginEqualsPreviousDestination:
                owInPayload.origin === rtPayload.destination,
              expectDestinationEqualsPreviousOrigin:
                owInPayload.destination === rtPayload.origin,
            }
          );
          Promise.allSettled([
            searchFlights(withFlightAgent(rtPayload)),
            searchFlights(withFlightAgent(owOutPayload)),
            searchFlights(withFlightAgent(owInPayload)),
          ])
            .then(async (settledResults) => {
              if (currentSearchRequestIdRef.current !== myRid) return;
              if (!parallelRoundTripSettledTransportOk(settledResults)) {
                setPlanesFlightSearchOutcome("error");
                console.log("[FLIGHT_SEARCH_DEBUG] error", "parallel_roundtrip_transport_failed");
                console.log("[FLIGHT_SEARCH_DEBUG] resultsCount", 0);
                message.error(
                  "אירעה שגיאה בעת חיפוש הטיסות, נא לנסות שוב"
                );
                setSearchResults(null);
                setRoundTripFlightResults(null);
                setOutboundOneWayFlightResults(null);
                setInboundOneWayFlightResults(null);
                setParallelRoundTripFlightSearchCompleted(false);
                _initAfterSearch(myRid);
                setAvailableFlights(null);
                return;
              }
              const rtDeals = sortFlightDealsByPerPersonAscending(
                dealsFromSearchFlightsSettled(
                  settledResults[0],
                  "roundTrip"
                )
              );
              const outDeals = sortFlightDealsByPerPersonAscending(
                dealsFromSearchFlightsSettled(
                  settledResults[1],
                  "outbound OneWay"
                )
              );
              const inDeals = sortFlightDealsByPerPersonAscending(
                dealsFromSearchFlightsSettled(
                  settledResults[2],
                  "inbound OneWay"
                )
              );
              setRoundTripFlightResults(rtDeals);
              setOutboundOneWayFlightResults(outDeals);
              setInboundOneWayFlightResults(inDeals);
              console.log("[parallel flight search] result counts:", {
                roundTrip: rtDeals.length,
                outboundOneWay: outDeals.length,
                inboundOneWay: inDeals.length,
              });
              const hasAny =
                rtDeals.length > 0 ||
                outDeals.length > 0 ||
                inDeals.length > 0;
              setParallelRoundTripFlightSearchCompleted(true);
              if (!hasAny) {
                setPlanesFlightSearchOutcome("empty");
                console.log("[FLIGHT_SEARCH_DEBUG] resultsCount", 0);
                console.log("[FLIGHT_SEARCH_DEBUG] error", null);
                setSearchResults(null);
                setAvailableFlights(null);
                _initAfterSearch(myRid);
                scheduleFlightAlternativesProbe({
                  flightTripType: "RoundTrip",
                  originToken,
                  destToken,
                  dep: depForFlightSearch,
                  ret: retForFlightSearch,
                  rooms: roomInfo.rooms,
                  requestId: myRid,
                });
                return;
              }
              setPlanesFlightSearchOutcome(null);
              console.log("[FLIGHT_SEARCH_DEBUG] resultsCount", rtDeals.length + outDeals.length + inDeals.length);
              console.log("[FLIGHT_SEARCH_DEBUG] error", null);
              if (
                rtDeals.length === 0 &&
                (outDeals.length > 0 || inDeals.length > 0)
              ) {
                setFlightResultsBrowseMode("selfCompose");
              } else if (rtDeals.length > 0) {
                setFlightResultsBrowseMode("roundTrip");
              }
              const dealForAvailable =
                pickAvailableFlightsDealFromParallelSettled(
                  settledResults,
                  rtDeals.length,
                  outDeals.length,
                  inDeals.length
                );
              if (dealForAvailable) {
                _logLdsFlightDealsFromSearch(dealForAvailable);
                setAvailableFlights(dealForAvailable);
              } else {
                setAvailableFlights(null);
              }
              _initAfterSearch(myRid);
            })
            .catch((err) => {
              if (currentSearchRequestIdRef.current !== myRid) return;
              if (process.env.NODE_ENV === "development") {
                console.error("[parallel flight search] unexpected error:", err);
              }
              setPlanesFlightSearchOutcome("error");
              logFlightsLoadingStateAudit({
                source: "SearchContext",
                phase: "search_request_failed",
                reason: "planes_parallel_rt_unexpected",
                message: err?.message || String(err),
                pathname:
                  typeof window !== "undefined"
                    ? window.location.pathname || ""
                    : "",
              });
              message.error(
                "אירעה שגיאה בעת חיפוש הטיסות, נא לנסות שוב"
              );
              setSearchResults(null);
              _initAfterSearch(myRid);
              setAvailableFlights(null);
              setParallelRoundTripFlightSearchCompleted(false);
            });
          return;
        }

        const planesFlightPayload = {
          dealType: "OneWay",
          origin: originToken,
          destination: destToken,
          dep_date: depForFlightSearch,
          room_compositions: roomInfo.rooms,
        };
        console.log(
          "[SearchContext submitSearch] selected tripType:",
          flightTripType
        );
        console.log(
          "[SearchContext submitSearch] flight search payload (pre-fhpService):",
          planesFlightPayload
        );
        searchFlights(withFlightAgent(planesFlightPayload))
          .then(async (flights) => {
            if (currentSearchRequestIdRef.current !== myRid) return;
            if (!isSearchFlightsAxiosResponseOk(flights)) {
              setPlanesFlightSearchOutcome("error");
              console.log("[FLIGHT_SEARCH_DEBUG] error", "oneway_transport_failed");
              console.log("[FLIGHT_SEARCH_DEBUG] resultsCount", 0);
              message.error(
                "אירעה שגיאה בעת חיפוש הטיסות, נא לנסות שוב"
              );
              setSearchResults(null);
              _initAfterSearch(myRid);
              setAvailableFlights(null);
              return;
            }
            const owDeals = dealsListFromSearchFlightsResponseBody(
              flights?.data
            );
            if (owDeals.length === 0) {
              setPlanesFlightSearchOutcome("empty");
              console.log("[FLIGHT_SEARCH_DEBUG] resultsCount", 0);
              console.log("[FLIGHT_SEARCH_DEBUG] error", null);
              setSearchResults(null);
              setAvailableFlights(null);
              _initAfterSearch(myRid);
              scheduleFlightAlternativesProbe({
                flightTripType: "OneWay",
                originToken,
                destToken,
                dep: depForFlightSearch,
                ret: retForFlightSearch,
                rooms: roomInfo.rooms,
                requestId: myRid,
              });
              return;
            }
            setPlanesFlightSearchOutcome(null);
            console.log("[FLIGHT_SEARCH_DEBUG] resultsCount", owDeals.length);
            console.log("[FLIGHT_SEARCH_DEBUG] error", null);
            _logLdsFlightDealsFromSearch(
              owDeals.length === 1 ? owDeals[0] : owDeals
            );
            setAvailableFlights(
              owDeals.length === 1 ? owDeals[0] : owDeals
            );
            _initAfterSearch(myRid);
          })
          .catch((err) => {
            if (currentSearchRequestIdRef.current !== myRid) return;
            if (process.env.NODE_ENV === "development") {
              console.error("[planes OneWay flight search]", err);
            }
            setPlanesFlightSearchOutcome("error");
            console.log("[FLIGHT_SEARCH_DEBUG] error", err?.message || "oneway_request_exception");
            console.log("[FLIGHT_SEARCH_DEBUG] resultsCount", 0);
            logFlightsLoadingStateAudit({
              source: "SearchContext",
              phase: "search_request_failed",
              reason: "planes_oneway_search_flights_rejected",
              message: err?.message || String(err),
              pathname:
                typeof window !== "undefined"
                  ? window.location.pathname || ""
                  : "",
            });
            message.error(
              "אירעה שגיאה בעת חיפוש הטיסות, נא לנסות שוב"
            );
            setSearchResults(null);
            _initAfterSearch(myRid);
            setAvailableFlights(null);
          });
        return;
      }
      case HOTELS:
        setTimeLeft(TWENTY_MIN);
        recordAgentRecentSearchSnapshot();
        await fetchSearchData();
        return;
      case PACKAGES: {
        setTimeLeft(TWENTY_MIN);
        const searchLoc = options?.locationOverride ?? location;
        const arrivalForSearch =
          options?.calendarDateArrivalOverride ?? calendarDateArrival;
        const leaveForSearch =
          options?.calendarDateLeaveOverride ?? calendarDateLeave;

        if (options?.locationOverride) {
          setLocation(options.locationOverride, { preserveSearchState: true });
        }
        if (options?.calendarDateArrivalOverride) {
          setCalendarDateArrival(options.calendarDateArrivalOverride);
        }
        if (options?.calendarDateLeaveOverride) {
          setCalendarDateLeave(options.calendarDateLeaveOverride);
        }

        const primaryPkg = getPrimaryLocation(searchLoc);
        const locAttrPkg = primaryPkg || searchLoc;
        const hintsPkg = getDestinationResolveHints(locAttrPkg, searchPhrase);
        const destOkPkg =
          String(hintsPkg.lds_code || "").trim().length >= 2 ||
          String(hintsPkg.name || "").trim().length >= 2;
        const isMonthMode = packageDateMode === "month";
        const datesOkPkg = isMonthMode
          ? isPackageMonthSearchReady(packageMonth, searchLoc)
          : String(arrivalForSearch || "").trim().length > 0 &&
            String(leaveForSearch || "").trim().length > 0;
        if (!datesOkPkg) {
          message.warning(
            isMonthMode ? "נא לבחור חודש." : "נא לבחור תאריכי יציאה וחזרה.",
          );
          _initAfterSearch(requestId);
          return;
        }
        if (
          isSearchResultsAttractionsEnabled() &&
          destOkPkg &&
          !isMonthMode &&
          datesOkPkg
        ) {
          scheduleParallelAttractionsSearch(requestId, {
            location: locAttrPkg,
            calendarDateArrival: arrivalForSearch,
            calendarDateLeave: leaveForSearch,
          });
        }
        const usedSearchOverrides = Boolean(options?.locationOverride);
        if (usedSearchOverrides) {
          submitSearchOverridesRef.current = {
            location: searchLoc,
            calendarDateArrival: arrivalForSearch,
            calendarDateLeave: leaveForSearch,
          };
        }
        try {
          await searchLDSResults();
        } finally {
          if (usedSearchOverrides) {
            submitSearchOverridesRef.current = null;
          }
        }
        return;
      }
      case ORGANIZED_TOURS: {
        if (import.meta.env?.DEV) {
          console.log("[ORG_TOUR_SEARCH_CLICK]", {
            source,
            tourMonth,
            location,
            pathname,
          });
        }
        setTimeLeft(TWENTY_MIN);
        if (import.meta.env?.DEV) {
          console.log("[ORG_TOUR_SEARCH_VALIDATE_START]", {
            tourMonth,
            location,
            rooms: roomInfo?.rooms,
          });
        }
        if (!isOrganizedTourSearchReady(tourMonth, location)) {
          if (import.meta.env?.DEV) {
            console.log("[ORG_TOUR_SEARCH_VALIDATE_FAIL]", {
              reason: "missing_tour_month_or_destination",
              tourMonth,
              location,
            });
          }
          message.warning(
            !String(tourMonth || "").trim()
              ? "נא לבחור חודש יציאה."
              : "נא לבחור יעד.",
          );
          _initAfterSearch(requestId);
          return;
        }
        if (_hasZeroAge(roomInfo.rooms)) {
          if (import.meta.env?.DEV) {
            console.log("[ORG_TOUR_SEARCH_VALIDATE_FAIL]", {
              reason: "zero_age_child",
            });
          }
          message.warning("ילד לא יכול להיות בגיל 0.");
          _initAfterSearch(requestId);
          return;
        }
        if (import.meta.env?.DEV) {
          const resolvedCity = resolveLdsSearchCityFromLocation(location);
          console.log("[ORG_TOUR_SEARCH_SUBMIT_START]", {
            tourMonth,
            city: resolvedCity.city,
            isAllDestinations: resolvedCity.isAllDestinations,
          });
        }
        const primaryTour = getPrimaryLocation(location);
        const locAttrTour = primaryTour || location;
        const hintsTour = getDestinationResolveHints(locAttrTour, searchPhrase);
        const destOkTour =
          String(hintsTour.lds_code || "").trim().length >= 2 ||
          String(hintsTour.name || "").trim().length >= 2;
        if (isSearchResultsAttractionsEnabled() && destOkTour) {
          scheduleParallelAttractionsSearch(requestId, {
            location: locAttrTour,
            calendarDateArrival,
            calendarDateLeave,
          });
        }
        if (import.meta.env?.DEV) {
          console.log("[ORG_TOUR_SEARCH_LOADING_ON]", { requestId });
        }
        try {
          await searchLDSResults(true);
        } catch (err) {
          if (import.meta.env?.DEV) {
            console.log("[ORG_TOUR_SEARCH_ERROR]", err);
          }
          message.error("אירעה שגיאה אנא נסה שנית.");
          _initAfterSearch(requestId);
        }
        return;
      }
      case DYNAMIC_PACKAGES: {
        clearDynamicPackageCheapestFlightSsotCache();
        setTimeLeft(TWENTY_MIN);
        const originTokenDp = resolveOriginTokenForSearchFlights(origin);
        const destTokenDp = resolveDestinationTokenForSearchFlights(location);
        if (!destTokenDp) {
          message.warning(
            "נא לבחור יעד לטיסה (קוד שדה או עיר) — היעד חסר בבקשה לשרת."
          );
          _initAfterSearch(requestId);
          return;
        }
        if (!String(depForFlightSearch || "").trim()) {
          message.warning("נא לבחור תאריך יציאה.");
          _initAfterSearch(requestId);
          return;
        }
        if (
          flightTripType === "RoundTrip" &&
          !String(retForFlightSearch || "").trim()
        ) {
          message.warning("נא לבחור תאריך חזרה.");
          _initAfterSearch(requestId);
          return;
        }
        const myRidDp = requestId;
        recordAgentRecentSearchSnapshot(
          { dynamicResolvedOriginToken: originTokenDp, dynamicResolvedDestToken: destTokenDp },
          { dep: depForFlightSearch, ret: retForFlightSearch },
        );
        scheduleParallelAttractionsSearch(myRidDp, {});

        if (flightTripType === "RoundTrip") {
          const rtPayload = {
            dealType: "RoundTrip",
            origin: originTokenDp,
            destination: destTokenDp,
            dep_date: depForFlightSearch,
            ret_date: retForFlightSearch,
            room_compositions: roomInfo.rooms,
          };
          const owOutPayload = {
            dealType: "OneWay",
            origin: originTokenDp,
            destination: destTokenDp,
            dep_date: depForFlightSearch,
            room_compositions: roomInfo.rooms,
          };
          const owInPayload = {
            dealType: "OneWay",
            origin: destTokenDp,
            destination: originTokenDp,
            dep_date: retForFlightSearch,
            room_compositions: roomInfo.rooms,
          };
          console.log(
            "[SearchContext submitSearch] selected tripType:",
            flightTripType
          );
          console.log("[parallel flight search] roundTrip payload:", rtPayload);
          console.log(
            "[parallel flight search] outbound OneWay payload:",
            owOutPayload,
            {
              note: "same route as RT outbound: origin → destination, dep_date = departure",
            }
          );
          console.log(
            "[parallel flight search] inbound OneWay payload:",
            owInPayload,
            {
              note: "reversed route: destination → origin, dep_date = return date (ret_date)",
              expectOriginEqualsPreviousDestination:
                owInPayload.origin === rtPayload.destination,
              expectDestinationEqualsPreviousOrigin:
                owInPayload.destination === rtPayload.origin,
            }
          );
          Promise.allSettled([
            searchFlights(withFlightAgent(rtPayload)),
            searchFlights(withFlightAgent(owOutPayload)),
            searchFlights(withFlightAgent(owInPayload)),
          ])
            .then(async (settledResults) => {
              if (currentSearchRequestIdRef.current !== myRidDp) return;
              if (!parallelRoundTripSettledTransportOk(settledResults)) {
                setPlanesFlightSearchOutcome("error");
                message.error(
                  "אירעה שגיאה בעת חיפוש הטיסות, נא לנסות שוב"
                );
                setSearchResults(null);
                setRoundTripFlightResults(null);
                setOutboundOneWayFlightResults(null);
                setInboundOneWayFlightResults(null);
                setParallelRoundTripFlightSearchCompleted(false);
                _initAfterSearch(myRidDp);
                setAvailableFlights(null);
                return;
              }
              const rtDeals = sortFlightDealsByPerPersonAscending(
                dealsFromSearchFlightsSettled(
                  settledResults[0],
                  "roundTrip"
                )
              );
              const outDeals = sortFlightDealsByPerPersonAscending(
                dealsFromSearchFlightsSettled(
                  settledResults[1],
                  "outbound OneWay"
                )
              );
              const inDeals = sortFlightDealsByPerPersonAscending(
                dealsFromSearchFlightsSettled(
                  settledResults[2],
                  "inbound OneWay"
                )
              );
              const dpPick = resolveDynamicPackageParallelFlightPick({
                rtDealsSorted: rtDeals,
                outDealsSorted: outDeals,
                inDealsSorted: inDeals,
                expectedOutboundDate: depForFlightSearch,
                expectedInboundDate: retForFlightSearch,
                hotelCheckIn: calendarDateArrival,
                hotelCheckOut: calendarDateLeave,
              });
              console.info(
                "[dynamic-packages] flight_date_intent_guard",
                dpPick.debug
              );
              setRoundTripFlightResults(dpPick.filteredRtDeals);
              setOutboundOneWayFlightResults(dpPick.filteredOutDeals);
              setInboundOneWayFlightResults(dpPick.filteredInDeals);
              const rtCt = dpPick.filteredRtDeals.length;
              const outCt = dpPick.filteredOutDeals.length;
              const inCt = dpPick.filteredInDeals.length;
              console.log(
                "[parallel flight search] result counts (dynamic filtered):",
                {
                  roundTrip: rtCt,
                  outboundOneWay: outCt,
                  inboundOneWay: inCt,
                }
              );
              const hasAny = rtCt > 0 || outCt > 0 || inCt > 0;
              setParallelRoundTripFlightSearchCompleted(true);
              if (!hasAny) {
                setPlanesFlightSearchOutcome("empty");
                setSearchResults(null);
                setAvailableFlights(null);
                _initAfterSearch(myRidDp);
                scheduleFlightAlternativesProbe({
                  flightTripType: "RoundTrip",
                  originToken: originTokenDp,
                  destToken: destTokenDp,
                  dep: depForFlightSearch,
                  ret: retForFlightSearch,
                  rooms: roomInfo.rooms,
                  requestId: myRidDp,
                });
                return;
              }
              setPlanesFlightSearchOutcome(null);
              setFlightResultsBrowseMode(dpPick.flightResultsBrowseMode);
              if (dpPick.autoSelfCompose) {
                applyDynamicPackageSelfComposeSelection({
                  outboundDeal: dpPick.autoOutboundDeal,
                  inboundDeal: dpPick.autoInboundDeal,
                  usdToIls: undefined,
                });
                _setAvailableFlights(dpPick.filteredOutDeals);
              } else if (dpPick.dealForAvailable) {
                _logLdsFlightDealsFromSearch(dpPick.dealForAvailable);
                setAvailableFlights(dpPick.dealForAvailable);
              } else {
                setAvailableFlights(null);
              }
              if (process.env.NODE_ENV === "development") {
                console.log(
                  "[dynamic-packages] submitSearch flights done (RT parallel), loading hotels"
                );
              }
              primeDynamicPackageHotelSearchPhraseFallback("rt_flights_done_before_hotels");
              try {
                await fetchSearchData();
              } finally {
                if (loadingRef.current) {
                  _initAfterSearch(myRidDp);
                }
              }
            })
            .catch((err) => {
              if (currentSearchRequestIdRef.current !== myRidDp) return;
              if (process.env.NODE_ENV === "development") {
                console.error("[parallel flight search] unexpected error:", err);
              }
              setPlanesFlightSearchOutcome("error");
              logFlightsLoadingStateAudit({
                source: "SearchContext",
                phase: "search_request_failed",
                reason: "dynamic_parallel_rt_unexpected",
                message: err?.message || String(err),
                pathname:
                  typeof window !== "undefined"
                    ? window.location.pathname || ""
                    : "",
              });
              message.error(
                "אירעה שגיאה בעת חיפוש הטיסות, נא לנסות שוב"
              );
              setSearchResults(null);
              _initAfterSearch(myRidDp);
              setAvailableFlights(null);
              setParallelRoundTripFlightSearchCompleted(false);
            });
          return;
        }

        const dpFlightPayload = {
          dealType: "OneWay",
          origin: originTokenDp,
          destination: destTokenDp,
          dep_date: depForFlightSearch,
          room_compositions: roomInfo.rooms,
        };
        console.log(
          "[SearchContext submitSearch] selected tripType:",
          flightTripType
        );
        console.log(
          "[SearchContext submitSearch] flight search payload (pre-fhpService):",
          dpFlightPayload
        );
        searchFlights(withFlightAgent(dpFlightPayload))
          .then(async (flights) => {
            if (currentSearchRequestIdRef.current !== myRidDp) return;
            if (!isSearchFlightsAxiosResponseOk(flights)) {
              setPlanesFlightSearchOutcome("error");
              message.error(
                "אירעה שגיאה בעת חיפוש הטיסות, נא לנסות שוב"
              );
              setSearchResults(null);
              _initAfterSearch(myRidDp);
              setAvailableFlights(null);
              return;
            }
            const owDealsDp = dealsListFromSearchFlightsResponseBody(
              flights?.data
            );
            const owFilteredDp = filterDynamicPackageOneWayDeals(
              owDealsDp,
              depForFlightSearch
            );
            console.info("[dynamic-packages] flight_date_intent_guard_oneway", {
              selected_nights_from_request: computeNightsFromSearchPhrase({
                checkIn: calendarDateArrival,
                checkOut: calendarDateLeave,
              }),
              computed_checkout_date: calendarDateLeave || null,
              outbound_departure_expected: depForFlightSearch,
              deals_raw: owDealsDp.length,
              deals_after_date_filter: owFilteredDp.length,
              reject_reason_if_mismatch:
                owDealsDp.length > 0 && owFilteredDp.length === 0
                  ? "one_way_deals_rejected_departure_date_mismatch"
                  : null,
            });
            if (owFilteredDp.length === 0) {
              setPlanesFlightSearchOutcome("empty");
              setSearchResults(null);
              setAvailableFlights(null);
              _initAfterSearch(myRidDp);
              scheduleFlightAlternativesProbe({
                flightTripType: "OneWay",
                originToken: originTokenDp,
                destToken: destTokenDp,
                dep: depForFlightSearch,
                ret: retForFlightSearch,
                rooms: roomInfo.rooms,
                requestId: myRidDp,
              });
              return;
            }
            setPlanesFlightSearchOutcome(null);
            _logLdsFlightDealsFromSearch(
              owFilteredDp.length === 1 ? owFilteredDp[0] : owFilteredDp
            );
            setAvailableFlights(
              owFilteredDp.length === 1 ? owFilteredDp[0] : owFilteredDp
            );
            if (process.env.NODE_ENV === "development") {
              console.log(
                "[dynamic-packages] submitSearch flights done (OW), loading hotels"
              );
            }
            primeDynamicPackageHotelSearchPhraseFallback("ow_flights_done_before_hotels");
            try {
              await fetchSearchData();
            } finally {
              if (loadingRef.current) {
                _initAfterSearch(myRidDp);
              }
            }
          })
          .catch((err) => {
            if (currentSearchRequestIdRef.current !== myRidDp) return;
            if (process.env.NODE_ENV === "development") {
              console.error("[dynamic OneWay flight search]", err);
            }
            setPlanesFlightSearchOutcome("error");
            logFlightsLoadingStateAudit({
              source: "SearchContext",
              phase: "search_request_failed",
              reason: "dynamic_oneway_search_flights_rejected",
              message: err?.message || String(err),
              pathname:
                typeof window !== "undefined"
                  ? window.location.pathname || ""
                  : "",
            });
            message.error(
              "אירעה שגיאה בעת חיפוש הטיסות, נא לנסות שוב"
            );
            setSearchResults(null);
            _initAfterSearch(myRidDp);
            setAvailableFlights(null);
          });
        return;
      }
      default:
        recordAgentRecentSearchSnapshot();
        await fetchSearchData();
        return;
    }
  }

  function applyFlightAlternativeSuggestion(suggestion) {
    if (!suggestion?.depDate) return;
    if (flightTripType === "OneWay") {
      flightSearchDateOverridesRef.current = {
        dep: suggestion.depDate,
        ret: "",
      };
      setCalendarDateArrival(suggestion.depDate);
      setCalendarDateLeave("");
    } else if (suggestion.retDate) {
      flightSearchDateOverridesRef.current = {
        dep: suggestion.depDate,
        ret: suggestion.retDate,
      };
      setCalendarDateArrival(suggestion.depDate);
      setCalendarDateLeave(suggestion.retDate);
    } else {
      return;
    }
    // /flights/results drops submitSearch unless the source is an explicit user action.
    void submitSearch({ source: "button" });
  }

  const getLocationByDestination = (destination, useEnglish = false) => {
    // Use the centralized geocoding service with caching
    // For dynamic packages, always use English to get English country names
    const geocodingLanguage = useEnglish ? 'en' : language;
    return getGeocodingByAddress(destination, geocodingLanguage);
  };

  // Guard to prevent infinite loops
  const isCalculatingSearchPhraseRef = useRef(false);
  const lastSearchPhraseParamsRef = useRef(null);
  /** Last computed phrase: returned when params unchanged so second explicit search still runs with valid phrase. */
  const lastSearchPhraseRef = useRef(null);
  /** Incremented at start of each fetchSearchData for debug correlation. */
  const searchRunIdRef = useRef(0);
  /** Last run debug payload for first-vs-second comparison (development). */
  const lastSearchRunDebugRef = useRef(null);
  
  const getSearchPhrase = useMemo(async () => {
    // Prevent infinite loops - check if already calculating
    if (isCalculatingSearchPhraseRef.current) {
      const inFlightPhrase = lastSearchPhraseRef.current;
      if (!cachedHotelSearchPhraseDropsSelection(location, inFlightPhrase)) {
        if (process.env.NODE_ENV === "development") {
          console.warn('[SearchContext] getSearchPhrase: Already calculating, skipping to prevent infinite loop');
        }
        return inFlightPhrase;
      }
    }
    
    // Check if parameters changed (include pendingAutocorrectApply so "Did you mean" triggers new phrase).
    // The city label alone is not an identity: a later selection carries hubwayzLocationId.
    const { hubwayzLocationId: selectedHubwayzLocationId, countryCode: selectedCountryCode } =
      hotelSearchPhraseCacheIdentity(location);
    const currentParams = JSON.stringify({
      location:
        location?.CITYNAME ||
        location?.CITYNAME_HEB ||
        location?.city ||
        location?.fullname,
      hubwayzLocationId: selectedHubwayzLocationId,
      countryCode: selectedCountryCode,
      roomInfo: (roomInfo?.rooms ?? []).map((room) => ({
        adults: room?.adults ?? room?.AdultsCount ?? 0,
        kidsAges: room?.kidsAges ?? room?.KidsAges ?? [],
        infants: room?.infants ?? room?.InfantsCount ?? 0,
      })),
      calendarDateArrival,
      calendarDateLeave,
      flightTripType,
      searchTab,
      pendingAutocorrectApply,
      selectedHotelCurrency,
    });
    
    if (lastSearchPhraseParamsRef.current === currentParams) {
      if (process.env.NODE_ENV === "development") {
        console.log('[SearchContext] getSearchPhrase: Parameters unchanged, returning last phrase (so second search still runs)');
      }
      return lastSearchPhraseRef.current;
    }
    
    isCalculatingSearchPhraseRef.current = true;
    lastSearchPhraseParamsRef.current = currentParams;
    
    try {
    const pathname = typeof window !== "undefined" ? window.location.pathname || "" : "";
    const shouldApplyDynamicPackageSearchPrerequisites =
      searchTab === DYNAMIC_PACKAGES && isDynamicPackageSearchRoute(pathname);
    const primaryLocation = getPrimaryLocation(location);
    let searchLocation = primaryLocation || location;

    // LDS autocomplete sometimes returns Hebrew display fields only (CITYNAME_HEB / COUNTRYNAME_HEB).
    // Dynamic package phrase logic expects CITYNAME / COUNTRYNAME — normalize before branching.
    if (
      shouldApplyDynamicPackageSearchPrerequisites &&
      searchLocation &&
      typeof searchLocation === "object"
    ) {
      const resolvedCity =
        (searchLocation.CITYNAME != null && String(searchLocation.CITYNAME).trim() !== ""
          ? searchLocation.CITYNAME
          : null) ||
        (searchLocation.CITYNAME_HEB != null &&
        String(searchLocation.CITYNAME_HEB).trim() !== ""
          ? searchLocation.CITYNAME_HEB
          : null);
      const resolvedCountry =
        (searchLocation.COUNTRYNAME != null &&
        String(searchLocation.COUNTRYNAME).trim() !== ""
          ? searchLocation.COUNTRYNAME
          : null) ||
        (searchLocation.COUNTRYNAME_HEB != null &&
        String(searchLocation.COUNTRYNAME_HEB).trim() !== ""
          ? searchLocation.COUNTRYNAME_HEB
          : null);
      if (resolvedCity || resolvedCountry) {
        searchLocation = {
          ...searchLocation,
          CITYNAME: resolvedCity || searchLocation.CITYNAME || "",
          COUNTRYNAME: resolvedCountry || searchLocation.COUNTRYNAME || "",
        };
      }
    }
    
    // Early return if location is invalid (e.g., "All Destinations") — לא כשיש בחירת מלון ספציפית
    if (
      searchLocation &&
      !hasHotelSpecificSelection(searchLocation) &&
      (searchLocation.CITYCODE === "ALL" ||
        (searchLocation.CITYNAME === "" &&
          !searchLocation.city &&
          !searchLocation.country &&
          !searchLocation.fullname) ||
        (searchLocation.CITYNAME === "" &&
          searchLocation.CITYCODE === "" &&
          ![
            searchLocation.fullname,
            searchLocation.CITYNAME_HEB,
            searchLocation.COUNTRYNAME_HEB,
            searchLocation.COUNTRYNAME,
            searchLocation.city,
            searchLocation.country,
          ].some((v) => v != null && String(v).trim() !== "")))
    ) {
      if (process.env.NODE_ENV === "development") {
        console.log(
          "[SearchContext] Invalid location (All Destinations or empty), skipping search phrase calculation"
        );
      }
      isCalculatingSearchPhraseRef.current = false;
      return null;
    }
    
    if (shouldApplyDynamicPackageSearchPrerequisites && searchLocation) {
      // Check if location is in LDS format (has COUNTRYNAME and CITYNAME)
      if (searchLocation.COUNTRYNAME && searchLocation.CITYNAME) {
        // LDS format - build destination query and use geocoding with validation
        console.log('[Dynamic Packages] LDS format detected:', {
          CITYNAME: searchLocation.CITYNAME,
          COUNTRYNAME: searchLocation.COUNTRYNAME,
          CITYCODE: searchLocation.CITYCODE
        });
        
        // Build destination query from LDS data
        const destinationName = searchLocation.COUNTRYNAME + " " + searchLocation.CITYNAME;
        
        // Skip geocoding for invalid destinations like "All Destinations"
        if (destinationName && 
            destinationName.trim() && 
            !destinationName.toLowerCase().includes('all destinations') &&
            !destinationName.toLowerCase().includes('כל היעדים')) {
          
          // Use English for dynamic packages to get English country names
          let city = null;
          let country = null;
          let lat =
            searchLocation.latitude ||
            searchLocation.lat ||
            searchLocation.LATITUDE ||
            searchLocation.Lat ||
            null;
          let lon =
            searchLocation.longitude ||
            searchLocation.lon ||
            searchLocation.LONGITUDE ||
            searchLocation.Lng ||
            null;
          
          // Try geocoding to get city/country and coordinates
          try {
            const res = await getLocationByDestination(destinationName, true);
            
            if (res && res.address_components) {
              const picked = pickCityAndCountryFromAddressComponents(res.address_components);
              logGeocodingMap("search_context_lds_destination_geocode", {
                destinationName,
                address_components: res.address_components,
                parsedCity: picked.city,
                parsedCountry: picked.country,
              });
              if (!city && picked.city) city = picked.city;
              if (!country && picked.country) country = picked.country;

              // Extract lat/lon from geocoding result (prefer geocoding coordinates)
              if (res.geometry && res.geometry.location) {
                let geocodedLat = null;
                let geocodedLon = null;
                
                if (typeof res.geometry.location.lat === 'function') {
                  geocodedLat = res.geometry.location.lat();
                } else if (typeof res.geometry.location.lat === 'number') {
                  geocodedLat = res.geometry.location.lat;
                }
                if (typeof res.geometry.location.lng === 'function') {
                  geocodedLon = res.geometry.location.lng();
                } else if (typeof res.geometry.location.lng === 'number') {
                  geocodedLon = res.geometry.location.lng;
                }
                
                // Use geocoded coordinates if valid
                if (geocodedLat != null && geocodedLon != null && 
                    !isNaN(geocodedLat) && !isNaN(geocodedLon) &&
                    geocodedLat !== 0 && geocodedLon !== 0) {
                  lat = geocodedLat;
                  lon = geocodedLon;
                  console.log('[Dynamic Packages] Using geocoded coordinates:', lat, lon);
                }
              }
              
              // Basic validation: check if geocoding returned completely wrong country
              // (e.g., searching for Paris but got Singapore)
              const originalCountryName = searchLocation.COUNTRYNAME.toLowerCase().trim();
              const extractedCountryName = country ? country.toLowerCase().trim() : '';
              
              // If country doesn't match at all, use LDS values instead
              if (country && extractedCountryName && 
                  !extractedCountryName.includes(originalCountryName) && 
                  !originalCountryName.includes(extractedCountryName) &&
                  !extractedCountryName.includes('france') && !originalCountryName.includes('france') &&
                  !extractedCountryName.includes('singapore') && !originalCountryName.includes('singapore')) {
                // Only warn if it's clearly wrong (different countries)
                console.warn('[Dynamic Packages] Geocoding returned different country, using LDS values:', {
                  originalCountry: searchLocation.COUNTRYNAME,
                  extractedCountry: country
                });
                city = null; // Force using LDS values
                country = null;
              }
            }
          } catch (error) {
            console.warn('[Dynamic Packages] Geocoding failed, will use LDS values:', error);
          }
          
          // Fallback: Use LDS values directly if geocoding failed or returned wrong results
          if (!city || !country) {
            console.log('[Dynamic Packages] Using LDS values directly:', {
              CITYNAME: searchLocation.CITYNAME,
              COUNTRYNAME: searchLocation.COUNTRYNAME
            });
            
            city = searchLocation.CITYNAME;
            country = searchLocation.COUNTRYNAME;
            
            // Transliterate if needed (Hebrew to English)
            if (city && containsHebrew(city)) {
              city = smartTransliterate(city);
              console.log('[Dynamic Packages] Transliterated city from LDS:', city);
            }
            if (country && containsHebrew(country)) {
              country = smartTransliterate(country);
              console.log('[Dynamic Packages] Transliterated country from LDS:', country);
            }
          }
          
          // CRITICAL: Ensure we have coordinates - try multiple methods
          if ((!lat || !lon || lat === null || lon === null || lat === 0 || lon === 0) && city && country) {
            // Try geocoding with final city/country
            const finalDestinationName = `${city}, ${country}`;
            console.log('[Dynamic Packages] Geocoding for coordinates with final city/country:', finalDestinationName);
            
            try {
              const coordRes = await getLocationByDestination(finalDestinationName, true);
              if (coordRes && coordRes.geometry && coordRes.geometry.location) {
                let geocodedLat = null;
                let geocodedLon = null;
                
                if (typeof coordRes.geometry.location.lat === 'function') {
                  geocodedLat = coordRes.geometry.location.lat();
                } else if (typeof coordRes.geometry.location.lat === 'number') {
                  geocodedLat = coordRes.geometry.location.lat;
                }
                if (typeof coordRes.geometry.location.lng === 'function') {
                  geocodedLon = coordRes.geometry.location.lng();
                } else if (typeof coordRes.geometry.location.lng === 'number') {
                  geocodedLon = coordRes.geometry.location.lng;
                }
                
                if (geocodedLat != null && geocodedLon != null && 
                    !isNaN(geocodedLat) && !isNaN(geocodedLon) &&
                    geocodedLat !== 0 && geocodedLon !== 0) {
                  lat = geocodedLat;
                  lon = geocodedLon;
                  console.log('[Dynamic Packages] Retrieved coordinates from geocoding:', lat, lon);
                } else {
                  console.warn('[Dynamic Packages] Geocoding returned invalid coordinates:', geocodedLat, geocodedLon);
                }
              } else {
                console.warn('[Dynamic Packages] Geocoding result missing geometry:', coordRes);
              }
            } catch (error) {
              if (process.env.NODE_ENV === "development") {
                console.warn("[Dynamic Packages] Geocoding failed for coordinates:", error);
              }
            }
          }

          // Fallback: IATA / airport code often geocodes reliably when "city, country" does not
          if (
            (!lat || !lon || lat === null || lon === null || lat === 0 || lon === 0) &&
            city &&
            country
          ) {
            const iata = String(
              searchLocation.AIRPORTCODE || searchLocation.CITYCODE || ""
            ).trim();
            if (/^[A-Za-z]{3}$/.test(iata)) {
              try {
                const coordRes = await getLocationByDestination(
                  `${iata.toUpperCase()} airport`,
                  true
                );
                if (coordRes?.geometry?.location) {
                  const loc = coordRes.geometry.location;
                  const glat =
                    typeof loc.lat === "function" ? loc.lat() : loc.lat;
                  const glon =
                    typeof loc.lng === "function" ? loc.lng() : loc.lng;
                  if (
                    glat != null &&
                    glon != null &&
                    !Number.isNaN(Number(glat)) &&
                    !Number.isNaN(Number(glon)) &&
                    Number(glat) !== 0 &&
                    Number(glon) !== 0
                  ) {
                    lat = glat;
                    lon = glon;
                    if (process.env.NODE_ENV === "development") {
                      console.log(
                        "[Dynamic Packages] Coordinates resolved via IATA geocode:",
                        iata,
                        lat,
                        lon
                      );
                    }
                  }
                }
              } catch {
                /* non-fatal */
              }
            }
          }

          // Still no coords: allowed — backend may use city/country only; avoid console.error noise
          if (!lat || !lon || lat === null || lon === null || lat === 0 || lon === 0) {
            if (process.env.NODE_ENV === "development") {
              console.warn(
                "[Dynamic Packages] No coordinates from geocoding; continuing with city/country only:",
                { city, country, CITYCODE: searchLocation?.CITYCODE }
              );
            }
          }
          
          // Validate that we have city and country
          if (!city || !country) {
            console.error('[Dynamic Packages] Missing city or country after processing:', { city, country, searchLocation });
            isCalculatingSearchPhraseRef.current = false;
            return null;
          }
          
          // Log final values for debugging
          console.log('[Dynamic Packages] ============================================');
          console.log('[Dynamic Packages] Final location values:', {
            city,
            country,
            lat,
            lon,
            originalCITYNAME: searchLocation.CITYNAME,
            originalCOUNTRYNAME: searchLocation.COUNTRYNAME,
            CITYCODE: searchLocation.CITYCODE
          });
          console.log('[Dynamic Packages] ============================================');
          
          // Special handling for Paris - ensure correct name and coordinates
          if (city && (city.toLowerCase().includes('paris') || searchLocation.CITYNAME?.toLowerCase().includes('paris'))) {
            console.log('[Dynamic Packages] PARIS DETECTED - Special handling:', {
              city,
              country,
              originalCITYNAME: searchLocation.CITYNAME,
              originalCOUNTRYNAME: searchLocation.COUNTRYNAME,
              currentLat: lat,
              currentLon: lon
            });
            // Ensure city is "Paris" (not "PARIS" or other variations)
            if (city.toUpperCase() === 'PARIS' || city.toLowerCase() === 'paris') {
              city = 'Paris';
            }
            // Ensure country is "France"
            if (country && (country.toLowerCase().includes('france') || country.toLowerCase().includes('צרפת'))) {
              country = 'France';
            }
            
            // If coordinates are missing or invalid, use Paris coordinates directly
            if (!lat || !lon || lat === 0 || lon === 0 || lat === null || lon === null) {
              console.log('[Dynamic Packages] PARIS - Setting coordinates manually (48.8566, 2.3522)');
              lat = 48.8566; // Paris latitude
              lon = 2.3522;  // Paris longitude
            }
            
            console.log('[Dynamic Packages] PARIS - After normalization:', { city, country, lat, lon });
          }

          // Athens — when Google/geocode returns no geometry (quota, policy) but city/country are known
          const cityLc = (city || "").toLowerCase();
          const countryLc = (country || "").toLowerCase();
          const ldsCityLc = (searchLocation.CITYNAME || "").toLowerCase();
          if (
            (cityLc.includes("athens") || ldsCityLc.includes("athens")) &&
            (countryLc.includes("greece") ||
              countryLc.includes("hellas") ||
              (searchLocation.COUNTRYNAME || "").toLowerCase().includes("greece"))
          ) {
            if (cityLc === "athens" || city?.toUpperCase() === "ATHENS") {
              city = "Athens";
            }
            if (!lat || !lon || lat === 0 || lon === 0 || lat === null || lon === null) {
              lat = 37.9838;
              lon = 23.7275;
            }
            if (process.env.NODE_ENV === "development") {
              console.log("[Dynamic Packages] Athens — applied coordinate fallback:", {
                city,
                country,
                lat,
                lon,
              });
            }
          }

          const ldsCityForHotelSearch =
            searchLocation.CITYNAME && String(searchLocation.CITYNAME).trim() !== ""
              ? String(searchLocation.CITYNAME).trim()
              : city;
          const ldsCountryForHotelSearch =
            searchLocation.COUNTRYNAME && String(searchLocation.COUNTRYNAME).trim() !== ""
              ? String(searchLocation.COUNTRYNAME).trim()
              : country;

          searchLocation = {
            ...searchLocation,
            city: ldsCityForHotelSearch,
            country: ldsCountryForHotelSearch,
            latitude: lat,
            longitude: lon,
            hotelserched: false,
          };
        } else {
          // Invalid destination, skip geocoding
          isCalculatingSearchPhraseRef.current = false;
          return null;
        }
      } else if (searchLocation.city || searchLocation.country || searchLocation.fullname) {
        // Google Places/Photon format - already has city/country or fullname
        // If city/country are in Hebrew, transliterate them to English
        let city = searchLocation.city;
        let country = searchLocation.country;
        let lat = searchLocation.latitude;
        let lon = searchLocation.longitude;
        
        // If we have fullname but no city/country, try to extract from fullname
        if (!city && !country && searchLocation.fullname) {
          // Try to get city and country from geocoding
          const res = await getLocationByDestination(searchLocation.fullname, true);
          if (res && res.address_components) {
            const picked = pickCityAndCountryFromAddressComponents(res.address_components);
            logGeocodingMap("search_context_places_fullname_geocode", {
              fullname: searchLocation.fullname,
              address_components: res.address_components,
              parsedCity: picked.city,
              parsedCountry: picked.country,
            });
            if (!city && picked.city) city = picked.city;
            if (!country && picked.country) country = picked.country;
            // Get lat/lon from geocoding result if not already present
            if (res.geometry && res.geometry.location && (!lat || !lon)) {
              lat = typeof res.geometry.location.lat === 'function' 
                ? res.geometry.location.lat() 
                : res.geometry.location.lat;
              lon = typeof res.geometry.location.lng === 'function'
                ? res.geometry.location.lng()
                : res.geometry.location.lng;
            }
          }
        }
        
        // If still no city/country, try to extract from formatted_address
        if ((!city || !country) && searchLocation.formatted_address) {
          // Try to parse formatted_address (e.g., "Dubai, United Arab Emirates")
          const parts = searchLocation.formatted_address.split(',').map(p => p.trim());
          if (parts.length >= 2) {
            if (!city && parts[0]) {
              city = parts[0];
            }
            if (!country && parts[parts.length - 1]) {
              country = parts[parts.length - 1];
            }
          }
        }
        
        // If we have city and country but no lat/lon, do geocoding to get coordinates
        if (city && country && (!lat || !lon)) {
          const geocodeQuery = `${city}, ${country}`;
          const res = await getLocationByDestination(geocodeQuery, true);
          if (res && res.geometry && res.geometry.location) {
            lat = typeof res.geometry.location.lat === 'function' 
              ? res.geometry.location.lat() 
              : res.geometry.location.lat;
            lon = typeof res.geometry.location.lng === 'function'
              ? res.geometry.location.lng()
              : res.geometry.location.lng;
          }
        }
        
        // Transliterate Hebrew to English if needed
        if (city && containsHebrew(city)) {
          city = smartTransliterate(city);
          console.log('[Dynamic Packages] Transliterated city:', searchLocation.city, '->', city);
        }
        if (country && containsHebrew(country)) {
          country = smartTransliterate(country);
          console.log('[Dynamic Packages] Transliterated country:', searchLocation.country, '->', country);
        }
        
        // Update searchLocation with transliterated values and coordinates
        searchLocation = {
          ...searchLocation,
          city: city || searchLocation.city,
          country: country || searchLocation.country,
          latitude: lat || searchLocation.latitude,
          longitude: lon || searchLocation.longitude,
        };
      } else {
        // Unknown format - try to use fullname if available
        if (searchLocation.fullname) {
          // Use English for dynamic packages to get English country names
          const res = await getLocationByDestination(searchLocation.fullname, true);
          if (res && res.address_components) {
            let city = null;
            let country = null;
            const picked = pickCityAndCountryFromAddressComponents(res.address_components);
            logGeocodingMap("search_context_unknown_format_fullname_geocode", {
              fullname: searchLocation.fullname,
              address_components: res.address_components,
              parsedCity: picked.city,
              parsedCountry: picked.country,
            });
            if (picked.city) city = picked.city;
            if (picked.country) country = picked.country;

            // Transliterate if needed
            if (city && containsHebrew(city)) {
              city = smartTransliterate(city);
            }
            if (country && containsHebrew(country)) {
              country = smartTransliterate(country);
            }
            
            if (city && country) {
              searchLocation = {
                city: city,
                country: country,
                latitude: typeof res.geometry.location.lat === 'function' 
                  ? res.geometry.location.lat() 
                  : res.geometry.location.lat,
                longitude: typeof res.geometry.location.lng === 'function'
                  ? res.geometry.location.lng()
                  : res.geometry.location.lng,
                hotelserched: false,
              };
            } else {
              console.error('[Dynamic Packages] Could not extract city or country from fullname geocoding:', res);
              isCalculatingSearchPhraseRef.current = false;
              return null; // Can't determine location
            }
          } else {
            isCalculatingSearchPhraseRef.current = false;
            return null; // Can't determine location
          }
        } else {
          isCalculatingSearchPhraseRef.current = false;
          return null; // No valid location data
        }
      }
    }

    if (!searchLocation) {
      isCalculatingSearchPhraseRef.current = false;
      return null;
    }

    // LDS / Sabre autocomplete often sets CITYNAME + COUNTRYNAME (or Hebrew variants) but not city/country.
    // The DYNAMIC_PACKAGES branch above normalizes that tab only; HOTELS (and others) must map the same fields
    // or fetchSearchData sees a null phrase and shows errorSearchMissingLocation.
    const ldsCityFallback =
      (searchLocation.CITYNAME != null &&
      String(searchLocation.CITYNAME).trim() !== ""
        ? String(searchLocation.CITYNAME).trim()
        : null) ||
      (searchLocation.CITYNAME_HEB != null &&
      String(searchLocation.CITYNAME_HEB).trim() !== ""
        ? String(searchLocation.CITYNAME_HEB).trim()
        : null);
    const ldsCountryFallback =
      (searchLocation.COUNTRYNAME != null &&
      String(searchLocation.COUNTRYNAME).trim() !== ""
        ? String(searchLocation.COUNTRYNAME).trim()
        : null) ||
      (searchLocation.COUNTRYNAME_HEB != null &&
      String(searchLocation.COUNTRYNAME_HEB).trim() !== ""
        ? String(searchLocation.COUNTRYNAME_HEB).trim()
        : null);

    const rawCity = searchLocation.city;
    const rawCountry = searchLocation.country;
    let city =
      rawCity != null && String(rawCity).trim() !== ""
        ? String(rawCity).trim()
        : ldsCityFallback;
    let country =
      rawCountry != null && String(rawCountry).trim() !== ""
        ? String(rawCountry).trim()
        : ldsCountryFallback;

    if (city && containsHebrew(city)) {
      city = smartTransliterate(city);
    }
    if (country && containsHebrew(country)) {
      country = smartTransliterate(country);
    }

    let lat =
      searchLocation.latitude ??
      searchLocation.lat ??
      searchLocation.LATITUDE ??
      searchLocation.Lat ??
      null;
    let lon =
      searchLocation.longitude ??
      searchLocation.lon ??
      searchLocation.LONGITUDE ??
      searchLocation.Lng ??
      null;
    if (lat != null && lat !== "") lat = Number(lat);
    if (lon != null && lon !== "") lon = Number(lon);
    if (lat != null && Number.isNaN(lat)) lat = null;
    if (lon != null && Number.isNaN(lon)) lon = null;
    const isHotel = searchLocation.hotelserched;
    
    // If we have city and country but no lat/lon, try to get them via geocoding
    if (
      shouldApplyDynamicPackageSearchPrerequisites &&
      city &&
      country &&
      (!lat || !lon || lat === null || lon === null)
    ) {
      console.log('[Dynamic Packages] Missing coordinates, attempting geocoding for:', city, country);
      const geocodeQuery = `${city}, ${country}`;
      try {
        const res = await getLocationByDestination(geocodeQuery, true);
        if (res && res.geometry && res.geometry.location) {
          if (typeof res.geometry.location.lat === 'function') {
            lat = res.geometry.location.lat();
          } else if (typeof res.geometry.location.lat === 'number') {
            lat = res.geometry.location.lat;
          }
          if (typeof res.geometry.location.lng === 'function') {
            lon = res.geometry.location.lng();
          } else if (typeof res.geometry.location.lng === 'number') {
            lon = res.geometry.location.lng;
          }
          console.log('[Dynamic Packages] Retrieved coordinates:', lat, lon);
        } else {
          console.warn('[Dynamic Packages] Failed to get coordinates for:', city, country, 'Response:', res);
        }
      } catch (error) {
        console.error('[Dynamic Packages] Error during geocoding:', error);
      }
    }

    const sortedRoomInfoJson = {
      rooms: roomInfo?.rooms.map((room) => {
        const kidsRaw = room.kidsAges ?? room.KidsAges;
        const kidsAges = Array.isArray(kidsRaw) ? kidsRaw : [];
        const inf = room.infants ?? room.InfantsCount;
        return {
          AdultsCount: room.adults,
          InfantsCount: inf != null && inf !== "" ? inf : 0,
          KidsAges: kidsAges,
        };
      }),
    };

    // Check if location contains hotel name (e.g., "Sheraton Grand Tel Aviv")
    // This happens when user searches for a specific hotel name
    let hotelName = null;
    if (location && typeof location === 'object') {
      // Check if it's a hotel search (hotelserched contains the hotel name)
      if (location.hotelserched && typeof location.hotelserched === 'string') {
        hotelName = location.hotelserched;
      } else if (location.hotelName) {
        hotelName = location.hotelName;
      } else if (location.fullname && typeof location.fullname === 'string') {
        // Check if fullname looks like a hotel name
        const fullnameStr = location.fullname.trim();
        if (fullnameStr.length > 15 || 
            /hotel|מלון|grand|plaza|tower|resort|suite|inn/i.test(fullnameStr)) {
          hotelName = fullnameStr;
        }
      }
    } else if (location && typeof location === 'string' && location.trim()) {
      // Try to extract hotel name from location string
      const locationStr = location.trim();
      // If it looks like a hotel name (contains common hotel words or is longer than typical city name)
      if (locationStr.length > 15 || 
          /hotel|מלון|grand|plaza|tower|resort|suite|inn/i.test(locationStr)) {
        hotelName = locationStr;
      }
    }

    // Intent detection: HOTEL_SEARCH vs DESTINATION_SEARCH (for ranking only)
    const queryForIntent = (location && typeof location === 'object')
      ? (location.hotelserched || location.fullname || [location.CITYNAME, location.COUNTRYNAME, location.CITYNAME_HEB, location.COUNTRYNAME_HEB, location.city, location.country].filter(Boolean).join(' ') || '')
      : (typeof location === 'string' ? location : '');
    const intent = detectSearchIntent(queryForIntent, hotelName);
    if (intent === INTENT_HOTEL_SEARCH && !(hotelName && hotelName.trim())) {
      hotelName = queryForIntent ? String(queryForIntent).trim() : null;
    }

    const hotelSpecificPick = hasHotelSpecificSelection(searchLocation);

    // Validate city/country — חיפוש יעד רגיל חייב; מלון ספציפי: השלמה מקואורדינטות / שם / fallback
    if (!city || !country) {
      if (hotelSpecificPick) {
        const geoQuery = String(
          searchLocation.hotelserched ||
            searchLocation.fullname ||
            searchLocation.hotelName ||
            ""
        ).trim();
        if (
          (lat == null || lon == null || (lat === 0 && lon === 0)) &&
          searchLocation
        ) {
          const plat =
            searchLocation.latitude ??
            searchLocation.lat ??
            searchLocation.LATITUDE ??
            searchLocation.Lat;
          const plon =
            searchLocation.longitude ??
            searchLocation.lon ??
            searchLocation.LONGITUDE ??
            searchLocation.Lng;
          if (
            plat != null &&
            plon != null &&
            plat !== "" &&
            plon !== "" &&
            !Number.isNaN(Number(plat)) &&
            !Number.isNaN(Number(plon))
          ) {
            lat = Number(plat);
            lon = Number(plon);
          }
        }
        if (
          lat != null &&
          lon != null &&
          !(lat === 0 && lon === 0) &&
          (!city || !country)
        ) {
          try {
            const result = await getGeocodingByCoordinates(lat, lon, "en");
            if (
              result?.ok &&
              result?.status === "success" &&
              result.data?.address_components
            ) {
              const picked = pickCityAndCountryFromAddressComponents(
                result.data.address_components
              );
              if (!city && picked.city) city = picked.city;
              if (!country && picked.country) country = picked.country;
            }
          } catch (e) {
            if (process.env.NODE_ENV === "development") {
              console.warn(
                "[SearchContext] getSearchPhrase: reverse geocode for hotel pick failed",
                e
              );
            }
          }
        }
        if ((!city || !country) && geoQuery.length >= 2) {
          try {
            const res = await getLocationByDestination(geoQuery, true);
            if (res?.address_components) {
              const picked = pickCityAndCountryFromAddressComponents(
                res.address_components
              );
              if (!city && picked.city) city = picked.city;
              if (!country && picked.country) country = picked.country;
              if (
                (lat == null ||
                  lon == null ||
                  lat === 0 ||
                  lon === 0 ||
                  Number.isNaN(lat) ||
                  Number.isNaN(lon)) &&
                res.geometry?.location
              ) {
                const glat = res.geometry.location.lat;
                const glon = res.geometry.location.lng;
                lat =
                  typeof glat === "function"
                    ? glat()
                    : typeof glat === "number"
                      ? glat
                      : lat;
                lon =
                  typeof glon === "function"
                    ? glon()
                    : typeof glon === "number"
                      ? glon
                      : lon;
              }
            }
          } catch (e) {
            if (process.env.NODE_ENV === "development") {
              console.warn(
                "[SearchContext] getSearchPhrase: forward geocode for hotel pick failed",
                e
              );
            }
          }
        }
        if (!city || !country) {
          const fallbackLabel =
            (geoQuery && geoQuery.split(",")[0]?.trim()) || "Hotel";
          city = city || fallbackLabel;
          country = country || "Unknown";
          if (process.env.NODE_ENV === "development") {
            console.warn(
              "[SearchContext] getSearchPhrase: hotel-specific search — fallback city/country",
              { city, country, geoQuery }
            );
          }
        }
      } else {
        // Typed or pasted destination (e.g. Hebrew "עיר, מדינה") may only populate fullname — geocode like hotel pick
        const destinationFreeText = String(
          searchLocation.fullname ||
            [
              searchLocation.CITYNAME_HEB,
              searchLocation.COUNTRYNAME_HEB,
              searchLocation.CITYNAME,
              searchLocation.COUNTRYNAME,
            ]
              .filter((p) => p != null && String(p).trim() !== "")
              .join(", ") ||
            ""
        ).trim();
        if (destinationFreeText.length >= 2 && (!city || !country)) {
          try {
            const res = await getLocationByDestination(destinationFreeText, true);
            if (res?.address_components) {
              const picked = pickCityAndCountryFromAddressComponents(
                res.address_components
              );
              if (!city && picked.city) city = picked.city;
              if (!country && picked.country) country = picked.country;
              if (city && containsHebrew(city)) city = smartTransliterate(city);
              if (country && containsHebrew(country))
                country = smartTransliterate(country);
              if (
                (lat == null ||
                  lon == null ||
                  lat === 0 ||
                  lon === 0 ||
                  Number.isNaN(lat) ||
                  Number.isNaN(lon)) &&
                res.geometry?.location
              ) {
                const glat = res.geometry.location.lat;
                const glon = res.geometry.location.lng;
                lat =
                  typeof glat === "function"
                    ? glat()
                    : typeof glat === "number"
                      ? glat
                      : lat;
                lon =
                  typeof glon === "function"
                    ? glon()
                    : typeof glon === "number"
                      ? glon
                      : lon;
              }
            }
          } catch (e) {
            if (process.env.NODE_ENV === "development") {
              console.warn(
                "[SearchContext] getSearchPhrase: forward geocode for destination free text failed",
                e
              );
            }
          }
        }
        if (!city || !country) {
          logHotelSearchEligibilityBlocked(
            "getSearchPhrase:missing_city_country_not_hotel_pick",
            {
              searchMode: searchTab,
              selectedHotel: searchLocation,
              hotelId:
                searchLocation?.HotelId ??
                searchLocation?.hotelId ??
                location?.HotelId ??
                location?.hotelId ??
                null,
              destination:
                searchLocation?.fullname ??
                searchLocation?.hotelserched ??
                null,
              city,
              country,
              locationSnapshot: location,
            }
          );
          const isInvalidLocation =
            location &&
            (location.CITYCODE === "ALL" ||
              (location.CITYNAME === "" &&
                !location.CITYNAME_HEB &&
                !location.city &&
                !location.country &&
                !location.fullname) ||
              (location.CITYCODE === "" &&
                location.CITYNAME === "" &&
                !location.CITYNAME_HEB));

          if (!isInvalidLocation) {
            if (process.env.NODE_ENV === "development") {
              console.warn("[Dynamic Packages] Missing city or country:", {
                city,
                country,
                location,
              });
            }
          } else if (process.env.NODE_ENV === "development") {
            console.log(
              "[Dynamic Packages] Skipping search - invalid location (All Destinations or empty):",
              {
                CITYCODE: location?.CITYCODE,
                CITYNAME: location?.CITYNAME,
                city: location?.city,
                country: location?.country,
              }
            );
          }
          isCalculatingSearchPhraseRef.current = false;
          return null;
        }
      }
    }

    // For dynamic packages, isHotel when searching for specific hotel (by name or intent)
    const finalIsHotel =
      searchTab === DYNAMIC_PACKAGES
        ? Boolean(hotelName && hotelName.trim()) ||
          intent === INTENT_HOTEL_SEARCH
        : isHotel === true ||
          (typeof isHotel === "string" && isHotel.trim() !== "");

    // CRITICAL VALIDATION: Log and validate city/country before sending to API
    if (shouldApplyDynamicPackageSearchPrerequisites) {
      console.log('[Dynamic Packages] ============================================');
      console.log('[Dynamic Packages] FINAL VALIDATION - Search Phrase Data:');
      console.log('[Dynamic Packages] City:', city);
      console.log('[Dynamic Packages] Country:', country);
      console.log('[Dynamic Packages] Original Location:', {
        CITYNAME: location?.CITYNAME,
        COUNTRYNAME: location?.COUNTRYNAME,
        CITYCODE: location?.CITYCODE,
        city: location?.city,
        country: location?.country
      });
      console.log('[Dynamic Packages] ============================================');
    }
    
    // Apply "Did you mean" override when user clicked suggestion (cleared in fetchSearchData after use)
    if (pendingAutocorrectApply) {
      hotelName = pendingAutocorrectApply;
    }
    
    // Only include lat/lon if they are valid numbers (not null or undefined)
    const searchPhrase = {
      isHotel: finalIsHotel || false,
      city: city,
      country: country,
      hotelName: hotelName,
      intent,
      userQuery: queryForIntent || hotelName || '',
      checkIn: calendarDateArrival,
      checkOut: calendarDateLeave,
      ...sortedRoomInfoJson,
      currency: selectedHotelCurrency,
      destinationCode:
        searchLocation?.CITYCODE ||
        searchLocation?.AIRPORTCODE ||
        location?.CITYCODE ||
        location?.AIRPORTCODE ||
        null,
      dynamic: searchTab === DYNAMIC_PACKAGES ? true : false,
    };
    
    // Add coordinates only when valid. Dynamic package search can continue with city/country only.
    if (process.env.NODE_ENV === "development" && shouldApplyDynamicPackageSearchPrerequisites) {
      console.log('[Dynamic Packages] Coordinates before adding to searchPhrase:', { lat, lon, latType: typeof lat, lonType: typeof lon });
    }

    const hasValidCoordinates =
      lat !== null &&
      lat !== undefined &&
      lon !== null &&
      lon !== undefined &&
      !Number.isNaN(Number(lat)) &&
      !Number.isNaN(Number(lon));

    if (shouldApplyDynamicPackageSearchPrerequisites && !hasValidCoordinates) {
      if (process.env.NODE_ENV === "development") {
        console.warn(
          "[Dynamic Packages] Missing coordinates on dynamic search route - continuing with city/country only",
          {
            originalLat: lat,
            originalLon: lon,
            city,
            country,
            searchLocation,
            pathname,
          }
        );
      }
    }

    if (hasValidCoordinates) {
      searchPhrase.lat = Number(lat);
      searchPhrase.lon = Number(lon);
    }
    
    lastSearchPhraseRef.current = searchPhrase;
    isCalculatingSearchPhraseRef.current = false;
    return searchPhrase;
    } catch (error) {
      console.error('[SearchContext] Error in getSearchPhrase:', error);
      isCalculatingSearchPhraseRef.current = false;
      return null;
    }
  }, [
    location,
    roomInfo,
    calendarDateArrival,
    calendarDateLeave,
    flightTripType,
    searchTab,
    pendingAutocorrectApply,
    selectedHotelCurrency,
  ]);

  function buildDynamicPackageLocationSearchPhraseFallback(basePhrase = null) {
    const primaryLoc = getPrimaryLocation(location);
    const loc = primaryLoc || location || {};
    let fallbackCity =
      loc.CITYNAME ||
      loc.CITYNAME_HEB ||
      loc.city ||
      null;
    let fallbackCountry =
      loc.COUNTRYNAME ||
      loc.COUNTRYNAME_HEB ||
      loc.country ||
      null;
    if (fallbackCity && containsHebrew(fallbackCity)) {
      fallbackCity = smartTransliterate(fallbackCity);
    }
    if (fallbackCountry && containsHebrew(fallbackCountry)) {
      fallbackCountry = smartTransliterate(fallbackCountry);
    }
    if (!fallbackCity || !fallbackCountry) return null;
    return {
      ...(basePhrase || {}),
      isHotel: Boolean(basePhrase?.isHotel),
      city: String(fallbackCity).trim(),
      country: String(fallbackCountry).trim(),
      hotelName: basePhrase?.hotelName || "",
      intent: basePhrase?.intent || INTENT_DESTINATION_SEARCH,
      userQuery: basePhrase?.userQuery || basePhrase?.hotelName || "",
      checkIn: basePhrase?.checkIn || calendarDateArrival,
      checkOut: basePhrase?.checkOut || calendarDateLeave,
      rooms: basePhrase?.rooms || roomInfo?.rooms || [],
      currency: basePhrase?.currency || selectedHotelCurrency,
      destinationCode: loc.CITYCODE || loc.AIRPORTCODE || basePhrase?.destinationCode || null,
      dynamic: true,
    };
  }

  function primeDynamicPackageHotelSearchPhraseFallback(reason) {
    if (searchTab !== DYNAMIC_PACKAGES) return null;
    const fallbackPhrase = buildDynamicPackageLocationSearchPhraseFallback(
      lastSearchPhraseRef.current
    );
    if (!fallbackPhrase) return null;
    lastSearchPhraseRef.current = fallbackPhrase;
    setSearchPhrase(fallbackPhrase);
    if (process.env.NODE_ENV === "development") {
      console.warn("[dynamic-packages] primed hotel search phrase fallback", {
        reason,
        city: fallbackPhrase.city,
        country: fallbackPhrase.country,
      });
    }
    return fallbackPhrase;
  }

  function fetchLdsDates(type, newOrigin, newLocation, categories, roomInfo) {
    if (!shouldPrefetchLdsDatesOnCurrentRoute()) {
      if (import.meta.env?.DEV) {
        console.log("[LDS_DATES_FETCH_SKIPPED]", {
          reason: "campaign_public_route",
          path: typeof window !== "undefined" ? window.location.pathname : null,
          type,
        });
      }
      setLoadingDates(false);
      return;
    }

    const normalizedLocation = getPrimaryLocation(newLocation);

    if (
      normalizedLocation == null ||
      typeof normalizedLocation !== "object"
    ) {
      if (import.meta.env?.DEV && type === ORGANIZED_TOURS) {
        console.log("[ORG_TOUR_DATES_FETCH_SKIPPED]", {
          reason: "invalid_or_empty_location_after_normalize",
        });
      }
      setLoadingDates(false);
      return;
    }

    const gen = ++fetchLdsDatesGenerationRef.current;

    const all =
      !categories || categories.length === 0 || categories?.includes("ALL");
    const { year, month } = getCurrentDate();

    const seats = roomInfo.rooms.reduce(
      (acc, curr) => acc + curr.adults + curr.kids,
      0
    );

    const locationCityMeta = resolveLdsSearchCityFromLocation(normalizedLocation);

    async function _fetch_all_dates(monthGap = 1, numOfMonths = 3) {
      let futureMonths = [];
      for (let i = 0; i < numOfMonths; i++) {
        if (i === 0) {
          futureMonths.push(getNextMonth(month, year, 0));
        } else {
          const prevMonth = futureMonths[i - 1].month;
          const prevYear = futureMonths[i - 1].year;
          futureMonths.push(getNextMonth(prevMonth, prevYear));
        }
      }

      if (import.meta.env?.DEV && type === ORGANIZED_TOURS) {
        console.log("[ORG_TOUR_DATES_FETCH_START]", {
          type,
          isAllDestinations: locationCityMeta.isAllDestinations,
          origin: newOrigin?.AIRPORTCODE,
          seats,
          batches: futureMonths.length,
        });
      }

      const responses = await Promise.all(
        futureMonths.map(async (futureMonth) => {
          const payload = {
            type,
            origin: newOrigin.AIRPORTCODE,
            city: locationCityMeta.city,
            categories: all ? [] : categories,
            date: `${futureMonth.month}/${futureMonth.year}`,
            seats,
          };
          if (import.meta.env?.DEV && type === ORGANIZED_TOURS) {
            console.log("[ORG_TOUR_DATES_FETCH_PAYLOAD]", {
              payload,
              month: futureMonth.month,
              year: futureMonth.year,
            });
          }
          if (process.env.NODE_ENV !== "production" && type !== ORGANIZED_TOURS) {
            console.log("LDS payload (fetchLdsDates)", JSON.stringify(payload));
          }
          try {
            const response = await getLdsDates(payload);
            if (!isLdsResponseEmpty(response)) {
              return response.data.calendarLink;
            }
          } catch (e) {
            if (import.meta.env?.DEV && type === ORGANIZED_TOURS) {
              console.warn("[ORG_TOUR_DATES_FETCH_RESPONSE]", {
                month: futureMonth.month,
                year: futureMonth.year,
                ok: false,
                message: String(e?.message || e || ""),
              });
            }
          }
          return null;
        })
      );

      const newLdsDates = responses
        .filter((res) => {
          return res !== null;
        })
        .flat();

      if (import.meta.env?.DEV && type === ORGANIZED_TOURS) {
        console.log("[ORG_TOUR_DATES_FETCH_RESPONSE]", {
          totalLinks: newLdsDates.length,
        });
      }
      if (
        import.meta.env?.DEV &&
        type === ORGANIZED_TOURS &&
        newLdsDates.length === 0
      ) {
        console.log("[ORG_TOUR_DATES_EMPTY_RESPONSE]", {
          isAllDestinations: locationCityMeta.isAllDestinations,
          citySent: locationCityMeta.city,
        });
      }

      if (fetchLdsDatesGenerationRef.current !== gen) return;
      setLdsDates(newLdsDates);
    }

    setError(false);
    setLoadingDates(true);
    setDatesError(false);
    setLdsDates([]);
    _fetch_all_dates(1, 12).finally(() => {
      if (fetchLdsDatesGenerationRef.current !== gen) return;
      setLoadingDates(false);
    });
    // else
    //   getLdsDates({ city: newLocation.AIRPORTCODE, date: `${month}/${year}`, organized: organized })
    //     .catch((err) => {
    //       setDatesError(
    //         err.exception || "אירעה שגיאה, אנא חפש יעד אחר או נסה שוב מאוחר יותר."
    //       );
    //     })
    //     .then((res) => {
    //       _fetch_all_dates();
    //       _fetch_all_dates(4, 8);
    //       if (isLdsResponseEmpty(res)) {
    //         setDatesError(
    //           "לא נמצאו תוצאות, אנא חפש יעד אחר או נסה שנית מאוחר יותר."
    //         );
    //       } else {
    //         setLdsDates(res.data.calendarLink);
    //       }
    //     })
    //     .finally(() => {
    //       setLoadingDates(false);
    //     });
  }

  const calculateDistanceByGeolocation = (lat1, lon1, lat2, lon2) => {
    var R = 6371; // km
    var dLat = ((lat2 - lat1) * Math.PI) / 180;
    var dLon = ((lon2 - lon1) * Math.PI) / 180;
    var a =
      0.5 -
      Math.cos(dLat) / 2 +
      (Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        (1 - Math.cos(dLon))) /
        2;

    return R * 2 * Math.asin(Math.sqrt(a));
  };

  const scheduleParallelAttractionsSearch = useCallback(
    (requestId, overrides = {}) => {
      if (!isSearchResultsAttractionsEnabled()) return;

      const loc = overrides.location ?? location;
      const dep = overrides.calendarDateArrival ?? calendarDateArrival;
      const ret = overrides.calendarDateLeave ?? calendarDateLeave;
      const rooms = overrides.roomInfo ?? roomInfo;
      const phrase = Object.prototype.hasOwnProperty.call(
        overrides,
        "searchPhrase"
      )
        ? overrides.searchPhrase
        : searchPhrase;

      if (
        import.meta.env.DEV &&
        typeof localStorage !== "undefined" &&
        localStorage.getItem("DEBUG_ATTRACTIONS_SEARCH") === "true"
      ) {
        console.log("[SearchContext] parallel attractions scheduled", requestId, {
          hasLocation: !!loc,
          dep: dep ?? null,
          ret: ret ?? null,
        });
      }

      setAttractionsSearchLoading(true);
      setAttractionsSearchError(null);
      setAttractionsSearchResults([]);
      setAttractionsSearchMeta(null);
      setAttractionsDestinationRules([]);

      void (async () => {
        try {
          const out = await runParallelAttractionsSearch({
            location: loc,
            calendarDateArrival: dep,
            calendarDateLeave: ret,
            roomInfo: rooms,
            searchPhrase: phrase,
          });
          if (import.meta.env.DEV) {
            console.log("ATTRACTIONS RESULT", out);
          }
          if (currentSearchRequestIdRef.current !== requestId) return;
          setAttractionsSearchResults(
            Array.isArray(out.attractions) ? out.attractions : []
          );
          setAttractionsSearchMeta({
            finished: true,
            skipped: !!out.skipped,
            ...(out.meta || {}),
          });
          if (out.error) setAttractionsSearchError(String(out.error));

          const rawInternal = out.meta?.internal_destination_id;
          const internalN =
            rawInternal != null ? Number(rawInternal) : NaN;
          const destLegacy =
            out.meta?.destinationId ?? out.meta?.destination_id;
          const legacyN =
            destLegacy != null ? Number(destLegacy) : NaN;
          const did = Number.isFinite(internalN) && internalN > 0
            ? internalN
            : Number.isFinite(legacyN) && legacyN > 0
              ? legacyN
              : NaN;
          const ldsRaw = String(out.meta?.lds_code || "").trim().toUpperCase();
          const flightScope =
            /^[A-Z]{3}$/.test(ldsRaw) ? ldsRaw : undefined;
          const rulesParams = { limit: 50 };
          if (Number.isFinite(did) && did > 0) {
            rulesParams.destination_id = did;
          }
          if (flightScope) {
            rulesParams.flight_scope_value = flightScope;
          }
          if (
            rulesParams.destination_id != null ||
            rulesParams.flight_scope_value
          ) {
            fetchAttractionRulesForPackage(rulesParams)
              .then((rr) => {
                if (currentSearchRequestIdRef.current !== requestId) return;
                setAttractionsDestinationRules(
                  Array.isArray(rr?.rules) ? rr.rules : []
                );
              })
              .catch(() => {
                if (currentSearchRequestIdRef.current === requestId) {
                  setAttractionsDestinationRules([]);
                }
              });
          } else if (currentSearchRequestIdRef.current === requestId) {
            setAttractionsDestinationRules([]);
          }
        } catch (e) {
          if (currentSearchRequestIdRef.current !== requestId) return;
          setAttractionsSearchError(String(e?.message || e));
          setAttractionsSearchMeta({ finished: true, skipped: false });
          setAttractionsDestinationRules([]);
        } finally {
          if (currentSearchRequestIdRef.current === requestId) {
            setAttractionsSearchLoading(false);
          }
        }
      })();
    },
    [
      location,
      calendarDateArrival,
      calendarDateLeave,
      roomInfo,
      searchPhrase,
    ]
  );

  function fetchSearchData() {
    const mapHotelSearchErrorForUi = (rawError, source) => {
      const technicalError = String(rawError ?? "").trim();
      const normalized = technicalError.toLowerCase();
      const isSupplierFailure =
        normalized.includes("gemonix login failed") ||
        normalized.includes("gimmonix login failed") ||
        normalized.includes("missing sessionid in provider response");

      if (isSupplierFailure) {
        if (process.env.NODE_ENV === "development") {
          console.warn("[HOTEL_SUPPLIER_SEARCH_ERROR_TRACE]", {
            source,
            technicalError,
          });
        }
        return "לא הצלחנו להשלים את החיפוש מול ספק המלונות. נסו שוב בעוד רגע או פנו לתמיכה.";
      }

      return technicalError || "אירעה שגיאה בחיפוש מלונות";
    };

    // Guard against errors to prevent crashes
    if (!getSearchPhrase || typeof getSearchPhrase.then !== 'function') {
      console.error('[SearchContext] getSearchPhrase is not a promise');
      setSearchResults(null);
      _initAfterSearch(currentSearchRequestIdRef.current);
      return Promise.resolve();
    }

    return getSearchPhrase
      .then((resolvedSearchPhrase) => {
        let searchPhrase = resolvedSearchPhrase;
        searchRunIdRef.current += 1;
        const runId = searchRunIdRef.current;
        const rawQuery = (searchPhrase?.userQuery || searchPhrase?.hotelName || '').trim();
        const normalizedQuery = searchPhrase?.hotelName ? normalizeHotelName(searchPhrase.hotelName) : '';
        if (process.env.NODE_ENV === "development") {
          console.log('[SearchContext] fetchSearchData: SEARCH RUN START', {
            runId,
            rawQuery,
            normalizedQuery,
            searchPhrase_hotelName: searchPhrase?.hotelName ?? null,
            searchPhrase_userQuery: searchPhrase?.userQuery ?? null,
            searchPhrase_intent: searchPhrase?.intent ?? null,
            location_fullname: location?.fullname ?? null,
            location_CITYNAME: location?.CITYNAME ?? null,
            pendingAutocorrectApply: pendingAutocorrectApply ?? null,
          });
        }
        // Dynamic packages can reach this after flight search with a stale/null phrase promise.
        // Recover from the current LDS location so hotel loading is not skipped.
        if ((!searchPhrase || !searchPhrase.city || !searchPhrase.country) && searchTab === DYNAMIC_PACKAGES) {
          const recoveredPhrase = buildDynamicPackageLocationSearchPhraseFallback(searchPhrase);
          if (recoveredPhrase) {
            searchPhrase = recoveredPhrase;
            if (process.env.NODE_ENV === "development") {
              console.warn("[dynamic-packages] fetchSearchData recovered missing searchPhrase from location", {
                city: searchPhrase.city,
                country: searchPhrase.country,
              });
            }
          }
        }

        // Check if searchPhrase is valid (null can happen when params unchanged and no prior phrase yet)
        if (!searchPhrase || !searchPhrase.city || !searchPhrase.country) {
          const pathname = typeof window !== "undefined" ? window.location.pathname || "" : "";
          if (isVacationPackageViewRoute(pathname)) {
            if (process.env.NODE_ENV === "development") {
              console.log(
                "[SearchContext] fetchSearchData: skipping dynamic search prerequisites on vacation package view route",
                { pathname, hasPhrase: !!searchPhrase }
              );
            }
            _initAfterSearch(currentSearchRequestIdRef.current);
            return;
          }
          if (process.env.NODE_ENV === "development") {
            console.warn('[SearchContext] fetchSearchData: searchPhrase null or invalid – skipping search', {
              runId,
              hasPhrase: !!searchPhrase,
              city: searchPhrase?.city,
              country: searchPhrase?.country,
            });
          }
          /** טיסות / חבילות דינמיות עם LDS — אין city/country במבנה hotel phrase; לא להציג שגיאת יעד */
          const skipHotelPhraseError =
            searchTab === PLANES || searchTab === DYNAMIC_PACKAGES;
          const primaryLoc = getPrimaryLocation(location);
          const hotelEligible = hasHotelSpecificSelection(primaryLoc || location);

          if (!skipHotelPhraseError) {
            logHotelSearchEligibilityBlocked(
              !searchPhrase
                ? "fetchSearchData:null_search_phrase"
                : "fetchSearchData:missing_city_or_country_on_phrase",
              {
                searchMode: searchTab,
                selectedHotel: primaryLoc || location || null,
                hotelId:
                  primaryLoc?.HotelId ??
                  primaryLoc?.hotelId ??
                  location?.HotelId ??
                  location?.hotelId ??
                  null,
                destination:
                  primaryLoc?.fullname ??
                  primaryLoc?.hotelserched ??
                  location?.fullname ??
                  location?.hotelserched ??
                  null,
                city: searchPhrase?.city ?? null,
                country: searchPhrase?.country ?? null,
                hotelEligible,
                runId,
              }
            );
          }

          if (skipHotelPhraseError) {
            if (process.env.NODE_ENV === "development") {
              console.log(
                "[SearchContext] fetchSearchData: no hotel phrase on flight-related tab — silent skip"
              );
              if (searchTab === DYNAMIC_PACKAGES) {
                console.warn(
                  "[dynamic-packages] fetchSearchData skipped — missing city/country on searchPhrase (hotels not loaded)",
                  { city: searchPhrase?.city, country: searchPhrase?.country }
                );
              }
            }
            _initAfterSearch(currentSearchRequestIdRef.current);
            return;
          }

          // Don't show error for invalid locations (like "All Destinations") or בחירת מלון בלי יעד טקסטואלי
          const isInvalidLocation =
            location &&
            (location.CITYCODE === "ALL" ||
              (!hotelEligible &&
                location.CITYNAME === "" &&
                !location.CITYNAME_HEB &&
                !location.city &&
                !location.country &&
                !location.fullname) ||
              (!hotelEligible &&
                location.CITYCODE === "" &&
                location.CITYNAME === "" &&
                !location.CITYNAME_HEB));

          if (!isInvalidLocation) {
            console.error("[Dynamic Packages] Invalid searchPhrase:", searchPhrase);
            message.error(i18n.t("notifications.errorSearchMissingLocation"));
          } else if (process.env.NODE_ENV === "development") {
            console.log(
              "[Dynamic Packages] Skipping search - invalid location selected (All Destinations or empty)"
            );
          }
          setSearchResults(null);
          _initAfterSearch(currentSearchRequestIdRef.current);
          return;
        }
        
        // Log searchPhrase for debugging - CRITICAL: Log dates being sent
        console.log('[SearchContext] fetchSearchData: Calling searchHotels with dates - checkIn:', searchPhrase.checkIn, 'checkOut:', searchPhrase.checkOut);
        if (process.env.NODE_ENV === "development") {
          console.log('[SearchContext] fetchSearchData: Calling searchHotels with searchPhrase:', searchPhrase);
          if (searchPhrase.hotelName) {
            console.log('[SearchContext] fetchSearchData: RELEVANCE DEBUG - User searched by hotel name:', {
              hotelNameRaw: searchPhrase.hotelName,
              hotelNameNormalized: searchPhrase.hotelName.toLowerCase().replace(/\s+/g, ' ').trim(),
            });
          }
        }
        
        // Validate date format before sending (DD/MM/YYYY)
        const dateFormatRegex = /^\d{2}\/\d{2}\/\d{4}$/;
        if (searchPhrase.checkIn && !dateFormatRegex.test(searchPhrase.checkIn)) {
          console.error('[SearchContext] Invalid checkIn date format:', searchPhrase.checkIn, 'Expected DD/MM/YYYY');
          message.error(`תאריך כניסה לא תקין: ${searchPhrase.checkIn}. נדרש פורמט DD/MM/YYYY`);
          setSearchResults(null);
          _initAfterSearch(currentSearchRequestIdRef.current);
          return;
        }
        if (searchPhrase.checkOut && !dateFormatRegex.test(searchPhrase.checkOut)) {
          console.error('[SearchContext] Invalid checkOut date format:', searchPhrase.checkOut, 'Expected DD/MM/YYYY');
          message.error(`תאריך יציאה לא תקין: ${searchPhrase.checkOut}. נדרש פורמט DD/MM/YYYY`);
          setSearchResults(null);
          _initAfterSearch(currentSearchRequestIdRef.current);
          return;
        }

        const phraseForState = searchPhrase?.productType
          ? searchPhrase
          : { ...searchPhrase, productType: searchTabRef.current };
        lastSearchPhraseRef.current = phraseForState;
        setSearchPhrase(phraseForState);
        try {
          const phraseJson = JSON.stringify(phraseForState);
          localStorage.setItem("currentSearchPhrase", phraseJson);
          sessionStorage.setItem("currentSearchPhrase", phraseJson);
        } catch (_) {
          /* ignore storage write failures */
        }

        if (
          !shouldRunHotelSearch({
            searchTab,
            checkIn: searchPhrase.checkIn,
            checkOut: searchPhrase.checkOut,
          })
        ) {
          if (process.env.NODE_ENV === "development") {
            console.log(
              "[SearchContext] fetchSearchData: skip hotel search",
              {
                searchTab,
                checkIn: searchPhrase.checkIn || null,
                checkOut: searchPhrase.checkOut || null,
              }
            );
          }
          _initAfterSearch(currentSearchRequestIdRef.current);
          return;
        }
        setLastSearchPayload({
          requestId: currentSearchRequestIdRef.current,
          dep_date: searchPhrase.checkIn,
          ret_date: searchPhrase.checkOut,
          source: searchTab === HOTELS ? "hotel_search" : "hotel_fetch",
          createdAt: Date.now(),
        });
        
        const requestIdAtCall = currentSearchRequestIdRef.current;
        const phraseSnap = searchCommitSnapshotRef.current;
        if (phraseSnap && phraseSnap.requestId === requestIdAtCall) {
          searchCommitSnapshotRef.current = {
            ...phraseSnap,
            product: searchTabRef.current,
            destinationKey:
              destinationKeyFromLocation(location) || destinationKeyFromPhrase(searchPhrase),
            checkIn: searchPhrase.checkIn || phraseSnap.checkIn,
            checkOut: searchPhrase.checkOut || phraseSnap.checkOut,
            occupancySignature: buildPackageOccupancySignature({
              rooms: searchPhrase.rooms || roomInfo?.rooms,
            }),
          };
        }
        const wizardSkipsComposite = shouldSkipCompositeHotelSearchForWizard(
          searchPhrase.rooms
        );
        const useHubwayzHotelsTab =
          searchTab === HOTELS && isHubwayzHotelsTabEnabled();
        const useCompositeSplit =
          !useHubwayzHotelsTab &&
          !wizardSkipsComposite &&
          isMultiRoomSplitSearchEnabled() &&
          searchTab === HOTELS &&
          Array.isArray(searchPhrase.rooms) &&
          searchPhrase.rooms.length > 1;

        if (searchTab === HOTELS) {
          console.log("[HOTELS ATTRACTIONS DEBUG] BEFORE SCHEDULE", {
            searchTab,
            location,
            searchPhrase,
          });
          console.log("[HOTELS ATTRACTIONS DEBUG] HOTELS GATE", {
            searchTab,
            enabled:
              typeof isSearchResultsAttractionsEnabled === "function"
                ? isSearchResultsAttractionsEnabled()
                : "n/a",
            location,
            searchPhrase,
          });
          if (useCompositeSplit) {
            setCompositeHotelSearchMeta({
              phase: "loading",
              room_progress: {
                total_rooms: searchPhrase.rooms.length,
                completed_rooms: 0,
                failed_rooms: 0,
              },
            });
          } else {
            setCompositeHotelSearchMeta(null);
          }
          scheduleParallelAttractionsSearch(requestIdAtCall, { searchPhrase });
        }

        const primaryLocForPins = getPrimaryLocation(location) || location;
        return resolveCanonicalDestinationIdForHotelSearch(
          primaryLocForPins,
          searchPhrase
        ).then((hrDestinationId) => {
          const hrAppliesTo =
            searchTab === DYNAMIC_PACKAGES ? "dynamic_packages" : "hotels_search";

          const hotelSearchPayload = {
            ...searchPhrase,
            ...(useCompositeSplit
              ? {
                  search_request_id: `sr-${requestIdAtCall}-${Date.now()}`,
                  omit_room_results: true,
                }
              : {}),
            ...buildHotelSearchRankingFields(hrDestinationId, hrAppliesTo),
          };

          if (shouldLogHotelSearchPipeline()) {
            console.log("[HOTEL_SEARCH_OUTBOUND]", {
              file: "SearchContext.js",
              function: "fetchSearchData",
              reason: useCompositeSplit
                ? "payload_sent_to_/searchHotelsComposite"
                : wizardSkipsComposite
                  ? "payload_sent_to_/searchHotels_wizard_skips_composite"
                  : "payload_sent_to_/searchHotels",
              city: searchPhrase.city,
              country: searchPhrase.country,
              lat: searchPhrase.lat,
              lon: searchPhrase.lon,
              checkIn: searchPhrase.checkIn,
              checkOut: searchPhrase.checkOut,
              currency: searchPhrase.currency,
              rooms: searchPhrase.rooms,
              isHotel: searchPhrase.isHotel,
              intent: searchPhrase.intent,
              hotelName: searchPhrase.hotelName ?? null,
              userQuery: searchPhrase.userQuery ?? null,
              dynamic: searchPhrase.dynamic,
              useCompositeSplit,
              wizardSkipsComposite,
              hr_destination_id: hrDestinationId ?? null,
              hr_applies_to: hrAppliesTo,
            });
          }

          if (isGeocodingMapDebugEnabled()) {
            logGeocodingMap("outbound_searchHotels_payload", {
              city: searchPhrase.city,
              country: searchPhrase.country,
              lat: searchPhrase.lat,
              lon: searchPhrase.lon,
              checkIn: searchPhrase.checkIn,
              checkOut: searchPhrase.checkOut,
              rooms: searchPhrase.rooms,
              userQuery: searchPhrase.userQuery ?? null,
              intent: searchPhrase.intent,
            });
          }

          if (
            process.env.NODE_ENV === "development" &&
            searchTab === DYNAMIC_PACKAGES
          ) {
            console.log("[dynamic-packages] fetching hotels after flights (fetchSearchData)", {
              city: searchPhrase.city,
              country: searchPhrase.country,
              dynamic: searchPhrase.dynamic,
            });
          }

          const hotelSearchEndpoint = useHubwayzHotelsTab
            ? "/api/hubwayz/hotels/search"
            : useCompositeSplit
              ? "/searchHotelsComposite"
              : "/searchHotels";
          const hotelFlowSupplierPerf =
            searchTab === HOTELS
              ? createHotelFlowPerfMark("supplier_searchHotels", {
                  endpoint: hotelSearchEndpoint,
                })
              : null;
          if (searchTab === HOTELS) {
            traceHotelFlowStep(HOTEL_FLOW_STEP.PAYLOAD_BUILD, {
              checkIn: searchPhrase.checkIn,
              checkOut: searchPhrase.checkOut,
              roomCount: searchPhrase.rooms?.length ?? 0,
              endpoint: hotelSearchEndpoint,
              hubwayz: useHubwayzHotelsTab,
            });
            traceHotelFlowStep(HOTEL_FLOW_STEP.SUPPLIER_REQUEST, {
              endpoint: hotelSearchEndpoint,
              composite: useCompositeSplit,
              hubwayz: useHubwayzHotelsTab,
            });
          }
          const hotelSearchSource = useHubwayzHotelsTab
            ? "fetchSearchData:hubwayz"
            : useCompositeSplit
              ? "fetchSearchData:composite"
              : "fetchSearchData:standard";
          const hotelSearchFingerprint = `${hotelSearchEndpoint}:${JSON.stringify(
            hotelSearchPayload
          )}`;
          const existingInFlightHotelSearch =
            hotelSearchInFlightRef.current.get(hotelSearchFingerprint);
          if (existingInFlightHotelSearch) {
            logHotelPerfAudit(
              "[PERF_AUDIT_FE_API_DUPLICATE]",
              `search-duplicate:${hotelSearchEndpoint}`,
              {
                endpoint: hotelSearchEndpoint,
                source: hotelSearchSource,
                requestId: requestIdAtCall,
                duplicateGapMs:
                  Date.now() - existingInFlightHotelSearch.startedAtMs,
                payloadBytes:
                  existingInFlightHotelSearch.payloadBytes ??
                  safeJsonSizeBytes(hotelSearchPayload),
              },
              4000
            );
          }

          const hotelSearchRequest =
            existingInFlightHotelSearch?.promise ||
            (useHubwayzHotelsTab
              ? searchHotelsViaHubwayz(hotelSearchPayload, location)
              : useCompositeSplit
                ? searchHotelsComposite(hotelSearchPayload)
                : searchHotels(hotelSearchPayload));

          if (!existingInFlightHotelSearch) {
            const payloadBytes = safeJsonSizeBytes(hotelSearchPayload);
            hotelSearchInFlightRef.current.set(hotelSearchFingerprint, {
              promise: hotelSearchRequest,
              startedAtMs: Date.now(),
              payloadBytes,
            });
            logHotelPerfAudit(
              "[PERF_AUDIT_ROUTE_LOAD]",
              `search-route:${hotelSearchEndpoint}`,
              {
                route:
                  typeof window !== "undefined" ? window.location.pathname : "",
                endpoint: hotelSearchEndpoint,
                source: hotelSearchSource,
                requestId: requestIdAtCall,
                payloadBytes,
              },
              2000
            );
            hotelSearchRequest.finally(() => {
              hotelSearchInFlightRef.current.delete(hotelSearchFingerprint);
            });
          }

          return hotelSearchRequest.then(async (res) => {
          if (searchTab === HOTELS) {
            hotelFlowSupplierPerf?.end({ httpStatus: res?.status ?? null });
          }
          if (currentSearchRequestIdRef.current !== requestIdAtCall) return;
          // Guard against invalid responses
          if (!res) {
            console.error('[SearchContext] fetchSearchData: No response from searchHotels');
            message.error(i18n.t("notifications.errorNoResponse"));
            setSearchResults(null);
            _initAfterSearch(requestIdAtCall);
            return;
          }

          // Check if response has error field (even with 200 status)
          // This happens when API returns 200 but with error in data
          if (res.data?.error) {
            const errorObj = res.data.error;
            const errorMessage = typeof errorObj === 'string'
              ? errorObj 
              : errorObj?.message || errorObj?.error || errorObj?.exception || "אירעה שגיאה";
            console.error('[SearchContext] fetchSearchData: API returned error in response');
            console.error('[SearchContext] fetchSearchData: Error object:', errorObj);
            console.error('[SearchContext] fetchSearchData: Error type:', typeof errorObj);
            console.error('[SearchContext] fetchSearchData: Full response:', JSON.stringify(res.data, null, 2));
            
            message.error(
              mapHotelSearchErrorForUi(errorMessage, "response_error_field")
            );
            setSearchResults(null);
            _initAfterSearch(requestIdAtCall);
            return;
          }

          // Normalized HTTP error envelope from fetchService (e.g. empty 500 body) or API fail without hotel payload
          if (
            res.data?.status === "fail" &&
            typeof res.data?.message === "string" &&
            !res.data?.session
          ) {
            if (process.env.NODE_ENV === "development") {
              console.warn("[SearchContext] fetchSearchData: hotel search returned fail envelope", {
                httpStatus: res.status,
                message: res.data.message,
              });
            }
            message.error(
              mapHotelSearchErrorForUi(
                res.data.message,
                "normalized_fail_envelope"
              )
            );
            setSearchResults(null);
            _initAfterSearch(requestIdAtCall);
            if (useCompositeSplit) setCompositeHotelSearchMeta(null);
            return;
          }

          // Check for top-level error (from axios error response)
          if (!res?.data || res.error) {
            const errorMessage = res?.error?.message || res?.error || "אירעה שגיאה";
            console.error('[SearchContext] fetchSearchData: Error in response:', res.error);
            message.error(errorMessage);
            setSearchResults(null);
            _initAfterSearch(requestIdAtCall);
            return;
          }

          let session;
          let result;
          if (useCompositeSplit) {
            const d = res.data || {};
            if (!d.session) {
              console.error(
                "[SearchContext] fetchSearchData: Composite response missing session",
                d
              );
              const errorMsg =
                (Array.isArray(d.errors) && d.errors[0]) ||
                d.message ||
                d.error;
              message.error(
                typeof errorMsg === "string"
                  ? errorMsg
                  : i18n.t("notifications.errorInvalidResponse")
              );
              setSearchResults(null);
              _initAfterSearch(requestIdAtCall);
              setCompositeHotelSearchMeta(null);
              return;
            }
            session = d.session;
            const totalRooms =
              d.room_progress?.total_rooms != null
                ? d.room_progress.total_rooms
                : searchPhrase.rooms?.length ?? 0;
            result = mapCompositeHotelResultsToLegacyRows(
              d.hotel_results || [],
              totalRooms
            );
            setCompositeHotelSearchMeta({
              phase: "done",
              search_request_id: d.search_request_id,
              search_status: d.search_status,
              room_progress: d.room_progress,
            });
          } else {
            if (!res.data.session || !res.data.Result) {
              console.error(
                "[SearchContext] fetchSearchData: Missing session or Result in response"
              );
              console.error(
                "[SearchContext] fetchSearchData: Response data:",
                res.data
              );
              console.error(
                "[SearchContext] fetchSearchData: Full response structure:",
                {
                  hasData: !!res.data,
                  dataKeys: res.data ? Object.keys(res.data) : [],
                  hasSession: !!res.data?.session,
                  hasResult: !!res.data?.Result,
                  responseStatus: res.status,
                  responseStatusText: res.statusText,
                  fullData: JSON.stringify(res.data, null, 2),
                }
              );

              const errorMsg =
                res.data?.error?.message || res.data?.error || res.data?.message;
              if (errorMsg) {
                message.error(
                  mapHotelSearchErrorForUi(
                    errorMsg,
                    "missing_session_or_result"
                  )
                );
              } else {
                message.error(i18n.t("notifications.errorInvalidResponse"));
              }
              setSearchResults(null);
              _initAfterSearch(requestIdAtCall);
              return;
            }
            session = res.data.session;
            result = res.data.Result || [];
          }

          if (!result) result = [];
          if (isHotelPriceTraceEnabled()) {
            const traceResultsCurrency = normalizeHotelSearchCurrencyCode(
              searchPhrase.currency ?? selectedHotelCurrency
            );
            logHotelPriceTrace("search_results_after_map", {
              stageFile: "SearchContext.js",
              stageFn: "fetchSearchData",
              useCompositeSplit,
              session,
              rowCount: result.length,
              uiSelectedHotelCurrency: selectedHotelCurrency,
              requestCurrencyInPhrase: searchPhrase.currency ?? null,
              /** מטבע שיישמר ב-state כמטבע ה-dataset של השורות (לפני setState) */
              resultsDatasetCurrency: traceResultsCurrency,
              sampleRows: result.slice(0, 5).map((h) => {
                const apiLowest = agentHotelSearchLowestPackagePrice(h);
                return {
                  hotelId: h?.ID ?? h?.Id ?? h?.id,
                  LowestPackagePrice: h?.LowestPackagePrice,
                  compositeRawTotal: h?._compositeRaw?.total_price,
                  roomQuotesOkPrices: Array.isArray(h?._compositeRaw?.room_quotes)
                    ? h._compositeRaw.room_quotes
                        .filter((q) => q?.status === "ok" && q.price != null)
                        .map((q) => q.price)
                    : null,
                  apiLowestPackagePrice: apiLowest,
                };
              }),
            });
          }
          const rawRowsForPriceAudit = isHotelCardPriceDebugEnabled()
            ? result.map((h) => (h && typeof h === "object" ? { ...h } : h))
            : null;
          const pipelineRawCount = result.length;

          if (shouldLogHotelSearchPipeline()) {
            const first20 = result.slice(0, 20).map((h) => ({
              id: h?.ID ?? h?.Id ?? h?.id,
              name: h?.DisplayName || h?.Name || h?.HotelName,
              city: h?.City ?? h?.city ?? h?.destination ?? null,
              supplier: h?.Supplier ?? h?.supplier ?? h?.Source ?? h?.source ?? null,
            }));
            console.log("[HOTEL_SEARCH_API_RAW]", {
              file: "SearchContext.js",
              function: "fetchSearchData",
              reason: "initial_API_Result_array",
              rawCount: pipelineRawCount,
              first20,
            });
          }

          // Log search results for debugging
          if (process.env.NODE_ENV === "development") {
            console.log('[SearchContext] fetchSearchData: Received hotels from API:', {
              hotelsCount: result.length,
              searchPhrase: {
                city: searchPhrase.city,
                country: searchPhrase.country,
                lat: searchPhrase.lat,
                lon: searchPhrase.lon,
                checkIn: searchPhrase.checkIn,
                checkOut: searchPhrase.checkOut
              }
            });
          }
          
          if (result.length === 0 && shouldNotifyAgentSystemToasts()) {
            // Check if coordinates are 0,0 (fallback values)
            if (searchPhrase.lat === 0 && searchPhrase.lon === 0) {
              console.warn('[SearchContext] fetchSearchData: No hotels found - coordinates are 0,0 (geocoding may have failed)');
              notifyAgentSearch("warning", {
                message: "לא נמצאו תוצאות",
                description:
                  "לא נמצאו תוצאות עבור התאריכים הספציפיים. ייתכן שהקואורדינטות לא נמצאו. נסה לבחור יעד אחר או לבצע חיפוש מחדש.",
                duration: 5,
                placement: "bottomRight",
              });
            } else {
              notifyAgentSearch("error", {
                message: "לא נמצאו תוצאות",
                description:
                  "לא נמצאו תוצאות עבור התאריכים הספציפיים, ניתן לבצע חיפוש מחדש לתאריכים שונים.",
                duration: 5,
                placement: "bottomRight",
              });
            }
          }
          
          setSessionId(session);

          // Hotel Name Index: build once per result set; additive boost only (fallback to existing relevance)
          const hotelIndex = buildHotelNameIndex(result);
          const queryForIndex = (searchPhrase.hotelName || searchPhrase.userQuery || '').trim();
          const indexMatchByDedupKey =
            searchPhrase.intent === INTENT_HOTEL_SEARCH && queryForIndex
              ? searchHotelNameIndex(queryForIndex, hotelIndex)
              : new Map();

          if (process.env.NODE_ENV === "development" && indexMatchByDedupKey.size > 0) {
            const normalizedQuery = normalizeHotelName(queryForIndex);
            const firstEntry = indexMatchByDedupKey.entries().next().value;
            const matchInfo = firstEntry ? firstEntry[1] : null;
            const firstDedupKey = firstEntry ? firstEntry[0] : null;
            const first = result.find((h) => getDedupKey(h) === firstDedupKey);
            const boostApplied = matchInfo ? INDEX_MATCH_BOOST[matchInfo.matchType] : null;
            console.log('[SearchContext] fetchSearchData: Hotel Index Debug', {
              query: queryForIndex,
              normalizedQuery,
              totalMatches: indexMatchByDedupKey.size,
              matchedHotel: first ? (first.DisplayName || first.Name) : null,
              dedupKey: firstDedupKey,
              matchType: matchInfo?.matchType,
              matchedValue: matchInfo?.matchedValue,
              boostApplied,
            });
          }

          // Run fuzzy autocorrect whenever we have a query (min length enforced inside getBestFuzzyHotelMatch).
          // Apply boost per-hotel only when that hotel has no strong index match, so a global strong match
          // for another hotel does not block autocorrect for the matched hotel.
          const fuzzyResult = queryForIndex
            ? getBestFuzzyHotelMatch(queryForIndex, hotelIndex)
            : null;
          const fuzzyMatchByDedupKey =
            fuzzyResult && fuzzyResult.dedupKey
              ? new Map([[fuzzyResult.dedupKey, fuzzyResult]])
              : new Map();

          if (process.env.NODE_ENV === "development" && fuzzyResult) {
            const firstFuzzy = result.find((h) => getDedupKey(h) === fuzzyResult.dedupKey);
            console.log('[SearchContext] fetchSearchData: Autocorrect Debug', {
              originalQuery: fuzzyResult.originalQuery,
              matchedValue: fuzzyResult.matchedValue,
              suggestedQuery: fuzzyResult.suggestedQuery,
              candidateSource: fuzzyResult.candidateSource,
              matchedHotel: firstFuzzy ? (firstFuzzy.DisplayName || firstFuzzy.Name) : null,
              similarityScore: fuzzyResult.similarityScore,
              confidence: fuzzyResult.confidence,
              boostApplied: isAutocorrectConfidenceHigh(fuzzyResult) ? AUTOCORRECT_BOOST : null,
            });
          }
          
          // Create deep copies of all hotels to avoid mutating original objects
          // This ensures that each search gets fresh objects without leftover markers
          // Guard against errors in distance calculation
          const hotelsWithDistance = result.map((hotel) => {
              try {
                // Guard against invalid hotel data (external/Gemonix expects GeoLocation)
                if (!hotel) {
                  logDroppedHotel(
                    hotel,
                    "SearchContext.fetchSearchData:normalizeHotelRow",
                    "null_or_falsy_hotel_object",
                    "SearchContext.js",
                    "fetchSearchData"
                  );
                  return null;
                }
                // Do not drop rows without GeoLocation — supplier rows may omit coords; distance stays null.
                if (!hotel.GeoLocation && !isInternalInventorySearchHotel(hotel)) {
                  if (shouldLogHotelSearchPipeline()) {
                    console.log("[HOTEL_SEARCH_KEEP_NO_GEO]", {
                      file: "SearchContext.js",
                      function: "fetchSearchData",
                      reason: "missing_GeoLocation_still_shown",
                      hotelId: hotel.ID ?? hotel.Id,
                      hotelName: hotel.DisplayName || hotel.Name,
                    });
                  }
                }

                // Create a new object copy
                const newHotel = { ...hotel };

                if (isInternalInventorySearchHotel(newHotel)) {
                  const sr = newHotel.StarRating;
                  if (sr === "-1" || sr === -1 || sr === "" || sr == null) {
                    newHotel.StarRating = null;
                  }
                }

                // Normalize "no image" placeholders coming from the API
                const noImagePlaceholders = [
                  "אין תמונות למלון זה",
                  "No images for this hotel",
                ];
                const isNoImagePlaceholder = (value) =>
                  typeof value === "string" &&
                  noImagePlaceholders.includes(value.trim());

                if (typeof newHotel.DefaultImage === "string") {
                  if (isNoImagePlaceholder(newHotel.DefaultImage)) {
                    newHotel.DefaultImage = null;
                  }
                } else if (newHotel.DefaultImage?.FullSize) {
                  if (isNoImagePlaceholder(newHotel.DefaultImage.FullSize)) {
                    newHotel.DefaultImage = null;
                  }
                }
                
                // Reset search markers for each hotel first
                newHotel.isSearchedHotel = false;
                newHotel.matchScore = 2;
                
                // Guard against invalid coordinates
                const lat = hotel.GeoLocation?.Latitude;
                const lon = hotel.GeoLocation?.Longitude;
                const searchLat = searchPhrase.lat;
                const searchLon = searchPhrase.lon;

                if (lat != null && lon != null && searchLat != null && searchLon != null) {
                  try {
                    newHotel.distance = calculateDistanceByGeolocation(
                      lat,
                      lon,
                      searchLat,
                      searchLon
                    );
                  } catch (error) {
                    console.warn('[SearchContext] fetchSearchData: Error calculating distance for hotel:', error);
                    newHotel.distance = null;
                  }
                } else {
                  newHotel.distance = null;
                }
              
              // Relevance engine: only when intent is hotel search
              if (searchPhrase.intent === INTENT_HOTEL_SEARCH && (searchPhrase.hotelName || '').trim()) {
                const hotelDisplayName = hotel.DisplayName || '';
                const searchNormalized = normalizeHotelName(searchPhrase.hotelName || '');
                const hotelNorm = normalizeHotelName(hotelDisplayName);
                const hotelMatchNames = getHotelMatchNames(hotelNorm);
                const { score: textMatchScore, exact: isExactMatch } = calculateTextMatchScore(
                  searchNormalized,
                  hotelDisplayName,
                  hotelMatchNames
                );
                const distanceKm = newHotel.distance != null && !Number.isNaN(Number(newHotel.distance)) ? Number(newHotel.distance) : 0;
                const popularityBoost = 0; // optional: from bookings data later
                let relevanceScore = calculateRelevanceScore(textMatchScore, distanceKm, popularityBoost, isExactMatch);

                const dedupKey = getDedupKey(hotel);
                const indexMatch = indexMatchByDedupKey.get(dedupKey);
                if (indexMatch && INDEX_MATCH_BOOST[indexMatch.matchType] != null) {
                  relevanceScore += INDEX_MATCH_BOOST[indexMatch.matchType];
                  newHotel._indexMatchType = indexMatch.matchType;
                  newHotel._indexMatchedValue = indexMatch.matchedValue;
                }

                const fuzzyMatch = fuzzyMatchByDedupKey.get(dedupKey);
                const hasStrongIndexMatchForHotel = indexMatch && (indexMatch.matchType === 'EXACT' || indexMatch.matchType === 'PREFIX' || indexMatch.matchType === 'PHRASE');
                if (fuzzyMatch && isAutocorrectConfidenceHigh(fuzzyMatch) && !hasStrongIndexMatchForHotel) {
                  relevanceScore += AUTOCORRECT_BOOST;
                  newHotel._autocorrectMatch = true;
                  newHotel._autocorrectValue = fuzzyMatch.correctedQuery;
                  newHotel._autocorrectScore = fuzzyMatch.similarityScore;
                }

                newHotel.textMatchScore = textMatchScore;
                newHotel.relevanceScore = relevanceScore;
                newHotel.matchScore = textMatchScore;
                newHotel.isSearchedHotel = textMatchScore < 5;

                if (process.env.NODE_ENV === "development") {
                  newHotel._relevanceDebug = {
                    searchRaw: searchPhrase.hotelName,
                    searchNormalized,
                    hotelDisplayName,
                    hotelNorm,
                    textMatchScore,
                    distanceKm,
                    relevanceScore,
                    isExactMatch,
                    indexMatchType: indexMatch?.matchType,
                    indexMatchedValue: indexMatch?.matchedValue,
                    autocorrectMatch: fuzzyMatch?.confidence >= 0.8,
                    autocorrectValue: fuzzyMatch?.correctedQuery,
                    autocorrectScore: fuzzyMatch?.similarityScore,
                  };
                }
              } else {
                newHotel.isSearchedHotel = false;
                newHotel.matchScore = 5;
                newHotel.textMatchScore = 5;
                newHotel.relevanceScore = (newHotel.distance != null ? Number(newHotel.distance) : 0) * 5;
              }
              
              return newHotel;
              } catch (error) {
                console.error('[SearchContext] fetchSearchData: Error processing hotel:', error, hotel);
                logDroppedHotel(
                  hotel,
                  "SearchContext.fetchSearchData:normalizeHotelRow",
                  `processing_exception:${error?.message || error}`,
                  "SearchContext.js",
                  "fetchSearchData"
                );
                return null; // Return null for failed hotels
              }
            });

          const failedHotels = hotelsWithDistance.filter((item) => item === null);
          if (failedHotels.length > 0 && process.env.NODE_ENV === "development") {
            console.warn("[SearchContext] fetchSearchData: Some hotels failed processing:", {
              failedCount: failedHotels.length,
              totalCount: hotelsWithDistance.length,
            });
          }

          result = hotelsWithDistance.filter((item) => item !== null);
          const afterMappingCount = result.length;

          // Deduplication by hotel identity: lowest LowestPackagePrice wins; tie-break by relevanceScore (lowest = best)
          if (
            searchPhrase.intent === INTENT_HOTEL_SEARCH &&
            result.length > 0 &&
            !isShowAllHotelResultsDebug()
          ) {
            const byKey = new Map();
            for (const h of result) {
              const key = getDeduplicationKey(h);
              const existing = byKey.get(key);
              const winner = pickBetterHotelRowForListDedup(h, existing);
              byKey.set(key, winner);
            }
            result = Array.from(byKey.values());
          } else if (
            searchPhrase.intent === INTENT_HOTEL_SEARCH &&
            result.length > 0 &&
            isShowAllHotelResultsDebug()
          ) {
            console.log("[HOTEL_SEARCH_PIPELINE]", {
              file: "SearchContext.js",
              function: "fetchSearchData",
              stage: "deduplication",
              reason: "skipped_SHOW_ALL_HOTEL_RESULTS_DEBUG",
            });
          }
          const afterDedupCount = result.length;
          
          // Guard against empty results after filtering
          if (result.length === 0) {
            console.warn('[SearchContext] fetchSearchData: No valid hotels after processing', {
              originalCount: res.data.Result?.length || 0,
              afterProcessing: result.length,
              failedCount: failedHotels.length
            });
            if (currentSearchRequestIdRef.current !== requestIdAtCall) {
              _initAfterSearch(requestIdAtCall);
              return;
            }
            setSearchResults(null);
            _initAfterSearch(requestIdAtCall);
            return;
          }
          
          // Order: server SSOT via _hr_sort_index (hotel_priority_rules). No client business re-rank.
          result = sortByServerHrOrder(result);
          if (
            process.env.NODE_ENV === "development" &&
            typeof localStorage !== "undefined" &&
            localStorage.getItem("DEBUG_HR_ORDER_GUARD") === "true" &&
            result.length > 1 &&
            result.every((h) => h._hr_sort_index != null)
          ) {
            let prev = -Infinity;
            let bad = false;
            for (const h of result) {
              const v = Number(h._hr_sort_index);
              if (v < prev) {
                bad = true;
                break;
              }
              prev = v;
            }
            if (bad) {
              console.warn(
                "[HOTEL_PRIORITY_RULES] frontend_detected_order_mutation _hr_sort_index not non-decreasing after restore"
              );
            }
          }

          // Full ranking debug + first-vs-second comparison (development)
          if (process.env.NODE_ENV === "development") {
            const runId = searchRunIdRef.current;
            const queryForLog = (searchPhrase.hotelName || searchPhrase.userQuery || '').trim();
            const intendedDedupKey = (fuzzyResult?.dedupKey || (indexMatchByDedupKey.size > 0 ? indexMatchByDedupKey.keys().next().value : null));
            const intendedHotel = result.find((h) => getDedupKey(h) === intendedDedupKey);
            const firstHotel = result[0] || null;
            const supplierResults = res.data.Result || [];
            const intendedInSupplier = intendedDedupKey
              ? supplierResults.some((h) => getDedupKey(h) === intendedDedupKey)
              : null;
            const byPosition = result.map((h, i) => ({
              position: i + 1,
              dedupKey: getDedupKey(h),
              name: h.DisplayName || h.Name,
              ID: h.ID ?? h.Id,
              textMatchScore: h.textMatchScore ?? h.matchScore,
              relevanceScore: h.relevanceScore,
              _indexMatchType: h._indexMatchType,
              _autocorrectMatch: h._autocorrectMatch,
            }));
            const intendedPosition = intendedHotel ? byPosition.findIndex((x) => x.dedupKey === intendedDedupKey) + 1 : null;
            const payload = {
              runId,
              rawQuery: queryForLog,
              normalizedQuery: normalizeHotelName(queryForLog),
              searchPhrase_hotelName: searchPhrase.hotelName ?? null,
              searchPhrase_userQuery: searchPhrase.userQuery ?? null,
              searchPhrase_intent: searchPhrase.intent ?? null,
              pendingAutocorrectApply: pendingAutocorrectApply ?? null,
              intendedDedupKey: intendedDedupKey ?? null,
              indexMatchType: intendedHotel ? (intendedHotel._indexMatchType ?? null) : null,
              fuzzyMatchType: intendedHotel && intendedHotel._autocorrectMatch ? 'FUZZY' : null,
              intended_textMatchScore: intendedHotel?.textMatchScore ?? null,
              intended_relevanceScore: intendedHotel?.relevanceScore ?? null,
              intended_finalPosition: intendedPosition ?? null,
              intended_inSupplierResults: intendedInSupplier,
              firstHotelDedupKey: firstHotel ? getDedupKey(firstHotel) : null,
              firstHotelName: firstHotel ? (firstHotel.DisplayName || firstHotel.Name) : null,
              totalResults: result.length,
              top10: byPosition.slice(0, 10),
            };
            console.log('[SearchContext] fetchSearchData: RANKING DEBUG', payload);
            const prev = lastSearchRunDebugRef.current;
            if (prev) {
              console.log('[SearchContext] fetchSearchData: FIRST vs SECOND COMPARISON', {
                first_runId: prev.runId,
                second_runId: payload.runId,
                same_intent: prev.searchPhrase_intent === payload.searchPhrase_intent,
                same_hotelName: prev.searchPhrase_hotelName === payload.searchPhrase_hotelName,
                first_intendedPosition: prev.intended_finalPosition,
                second_intendedPosition: payload.intended_finalPosition,
                first_intended_relevanceScore: prev.intended_relevanceScore,
                second_intended_relevanceScore: payload.intended_relevanceScore,
                first_intent: prev.searchPhrase_intent,
                second_intent: payload.searchPhrase_intent,
              });
            }
            lastSearchRunDebugRef.current = payload;
          }

          // Full pipeline debug for target hotel (e.g. AC Hotel by Marriott Miami Aventura)
          if (process.env.NODE_ENV === "development" && TARGET_DEBUG_HOTEL_NAME) {
            const rawSupplier = res.data.Result || [];
            const targetLower = TARGET_DEBUG_HOTEL_NAME.toLowerCase();
            const target = rawSupplier.find(
              (h) => (h.DisplayName || h.Name || h.HotelName || "")
                .toLowerCase()
                .includes(targetLower)
            );
            if (target) {
              const targetDedupKey = getDedupKey(target);
              const supplierPosition = rawSupplier.findIndex((h) => getDedupKey(h) === targetDedupKey) + 1;
              const rawName = (target.DisplayName || target.Name || target.HotelName || "").trim();
              const normalizedName = normalizeHotelName(rawName);
              const tokens = normalizedName ? normalizedName.split(/\s+/).filter(Boolean) : [];

              console.log("[SearchContext] PIPELINE 1 – Supplier Results", {
                targetHotel: rawName,
                inSupplier: !!target,
                hotelId: target.ID ?? target.Id ?? null,
                DisplayName: target.DisplayName,
                Name: target.Name,
                HotelName: target.HotelName,
                GeoLocation: target.GeoLocation,
                destination: target.destination ?? target.city ?? null,
                positionInRawSupplier: supplierPosition,
                totalSupplierResults: rawSupplier.length,
              });

              console.log("[SearchContext] PIPELINE 2 – Normalization", {
                rawName,
                normalizedName,
                tokens,
              });

              const indexEntry = hotelIndex[targetDedupKey];
              const indexTokens = indexEntry ? (indexEntry.tokens || []) : [];
              const indexTokenSet = indexEntry && indexEntry.tokenSet ? [...indexEntry.tokenSet] : [];
              console.log("[SearchContext] PIPELINE 3 – Hotel Name Index", {
                dedupKey: targetDedupKey,
                inIndex: !!indexEntry,
                canonicalName: indexEntry?.canonicalName ?? null,
                normalizedAliases: indexEntry?.normalizedAliases ?? null,
                tokens: indexTokens,
                tokenSet: indexTokenSet,
              });

              const testQueries = [
                "ac aventura",
                "ac hotel aventura",
                "ac hotel miami aventura",
                "ac marriott aventura",
                "ac hotel by marriott aventura",
              ];
              const indexMatchForQueries = {};
              testQueries.forEach((q) => {
                const matchMap = searchHotelNameIndex(q, hotelIndex);
                const match = matchMap.get(targetDedupKey);
                indexMatchForQueries[q] = match
                  ? {
                      matchType: match.matchType,
                      matchedValue: match.matchedValue,
                      boostApplied: INDEX_MATCH_BOOST[match.matchType] ?? null,
                    }
                  : null;
              });
              console.log("[SearchContext] PIPELINE 4 – Index Match (test queries)", indexMatchForQueries);
              const actualQueryMatch = indexMatchByDedupKey.get(targetDedupKey);
              console.log("[SearchContext] PIPELINE 4 – Index Match (actual query)", {
                queryForIndex,
                matchType: actualQueryMatch?.matchType ?? null,
                matchedValue: actualQueryMatch?.matchedValue ?? null,
                boostApplied: actualQueryMatch ? (INDEX_MATCH_BOOST[actualQueryMatch.matchType] ?? null) : null,
              });

              const isFuzzyTarget = fuzzyResult && fuzzyResult.dedupKey === targetDedupKey;
              console.log("[SearchContext] PIPELINE 5 – Fuzzy Autocorrect", {
                isTargetFuzzyMatch: isFuzzyTarget,
                similarityScore: isFuzzyTarget ? fuzzyResult.similarityScore : null,
                confidence: isFuzzyTarget ? fuzzyResult.confidence : null,
                correctedQuery: isFuzzyTarget ? fuzzyResult.correctedQuery : null,
                candidateSource: isFuzzyTarget ? fuzzyResult.candidateSource : null,
              });

              const processedTarget = result.find((h) => getDedupKey(h) === targetDedupKey);
              const rd = processedTarget?._relevanceDebug;
              const indexBoost = processedTarget?._indexMatchType != null ? (INDEX_MATCH_BOOST[processedTarget._indexMatchType] ?? 0) : 0;
              const autocorrectBoost = processedTarget?._autocorrectMatch ? AUTOCORRECT_BOOST : 0;
              console.log("[SearchContext] PIPELINE 6 – Relevance Score", {
                targetDedupKey,
                textMatchScore: processedTarget?.textMatchScore ?? rd?.textMatchScore ?? null,
                distanceKm: rd?.distanceKm ?? null,
                popularityBoost: 0,
                indexBoost,
                autocorrectBoost: processedTarget?._autocorrectMatch ? autocorrectBoost : 0,
                finalRelevanceScore: processedTarget?.relevanceScore ?? null,
              });

              const finalPosition = processedTarget ? result.findIndex((h) => getDedupKey(h) === targetDedupKey) + 1 : null;
              const targetScore = processedTarget?.relevanceScore ?? Infinity;
              const betterHotels = result.filter((h) => (h.relevanceScore ?? Infinity) < targetScore);
              const competitorsFullBreakdown = betterHotels.map((h) => ({
                displayName: h.DisplayName || h.Name,
                relevanceScore: h.relevanceScore,
                textMatchScore: h.textMatchScore ?? h._relevanceDebug?.textMatchScore ?? null,
                indexMatchType: h._indexMatchType ?? null,
                indexBoost: h._indexMatchType != null ? (INDEX_MATCH_BOOST[h._indexMatchType] ?? 0) : 0,
                autocorrectBoost: h._autocorrectMatch ? AUTOCORRECT_BOOST : 0,
                distanceKm: h._relevanceDebug?.distanceKm ?? (h.distance != null ? Number(h.distance) : null),
                boostSource: h._indexMatchType ? "index" : h._autocorrectMatch ? "fuzzy" : "base",
              }));
              console.log("[SearchContext] PIPELINE 7 – Sorting", {
                finalPosition,
                totalResults: result.length,
                targetRelevanceScore: processedTarget?.relevanceScore ?? null,
                countWithBetterScore: betterHotels.length,
                competitorsFullBreakdown,
              });

              const isGeoKey = /^[^_]+_[0-9.-]+_[0-9.-]+$/.test(targetDedupKey) && !String(target.ID ?? target.Id).trim();
              console.log("[SearchContext] PIPELINE 8 – Deduplication", {
                dedupKey: targetDedupKey,
                hotelId: target.ID ?? target.Id ?? null,
                normalizedName,
                isGeoBasedKey: isGeoKey,
              });

              const targetRunPayload = {
                runId: searchRunIdRef.current,
                searchPhrase_intent: searchPhrase.intent,
                searchPhrase_hotelName: searchPhrase.hotelName,
                searchPhrase_userQuery: searchPhrase.userQuery,
                targetInSupplier: !!target,
                targetSupplierPosition: supplierPosition,
                targetIndexMatchType: actualQueryMatch?.matchType ?? null,
                targetFuzzyMatch: isFuzzyTarget,
                targetFinalPosition: finalPosition,
                targetRelevanceScore: processedTarget?.relevanceScore ?? null,
                totalResults: result.length,
              };
              const prevTarget = lastSearchRunDebugRef.current?.targetPayload;
              if (prevTarget) {
                console.log("[SearchContext] PIPELINE 9 – Repeat Search Comparison", {
                  first_runId: prevTarget.runId,
                  second_runId: targetRunPayload.runId,
                  diff_intent: prevTarget.searchPhrase_intent !== targetRunPayload.searchPhrase_intent,
                  diff_hotelName: prevTarget.searchPhrase_hotelName !== targetRunPayload.searchPhrase_hotelName,
                  diff_userQuery: prevTarget.searchPhrase_userQuery !== targetRunPayload.searchPhrase_userQuery,
                  first_targetPosition: prevTarget.targetFinalPosition,
                  second_targetPosition: targetRunPayload.targetFinalPosition,
                  first_targetRelevance: prevTarget.targetRelevanceScore,
                  second_targetRelevance: targetRunPayload.targetRelevanceScore,
                  first_indexMatch: prevTarget.targetIndexMatchType,
                  second_indexMatch: targetRunPayload.targetIndexMatchType,
                });
              }
              if (lastSearchRunDebugRef.current) {
                lastSearchRunDebugRef.current.targetPayload = targetRunPayload;
              } else {
                lastSearchRunDebugRef.current = { targetPayload: targetRunPayload };
              }
            }
          }

          // Highlight: first non-internal row (relevance winner among supplier), else first row after internal pin
          if (searchPhrase.intent === INTENT_HOTEL_SEARCH && result.length > 0) {
            result.forEach((h) => { h.isSearchedHotel = false; });
            const highlightHotel =
              result.find((h) => !isInternalInventorySearchHotel(h)) ?? result[0];
            const firstDedupKey = getDedupKey(highlightHotel);
            setTargetHotelDedupKey(firstDedupKey || null);
            highlightHotel.isSearchedHotel = true;
          } else {
            setTargetHotelDedupKey(null);
          }

          // Search analytics: one event per search run (hotel name match type, for Index/alias/autocorrect impact)
          if (searchPhrase.intent === INTENT_HOTEL_SEARCH) {
            const queryForAnalytics = (searchPhrase.hotelName || searchPhrase.userQuery || '').trim();
            const normalizedQueryAnalytics = normalizeHotelName(queryForAnalytics);
            const intendedDedupKeyAnalytics = fuzzyResult?.dedupKey ?? (indexMatchByDedupKey.size > 0 ? indexMatchByDedupKey.keys().next().value : null);
            const intendedHotelAnalytics = intendedDedupKeyAnalytics ? result.find((h) => getDedupKey(h) === intendedDedupKeyAnalytics) : null;
            const firstHotelAnalytics =
              result.find((h) => !isInternalInventorySearchHotel(h)) ?? result[0] ?? null;
            const matchTypeFromHotel = (h) => h?._indexMatchType ?? (h?._autocorrectMatch ? 'FUZZY' : null);
            const bestMatchType = matchTypeFromHotel(intendedHotelAnalytics) ?? matchTypeFromHotel(firstHotelAnalytics);
            const intendedPositionAnalytics = intendedHotelAnalytics && intendedDedupKeyAnalytics
              ? result.findIndex((h) => getDedupKey(h) === intendedDedupKeyAnalytics) + 1
              : null;
            trackSearchMatch({
              runId: searchRunIdRef.current,
              intent: searchPhrase.intent ?? '',
              normalizedQuery: normalizedQueryAnalytics,
              matchType: bestMatchType,
              hasFuzzyApplied: result.some((h) => h._autocorrectMatch),
              totalResults: result.length,
              firstHotelDedupKey: firstHotelAnalytics ? getDedupKey(firstHotelAnalytics) : null,
              intendedPosition: intendedPositionAnalytics,
              intendedHotelName: intendedHotelAnalytics ? (intendedHotelAnalytics.DisplayName || intendedHotelAnalytics.Name) : null,
            });
          }

          if (shouldLogHotelSearchPipeline()) {
            const finalIds = result.map((h) => h?.ID ?? h?.Id).filter((x) => x != null);
            const rawServerList = useCompositeSplit
              ? res.data.hotel_results || []
              : res.data.Result || [];
            const serverIds = rawServerList
              .map((h) =>
                useCompositeSplit ? h?.hotel_id : (h?.ID ?? h?.Id)
              )
              .filter((x) => x != null);
            const missingAfterContext = serverIds.filter((id) => !finalIds.includes(id));
            console.log("[HOTEL_SEARCH_PIPELINE_SUMMARY]", {
              file: "SearchContext.js",
              function: "fetchSearchData",
              reason: "counts_before_setSearchResults",
              rawCount: pipelineRawCount,
              afterMappingCount,
              afterDedupCount,
              finalSentToReactStateCount: result.length,
              missingIdsAfterContextProcessing: missingAfterContext.slice(0, 80),
            });
            if (typeof window !== "undefined") {
              window.__HR_LAST_HOTEL_SEARCH__ = {
                serverIds,
                clientStateIds: finalIds,
                pipelineRawCount,
                afterMappingCount,
                afterDedupCount,
                ts: Date.now(),
              };
            }
          }

          if (rawRowsForPriceAudit && searchPhrase.intent === INTENT_HOTEL_SEARCH) {
            logHotelCardPriceDebug(result, rawRowsForPriceAudit, {
              useCompositeSplit,
            });
          }

          setHotelSearchResultsCurrency(
            normalizeHotelSearchCurrencyCode(
              searchPhrase.currency ?? selectedHotelCurrency
            )
          );
          if (searchPhrase.dynamic) {
            result = attachDynamicPackageFutureCxlToHotelSearchResults(result, {
              now: new Date(),
            });
          }
          if (searchTab === HOTELS) {
            traceHotelFlowStep(HOTEL_FLOW_STEP.RESPONSE_NORMALIZE, {
              resultCount: Array.isArray(result) ? result.length : 0,
            });
          }
          if (!commitActiveSearchRows(result, requestIdAtCall, "hotel_search")) {
            _initAfterSearch(requestIdAtCall);
            return;
          }
          setPendingAutocorrectApply(null);
          // Show "Did you mean" only when: hotel search, fuzzy applied, confidence high, query !== suggestion
          if (searchPhrase.intent === INTENT_HOTEL_SEARCH && fuzzyResult && isAutocorrectConfidenceHigh(fuzzyResult)) {
            const appliedToHotel = result.some((h) => h._autocorrectMatch);
            const originalNorm = normalizeHotelName(fuzzyResult.originalQuery || '');
            const suggestedNorm = normalizeHotelName(fuzzyResult.suggestedQuery || '');
            if (appliedToHotel && originalNorm !== suggestedNorm) {
              const suggestion = {
                originalQuery: fuzzyResult.originalQuery,
                suggestedQuery: fuzzyResult.suggestedQuery,
                confidence: fuzzyResult.confidence,
                candidateSource: fuzzyResult.candidateSource,
              };
              lastAutocorrectSuggestionRef.current = suggestion;
              setAutocorrectSuggestion(suggestion);
              trackDidYouMeanShown({
                runId: searchRunIdRef.current,
                originalQuery: fuzzyResult.originalQuery ?? '',
                suggestedQuery: fuzzyResult.suggestedQuery ?? '',
                confidence: fuzzyResult.confidence,
                candidateSource: fuzzyResult.candidateSource,
              });
            } else {
              lastAutocorrectSuggestionRef.current = null;
              setAutocorrectSuggestion(null);
            }
          } else {
            lastAutocorrectSuggestionRef.current = null;
            setAutocorrectSuggestion(null);
          }
          _initAfterSearch(requestIdAtCall);
        })
        .catch((error) => {
          if (currentSearchRequestIdRef.current !== requestIdAtCall) return;
          console.error('[SearchContext] fetchSearchData: Error in searchHotels:', error);
          const errorMessage = error?.message || error?.toString() || "אירעה שגיאה בחיפוש מלונות";
          message.error(
            mapHotelSearchErrorForUi(errorMessage, "request_rejected")
          );
          setSearchResults(null);
          setPendingAutocorrectApply(null);
          setCompositeHotelSearchMeta(null);
          _initAfterSearch(requestIdAtCall);
          setError(true);
        });
        });
    })
    .catch((error) => {
      console.error('[Dynamic Packages] Error in getSearchPhrase:', error);
      message.error(i18n.t("notifications.errorPreparingSearch"));
      setSearchResults(null);
      _initAfterSearch(currentSearchRequestIdRef.current);
    });
  }

  fetchSearchDataRef.current = fetchSearchData;

  useEffect(() => {
    if (searchResults == null) {
      setHotelSearchResultsCurrency(null);
    }
  }, [searchResults]);

  useEffect(() => {
    if (!pendingHotelCurrencyRefetchRef.current) return;
    pendingHotelCurrencyRefetchRef.current = false;
    const hotelLikeTab = searchTab === HOTELS || searchTab === DYNAMIC_PACKAGES;
    if (!hotelLikeTab || !isSearched) return;
    Promise.resolve().then(() => {
      try {
        fetchSearchDataRef.current();
      } catch (e) {
        console.error("[SearchContext] Refetch after hotel currency change failed", e);
      }
    });
  }, [selectedHotelCurrency, searchTab, isSearched]);

  const hotelSearchListDisplayCurrency = useMemo(() => {
    const hotelTabs = searchTab === HOTELS || searchTab === DYNAMIC_PACKAGES;
    const hasRows = Array.isArray(searchResults) && searchResults.length > 0;
    if (hotelTabs && hasRows && hotelSearchResultsCurrency != null) {
      return hotelSearchResultsCurrency;
    }
    return selectedHotelCurrency;
  }, [
    searchTab,
    searchResults,
    hotelSearchResultsCurrency,
    selectedHotelCurrency,
  ]);

  // Helper function to generate alternative room compositions with same total people
  // Converts kids to adults to find alternative room compositions
  // Works for both single-room and multi-room searches
  const generateAlternativeRoomCompositions = (originalCompositions) => {
    if (!originalCompositions || originalCompositions.length === 0) return [];
    
    // Calculate total people across all rooms
    let totalAdults = 0;
    let totalKids = 0;
    const defaultKidsAge = 2; // Default age for kids
    
    originalCompositions.forEach(room => {
      totalAdults += room.adults || 0;
      totalKids += room.kids || 0;
    });
    
    const totalPeople = totalAdults + totalKids;
    
    // If no kids or only adults, no alternatives needed
    if (totalKids === 0 || totalPeople <= 1) return [];
    
    const alternatives = [];
    const numRooms = originalCompositions.length;
    
    // Generate alternatives: convert kids to adults while keeping same total people
    // Example: 1 adult + 3 kids (4 total) -> 2 adults + 2 kids, 3 adults + 1 kid, 4 adults
    // We want alternatives where we have more adults and fewer kids (but same total people)
    for (let newAdults = totalAdults + 1; newAdults <= totalPeople; newAdults++) {
      const newKids = totalPeople - newAdults;
      // Only include alternatives where we have fewer kids than original (more adults)
      if (newKids >= 0 && newKids < totalKids && newAdults > totalAdults) {
        // Distribute the new adults and kids across rooms
        // Strategy: Distribute proportionally based on original room sizes
        const newRoomCompositions = [];
        let remainingAdults = newAdults;
        let remainingKids = newKids;
        
        // Calculate total people per room for proportional distribution
        const roomPeopleCounts = originalCompositions.map(room => 
          (room.adults || 0) + (room.kids || 0)
        );
        const totalRoomPeople = roomPeopleCounts.reduce((sum, count) => sum + count, 0);
        
        for (let i = 0; i < numRooms; i++) {
          const originalRoom = originalCompositions[i];
          const roomPeople = roomPeopleCounts[i];
          
          if (roomPeople === 0) {
            // Empty room - assign minimum (1 adult)
            newRoomCompositions.push({
              adults: 1,
              kids: 0,
              infants: originalRoom.infants || 0,
              kidsAges: [],
            });
            remainingAdults -= 1;
            continue;
          }
          
          // Calculate proportion
          const proportion = totalRoomPeople > 0 ? roomPeople / totalRoomPeople : 1 / numRooms;
          
          // Allocate adults and kids proportionally
          let roomAdults = Math.max(1, Math.floor(newAdults * proportion));
          let roomKids = Math.floor(newKids * proportion);
          
          // Last room gets all remaining to ensure exact distribution
          if (i === numRooms - 1) {
            roomAdults = remainingAdults;
            roomKids = remainingKids;
          } else {
            // Ensure at least 1 adult per room and don't exceed remaining
            const minAdultsForOtherRooms = numRooms - i - 1; // At least 1 adult per remaining room
            roomAdults = Math.max(1, Math.min(roomAdults, remainingAdults - minAdultsForOtherRooms));
            roomKids = Math.min(roomKids, remainingKids);
          }
          
          // Update remaining counts
          remainingAdults -= roomAdults;
          remainingKids -= roomKids;
          
          // Create kidsAges array
          const newKidsAges = Array(roomKids).fill(defaultKidsAge);
          
          newRoomCompositions.push({
            adults: roomAdults,
            kids: roomKids,
            infants: originalRoom.infants || 0,
            kidsAges: newKidsAges,
          });
        }
        
        // Only add if distribution was successful (all adults and kids allocated)
        if (remainingAdults === 0 && remainingKids === 0) {
          alternatives.push(newRoomCompositions);
        }
      }
    }
    
    return alternatives;
  };

  const normalizeRoomCompositions = (rooms) => {
    const normalized = (rooms || []).map((room) => {
      const adults = room.adults ?? room.AdultsCount ?? room[ADULTS] ?? 2;
      const kids = room.kids ?? room.KidsCount ?? room[KIDS] ?? 0;
      const infants = room.infants ?? room.InfantsCount ?? room[INFANTS] ?? 0;
      let kidsAges = room.kidsAges ?? room.KidsAges ?? room[KIDS_AGES] ?? [];
      if (!Array.isArray(kidsAges)) {
        kidsAges = [];
      }
      while (kidsAges.length < kids) {
        kidsAges.push(2);
      }
      if (kidsAges.length > kids) {
        kidsAges = kidsAges.slice(0, kids);
      }
      return { adults, kids, infants, kidsAges };
    });

    if (normalized.length === 0) {
      normalized.push({ adults: 2, kids: 0, infants: 0, kidsAges: [] });
    }

    return normalized;
  };

  const buildLdsPayload = (isOrganizedTour = false, alternativeRoomCompositions = null) => {
    const overrides = submitSearchOverridesRef.current;
    const effectiveLocation = overrides?.location ?? location;
    const effectiveArrival = overrides?.calendarDateArrival ?? calendarDateArrival;
    const effectiveLeave = overrides?.calendarDateLeave ?? calendarDateLeave;
    // const { adults, kids } = getPeoplesSum(roomInfo);
    
    // Use alternative room compositions if provided, otherwise use original
    let roomCompositions;
    if (alternativeRoomCompositions) {
      roomCompositions = alternativeRoomCompositions;
    } else {
      // Ensure roomInfo.rooms exists and has valid data
      // Map rooms to ensure 'adults' field exists (convert from ADULTS constant if needed)
      roomCompositions = normalizeRoomCompositions(roomInfo?.rooms);
    }
    
    // If no rooms, add default room with 2 adults
    if (roomCompositions.length === 0) {
      roomCompositions.push({
        adults: 2,
        kids: 0,
        infants: 0,
        kidsAges: [],
      });
    }
    
    const allLocations = getAllLocations(effectiveLocation);
    const { city: cityValue, isAllDestinations } =
      resolveLdsSearchCityFromLocation(effectiveLocation);

    const { city: cityForPayload, categories: categoriesForPayload } =
      resolveLdsSearchPayloadFilters({ cityValue, categories });

    if (import.meta.env?.DEV && isOrganizedTour) {
      console.log("[ORG_TOUR_DESTINATION_NORMALIZE]", {
        location,
        city: cityValue,
        isAllDestinations,
      });
      if (isAllDestinations) {
        console.log("[ORG_TOUR_ALL_DESTINATIONS_SELECTED]", { city: cityValue });
      }
    }

    if (isOrganizedTour) {
      const payload = {
        city: cityForPayload,
        categories: categoriesForPayload,
        min_dep_date: dayjs(tourMonth, "MM/YYYY")
          .startOf("month")
          .format("DD/MM/YYYY"),
        max_dep_date: dayjs(tourMonth, "MM/YYYY")
          .endOf("month")
          .format("DD/MM/YYYY"),
        min_ret_date: dayjs(tourMonth, "MM/YYYY")
          .startOf("month")
          .format("DD/MM/YYYY"),
        max_ret_date: dayjs(tourMonth, "MM/YYYY")
          .endOf("month")
          .format("DD/MM/YYYY"),
        room_compositions: roomCompositions,
        ...(allLocations.length > 1 && {
          cities: allLocations
            .map((loc) => resolveLdsSearchCityFromLocation(loc).city)
            .filter(Boolean),
        }),
      };
      if (import.meta.env?.DEV) {
        console.log("[ORG_TOUR_SEARCH_PAYLOAD]", payload);
      }
      return withCorporateSearchChannel(payload);
    } else {
      /* LDS /searchLds דורש dd/MM/yyyy; URL vacation-packages ו-localStorage עלולים להיות YYYY-MM-DD */
      const depLds = toLdsDdMmYyyy(effectiveArrival);
      const retLds = toLdsDdMmYyyy(effectiveLeave);
      return withCorporateSearchChannel({
        // num_infants=0, todo(sagiv): check if need
        dep_date: depLds ?? effectiveArrival ?? "",
        ret_date: retLds ?? effectiveLeave ?? "",
        city: cityForPayload,
        categories: categoriesForPayload,
        date_range: dateRange,
        room_compositions: roomCompositions,
        ...(allLocations.length > 1 && {
          cities: allLocations
            .map((loc) => resolveLdsSearchCityFromLocation(loc).city)
            .filter(Boolean),
        }),
      });
    }
  };

  async function searchLDSResultsForPackageMonth(ldsSearchSessionId, signal) {
    const isMiniSite = isPublicConsumerSearchSurface();
    const monthKey = String(packageMonth || "").trim();
    const effectiveLdsDates =
      Array.isArray(ldsDates) && ldsDates.length > 0
        ? ldsDates
        : getFallbackLdsDatesForAllDestinations();
    const dateCombinations = getPackageMonthDateCombinations(
      effectiveLdsDates,
      monthKey,
      50,
    );

    if (dateCombinations.length === 0) {
      setError("לא נמצאו תוצאות");
      setSearchResults(null);
      if (!isMiniSite) {
        notifyAgentSearch("info", {
          message: "אין תאריכים זמינים",
          description: "לא נמצאו תאריכי יציאה זמינים בחודש שנבחר",
          duration: 4,
        });
      }
      _initAfterSearch(ldsSearchSessionId);
      return;
    }

    const { city: cityValue } = resolveLdsSearchCityFromLocation(location);
    const roomCompositions = normalizeRoomCompositions(roomInfo?.rooms);
    const { city: cityForPayload, categories: categoriesForPayload } =
      resolveLdsSearchPayloadFilters({ cityValue, categories });
    const isPublicSite = isVacationPackagesB2CPathname();
    
    const currentCategory =
      categories && categories.length > 0 && !categories.includes("ALL")
        ? categories[0]
        : null;

    setLastSearchPayload({
      requestId: ldsSearchSessionId,
      payload: {
        packageMonth: monthKey,
        city: cityValue,
        dateCombinationsCount: dateCombinations.length,
      },
      dep_date: null,
      ret_date: null,
      source: isPublicSite ? "public" : "agent",
      createdAt: Date.now(),
    });

    const searchPromises = dateCombinations.map(({ depDate, retDate }) => {
      const searchData = {
        dep_date: depDate,
        ret_date: retDate,
        city: cityForPayload,
        categories: categoriesForPayload,
        date_range: dateRange,
        room_compositions: roomCompositions,
      };
      return searchLdsRequestTracked(searchData, {
        signal,
        requestId: ldsSearchSessionId,
        source: "searchLDSResults.packageMonth",
      })
        .then((res) => res?.data)
        .then((res) => {
          if (res?.opStatus?.status !== "SUCCESS") return [];
          const deals = isArray(res.dealGroup)
            ? res.dealGroup
            : isArray(res.deal)
              ? res.deal
              : res.dealGroup
                ? [res.dealGroup]
                : [];
          return (Array.isArray(deals) ? deals : []).map((deal) => ({
            ...deal,
            category: currentCategory,
            __packageMonthSearch: monthKey,
            __searchPayloadDates: { checkIn: depDate, checkOut: retDate },
          }));
        })
        .catch((err) => {
          if (isSearchLdsAbortError(err)) return [];
          return [];
        });
    });

    try {
      const allResults = (await Promise.all(searchPromises)).flat();
      if (ldsSearchSessionId !== currentSearchRequestIdRef.current) return;

      const seenDealKeys = new Set();
      const uniqueResults = [];
      for (const deal of allResults) {
        const dealId = deal?.fullDeal?.dealId || deal?.dealId;
        const hotelId =
          deal?.fullDeal?.hotel?.hotelId ||
          deal?.hotel?.hotelId ||
          deal?.hotelId ||
          "unknown";
        const depDate = deal?.fullDeal?.depDate || deal?.depDate || "";
        const retDate = deal?.fullDeal?.retDate || deal?.retDate || "";
        const key = dealId
          ? `id:${dealId}`
          : `key:${hotelId}:${depDate}:${retDate}`;
        if (seenDealKeys.has(key)) continue;
        seenDealKeys.add(key);
        uniqueResults.push(deal);
      }

      if (uniqueResults.length === 0) {
        setError("לא נמצאו תוצאות");
        if (ldsSearchSessionId === currentSearchRequestIdRef.current) {
          setSearchResults(null);
        }
        if (!isMiniSite) {
          notifyAgentSearch("error", {
            message: "לא נמצאו תוצאות",
            description: "לא נמצאו חבילות זמינות בחודש שנבחר",
            duration: 5,
          });
        }
      } else if (commitActiveSearchRows(uniqueResults, ldsSearchSessionId, "package_month")) {
        setError(false);
      }
    } catch (err) {
      if (!isSearchLdsAbortError(err)) {
        setError("אירעה שגיאה אנא נסה שנית.");
      }
    } finally {
      if (currentSearchRequestIdRef.current === ldsSearchSessionId) {
        try {
          recordAgentRecentSearchSnapshot();
        } catch (_) {
          /* ignore */
        }
      }
      _initAfterSearch(ldsSearchSessionId);
    }
  }

  function searchLDSResults(isOrganizedTour = false) {
    const ldsSearchSessionId = currentSearchRequestIdRef.current;
    const signal = abortControllerRef.current?.signal;
    const overrides = submitSearchOverridesRef.current;
    const effectiveLocation = overrides?.location ?? location;
    const ldsSnap = searchCommitSnapshotRef.current;
    if (ldsSnap && ldsSnap.requestId === ldsSearchSessionId) {
      searchCommitSnapshotRef.current = {
        ...ldsSnap,
        product: isOrganizedTour ? ORGANIZED_TOURS : searchTabRef.current,
        destinationKey: destinationKeyFromLocation(effectiveLocation) || ldsSnap.destinationKey,
        checkIn: calendarDateArrivalRef.current || ldsSnap.checkIn,
        checkOut: calendarDateLeaveRef.current || ldsSnap.checkOut,
        occupancySignature: buildPackageOccupancySignature({ rooms: roomInfo?.rooms }),
      };
    }

    if (
      !isOrganizedTour &&
      packageDateMode === "month" &&
      String(packageMonth || "").trim()
    ) {
      return searchLDSResultsForPackageMonth(ldsSearchSessionId, signal);
    }

    const allLocations = getAllLocations(effectiveLocation);
    
    // If multiple locations selected, search each one separately and combine results
    if (allLocations.length > 1) {
      return searchMultipleLocations(
        allLocations,
        isOrganizedTour,
        ldsSearchSessionId,
      );
    }
    
    // Single location search (original behavior)
    const data = buildLdsPayload(isOrganizedTour);
    const isDedicatedDestinationRoute =
      typeof window !== "undefined" &&
      window.location.pathname.includes("/vacation-packages/destination/");
    const destinationSearchParams =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search || "")
        : null;
    const destinationCodeFromQuery = String(
      destinationSearchParams?.get("destinationCode") || ""
    )
      .trim()
      .toUpperCase();
    const destinationNameFromQuery = String(
      destinationSearchParams?.get("destinationName") || ""
    ).trim();
    const destinationCodeForDebug = String(
      destinationCodeFromQuery || data?.destinationCode || data?.city || ""
    )
      .trim()
      .toUpperCase();
    const destinationNameForDebug = String(
      destinationNameFromQuery ||
        (destinationCodeForDebug
          ? translateLDSDestinations(destinationCodeForDebug)?.CITYNAME_HEB ||
            translateLDSDestinations(destinationCodeForDebug)?.CITYNAME ||
            ""
          : "")
    ).trim();
    const cityForDebug = String(data?.city || "").trim();
    const primaryPayload =
      isDedicatedDestinationRoute && !isOrganizedTour
        ? {
            ...data,
            ...(destinationCodeForDebug
              ? { destinationCode: destinationCodeForDebug }
              : {}),
            ...(destinationNameForDebug
              ? { destinationName: destinationNameForDebug }
              : {}),
          }
        : data;
    const currentPathname =
      typeof window !== "undefined" ? String(window.location.pathname || "") : "";
    const isPublicVacationPackagesPath =
      currentPathname.includes("/vacation-packages") &&
      !currentPathname.includes("/agent/");
    const payloadSource = isPublicVacationPackagesPath ? "public" : "agent";
    const activeSearchPayload = {
      requestId: ldsSearchSessionId,
      payload: primaryPayload,
      dep_date: primaryPayload?.dep_date || null,
      ret_date: primaryPayload?.ret_date || null,
      source: payloadSource,
      createdAt: Date.now(),
    };
    setLastSearchPayload(activeSearchPayload);
    const normalizedSearchKey = [
      String(primaryPayload?.destinationCode || destinationCodeForDebug || "").trim().toUpperCase(),
      String(primaryPayload?.city || "").trim().toUpperCase(),
      String(primaryPayload?.dep_date || "").trim(),
      String(primaryPayload?.ret_date || "").trim(),
      String(primaryPayload?.date_range ?? "").trim(),
      JSON.stringify(primaryPayload?.room_compositions || []),
    ].join("|");
    const queryLimit =
      typeof window !== "undefined"
        ? Number(new URLSearchParams(window.location.search || "").get("limit"))
        : null;
    const queryPage =
      typeof window !== "undefined"
        ? Number(new URLSearchParams(window.location.search || "").get("page"))
        : null;
    if (process.env.NODE_ENV === "development" && !isOrganizedTour) {
      console.log("[LDS_AGENT_VS_PUBLIC_PAYLOAD_DIFF]", {
        source: payloadSource,
        pathname: currentPathname || null,
        normalizedSearchKey,
        payload: primaryPayload,
        activeFilters: {
          categories: categories?.includes("ALL") ? [] : categories || [],
          city: primaryPayload?.city || null,
          destinationCode: primaryPayload?.destinationCode || destinationCodeForDebug || null,
          destinationName: primaryPayload?.destinationName || destinationNameForDebug || null,
          dateRange: Number(primaryPayload?.date_range || 0),
          roomCompositionsCount: Array.isArray(primaryPayload?.room_compositions)
            ? primaryPayload.room_compositions.length
            : 0,
        },
        flexibleFields: {
          date_range: Number(primaryPayload?.date_range || 0),
          dep_date: primaryPayload?.dep_date || null,
          ret_date: primaryPayload?.ret_date || null,
        },
        page: Number.isFinite(queryPage) && queryPage > 0 ? queryPage : null,
        limit: Number.isFinite(queryLimit) && queryLimit > 0 ? queryLimit : null,
      });
    }
    const shouldLogPublicDestinationFlex =
      isDedicatedDestinationRoute && process.env.NODE_ENV === "development";
    const parseLdsDate = (raw) => {
      const v = String(raw || "").trim();
      const parts = v.split("/");
      if (parts.length !== 3) return null;
      const [d, m, y] = parts.map((n) => parseInt(n, 10));
      if (!d || !m || !y) return null;
      return new Date(y, m - 1, d);
    };
    const nightsForDebug = (() => {
      const dep = parseLdsDate(primaryPayload?.dep_date);
      const ret = parseLdsDate(primaryPayload?.ret_date);
      if (!dep || !ret) return null;
      const diff = Math.round((ret.getTime() - dep.getTime()) / (1000 * 60 * 60 * 24));
      return Number.isFinite(diff) ? diff : null;
    })();
    if (shouldLogPublicDestinationFlex) {
      console.log("[PUBLIC_DESTINATION_FLEX_SEARCH_DEBUG]", {
        destinationCode: destinationCodeForDebug || null,
        destinationName: destinationNameForDebug || null,
        city: cityForDebug || null,
        checkIn: primaryPayload?.dep_date || null,
        checkOut: primaryPayload?.ret_date || null,
        nights: nightsForDebug,
        flexible: Number(primaryPayload?.date_range || 0) > 0,
        finalPayload: primaryPayload,
        phase: "before_search",
      });
    }
    // אטרקציות מופעלות מ-submitSearch עם אותו requestId — לא כאן (מונע כפילות / בלבול request id)
    // Check if we're in mini site (vacation-packages page or agent mini site)
    const isMiniSite = isPublicConsumerSearchSurface();
    
    // Get original room compositions to generate alternatives
    const originalRoomCompositions = (roomInfo?.rooms || []).map((room) => {
      const adults = room.adults ?? room[ADULTS] ?? 2;
      const kids = room.kids ?? room[KIDS] ?? 0;
      const infants = room.infants ?? room[INFANTS] ?? 0;
      let kidsAges = room.kidsAges ?? room[KIDS_AGES] ?? [];
      if (!Array.isArray(kidsAges)) kidsAges = [];
      while (kidsAges.length < kids) kidsAges.push(2);
      if (kidsAges.length > kids) kidsAges = kidsAges.slice(0, kids);
      return { adults, kids, infants, kidsAges };
    });
    
    // Generate alternative room compositions with same total people
    const alternativeCompositions = isOrganizedTour
      ? []
      : generateAlternativeRoomCompositions(originalRoomCompositions);
    
    // Optimized helper function to check if deal matches searched hotel name
    // Pre-compile regex patterns outside the function for better performance
    const CITY_WORDS_PATTERN = /\b(dubai|דובאי|tel aviv|תל אביב|paris|פריז|london|לונדון|new york|ניו יורק|rome|רומא|istanbul|איסטנבול|athens|אתונה|barcelona|ברצלונה|madrid|מדריד|amsterdam|אמסטרדם|prague|פראג|eilat|אילת|eilath|jerusalem|ירושלים|haifa|חיפה|netanya|נתניה)\b/gi;
    const COMMON_WORDS_PATTERN = /\b(hotel|מלון|the|of|and|in|on|at)\b/gi;
    const SPACES_PATTERN = /\s+/g;
    
    const hebrewToEnglishMap = {
      'שרתון': 'sheraton',
      'הילטון': 'hilton',
      'היאט': 'hyatt',
      'מריוט': 'marriott',
      'רדיסון': 'radisson',
      'קראון': 'crown',
      'אינטרקונטיננטל': 'intercontinental',
      'דן': 'dan',
      'דן הוטלס': 'dan hotels',
      'דן אקספרס': 'dan express',
      'ישרוטל': 'isrotel',
      'רויאל': 'royal',
      'גארדן': 'garden'
    };
    
    // Simplified and optimized check function
    const checkIfSearchedHotel = (deal, searchedHotelName) => {
              if (!searchedHotelName) return false;
              
              const dealHotelName = deal?.hotel?.hotelName || 
                                   deal?.fullDeal?.hotel?.hotelName ||
                                   deal?.hotel?.name ||
                                   deal?.fullDeal?.hotel?.name ||
                                   deal?.hotel?.DisplayName ||
                                   deal?.fullDeal?.hotel?.DisplayName ||
                                   deal?.hotelName ||
                                   deal?.name ||
                                   deal?.DisplayName || '';
              
              if (!dealHotelName) return false;
              
              const dealNameLower = dealHotelName.toLowerCase().trim();
              const searchNameLower = searchedHotelName.toLowerCase().trim();
              
              // Quick exact match check first
              if (dealNameLower === searchNameLower) return true;
              
              // Simplified cleaning - use pre-compiled regex
              const cleanName = (name) => {
                return name
                  .replace(CITY_WORDS_PATTERN, '')
                  .replace(COMMON_WORDS_PATTERN, '')
                  .replace(SPACES_PATTERN, ' ')
                  .trim();
              };
              
              const dealHotelNameOnly = cleanName(dealNameLower);
              const searchHotelNameOnly = cleanName(searchNameLower);
              
              // Check for exact match after cleaning
              if (dealHotelNameOnly === searchHotelNameOnly) return true;
              
              // Quick substring check (most common case)
              if (dealHotelNameOnly.length >= 3 && searchHotelNameOnly.length >= 3) {
                if (dealHotelNameOnly.includes(searchHotelNameOnly) || 
                    searchHotelNameOnly.includes(dealHotelNameOnly)) {
                  return true;
                }
              }
              
              // Quick Hebrew/English match check (only for known brands)
              for (const [hebrew, english] of Object.entries(hebrewToEnglishMap)) {
                const hasHebrew = searchHotelNameOnly.includes(hebrew) || dealHotelNameOnly.includes(hebrew);
                const hasEnglish = searchHotelNameOnly.includes(english) || dealHotelNameOnly.includes(english);
                
                if (hasHebrew && hasEnglish) {
                  // For short words, require start match
                  if (hebrew.length <= 3 || english.length <= 3) {
                    if ((searchHotelNameOnly.startsWith(hebrew) || searchHotelNameOnly.startsWith(english)) &&
                        (dealHotelNameOnly.startsWith(hebrew) || dealHotelNameOnly.startsWith(english))) {
                      return true;
                    }
                  } else {
                    return true;
                  }
                }
              }
              
              return false;
    };
    
    const extractLdsDeals = (res) => {
      if (res?.opStatus?.status === "NO_RESULTS") return [];
      if (res?.opStatus?.status !== "SUCCESS") return [];
      if (isOrganizedTour) {
        return isArray(res.deal) ? res.deal : [res.deal];
      }
      return isArray(res.dealGroup)
        ? res.dealGroup
        : isArray(res.deal)
          ? res.deal
          : [res.dealGroup];
    };
    const summarizeDealsForCompare = (rows) => {
      const arr = Array.isArray(rows) ? rows : [];
      const hotelKeys = new Set();
      const dealKeys = new Set();
      const sampleHotels = [];
      const sampleDealKeys = [];
      for (const row of arr) {
        const full = row?.fullDeal || row;
        const hotel = full?.hotel || row?.hotel || null;
        const hotelName = String(
          hotel?.hotelNameEn || hotel?.hotelName || full?.hotelName || row?.hotelName || ""
        ).trim();
        const hotelCity = String(hotel?.hotelCity || row?.hotelCity || "").trim().toUpperCase();
        const hotelId =
          hotel?.hotelId ??
          hotel?.id ??
          hotel?.hotelID ??
          hotel?.hotel_id ??
          full?.hotelId ??
          row?.hotelId ??
          null;
        const hotelKey = `${String(hotelId ?? "").trim() || "na"}|${hotelCity}|${hotelName.toLowerCase()}`;
        hotelKeys.add(hotelKey);
        if (hotelName && sampleHotels.length < 20 && !sampleHotels.includes(hotelName)) {
          sampleHotels.push(hotelName);
        }
        const dealId =
          full?.dealId ??
          full?.ldsDealId ??
          row?.dealId ??
          row?.ldsDealId ??
          `${hotelKey}|${String(full?.depDate || row?.depDate || "").trim()}`;
        const dealKey = String(dealId).trim();
        if (dealKey) {
          dealKeys.add(dealKey);
          if (sampleDealKeys.length < 20 && !sampleDealKeys.includes(dealKey)) {
            sampleDealKeys.push(dealKey);
          }
        }
      }
      return {
        rawCount: arr.length,
        uniqueHotelsCount: hotelKeys.size,
        uniqueDealsCount: dealKeys.size,
        sampleHotels,
        sampleDealKeys,
      };
    };
    const filterStaleResultsBySearchDates = (rows, action) => {
      if (isOrganizedTour) return Array.isArray(rows) ? rows : [];
      const expectedDates = {
        checkIn: primaryPayload?.dep_date || calendarDateArrival,
        checkOut: primaryPayload?.ret_date || calendarDateLeave,
      };
      return (Array.isArray(rows) ? rows : []).filter((row) => {
        const staleCheck = isSearchResultStaleForDates(row, expectedDates);
        if (staleCheck.isStaleResult && import.meta.env?.DEV) {
          console.warn("[SEARCH_RESULTS_STALE_GUARD_TRACE]", {
            expectedCheckIn: staleCheck.expectedCheckIn,
            expectedCheckOut: staleCheck.expectedCheckOut,
            resultCheckIn: staleCheck.checkIn,
            resultCheckOut: staleCheck.checkOut,
            isStaleResult: true,
            action,
            requestId: ldsSearchSessionId,
            dealId: row?.fullDeal?.dealId || row?.dealId || null,
            hotelName:
              row?.fullDeal?.hotel?.hotelName ||
              row?.hotel?.hotelName ||
              row?.hotelName ||
              null,
          });
        }
        return !staleCheck.isStaleResult;
      });
    };

    // Perform main search
    if (import.meta.env?.DEV && isOrganizedTour) {
      console.log("[ORG_TOUR_SEARCH_API_CALL]", {
        endpoint: "/searchLds",
        payload: primaryPayload,
      });
    }
    const mainSearchPromise = searchLdsRequestTracked(primaryPayload, {
      signal,
      requestId: ldsSearchSessionId,
      source: "searchLDSResults.main",
    })
      .then((res) => res?.data)
      .then((res) => {
        if (import.meta.env?.DEV && isOrganizedTour) {
          console.log("[ORG_TOUR_SEARCH_API_RESPONSE]", {
            status: res?.opStatus?.status,
            dealCount: isOrganizedTour
              ? (isArray(res?.deal) ? res.deal.length : res?.deal ? 1 : 0)
              : undefined,
          });
        }
        if (res?.opStatus?.status === "NO_RESULTS") {
          // Don't show error immediately - wait for alternative searches to complete
          // Only show error if all searches (main + alternatives) return no results
          // Don't show notification in mini site
          if (!isMiniSite) {
            // Don't show notification here - will show later if all searches fail
          }
          // Return empty array to allow alternative searches to proceed
          return [];
        } else if (res?.opStatus?.status === "SUCCESS") {
          return extractLdsDeals(res);
          // // use for debug only
          // dealsArr = dealsArr.filter((item) => {
          //   return item.fullDeal.supplierDetails.supplierId != "KV";
          // });

          // // filter only rooms with infants
          // dealsArr = dealsArr.filter((item) => {
          //   const infantFare = item.fullDeal?.dealFare?.infantFare;
          //   return infantFare !== undefined && infantFare !== null;
          // });
        } else {
          setError("אירעה שגיאה אנא נסה שנית.");
          return [];
        }
      })
      .catch((err) => {
        if (isSearchLdsAbortError(err)) {
          return [];
        }
        if (import.meta.env?.DEV && isOrganizedTour) {
          console.log("[ORG_TOUR_SEARCH_API_ERROR]", {
            message: err?.message || String(err),
            response: err?.response?.data,
          });
        }
        setError("אירעה שגיאה אנא נסה שנית.");
        return [];
      });
    
    // Perform additional searches with alternative room compositions
    const alternativeSearchPromises = alternativeCompositions.map((altComposition, altIdx) => {
      const altData = buildLdsPayload(isOrganizedTour, altComposition);
      return searchLdsRequestTracked(altData, {
        signal,
        requestId: ldsSearchSessionId,
        source: `searchLDSResults.alt${altIdx}`,
      })
        .then((res) => res?.data)
        .then((res) => {
          if (res?.opStatus?.status === "SUCCESS") return extractLdsDeals(res);
          return [];
        })
        .catch((err) => {
          if (isSearchLdsAbortError(err)) return [];
          return [];
        });
    });
    
    const orgTourSearchT0 =
      import.meta.env?.DEV && isOrganizedTour ? orgTourPerfNow() : null;
    const orgTourLdsCallCount = isOrganizedTour
      ? 1 + alternativeSearchPromises.length
      : 0;
    if (import.meta.env?.DEV && isOrganizedTour) {
      logOrgTourSearchPerf("ORG_TOUR_SEARCH_PERF_START", {
        ldsCalls: orgTourLdsCallCount,
      });
    }

    // Combine all search results (main + alternatives)
    return Promise.all([mainSearchPromise, ...alternativeSearchPromises])
      .then(async (allResults) => {
        if (ldsSearchSessionId !== currentSearchRequestIdRef.current) {
          if (import.meta.env?.DEV) {
            console.warn("[SEARCH_RESULTS_STALE_GUARD_TRACE]", {
              expectedCheckIn: primaryPayload?.dep_date || calendarDateArrival || null,
              expectedCheckOut: primaryPayload?.ret_date || calendarDateLeave || null,
              resultCheckIn: null,
              resultCheckOut: null,
              isStaleResult: true,
              action: "ignore_stale_response_request_id",
              requestId: ldsSearchSessionId,
              activeRequestId: currentSearchRequestIdRef.current,
            });
          }
          return;
        }
        const mainResults = allResults[0] || [];
        const alternativeResults = allResults.slice(1).flat();
        const rawCombinedResults = [...mainResults, ...alternativeResults];
        if (process.env.NODE_ENV === "development" && !isOrganizedTour) {
          console.log("[AGENT_PUBLIC_RAW_COMPARE]", {
            source: payloadSource,
            payload: primaryPayload,
            ...summarizeDealsForCompare(rawCombinedResults),
          });
          if (payloadSource === "public") {
            console.log("[PUBLIC_SEARCH_BROKEN_AUDIT]", {
              route:
                typeof window !== "undefined" ? window.location.pathname || null : null,
              payload: primaryPayload,
              searchSent: true,
              responseCount: Array.isArray(rawCombinedResults) ? rawCombinedResults.length : 0,
              afterPublicFlowCount: null,
              afterResultsCount: null,
              error: null,
            });
          }
        }
        // Combine and deduplicate results by dealId
        // Use a more robust deduplication that handles cases where dealId might be missing
        const seenDealIds = new Set();
        const seenDealKeys = new Set(); // For deals without dealId, use a composite key
        let combinedResults = [];
        
        // Helper function to create a unique key for a deal
        // This key is used to deduplicate results - same dealId = same deal (even if from different room compositions)
        const getDealKey = (deal) => {
          const dealId = deal?.fullDeal?.dealId || deal?.dealId;
          if (dealId) {
            // If we have a dealId, use it as the unique key
            // This means the same deal from different room compositions will be deduplicated
            return `id:${dealId}`;
          }
          // If no dealId, create a composite key from hotel, dates, and supplier
          // This is a fallback for deals without dealId
          const hotelId = deal?.fullDeal?.hotel?.hotelId || deal?.hotel?.hotelId || deal?.hotelId || 'unknown';
          const depDate = deal?.fullDeal?.depDate || deal?.depDate || '';
          const retDate = deal?.fullDeal?.retDate || deal?.retDate || '';
          const supplierId = deal?.fullDeal?.supplierDetails?.supplierId || deal?.supplierDetails?.supplierId || deal?.supplierId || 'unknown';
          return `key:${hotelId}:${depDate}:${retDate}:${supplierId}`;
        };
        
        // Add main results first (they have priority)
        mainResults.forEach(deal => {
          const dealKey = getDealKey(deal);
          if (!seenDealKeys.has(dealKey)) {
            seenDealKeys.add(dealKey);
            combinedResults.push(deal);
          }
        });
        
        // Add alternative results (only if not already present)
        alternativeResults.forEach(deal => {
          const dealKey = getDealKey(deal);
          if (!seenDealKeys.has(dealKey)) {
            seenDealKeys.add(dealKey);
            // Mark as alternative result for potential filtering/display
            combinedResults.push({
              ...deal,
              isAlternativeRoomComposition: true,
            });
          }
        });
        let fallbackPerformed = false;
        if (combinedResults.length === 0 && isDedicatedDestinationRoute && !isOrganizedTour) {
          const destinationCode = String(
            destinationCodeForDebug || data?.destinationCode || data?.city || ""
          )
            .trim()
            .toUpperCase();
          const translated = destinationCode ? translateLDSDestinations(destinationCode) : null;
          const fallbackCity = String(
            translated?.CITYNAME || translated?.CITYNAME_HEB || ""
          )
            .trim()
            .toUpperCase();
          const currentCity = String(data?.city || "").trim().toUpperCase();
          if (fallbackCity && fallbackCity !== currentCity) {
            fallbackPerformed = true;
            const fallbackPayload = {
              ...data,
              city: fallbackCity,
              destinationCode: destinationCode || undefined,
              destinationName: destinationNameForDebug || undefined,
            };
            if (shouldLogPublicDestinationFlex) {
              console.log("[PUBLIC_DESTINATION_FLEX_SEARCH_DEBUG]", {
                destinationCode: destinationCode || null,
                destinationName: destinationNameForDebug || null,
                city: fallbackCity,
                checkIn: fallbackPayload?.dep_date || null,
                checkOut: fallbackPayload?.ret_date || null,
                nights: nightsForDebug,
                flexible: Number(fallbackPayload?.date_range || 0) > 0,
                finalPayload: fallbackPayload,
                phase: "fallback_city_name",
              });
            }
            try {
              const fallbackRes = await searchLdsRequestTracked(fallbackPayload, {
                signal,
                requestId: ldsSearchSessionId,
                source: "searchLDSResults.fallbackCity",
              }).then((res) => res?.data);
              const fallbackDeals = extractLdsDeals(fallbackRes).filter(Boolean);
              if (fallbackDeals.length > 0) {
                combinedResults = fallbackDeals;
              }
            } catch (_err) {
              // Keep original empty result behavior if fallback request fails
            }
          }
        }
        if (shouldLogPublicDestinationFlex) {
          console.log("[PUBLIC_DESTINATION_FLEX_SEARCH_DEBUG]", {
            destinationCode: destinationCodeForDebug || null,
            destinationName: destinationNameForDebug || null,
            city: cityForDebug || null,
            checkIn: primaryPayload?.dep_date || null,
            checkOut: primaryPayload?.ret_date || null,
            nights: nightsForDebug,
            flexible: Number(primaryPayload?.date_range || 0) > 0,
            resultsCount: Array.isArray(combinedResults) ? combinedResults.length : 0,
            fallbackPerformed,
            phase: "after_search",
          });
        }
        if (process.env.NODE_ENV === "development" && !isOrganizedTour) {
          console.log("[AGENT_PUBLIC_RAW_COMPARE]", {
            source: payloadSource,
            payload: primaryPayload,
            phase: "post_dedupe",
            ...summarizeDealsForCompare(combinedResults),
          });
        }
        
        // Get current category
        const currentCategory = categories && categories.length > 0 && !categories.includes("ALL") ? categories[0] : null;
        
        // If no results from any search (main or alternatives), show error
        if (combinedResults.length === 0) {
          setError("לא נמצאו תוצאות");
          if (!isMiniSite) {
            notifyAgentSearch("error", {
              message: "לא נמצאו תוצאות",
              description:
                "לא נמצאו תוצאות עבור התאריכים הספציפיים, ניתן לבצע חיפוש מחדש לתאריכים שונים.",
              duration: 5,
              placement: "bottomRight",
            });
          }
        }
        
        combinedResults = filterStaleResultsBySearchDates(
          combinedResults,
          "drop_stale_result_before_context_update",
        );

        // Get captured request ID to prevent race conditions
        const currentRequestId = ldsSearchSessionId;
        
        // Check if this request was cancelled
        if (
          abortControllerRef.current?.signal.aborted ||
          currentRequestId !== currentSearchRequestIdRef.current
        ) {
          if (DEBUG_SEARCH) {
            console.log(`[SearchContext] Search #${currentRequestId} was cancelled, ignoring results`);
          }
          return;
        }
        
        // Set combined results with race condition protection
        if (isOrganizedTour) {
          const toursWithCategory = combinedResults.map(tour => ({
            ...tour,
            category: currentCategory,
          }));
          
          // Only update if this is still the latest request
          if (currentRequestId === currentSearchRequestIdRef.current) {
            if (DEBUG_SEARCH) {
              console.log(`[SearchContext] Setting search results #${currentRequestId} (tours):`, toursWithCategory.length);
            }
            commitActiveSearchRows(toursWithCategory, currentRequestId, "organized_tour");
            if (import.meta.env?.DEV) {
              console.log("[ORG_TOUR_SEARCH_RESULTS_COUNT]", {
                count: toursWithCategory.length,
              });
            }
            if (orgTourSearchT0 != null) {
              const ms = orgTourPerfNow() - orgTourSearchT0;
              logOrgTourSearchPerf("ORG_TOUR_SEARCH_PERF_END", {
                ms: Math.round(ms * 10) / 10,
                ldsCalls: orgTourLdsCallCount,
                resultCount: toursWithCategory.length,
              });
              pushOrgTourTracePanelSample({
                kind: "search",
                ms: Math.round(ms * 10) / 10,
                ldsCalls: orgTourLdsCallCount,
                results: toursWithCategory.length,
              });
            }
          } else if (DEBUG_SEARCH) {
            console.log(`[SearchContext] Ignoring stale results for request #${currentRequestId}, current is #${currentSearchRequestIdRef.current}`);
          }
        } else {
          const dealsWithCategory = combinedResults.map(deal => ({
            ...deal,
            category: currentCategory,
            isSearchedHotel: false, // Will be updated asynchronously
            __searchRequestId: currentRequestId,
            __searchPayloadDates: {
              checkIn: primaryPayload?.dep_date || calendarDateArrival || null,
              checkOut: primaryPayload?.ret_date || calendarDateLeave || null,
            },
            __resultSource: payloadSource,
          }));
          
          // Only update if this is still the latest request
          if (currentRequestId === currentSearchRequestIdRef.current) {
            if (DEBUG_SEARCH) {
              console.log(`[SearchContext] Setting search results #${currentRequestId} (deals):`, dealsWithCategory.length);
            }
            if (!commitActiveSearchRows(dealsWithCategory, currentRequestId, "package_search")) {
              return;
            }
            if (!isOrganizedTour) {
              logPackageOccupancyTrace("search_response", {
                searchId: currentRequestId,
                roomsComposition: roomInfo?.rooms,
                currentOccupancySignature: buildPackageOccupancySignature({
                  rooms: roomInfo?.rooms,
                }),
              });
            }
            
            // Update isSearchedHotel asynchronously (with race condition protection)
            getSearchPhrase.then((searchPhrase) => {
              // Check again if this is still the latest request
              if (currentRequestId !== currentSearchRequestIdRef.current) {
                if (DEBUG_SEARCH) {
                  console.log(`[SearchContext] Ignoring async hotel update for stale request #${currentRequestId}`);
                }
                return;
              }
              
              const hotelName = searchPhrase?.hotelName;
              if (!hotelName) return;
              
              setSearchResults(prevResults => {
                // Double-check we're still on the latest request
                if (currentRequestId !== currentSearchRequestIdRef.current) {
                  if (DEBUG_SEARCH) {
                    console.log(`[SearchContext] Ignoring hotel update - request #${currentRequestId} is stale`);
                  }
                  return prevResults;
                }
                
                if (!prevResults || prevResults.length === 0) return prevResults;
                return prevResults.map(deal => ({
                  ...deal,
                  isSearchedHotel: checkIfSearchedHotel(deal, hotelName),
                }));
              });
            }).catch(() => {
              // Silently fail
            });
          } else if (DEBUG_SEARCH) {
            console.log(`[SearchContext] Ignoring stale results for request #${currentRequestId}, current is #${currentSearchRequestIdRef.current}`);
          }
        }
      })
      .catch((err) => {
        if (!isSearchLdsAbortError(err)) {
          setError("אירעה שגיאה אנא נסה שנית.");
        }
        throw err;
      })
      .finally(() => {
        if (import.meta.env?.DEV && isOrganizedTour) {
          console.log("[ORG_TOUR_SEARCH_FINALLY]", {
            stale: currentSearchRequestIdRef.current !== ldsSearchSessionId,
          });
        }
        if (
          isOrganizedTour &&
          currentSearchRequestIdRef.current === ldsSearchSessionId
        ) {
          bumpOrganizedTourSearchHandshake();
        }
        if (currentSearchRequestIdRef.current === ldsSearchSessionId) {
          try {
            recordAgentRecentSearchSnapshot();
          } catch (_) {
            /* ignore */
          }
        }
        _initAfterSearch(ldsSearchSessionId);
      });
  }

  // Search multiple locations and combine results
  async function searchMultipleLocations(
    locations,
    isOrganizedTour = false,
    ldsSearchSessionId,
  ) {
    const signal = abortControllerRef.current?.signal;
    const isMiniSite = isPublicConsumerSearchSurface();
    // submitSearch כבר קרא ל-_initBeforeSearch ול-scheduleParallelAttractionsSearch עם אותו requestId
    setTimeLeft(TWENTY_MIN);

    const allResults = [];
    const seenDealIds = new Set(); // Track unique deals to avoid duplicates
    const searchPromises = [];
    let completedSearches = 0;
    const capturedSession =
      typeof ldsSearchSessionId === "number"
        ? ldsSearchSessionId
        : currentSearchRequestIdRef.current;
    const totalSearches = locations.length;
    
    // Limit concurrent requests to avoid overwhelming the server
    const MAX_CONCURRENT_REQUESTS = 3; // Process 3 locations at a time
    let currentBatchIndex = 0;
    
    // Get current category from state
    const currentCategory = categories && categories.length > 0 && !categories.includes("ALL") ? categories[0] : null;
    const expectedMultiDates = {
      checkIn: toLdsDdMmYyyy(calendarDateArrival) ?? calendarDateArrival ?? "",
      checkOut: toLdsDdMmYyyy(calendarDateLeave) ?? calendarDateLeave ?? "",
    };
    
    // Debounce timer for progressive updates
    let updateTimer = null;
    const DEBOUNCE_DELAY = 300; // Update UI every 300ms max
    
    // Function to update results progressively with debouncing
    const updateResults = (newDeals) => {
      if (!Array.isArray(newDeals) || newDeals.length === 0) {
        return;
      }
      
      if (capturedSession !== currentSearchRequestIdRef.current) {
        if (import.meta.env?.DEV) {
          console.warn("[SEARCH_RESULTS_STALE_GUARD_TRACE]", {
            expectedCheckIn: expectedMultiDates.checkIn || null,
            expectedCheckOut: expectedMultiDates.checkOut || null,
            resultCheckIn: null,
            resultCheckOut: null,
            isStaleResult: true,
            action: "ignore_stale_multi_location_update_request_id",
            requestId: capturedSession,
            activeRequestId: currentSearchRequestIdRef.current,
          });
        }
        return;
      }

      // Filter out stale results, duplicates and add category
      const uniqueDeals = newDeals.filter(deal => {
        if (!isOrganizedTour) {
          const staleCheck = isSearchResultStaleForDates(deal, expectedMultiDates);
          if (staleCheck.isStaleResult) {
            if (import.meta.env?.DEV) {
              console.warn("[SEARCH_RESULTS_STALE_GUARD_TRACE]", {
                expectedCheckIn: staleCheck.expectedCheckIn,
                expectedCheckOut: staleCheck.expectedCheckOut,
                resultCheckIn: staleCheck.checkIn,
                resultCheckOut: staleCheck.checkOut,
                isStaleResult: true,
                action: "drop_stale_multi_location_result",
                requestId: capturedSession,
                dealId: deal?.fullDeal?.dealId || deal?.dealId || null,
              });
            }
            return false;
          }
        }
        const dealId = deal?.fullDeal?.dealId || deal?.dealId || JSON.stringify(deal);
        if (seenDealIds.has(dealId)) {
          return false;
        }
        seenDealIds.add(dealId);
        return true;
      });
      
      // Add category to each deal
      const dealsWithCategory = uniqueDeals.map(deal => ({
        ...deal,
        category: currentCategory,
        __searchRequestId: capturedSession,
        __searchPayloadDates: expectedMultiDates,
        __resultSource: "searchLDSResults.multiLocation",
      }));
      
      allResults.push(...dealsWithCategory);
      
      // Debounce UI updates to avoid too many re-renders
      if (updateTimer) {
        clearTimeout(updateTimer);
      }
      
      updateTimer = setTimeout(() => {
        // Only update if no newer search has started
        const currentRequestId = capturedSession;
        if (currentRequestId !== currentSearchRequestIdRef.current) {
          if (import.meta.env?.DEV) {
            console.warn("[SEARCH_RESULTS_STALE_GUARD_TRACE]", {
              expectedCheckIn: expectedMultiDates.checkIn || null,
              expectedCheckOut: expectedMultiDates.checkOut || null,
              resultCheckIn: null,
              resultCheckOut: null,
              isStaleResult: true,
              action: "ignore_stale_multi_location_debounced_update",
              requestId: capturedSession,
              activeRequestId: currentSearchRequestIdRef.current,
            });
          }
          updateTimer = null;
          return;
        }
        if (DEBUG_SEARCH) {
          console.log(`[SearchContext] Progressive update for request #${currentRequestId}:`, allResults.length, 'deals');
        }
        setSearchResults([...allResults]);
        updateTimer = null;
      }, DEBOUNCE_DELAY);
    };
    
    // Helper function to process a single location search
    const processLocationSearch = (loc) => {
      // Build payload with this location
      const roomCompositions = (roomInfo?.rooms || []).map((room) => {
        const adults = room.adults ?? room[ADULTS] ?? 2;
        const kids = room.kids ?? room[KIDS] ?? 0;
        const infants = room.infants ?? room[INFANTS] ?? 0;
        let kidsAges = room.kidsAges ?? room[KIDS_AGES] ?? [];
        
        if (!Array.isArray(kidsAges)) {
          kidsAges = [];
        }
        
        while (kidsAges.length < kids) {
          kidsAges.push(2);
        }
        
        if (kidsAges.length > kids) {
          kidsAges = kidsAges.slice(0, kids);
        }
        
        return {
          adults: adults,
          kids: kids,
          infants: infants,
          kidsAges: kidsAges,
        };
      });
      
      if (roomCompositions.length === 0) {
        roomCompositions.push({
          adults: 2,
          kids: 0,
          infants: 0,
          kidsAges: [],
        });
      }
      
      const { city: searchCity } = resolveLdsSearchCityFromLocation(loc);
      const { city: cityForPayload, categories: categoriesForPayload } =
        resolveLdsSearchPayloadFilters({ cityValue: searchCity, categories });
      
      let data;
      if (isOrganizedTour) {
        data = {
          city: cityForPayload,
          categories: categoriesForPayload,
          min_dep_date: dayjs(tourMonth, "MM/YYYY")
            .startOf("month")
            .format("DD/MM/YYYY"),
          max_dep_date: dayjs(tourMonth, "MM/YYYY")
            .endOf("month")
            .format("DD/MM/YYYY"),
          min_ret_date: dayjs(tourMonth, "MM/YYYY")
            .startOf("month")
            .format("DD/MM/YYYY"),
          max_ret_date: dayjs(tourMonth, "MM/YYYY")
            .endOf("month")
            .format("DD/MM/YYYY"),
          room_compositions: roomCompositions,
        };
      } else {
        const depLds = toLdsDdMmYyyy(calendarDateArrival);
        const retLds = toLdsDdMmYyyy(calendarDateLeave);
        data = {
          dep_date: depLds ?? calendarDateArrival ?? "",
          ret_date: retLds ?? calendarDateLeave ?? "",
          city: cityForPayload,
          categories: categoriesForPayload,
          date_range: dateRange,
          room_compositions: roomCompositions,
        };
      }

      if (import.meta.env?.DEV && isOrganizedTour) {
        console.log("[ORG_TOUR_SEARCH_PAYLOAD]", data);
      }
      
      return searchLdsRequestTracked(data, {
        signal,
        requestId: capturedSession,
        source: "searchLDSResults.multiLocation",
      })
        .then((res) => res?.data)
        .then((res) => {
          completedSearches++;
          
          if (res?.opStatus?.status === "SUCCESS") {
            let dealsArr;
            if (isOrganizedTour) {
              dealsArr = isArray(res.deal) ? res.deal : [res.deal];
            } else {
              dealsArr = isArray(res.dealGroup)
                ? res.dealGroup
                : isArray(res.deal)
                  ? res.deal
                  : [res.dealGroup];
            }
            
            if (dealsArr && dealsArr.length > 0) {
              updateResults(dealsArr);
            }
          }
          // NO_RESULTS is handled by completedSearches++ in finally
        })
        .catch((err) => {
          console.error(`Error searching location ${loc?.AIRPORTCODE}:`, err);
          completedSearches++;
        })
        .finally(() => {
          if (completedSearches === totalSearches) {
            if (capturedSession !== currentSearchRequestIdRef.current) {
              if (updateTimer) {
                clearTimeout(updateTimer);
                updateTimer = null;
              }
              if (import.meta.env?.DEV) {
                console.warn("[SEARCH_RESULTS_STALE_GUARD_TRACE]", {
                  expectedCheckIn: expectedMultiDates.checkIn || null,
                  expectedCheckOut: expectedMultiDates.checkOut || null,
                  resultCheckIn: null,
                  resultCheckOut: null,
                  isStaleResult: true,
                  action: "ignore_stale_multi_location_final_update",
                  requestId: capturedSession,
                  activeRequestId: currentSearchRequestIdRef.current,
                });
              }
              _initAfterSearch(capturedSession);
              return;
            }
            // Clear any pending debounce timer
            if (updateTimer) {
              clearTimeout(updateTimer);
              updateTimer = null;
            }
            
            // Final update with all results
            if (allResults.length === 0) {
              setError("לא נמצאו תוצאות");
              if (!isMiniSite) {
                notifyAgentSearch("error", {
                  message: "לא נמצאו תוצאות",
                  description: "לא נמצאו תוצאות עבור היעדים שנבחרו",
                  duration: 5,
                });
              }
              setSearchResults(isOrganizedTour ? [] : null);
            } else {
              // Ensure final results are displayed
              setSearchResults([...allResults]);
            }
            if (import.meta.env?.DEV && isOrganizedTour) {
              console.log("[ORG_TOUR_SEARCH_FINALLY]", {
                multi: true,
                stale:
                  currentSearchRequestIdRef.current !== capturedSession,
              });
            }
            if (
              isOrganizedTour &&
              currentSearchRequestIdRef.current === capturedSession
            ) {
              bumpOrganizedTourSearchHandshake();
            }
            _initAfterSearch(capturedSession);
            if (currentSearchRequestIdRef.current === capturedSession) {
              try {
                recordAgentRecentSearchSnapshot();
              } catch (_) {
                /* ignore */
              }
            }
          }
        });
    };
    
    // Process locations in batches to limit concurrent requests
    const processBatch = async (batch) => {
      const batchPromises = batch.map(loc => processLocationSearch(loc));
      await Promise.all(batchPromises);
    };
    
    // Split locations into batches
    for (let i = 0; i < locations.length; i += MAX_CONCURRENT_REQUESTS) {
      const batch = locations.slice(i, i + MAX_CONCURRENT_REQUESTS);
      await processBatch(batch);
    }
    
    return allResults;
  }

  function _hasZeroAge(rooms) {
    for (const room of rooms) {
      if (room.kidsAges.includes(0)) {
        return true;
      }
    }
    return false;
  }

  // Search for any category with all destinations
  // maxMonths: how many months ahead to search (default 12); use 6 for "half year" quick search
  async function searchByCategory(categoryName, maxMonths = 12) {
    // Check if we're in mini site
    const isMiniSite = isPublicConsumerSearchSurface();
    let categorySearchRid;
    let updateTimer = null;
    const CATEGORY_RESULTS_DEBOUNCE_MS = 300;

    categorySearchInFlightRef.current = true;
    categorySearchResultsRidRef.current = null;
    
    try {
      setIsSearched(true);
      categorySearchRid = _initBeforeSearch();
      setTimeLeft(TWENTY_MIN);
      
      // Get all destinations for the specified category
      // Include destinations that match the category OR have "ALL" subject (to search all destinations for this category)
      const categoryNameUpper = (categoryName || "").toUpperCase();
      const categoryDestinations = destinations.filter(
        (dest) => {
          if (!dest || dest.dealType !== "FlightsHotel") return false;
          const subject = (dest.subject || "").toString().trim();
          // Match category name (case-insensitive)
          if (subject.toUpperCase() === categoryNameUpper) return true;
          // Include destinations with "ALL" subject (these apply to all categories)
          if (subject === "ALL" || subject === "") return true;
          return false;
        }
      );
      
      if (categoryDestinations.length === 0) {
        if (!isMiniSite) {
          notifyAgentSearch("warning", {
            message: "לא נמצאו יעדים",
            description: `לא נמצאו יעדים זמינים עבור קטגוריית ${categoryName}`,
            duration: 3,
          });
        }
        return;
      }
      
      // Instead of looping through cities, send empty city to get ALL destinations
      // The backend will return all available destinations when city is empty/null
      
      const seats = roomInfo.rooms.reduce((acc, curr) => acc + curr.adults + curr.kids, 0);
      const { year, month } = getCurrentDate();
      const futureMonths = [];
      const MAX_MONTHS = Math.max(1, Math.min(24, maxMonths));
      for (let i = 0; i < MAX_MONTHS; i++) {
        if (i === 0) {
          futureMonths.push(getNextMonth(month, year, 0));
        } else {
          const prevMonth = futureMonths[i - 1].month;
          const prevYear = futureMonths[i - 1].year;
          futureMonths.push(getNextMonth(prevMonth, prevYear));
        }
      }
      
      const allResults = [];
      const seenDealIds = new Set(); // Track unique deals to avoid duplicates
      const searchPromises = [];
      let totalSearches = 0;
      let completedSearches = 0;
      
      // Function to sort results by departure date (earliest first)
      const sortResultsByDate = (results) => {
        return [...results].sort((a, b) => {
          // Get departure dates from searchDate or from deal
          const dateA = a?.searchDate?.depDate || a?.fullDeal?.depDate || a?.depDate || "";
          const dateB = b?.searchDate?.depDate || b?.fullDeal?.depDate || b?.depDate || "";
          
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1; // Put items without date at the end
          if (!dateB) return -1;
          
          // Compare dates (format: YYYYMMDD or DD/MM/YYYY)
          try {
            let dateAValue, dateBValue;
            
            // Handle DD/MM/YYYY format
            if (dateA.includes("/")) {
              const [dayA, monthA, yearA] = dateA.split("/");
              dateAValue = new Date(parseInt(yearA), parseInt(monthA) - 1, parseInt(dayA));
            } else {
              // Handle YYYYMMDD format
              dateAValue = new Date(dateA.substring(0, 4), parseInt(dateA.substring(4, 6)) - 1, dateA.substring(6, 8));
            }
            
            if (dateB.includes("/")) {
              const [dayB, monthB, yearB] = dateB.split("/");
              dateBValue = new Date(parseInt(yearB), parseInt(monthB) - 1, parseInt(dayB));
            } else {
              dateBValue = new Date(dateB.substring(0, 4), parseInt(dateB.substring(4, 6)) - 1, dateB.substring(6, 8));
            }
            
            return dateAValue - dateBValue; // Ascending order (earliest first)
          } catch (e) {
            return 0;
          }
        });
      };
      
      // Function to update results progressively
      const flushCategoryResults = () => {
        if (categorySearchRid !== currentSearchRequestIdRef.current) {
          return;
        }
        commitActiveSearchRows(sortResultsByDate(allResults), categorySearchRid, "category_search");
      };

      const updateResults = (newDeals) => {
        if (categorySearchRid !== currentSearchRequestIdRef.current) {
          return;
        }
        if (!Array.isArray(newDeals) || newDeals.length === 0) {
          return;
        }
        
        // Filter out duplicates and add new deals
        const uniqueNewDeals = newDeals.filter((deal) => {
          const dealId = deal?.fullDeal?.dealId || deal?.dealId;
          const isDuplicate = dealId && seenDealIds.has(dealId);
          
          if (isDuplicate) {
            return false; // Skip duplicate
          }
          
          if (dealId) {
            seenDealIds.add(dealId);
          }
          
          return true;
        });
        
        if (uniqueNewDeals.length > 0) {
          allResults.push(...uniqueNewDeals);
          if (updateTimer) {
            clearTimeout(updateTimer);
          }
          updateTimer = setTimeout(() => {
            updateTimer = null;
            flushCategoryResults();
          }, CATEGORY_RESULTS_DEBOUNCE_MS);
        }
      };
      
      // Fetch dates for ALL destinations (city: "ALL")
      const datePromises = futureMonths.map(async (futureMonth) => {
        try {
          const response = await getLdsDates({
            type: "FlightsHotel",
            origin: origin?.AIRPORTCODE,
            city: "",
            categories: [categoryName],
            date: `${futureMonth.month}/${futureMonth.year}`,
            seats: seats,
          });
          if (!isLdsResponseEmpty(response)) {
            return response.data.calendarLink;
          }
          return null;
        } catch (err) {
          return null;
        }
      });
      
      const DATES_TIMEOUT_MS = 20000;
      let datesTimeoutId;
      const datesTimeoutPromise = new Promise((resolve) => {
        datesTimeoutId = setTimeout(() => resolve("timeout"), DATES_TIMEOUT_MS);
      });
      const dateResponsesOrTimeout = await Promise.race([
        Promise.all(datePromises).then((rows) => {
          clearTimeout(datesTimeoutId);
          return rows;
        }),
        datesTimeoutPromise,
      ]);
      let destinationDates =
        dateResponsesOrTimeout === "timeout"
          ? []
          : dateResponsesOrTimeout.filter((res) => res !== null).flat();
      
      // When searching "all destinations" (city: ""), getLdsDates often returns no data — use fallback dates so search can run
      if (!destinationDates || destinationDates.length === 0) {
        const fallbackDates = getFallbackLdsDatesForAllDestinations();
        const cutoff = dayjs().add(MAX_MONTHS, "month");
        destinationDates = fallbackDates.filter((link) => {
          const dep = link?.depDate && link.depDate.split("/").length === 3
            ? dayjs(link.depDate, "DD/MM/YYYY")
            : null;
          return dep && dep.isBefore(cutoff);
        });
      }
      
      if (!destinationDates || destinationDates.length === 0) {
        if (!isMiniSite) {
          notifyAgentSearch("warning", {
            message: "לא נמצאו תאריכים",
            description: `לא נמצאו תאריכים זמינים עבור קטגוריית ${categoryName}`,
            duration: 3,
          });
        }
        return;
      }
      
      // Get unique date combinations
      const dateCombinations = new Set();
      destinationDates.forEach((link) => {
        if (link && link.depDate && link.retDate) {
          dateCombinations.add(`${link.depDate}|${link.retDate}`);
        }
      });
      
      // Sort date combinations by departure date (earliest first)
      // Parse dates and sort them
      const sortedDateCombinations = Array.from(dateCombinations).sort((a, b) => {
        const [depDateA] = a.split("|");
        const [depDateB] = b.split("|");
        
        // Parse dates (format: DD/MM/YYYY)
        const parseDate = (dateStr) => {
          if (!dateStr) return new Date(0); // Very old date if missing
          try {
            const [day, month, year] = dateStr.split("/");
            return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          } catch (e) {
            return new Date(0);
          }
        };
        
        const dateA = parseDate(depDateA);
        const dateB = parseDate(depDateB);
        
        return dateA - dateB; // Ascending order (earliest first)
      });
      
      // Limit date combinations to prevent too many requests (max 50 total).
      // Spread the 50 across the full period (maxMonths) so we get results from all months, not just the first few days.
      const MAX_DATE_COMBINATIONS = 50;
      const n = sortedDateCombinations.length;
      const limitedCombinations =
        n <= MAX_DATE_COMBINATIONS
          ? sortedDateCombinations
          : Array.from({ length: MAX_DATE_COMBINATIONS }, (_, i) => {
              const idx = n === 1 ? 0 : Math.round((i * (n - 1)) / (MAX_DATE_COMBINATIONS - 1));
              return sortedDateCombinations[Math.min(idx, n - 1)];
            });
      
      totalSearches = limitedCombinations.length;
      
      limitedCombinations.forEach((dateCombo) => {
        const [depDate, retDate] = dateCombo.split("|");
        const searchData = {
          dep_date: depDate,
          ret_date: retDate,
          city: null, // Empty city = ALL destinations
          categories: [categoryName], // Filter by category
          date_range: 0,
          room_compositions: normalizeRoomCompositions(roomInfo?.rooms),
        };
        
        searchPromises.push(
          searchLdsRequestTracked(searchData, {
            signal: abortControllerRef.current?.signal,
            requestId: categorySearchRid,
            source: "searchByCategory",
          })
            .then((res) => res?.data)
            .then((res) => {
              completedSearches++;
              if (categorySearchRid !== currentSearchRequestIdRef.current) {
                return;
              }
              
              if (res?.opStatus?.status === "SUCCESS") {
                let deals = isArray(res.dealGroup)
                  ? res.dealGroup
                  : isArray(res.deal)
                    ? res.deal
                    : res.dealGroup
                      ? [res.dealGroup]
                      : [];
                
                if (Array.isArray(deals)) {
                  deals = deals.map((deal) => ({
                    ...deal,
                    searchDate: { depDate, retDate },
                    searchDestination: null, // No specific destination when searching all
                    category: categoryName, // Add category to deal so it's used in price calculation
                  }));
                  
                  // Update results immediately as they come in
                  updateResults(deals);
                }
              }
            })
            .catch((err) => {
              completedSearches++;
              // Silent fail - continue with other searches
            })
        );
      });
      
      // Add timeout to prevent infinite waiting (max 5 minutes)
      const SEARCH_TIMEOUT = 5 * 60 * 1000; // 5 minutes
      let searchTimeoutId;
      const timeoutPromise = new Promise((_, reject) => {
        searchTimeoutId = setTimeout(() => reject(new Error('Search timeout')), SEARCH_TIMEOUT);
      });
      
      try {
        await Promise.race([
          Promise.all(searchPromises),
          timeoutPromise
        ]);
      } catch (error) {
        if (error.message === 'Search timeout') {
          if (!isMiniSite) {
            notifyAgentSearch("warning", {
              message: "חיפוש הופסק",
              description: `החיפוש לקח יותר מדי זמן. מציג ${allResults.length} תוצאות שנמצאו עד כה.`,
              duration: 5,
            });
          }
        } else if (isSearchLdsAbortError(error)) {
          // Replaced by a newer search — do not surface as a user error
        } else {
          throw error;
        }
      } finally {
        clearTimeout(searchTimeoutId);
      }
      
      if (updateTimer) {
        clearTimeout(updateTimer);
        updateTimer = null;
      }

      if (categorySearchRid !== currentSearchRequestIdRef.current) {
        return;
      }

      // Results are already updated progressively, just show final summary
      if (allResults.length > 0) {
        commitActiveSearchRows(sortResultsByDate(allResults), categorySearchRid, "category_search");
        
        // Don't show notification in mini site
        if (!isMiniSite) {
          notifyAgentSearch("success", {
            message: `חיפוש ${categoryName} הושלם`,
            description: `נמצאו ${allResults.length} תוצאות עבור כל היעדים והתאריכים בקטגוריית ${categoryName}`,
            duration: 3,
          });
        }
      } else {
        setError("לא נמצאו תוצאות");
        setSearchResults(null);
        // Don't show notification in mini site
        if (!isMiniSite) {
          notifyAgentSearch("info", {
            message: "לא נמצאו תוצאות",
            description: `לא נמצאו תוצאות עבור קטגוריית ${categoryName} בכל היעדים והתאריכים`,
            duration: 3,
          });
        }
      }
    } catch (error) {
      if (!isSearchLdsAbortError(error)) {
        console.error(`Error in searchByCategory for ${categoryName}:`, error);
        setError("אירעה שגיאה אנא נסה שנית.");
        if (!isMiniSite) {
          notifyAgentSearch("error", {
            message: "שגיאה בחיפוש",
            description: `אירעה שגיאה בעת חיפוש ${categoryName}`,
            duration: 5,
          });
        }
      }
    } finally {
      if (updateTimer) {
        clearTimeout(updateTimer);
        updateTimer = null;
      }
      const isLatestCategorySession =
        categorySearchRid != null &&
        categorySearchRid === currentSearchRequestIdRef.current;
      // Do not _setCategories(newArray) here — a fresh ["ALL"] identity retriggers
      // the [categories] fetchLdsDates effect and can remount compact search.
      if (isLatestCategorySession) {
        categorySearchInFlightRef.current = false;
        categorySearchResultsRidRef.current = categorySearchRid;
        _initAfterSearch(categorySearchRid);
        if (pendingMainSearchLoadsRef.current === 0 && loadingRef.current) {
          setSearchLoading(false, {
            source: "SearchContext.searchByCategory.finally",
            requestId: categorySearchRid,
            reason: "guarantee_clear_after_category_search",
          });
        }
      } else if (categorySearchRid != null) {
        _initAfterSearch(categorySearchRid);
      } else {
        categorySearchInFlightRef.current = false;
        forceStopLoading();
      }
    }
  }

  // Search for SHOW category with all destinations (wrapper for backward compatibility)
  async function searchShows() {
    return searchByCategory("SHOW");
  }

  // Search for a specific hotel across all dates in a month (all available date combinations)
  // Can search across multiple months if monthsCount > 1
  // showAllResults: if true, return all deals without filtering (for debugging)
  async function searchHotelForMonth(hotelId, hotelName, month, year, monthsCount = 1, showAllResults = false, onResultCallback = null, skipGlobalLoading = false) {
    if (!hotelId && !hotelName) {
      notifyAgentSearch("error", {
        message: "שגיאה",
        description: "יש לספק מזהה מלון או שם מלון",
        duration: 3,
      });
      return [];
    }

      if (searchHotelForMonthAbortRef.current) {
        searchHotelForMonthAbortRef.current.abort();
      }
      searchHotelForMonthSessionRef.current += 1;
      const monthSearchSessionId = searchHotelForMonthSessionRef.current;
      const monthAbortController = new AbortController();
      searchHotelForMonthAbortRef.current = monthAbortController;
      const monthSignal = monthAbortController.signal;

      const monthClearLoadingIfLatest = () => {
        if (
          !skipGlobalLoading &&
          monthSearchSessionId === searchHotelForMonthSessionRef.current
        ) {
          setSearchLoading(false, {
            source: "SearchContext.searchHotelForMonth",
            sessionId: monthSearchSessionId,
            reason: "month_search_session_complete_or_abort",
          });
        }
      };

      // Only set global loading if not skipped (to avoid interfering with modals)
      if (!skipGlobalLoading) {
        setSearchLoading(true, {
          source: "SearchContext.searchHotelForMonth",
          sessionId: monthSearchSessionId,
          reason: "month_search_started",
        });
      }
      setError(false);

      const dlog = (...args) => {
        if (import.meta.env.DEV) console["log"](...args);
      };
      const dwarn = (...args) => {
        if (import.meta.env.DEV) console["warn"](...args);
      };
      const derr = (...args) => {
        if (import.meta.env.DEV) console["error"](...args);
      };

      const monthlyRecentPayload = buildMonthlyHotelPricingRecentPayload({
        hotelId,
        hotelName,
        month,
        year,
        monthsCount,
        roomInfo,
      });
      if (isTripMasterSurface()) {
        recordTripMasterPublicSearch(monthlyRecentPayload);
      } else {
        void persistAgentRecentSearch(monthlyRecentPayload);
      }

      dlog('[searchHotelForMonth] Starting search:', {
        hotelId,
        hotelName,
        month,
        year,
        monthsCount,
        showAllResults
      });

      try {
        // Get available dates for multiple months
        const seats = roomInfo?.rooms?.reduce((sum, room) => {
          return sum + (room.adults || 0) + (room.kids || 0);
        }, 0) || 2;

        let allAvailableDates = [];
        
        // Fetch dates for all requested months
        const monthPromises = [];
        for (let i = 0; i < monthsCount; i++) {
          // Calculate the actual month and year for this iteration
          let currentMonth = month + i;
          let currentYear = year;
          
          // Handle month overflow (e.g., month 13 = January of next year)
          while (currentMonth > 12) {
            currentMonth -= 12;
            currentYear += 1;
          }
          
          dlog('[searchHotelForMonth] Fetching dates for:', { currentMonth, currentYear });
        
        // Format date as MM/YYYY (with padding for month) - API expects 2-digit month based on error message
        const formattedMonth = String(currentMonth).padStart(2, '0');
        const dateString = `${formattedMonth}/${currentYear}`;
        
        dlog('[searchHotelForMonth] 📞 Calling getLdsDates with:', {
          dateString,
          currentMonth,
          currentYear,
          formattedMonth,
          originalFormat: `${currentMonth}/${currentYear}`,
          formattedFormat: dateString
        });
        
        const primaryLoc = getPrimaryLocation(location);
        const cityValue = primaryLoc?.AIRPORTCODE ?? location?.AIRPORTCODE ?? "";
        const monthPayload = {
          type: "FlightsHotel",
          origin: origin.AIRPORTCODE,
          city: cityValue,
          categories: categories?.includes("ALL") ? [] : categories,
          date: dateString,
          seats,
          hotel_monthly_pricing: true,
        };
        dlog("LDS payload (searchHotelForMonth)", JSON.stringify(monthPayload));
        monthPromises.push(
          getLdsDates(monthPayload)
            .then((datesResponse) => {
              // Log full response structure for debugging
              dlog('[searchHotelForMonth] 📥 Received dates response for:', {
                dateString,
                currentMonth,
                currentYear,
                hasData: !isLdsResponseEmpty(datesResponse),
                responseStructure: {
                  hasData: !!datesResponse?.data,
                  hasCalendarLink: !!datesResponse?.data?.calendarLink,
                  calendarLinkType: datesResponse?.data?.calendarLink ? 
                    (Array.isArray(datesResponse.data.calendarLink) ? 'array' : typeof datesResponse.data.calendarLink) : 'none',
                  calendarLinkValue: datesResponse?.data?.calendarLink,
                  opStatus: datesResponse?.opStatus,
                  fullResponse: datesResponse
                },
                calendarLinkCount: datesResponse?.data?.calendarLink ? 
                  (Array.isArray(datesResponse.data.calendarLink) ? datesResponse.data.calendarLink.length : 1) : 0,
                sampleDates: datesResponse?.data?.calendarLink ? 
                  (Array.isArray(datesResponse.data.calendarLink) ? 
                    datesResponse.data.calendarLink.slice(0, 3).map(d => ({
                      depDate: d.depDate || d.dep_date,
                      retDate: d.retDate || d.ret_date
                    })) : 
                    [{
                      depDate: datesResponse.data.calendarLink.depDate || datesResponse.data.calendarLink.dep_date,
                      retDate: datesResponse.data.calendarLink.retDate || datesResponse.data.calendarLink.ret_date
                    }]
                  ) : []
              });
              
              if (!isLdsResponseEmpty(datesResponse) && datesResponse?.data?.calendarLink) {
                const monthDates = Array.isArray(datesResponse.data.calendarLink)
                  ? datesResponse.data.calendarLink
                  : [datesResponse.data.calendarLink];
                
                // Log sample dates to verify they match the requested month
                const sampleDates = monthDates.slice(0, 5).map(d => {
                  const depDate = d.depDate || d.dep_date;
                  if (depDate) {
                    const [day, month, year] = depDate.split("/");
                    return {
                      depDate,
                      parsedMonth: parseInt(month),
                      parsedYear: parseInt(year),
                      requestedMonth: currentMonth,
                      requestedYear: currentYear,
                      matches: parseInt(month) === currentMonth && parseInt(year) === currentYear
                    };
                  }
                  return { depDate, error: 'no depDate' };
                });
                
                dlog('[searchHotelForMonth] 📅 Sample dates from getLdsDates:', sampleDates);
                
                // Keep all dates from API, but log which ones match the requested month
                // We'll filter by month later in the UI components
                const datesWithMetadata = monthDates.map(date => {
                  const depDate = date.depDate || date.dep_date;
                  let matchesRequestedMonth = false;
                  
                  if (depDate) {
                    try {
                      const [day, month, year] = depDate.split("/");
                      const depMonth = parseInt(month);
                      const depYear = parseInt(year);
                      matchesRequestedMonth = depMonth === currentMonth && depYear === currentYear;
                    } catch (e) {
                      // If we can't parse, assume it doesn't match
                    }
                  }
                  
                  return {
                    ...date,
                    month: currentMonth,
                    year: currentYear,
                    matchesRequestedMonth // Add flag for later filtering
                  };
                });
                
                // Log how many dates match the requested month
                const matchingDates = datesWithMetadata.filter(d => d.matchesRequestedMonth);
                dlog('[searchHotelForMonth] 📊 Dates by month:', {
                  requested: { month: currentMonth, year: currentYear },
                  totalFromAPI: monthDates.length,
                  matchingRequestedMonth: matchingDates.length,
                  fromOtherMonths: monthDates.length - matchingDates.length,
                  sampleMatching: matchingDates.slice(0, 3).map(d => ({
                    depDate: d.depDate || d.dep_date,
                    matches: d.matchesRequestedMonth
                  }))
                });
                
                return datesWithMetadata;
              }
              return [];
            })
            .catch((err) => {
              dwarn(`[searchHotelForMonth] Error fetching dates for ${currentMonth}/${currentYear}:`, err);
              return [];
            })
        );
      }

      // Wait for all month requests to complete
      const monthResults = await Promise.all(monthPromises);
      allAvailableDates = monthResults.flat();

      if (allAvailableDates.length === 0) {
        notifyAgentSearch("warning", {
          message: "לא נמצאו תאריכים",
          description: `לא נמצאו תאריכים זמינים לחודשים ${month}/${year}${monthsCount > 1 ? ` - ${month + monthsCount - 1}/${year}` : ''}`,
          duration: 3,
        });
        monthClearLoadingIfLatest();
        return [];
      }

      // Log all available dates to see what we got
      dlog('[searchHotelForMonth] 📅 All available dates:', {
        total: allAvailableDates.length,
        sampleDates: allAvailableDates.slice(0, 20).map(d => ({
          depDate: d.depDate || d.dep_date,
          retDate: d.retDate || d.ret_date,
          month: d.month,
          year: d.year
        })),
        dateRange: allAvailableDates.length > 0 ? {
          first: allAvailableDates[0]?.depDate || allAvailableDates[0]?.dep_date,
          last: allAvailableDates[allAvailableDates.length - 1]?.depDate || allAvailableDates[allAvailableDates.length - 1]?.dep_date
        } : null
      });
      
      // Don't limit searches - search all available dates for the month
      // This ensures we get all possible dates, not just the first 30
      const datesToSearch = allAvailableDates;
      
      dlog('[searchHotelForMonth] 🔍 Searching all available dates:', {
        totalAvailable: allAvailableDates.length,
        datesToSearch: datesToSearch.length,
        dateRange: datesToSearch.length > 0 ? {
          first: datesToSearch[0]?.depDate || datesToSearch[0]?.dep_date,
          last: datesToSearch[datesToSearch.length - 1]?.depDate || datesToSearch[datesToSearch.length - 1]?.dep_date
        } : null
      });

      const allResults = [];
      const searchPromises = [];
      let completedSearches = 0;

      datesToSearch.forEach((dateLink) => {
        const depDate = dateLink.depDate || dateLink.dep_date;
        const retDate = dateLink.retDate || dateLink.ret_date;
        
        // Get the month/year this dateLink belongs to (from metadata added in getLdsDates response)
        const dateLinkMonth = dateLink.month;
        const dateLinkYear = dateLink.year;

        if (!depDate || !retDate) return;
        
        // Log first few searches to verify we're searching for the right month
        if (datesToSearch.indexOf(dateLink) < 3) {
          const [depDay, depMonth, depYear] = depDate.split("/");
          dlog('[searchHotelForMonth] 🔍 Searching with dateLink:', {
            depDate,
            retDate,
            dateLinkMonth,
            dateLinkYear,
            actualDepMonth: parseInt(depMonth),
            actualDepYear: parseInt(depYear),
            matches: parseInt(depMonth) === dateLinkMonth && parseInt(depYear) === dateLinkYear
          });
        }

        const searchData = {
          dep_date: depDate,
          ret_date: retDate,
          city: getPrimaryLocation(location)?.AIRPORTCODE || location?.AIRPORTCODE,
          categories: categories?.includes("ALL") ? [] : categories,
          date_range: 0,
          room_compositions: normalizeRoomCompositions(roomInfo?.rooms),
          hotel_monthly_pricing: true,
        };

        searchPromises.push(
          searchLds(searchData, { signal: monthSignal })
            .then((res) => res?.data)
            .then((res) => {
              completedSearches++;
              
              if (res?.opStatus?.status === "SUCCESS") {
                let deals = isArray(res.dealGroup)
                  ? res.dealGroup
                  : isArray(res.deal)
                    ? res.deal
                    : res.dealGroup
                      ? [res.dealGroup]
                      : [];

                if (Array.isArray(deals) && deals.length > 0) {
                  // Log first deal structure for debugging (only first time)
                  if (allResults.length === 0 && completedSearches === 0) {
                    dlog('[searchHotelForMonth] 🔍 First deal structure:', {
                      deal: deals[0],
                      dealKeys: Object.keys(deals[0] || {}),
                      hotel: deals[0]?.hotel,
                      fullDeal: deals[0]?.fullDeal,
                      fullDealHotel: deals[0]?.fullDeal?.hotel,
                      searchingFor: { hotelId, hotelName },
                      showAllResults
                    });
                  }
                  
                  // Filter deals to match the specific hotel
                  // Try multiple ways to find the hotel in the deal structure
                  const hotelDeals = deals.filter((deal) => {
                      // Helper function to safely extract hotel ID from deal
                      const extractHotelId = (d) => {
                        // Try all possible paths for hotel ID - check multiple variations
                        const possibleIds = [
                          d?.hotel?.hotelId,
                          d?.hotel?.id,
                          d?.hotel?.LdsId,
                          d?.hotel?.ldsId,
                          d?.hotel?.LDSId,
                          d?.hotel?.ID,
                          d?.hotel?.HotelId,
                          d?.fullDeal?.hotel?.hotelId,
                          d?.fullDeal?.hotel?.id,
                          d?.fullDeal?.hotel?.LdsId,
                          d?.fullDeal?.hotel?.ldsId,
                          d?.fullDeal?.hotel?.LDSId,
                          d?.fullDeal?.hotel?.ID,
                          d?.fullDeal?.hotel?.HotelId,
                          d?.deal?.hotel?.hotelId,
                          d?.deal?.hotel?.id,
                          d?.deal?.hotel?.LdsId,
                          d?.deal?.hotel?.ID,
                          d?.hotelId,
                          d?.id,
                          d?.ID,
                          d?.HotelId,
                          d?.LdsId,
                          d?.ldsId,
                          d?.LDSId
                        ];
                        
                        // Return first non-null, non-undefined value
                        for (const id of possibleIds) {
                          if (id !== null && id !== undefined && id !== '') {
                            return id;
                          }
                        }
                        return null;
                      };
                      
                      // Helper function to safely extract hotel name from deal
                      const extractHotelName = (d) => {
                        // Try all possible paths for hotel name - check multiple variations
                        const possibleNames = [
                          d?.hotel?.hotelName,
                          d?.hotel?.name,
                          d?.hotel?.DisplayName,
                          d?.hotel?.Name,
                          d?.hotel?.HotelName,
                          d?.fullDeal?.hotel?.hotelName,
                          d?.fullDeal?.hotel?.name,
                          d?.fullDeal?.hotel?.DisplayName,
                          d?.fullDeal?.hotel?.Name,
                          d?.fullDeal?.hotel?.HotelName,
                          d?.deal?.hotel?.hotelName,
                          d?.deal?.hotel?.name,
                          d?.deal?.hotel?.DisplayName,
                          d?.deal?.hotel?.Name,
                          d?.hotelName,
                          d?.name,
                          d?.Name,
                          d?.DisplayName,
                          d?.HotelName
                        ];
                        
                        // Return first non-null, non-undefined value
                        for (const name of possibleNames) {
                          if (name !== null && name !== undefined && name !== '') {
                            return name;
                          }
                        }
                        return null;
                      };
                      
                      const dealHotelId = extractHotelId(deal);
                      const dealHotelName = extractHotelName(deal);
                      
                      // Debug: Log first few deals to see structure
                      if (deals.indexOf(deal) < 3 && completedSearches < 2) {
                        dlog('[searchHotelForMonth] 🔍 Checking deal structure:', {
                          dealIndex: deals.indexOf(deal),
                          searchingFor: { hotelId, hotelName },
                          found: { dealHotelId, dealHotelName },
                          dealKeys: Object.keys(deal).slice(0, 20),
                          hotelKeys: deal?.hotel ? Object.keys(deal.hotel) : null,
                          fullDealKeys: deal?.fullDeal ? Object.keys(deal.fullDeal) : null,
                          fullDealHotelKeys: deal?.fullDeal?.hotel ? Object.keys(deal.fullDeal.hotel) : null,
                          dealHotel: deal?.hotel,
                          fullDealHotel: deal?.fullDeal?.hotel
                        });
                      }
                      
                      // Helper function to normalize and compare IDs
                      const compareIds = (id1, id2) => {
                        if (!id1 || !id2) return false;
                        const str1 = String(id1).trim();
                        const str2 = String(id2).trim();
                        
                        // Try exact string match
                        if (str1 === str2) return true;
                        
                        // Try numeric comparison
                        const num1 = Number(str1);
                        const num2 = Number(str2);
                        if (!isNaN(num1) && !isNaN(num2) && num1 === num2) return true;
                        
                        // Try comparing as strings after converting to number and back
                        if (String(num1) === String(num2)) return true;
                        
                        return false;
                      };
                      
                      // Helper function to normalize and compare names
                      const compareNames = (name1, name2) => {
                        if (!name1 || !name2) return false;
                        const norm1 = name1.toLowerCase().trim();
                        const norm2 = name2.toLowerCase().trim();
                        
                        // Helper function to remove city names and common words, extract hotel name only
                        const extractHotelNameOnly = (name) => {
                          // Remove common city names and location words
                          const cityWords = ['dubai', 'דובאי', 'tel aviv', 'תל אביב', 'paris', 'פריז', 'london', 'לונדון', 
                                            'new york', 'ניו יורק', 'rome', 'רומא', 'istanbul', 'איסטנבול', 'athens', 'אתונה',
                                            'barcelona', 'ברצלונה', 'madrid', 'מדריד', 'amsterdam', 'אמסטרדם', 'prague', 'פראג',
                                            'eilat', 'אילת', 'eilath', 'jerusalem', 'ירושלים', 'haifa', 'חיפה', 'netanya', 'נתניה'];
                          let cleaned = name;
                          cityWords.forEach(city => {
                            cleaned = cleaned.replace(new RegExp(city, 'gi'), '').trim();
                          });
                          // Remove common words
                          const commonWords = ['hotel', 'מלון', 'the', 'of', 'and', 'in', 'on', 'at'];
                          commonWords.forEach(word => {
                            cleaned = cleaned.replace(new RegExp(`\\b${word}\\b`, 'gi'), '').trim();
                          });
                          return cleaned.replace(/\s+/g, ' ').trim();
                        };
                        
                        const name1HotelOnly = extractHotelNameOnly(norm1);
                        const name2HotelOnly = extractHotelNameOnly(norm2);
                        
                        // Try exact match (after cleaning)
                        if (name1HotelOnly === name2HotelOnly || norm1 === norm2) return true;
                        
                        // Helper function to extract keywords (words) from a name
                        const extractKeywords = (name) => {
                          return name.split(/\s+/).filter(word => word.length >= 3); // Only words with 3+ characters
                        };
                        
                        // Try partial match - but only if the hotel name part matches (not city)
                        // For short names (like "דן" / "DAN"), allow 3+ character matches
                        // But require that at least one significant keyword matches
                        const minMatchLength = Math.min(name1HotelOnly.length, name2HotelOnly.length) >= 4 ? 4 : 3;
                        const name1Keywords = extractKeywords(name1HotelOnly);
                        const name2Keywords = extractKeywords(name2HotelOnly);
                        
                        // Check if at least one keyword from name1 appears in name2 (or vice versa)
                        const hasKeywordMatch = name1Keywords.length > 0 && name2Keywords.length > 0 &&
                          (name1Keywords.some(keyword => name2HotelOnly.includes(keyword)) ||
                           name2Keywords.some(keyword => name1HotelOnly.includes(keyword)));
                        
                        if (name1HotelOnly.length > 0 && name2HotelOnly.length > 0 && 
                            (name1HotelOnly.includes(name2HotelOnly) || name2HotelOnly.includes(name1HotelOnly)) &&
                            Math.min(name1HotelOnly.length, name2HotelOnly.length) >= minMatchLength &&
                            hasKeywordMatch) {
                          return true;
                        }
                        
                        // Special check for Hebrew/English hotel name matches
                        const hebrewToEnglishMap = {
                          'שרתון': 'sheraton',
                          'הילטון': 'hilton',
                          'היאט': 'hyatt',
                          'מריוט': 'marriott',
                          'רדיסון': 'radisson',
                          'קראון': 'crown',
                          'אינטרקונטיננטל': 'intercontinental',
                          'דן': 'dan',
                          'דן הוטלס': 'dan hotels',
                          'דן אקספרס': 'dan express',
                          'ישרוטל': 'isrotel',
                          'רויאל': 'royal',
                          'גארדן': 'garden'
                        };
                        
                        for (const [hebrew, english] of Object.entries(hebrewToEnglishMap)) {
                          // Check if either name contains the Hebrew/English pair
                          const name1HasHebrew = name1HotelOnly.includes(hebrew);
                          const name1HasEnglish = name1HotelOnly.includes(english);
                          const name2HasHebrew = name2HotelOnly.includes(hebrew);
                          const name2HasEnglish = name2HotelOnly.includes(english);
                          
                          // Match if one has Hebrew and the other has English, or vice versa
                          // But only if this is a significant match (not just a small word)
                          if ((name1HasHebrew && name2HasEnglish) || (name1HasEnglish && name2HasHebrew)) {
                            // For short words like "דן", require that it's the main part of the name
                            if (hebrew.length <= 3 || english.length <= 3) {
                              // For short words, check if they appear as standalone words or at the start
                              const name1StartsWith = name1HotelOnly.startsWith(hebrew) || name1HotelOnly.startsWith(english);
                              const name2StartsWith = name2HotelOnly.startsWith(hebrew) || name2HotelOnly.startsWith(english);
                              if (name1StartsWith && name2StartsWith) {
                                return true;
                              }
                            } else {
                              // For longer words, any match is significant
                              return true;
                            }
                          }
                          
                          // Also check original names (before removing cities) for better matching
                          if ((norm1.includes(hebrew) && norm2.includes(english)) ||
                              (norm1.includes(english) && norm2.includes(hebrew))) {
                            // Same logic for short words
                            if (hebrew.length <= 3 || english.length <= 3) {
                              const name1StartsWith = norm1.startsWith(hebrew) || norm1.startsWith(english);
                              const name2StartsWith = norm2.startsWith(hebrew) || norm2.startsWith(english);
                              if (name1StartsWith && name2StartsWith) {
                                return true;
                              }
                            } else {
                              return true;
                            }
                          }
                        }
                        
                        return false;
                      };
                      
                      // Match by ID if available (more reliable)
                      if (hotelId) {
                        // Try ID match first
                        if (dealHotelId && compareIds(hotelId, dealHotelId)) {
                          return true;
                        }
                        
                        // If ID match fails, try name as fallback (only if name is provided)
                        if (hotelName && dealHotelName && compareNames(hotelName, dealHotelName)) {
                          return true;
                        }
                        
                        // If neither ID nor name matches, exclude this deal
                        return false;
                      } 
                      
                      // Match by name if ID not available
                      if (hotelName && dealHotelName) {
                        return compareNames(hotelName, dealHotelName);
                      }
                      
                      // If neither ID nor name provided, don't filter (shouldn't happen)
                      return false;
                    });
                  
                  // Log filtering results for debugging (only first few times)
                  if (completedSearches < 3) {
                    dlog('[searchHotelForMonth] 🔍 Filtering results:', {
                      totalDeals: deals.length,
                      filteredDeals: hotelDeals.length,
                      searchingFor: {
                        hotelId,
                        hotelIdString: String(hotelId),
                        hotelName,
                        hotelNameString: String(hotelName)
                      },
                      sampleDeal: deals.length > 0 ? {
                        dealHotelId: deals[0]?.hotel?.hotelId || 
                                    deals[0]?.fullDeal?.hotel?.hotelId ||
                                    deals[0]?.hotel?.id ||
                                    deals[0]?.fullDeal?.hotel?.id ||
                                    deals[0]?.hotelId ||
                                    deals[0]?.id ||
                                    deals[0]?.hotel?.LdsId ||
                                    deals[0]?.fullDeal?.hotel?.LdsId,
                        dealHotelName: deals[0]?.hotel?.hotelName || 
                                      deals[0]?.fullDeal?.hotel?.hotelName ||
                                      deals[0]?.hotel?.name ||
                                      deals[0]?.fullDeal?.hotel?.name ||
                                      deals[0]?.hotel?.DisplayName ||
                                      deals[0]?.fullDeal?.hotel?.DisplayName ||
                                      deals[0]?.hotelName ||
                                      deals[0]?.name ||
                                      deals[0]?.DisplayName
                      } : null,
                      allHotelIds: [...new Set(deals.map(d => {
                        return d?.hotel?.hotelId || 
                               d?.fullDeal?.hotel?.hotelId || 
                               d?.hotel?.id ||
                               d?.fullDeal?.hotel?.id ||
                               d?.hotelId ||
                               d?.id ||
                               d?.hotel?.LdsId ||
                               d?.fullDeal?.hotel?.LdsId;
                      }).filter(Boolean))],
                      allHotelNames: [...new Set(deals.map(d => {
                        return d?.hotel?.hotelName || 
                               d?.fullDeal?.hotel?.hotelName ||
                               d?.hotel?.name ||
                               d?.fullDeal?.hotel?.name ||
                               d?.hotel?.DisplayName ||
                               d?.fullDeal?.hotel?.DisplayName ||
                               d?.hotelName ||
                               d?.name ||
                               d?.DisplayName;
                      }).filter(Boolean))],
                    });
                  }
                  
                  // Log if no matches found for debugging (only first few times)
                  if (!showAllResults && hotelDeals.length === 0 && deals.length > 0 && completedSearches < 3) {
                    const sampleDeal = deals[0];
                    const sampleDealHotelId = sampleDeal?.hotel?.hotelId || 
                                             sampleDeal?.fullDeal?.hotel?.hotelId || 
                                             sampleDeal?.hotel?.id ||
                                             sampleDeal?.fullDeal?.hotel?.id ||
                                             sampleDeal?.hotelId ||
                                             sampleDeal?.id ||
                                             sampleDeal?.deal?.hotel?.hotelId ||
                                             sampleDeal?.deal?.hotel?.id;
                    const sampleDealHotelName = sampleDeal?.hotel?.hotelName || 
                                                sampleDeal?.fullDeal?.hotel?.hotelName ||
                                                sampleDeal?.hotel?.name ||
                                                sampleDeal?.fullDeal?.hotel?.name ||
                                                sampleDeal?.hotelName ||
                                                sampleDeal?.name ||
                                                sampleDeal?.deal?.hotel?.hotelName ||
                                                sampleDeal?.deal?.hotel?.name;
                    
                    dwarn('[searchHotelForMonth] ⚠️ No hotel matches found for date:', depDate, {
                      searchingFor: {
                        hotelId,
                        hotelIdType: typeof hotelId,
                        hotelIdString: String(hotelId),
                        hotelName,
                        hotelNameType: typeof hotelName,
                        hotelNameString: String(hotelName)
                      },
                      foundInDeal: {
                        sampleDealHotelId,
                        sampleDealHotelIdType: typeof sampleDealHotelId,
                        sampleDealHotelIdString: String(sampleDealHotelId),
                        sampleDealHotelName,
                        sampleDealHotelNameType: typeof sampleDealHotelName,
                        sampleDealHotelNameString: String(sampleDealHotelName)
                      },
                      comparison: {
                        idMatch: hotelId && sampleDealHotelId ? String(hotelId).trim() === String(sampleDealHotelId).trim() : false,
                        nameMatch: hotelName && sampleDealHotelName ? hotelName.toLowerCase().trim() === sampleDealHotelName.toLowerCase().trim() : false,
                        nameIncludes: hotelName && sampleDealHotelName ? sampleDealHotelName.toLowerCase().includes(hotelName.toLowerCase()) : false
                      },
                      totalDeals: deals.length,
                      sampleDealStructure: {
                        hasHotel: !!sampleDeal?.hotel,
                        hasFullDeal: !!sampleDeal?.fullDeal,
                        hasFullDealHotel: !!sampleDeal?.fullDeal?.hotel,
                        hotelKeys: sampleDeal?.hotel ? Object.keys(sampleDeal.hotel) : [],
                        fullDealHotelKeys: sampleDeal?.fullDeal?.hotel ? Object.keys(sampleDeal.fullDeal.hotel) : [],
                        allKeys: Object.keys(sampleDeal || {})
                      },
                      allDealHotelIds: [...new Set(deals.map(d => {
                        return d?.hotel?.hotelId || 
                               d?.fullDeal?.hotel?.hotelId || 
                               d?.hotel?.id ||
                               d?.fullDeal?.hotel?.id ||
                               d?.hotelId ||
                               d?.id ||
                               d?.deal?.hotel?.hotelId ||
                               d?.deal?.hotel?.id;
                      }).filter(Boolean))],
                      allDealHotelNames: [...new Set(deals.map(d => {
                        return d?.hotel?.hotelName || 
                               d?.fullDeal?.hotel?.hotelName ||
                               d?.hotel?.name ||
                               d?.fullDeal?.hotel?.name ||
                               d?.hotelName ||
                               d?.name ||
                               d?.deal?.hotel?.hotelName ||
                               d?.deal?.hotel?.name;
                      }).filter(Boolean))],
                    });
                  }

                  if (hotelDeals.length > 0) {
                    // Log successful matches for debugging (only first few times)
                    if (completedSearches < 3) {
                      const sampleMatch = hotelDeals[0];
                      const matchHotelId = sampleMatch?.hotel?.hotelId || 
                                          sampleMatch?.fullDeal?.hotel?.hotelId ||
                                          sampleMatch?.hotel?.id ||
                                          sampleMatch?.fullDeal?.hotel?.id ||
                                          sampleMatch?.hotelId ||
                                          sampleMatch?.id;
                      const matchHotelName = sampleMatch?.hotel?.hotelName || 
                                            sampleMatch?.fullDeal?.hotel?.hotelName ||
                                            sampleMatch?.hotel?.name ||
                                            sampleMatch?.fullDeal?.hotel?.name ||
                                            sampleMatch?.hotelName ||
                                            sampleMatch?.name;
                      dlog('[searchHotelForMonth] ✅ Found hotel deals for date:', depDate, {
                        totalMatches: hotelDeals.length,
                        searchingFor: {
                          hotelId,
                          hotelIdString: String(hotelId),
                          hotelName,
                          hotelNameString: String(hotelName)
                        },
                        matchedDeal: {
                          matchHotelId,
                          matchHotelIdString: String(matchHotelId),
                          matchHotelName,
                          matchHotelNameString: String(matchHotelName)
                        },
                        comparison: {
                          idMatch: hotelId && matchHotelId ? String(hotelId).trim() === String(matchHotelId).trim() : false,
                          nameMatch: hotelName && matchHotelName ? hotelName.toLowerCase().trim() === matchHotelName.toLowerCase().trim() : false
                        }
                      });
                    }
                    // Calculate actual nights from dates
                    const parseDate = (dateStr) => {
                      if (!dateStr) return null;
                      try {
                        const [day, month, year] = dateStr.split("/");
                        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                      } catch (e) {
                        return null;
                      }
                    };
                    const depDateObj = parseDate(depDate);
                    const retDateObj = parseDate(retDate);
                    const actualNights = depDateObj && retDateObj 
                      ? Math.round((retDateObj - depDateObj) / (1000 * 60 * 60 * 24))
                      : 0;

                    const dealsWithDates = hotelDeals.map((deal) => ({
                      ...deal,
                      depDate, // Add depDate directly for easier access
                      retDate, // Add retDate directly for easier access
                      searchDate: { depDate, retDate },
                      nights: actualNights,
                      // Add metadata about which month this search was for
                      searchMonth: dateLinkMonth,
                      searchYear: dateLinkYear,
                      // Mark as searched hotel since it passed the filter
                      isSearchedHotel: true,
                    }));
                    allResults.push(...dealsWithDates);
                    
                    // If callback provided, send results immediately (streaming results)
                    if (onResultCallback && typeof onResultCallback === 'function') {
                      try {
                        // Calculate prices for incremental results before sending
                        const dealsWithPrices = dealsWithDates.map((deal) => {
                          const fullDeal = deal.fullDeal || deal;
                          const resolvedMonthlyPrice = resolveMonthlyDisplayPrice(deal, {
                            trace: true,
                          });
                          if (resolvedMonthlyPrice.totalPrice > 0) {
                            const actualNights = deal.nights || 0;
                            const pricePerNight = actualNights > 0 ? round2(resolvedMonthlyPrice.totalPrice / actualNights) : 0;
                            return {
                              ...deal,
                              priceInfo: resolvedMonthlyPrice.priceInfo,
                              totalPrice: resolvedMonthlyPrice.totalPrice,
                              pricePerNight,
                              monthlyPriceAudit: resolvedMonthlyPrice.audit,
                            };
                          }
                          if (!fullDeal.dealFare) {
                            return {
                              ...deal,
                              priceInfo: resolvedMonthlyPrice.priceInfo,
                              totalPrice: resolvedMonthlyPrice.totalPrice,
                              pricePerNight: 0,
                              monthlyPriceAudit: resolvedMonthlyPrice.audit,
                            };
                          }
                          try {
                            const priceInfo = calculateLDSDealFareForRoomsComposition(fullDeal);
                            const fallbackMonthlyPrice = resolveMonthlyDisplayPrice(deal, {
                              fallbackPriceInfo: priceInfo,
                              trace: true,
                            });
                            const totalPrice = fallbackMonthlyPrice.totalPrice;
                            const actualNights = deal.nights || 0;
                            const pricePerNight = actualNights > 0 ? round2(totalPrice / actualNights) : 0;
                            return {
                              ...deal,
                              priceInfo: fallbackMonthlyPrice.priceInfo,
                              totalPrice,
                              pricePerNight,
                              monthlyPriceAudit: fallbackMonthlyPrice.audit,
                            };
                          } catch (error) {
                            derr("Error calculating price for streaming deal:", error);
                            return {
                              ...deal,
                              priceInfo: resolvedMonthlyPrice.priceInfo,
                              totalPrice: resolvedMonthlyPrice.totalPrice,
                              pricePerNight: 0,
                              monthlyPriceAudit: resolvedMonthlyPrice.audit,
                            };
                          }
                        });
                        // Send incremental results with prices
                        onResultCallback(dealsWithPrices, allResults.length);
                      } catch (callbackError) {
                        dwarn('[searchHotelForMonth] Error in onResultCallback:', callbackError);
                      }
                    }
                    
                    // Log with month info to debug
                    const [depDay, depMonth, depYear] = depDate.split("/");
                    dlog(`[searchHotelForMonth] Found ${hotelDeals.length} hotel deals for ${depDate} - ${retDate}`, {
                      searchRequested: { month: dateLinkMonth, year: dateLinkYear },
                      dealActual: { month: parseInt(depMonth), year: parseInt(depYear) },
                      matches: parseInt(depMonth) === dateLinkMonth && parseInt(depYear) === dateLinkYear
                    });
                  } else {
                    dlog(`[searchHotelForMonth] No hotel deals found for ${depDate} - ${retDate} (total deals: ${deals.length})`);
                  }
                }
              }
            })
            .catch((err) => {
              completedSearches++;
              if (isSearchLdsAbortError(err)) return;
              derr(`[searchHotelForMonth] Error searching for ${depDate} - ${retDate}:`, err);
            })
        );
      });

      // Add timeout (max 3 minutes for monthly search)
      const SEARCH_TIMEOUT = 3 * 60 * 1000;
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Search timeout')), SEARCH_TIMEOUT);
      });

      let hasTimeout = false;
      try {
        const results = await Promise.race([
          Promise.allSettled(searchPromises), // Use allSettled instead of all to handle partial failures
          timeoutPromise
        ]);
        
        // Process allSettled results to log any failures
        if (Array.isArray(results)) {
          const fulfilled = results.filter(r => r.status === 'fulfilled').length;
          const rejected = results.filter(r => r.status === 'rejected').length;
          
          if (rejected > 0) {
            dlog(`[searchHotelForMonth] Completed: ${fulfilled} succeeded, ${rejected} failed out of ${results.length} total searches`);
            
            // Log first few failures for debugging
            results.filter(r => r.status === 'rejected').slice(0, 3).forEach((r, idx) => {
              dwarn(`[searchHotelForMonth] Search ${idx + 1} failed:`, r.reason);
            });
          }
        }
      } catch (error) {
        if (error.message === 'Search timeout') {
          hasTimeout = true;
          dwarn(`[searchHotelForMonth] Search timeout after ${SEARCH_TIMEOUT}ms. Found ${allResults.length} results so far.`);
          notifyAgentSearch("warning", {
            message: "חיפוש הופסק",
            description: `החיפוש לקח יותר מדי זמן. מציג ${allResults.length} תוצאות שנמצאו עד כה.`,
            duration: 5,
          });
        } else {
          // Only throw if it's not a timeout and we have no results
          // If we have results, we should still show them even if there were some errors
          if (allResults.length === 0) {
            throw error;
          } else {
            // Log the error but don't throw - we have some results to show
            dwarn("[searchHotelForMonth] Some searches failed, but showing available results:", error);
          }
        }
      }
      
      // Log final results summary
      dlog(`[searchHotelForMonth] ✅ Search completed. Total results: ${allResults.length}`, {
        totalSearches: searchPromises.length,
        resultsFound: allResults.length,
        hasTimeout
      });

      // Sort results by departure date
      const sortedResults = allResults.sort((a, b) => {
        const dateA = a.searchDate?.depDate || '';
        const dateB = b.searchDate?.depDate || '';
        // Parse dates (format: DD/MM/YYYY)
        const parseDate = (dateStr) => {
          if (!dateStr) return new Date(0);
          try {
            const [day, month, year] = dateStr.split("/");
            return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          } catch (e) {
            return new Date(0);
          }
        };
        return parseDate(dateA) - parseDate(dateB);
      });

      // Only show success/info messages if we didn't timeout
      // (timeout already shows a warning message)
      if (!hasTimeout) {
        if (sortedResults.length > 0) {
          let monthsText = `בחודש ${month}/${year}`;
          if (monthsCount > 1) {
            const endMonth = month + monthsCount - 1;
            let endYear = year;
            let actualEndMonth = endMonth;
            while (actualEndMonth > 12) {
              actualEndMonth -= 12;
              endYear += 1;
            }
            monthsText = `בחודשים ${month}/${year} - ${actualEndMonth}/${endYear}`;
          }
          notifyAgentSearch("success", {
            message: "חיפוש הושלם",
            description: `נמצאו ${sortedResults.length} תוצאות עבור המלון ${monthsText}`,
            duration: 3,
          });
        } else {
          let monthsText = `בחודש ${month}/${year}`;
          if (monthsCount > 1) {
            const endMonth = month + monthsCount - 1;
            let endYear = year;
            let actualEndMonth = endMonth;
            while (actualEndMonth > 12) {
              actualEndMonth -= 12;
              endYear += 1;
            }
            monthsText = `בחודשים ${month}/${year} - ${actualEndMonth}/${endYear}`;
          }
          notifyAgentSearch("info", {
            message: "לא נמצאו תוצאות",
            description: `לא נמצאו תוצאות עבור המלון ${monthsText}`,
            duration: 3,
          });
        }
      }

      return sortedResults;
    } catch (error) {
      derr("Error in searchHotelForMonth:", error);
      derr("Error details:", {
        message: error.message,
        stack: error.stack,
        hotelId,
        hotelName,
        month,
        year,
        monthsCount,
      });
      setError("אירעה שגיאה אנא נסה שנית.");
      notifyAgentSearch("error", {
        message: "שגיאה בחיפוש",
        description: `אירעה שגיאה בעת חיפוש המלון לחודש${monthsCount > 1 ? 'ים' : ''}. ${error.message ? `פרטים: ${error.message}` : ''}`,
        duration: 5,
      });
      return [];
    } finally {
      monthClearLoadingIfLatest();
    }
  }

  // Search for all available dates
  async function searchAllAvailableDates() {
    // Check if we're in mini site
    const isMiniSite = isPublicConsumerSearchSurface();
    
    if (!ldsDates || !Array.isArray(ldsDates) || ldsDates.length === 0) {
      if (!isMiniSite) {
        notifyAgentSearch("warning", {
          message: "אין תאריכים זמינים",
          description: "לא נמצאו תאריכים זמינים לחיפוש",
          duration: 3,
        });
      }
      return;
    }

    setIsSearched(true);
    const allDatesSearchRid = _initBeforeSearch();
    setTimeLeft(TWENTY_MIN);

    // Get all unique date combinations from ldsDates
    const dateCombinations = new Set();
    ldsDates.forEach((link) => {
      if (link && link.depDate && link.retDate) {
        dateCombinations.add(`${link.depDate}|${link.retDate}`);
      }
    });

    const allResults = [];
    const searchPromises = [];

    // Limit to first 50 combinations to avoid too many requests
    const limitedCombinations = Array.from(dateCombinations).slice(0, 50);

    limitedCombinations.forEach((dateCombo) => {
      const [depDate, retDate] = dateCombo.split("|");
      const searchData = {
        dep_date: depDate,
        ret_date: retDate,
        city:
          getPrimaryLocation(location)?.AIRPORTCODE ||
          getPrimaryLocation(location)?.CITYCODE ||
          location?.AIRPORTCODE ||
          location?.CITYCODE,
        categories: categories?.includes("ALL") ? [] : categories,
        date_range: 0,
        room_compositions: normalizeRoomCompositions(roomInfo?.rooms),
      };

      searchPromises.push(
        searchLdsRequestTracked(searchData, {
          signal: abortControllerRef.current?.signal,
          requestId: allDatesSearchRid,
          source: "searchAllAvailableDates",
        })
          .then((res) => res?.data)
          .then((res) => {
            if (res?.opStatus?.status === "SUCCESS") {
              let deals = isArray(res.dealGroup)
                ? res.dealGroup
                : isArray(res.deal)
                  ? res.deal
                  : res.dealGroup
                    ? [res.dealGroup]
                    : [];
              
              // Add date info and category to each deal
              if (Array.isArray(deals)) {
                deals = deals.map((deal) => ({
                  ...deal,
                  searchDate: { depDate, retDate },
                  category: categoryName, // Add category to deal so it's used in price calculation
                }));
                allResults.push(...deals);
              }
            }
          })
          .catch((err) => {
            console.error(`Error searching for ${depDate} - ${retDate}:`, err);
          })
      );
    });

    try {
      await Promise.all(searchPromises);
      
      // Remove duplicates based on dealId
      const uniqueResults = [];
      const seenDealIds = new Set();
      
      allResults.forEach((deal) => {
        const dealId = deal?.fullDeal?.dealId || deal?.dealId;
        if (dealId && !seenDealIds.has(dealId)) {
          seenDealIds.add(dealId);
          uniqueResults.push(deal);
        } else if (!dealId) {
          // If no dealId, include it anyway
          uniqueResults.push(deal);
        }
      });

      if (uniqueResults.length > 0) {
        if (commitActiveSearchRows(uniqueResults, allDatesSearchRid, "all_available_dates")) {
          notifyAgentSearch("success", {
            message: "חיפוש הושלם",
            description: `נמצאו ${uniqueResults.length} תוצאות עבור כל התאריכים הזמינים`,
            duration: 3,
          });
        }
      } else if (allDatesSearchRid === currentSearchRequestIdRef.current) {
        setError("לא נמצאו תוצאות");
        setSearchResults(null);
        notifyAgentSearch("info", {
          message: "לא נמצאו תוצאות",
          description: "לא נמצאו תוצאות עבור התאריכים הזמינים",
          duration: 3,
        });
      }
    } catch (err) {
      setError("אירעה שגיאה אנא נסה שנית.");
      notifyAgentSearch("error", {
        message: "שגיאה בחיפוש",
        description: "אירעה שגיאה בעת חיפוש כל התאריכים",
        duration: 5,
      });
    } finally {
      _initAfterSearch(allDatesSearchRid);
    }
  }

  function _flightSearchNeedsReturnDate() {
    if (searchTab === ORGANIZED_TOURS) return false;
    return (
      searchTab === PLANES || searchTab === DYNAMIC_PACKAGES
        ? flightTripType === "RoundTrip"
        : true
    );
  }

  function isSearchDisabled() {
    if (error || loading) {
      return true;
    }

    if (searchTab === ORGANIZED_TOURS) {
      const resolvedGate = resolveLdsSearchCityFromLocation(location);
      const readyTour = isOrganizedTourSearchReady(tourMonth, location);
      if (import.meta.env?.DEV) {
        console.log("[ORG_TOUR_DATES_READY_CHECK]", {
          ready: readyTour && !_hasZeroAge(roomInfo.rooms),
          tourMonth,
          loadingDates,
          isAllDestinations: resolvedGate.isAllDestinations,
          cityResolvedForApi: resolvedGate.city,
          ldsDatesCount: Array.isArray(ldsDates) ? ldsDates.length : null,
        });
      }
      if (!readyTour) return true;
      if (_hasZeroAge(roomInfo.rooms)) return true;
      return false;
    }

    if (searchTab === PACKAGES && packageDateMode === "month") {
      if (!isPackageMonthSearchReady(packageMonth, location)) return true;
      if (_hasZeroAge(roomInfo.rooms)) return true;
      return false;
    }

    const datesOk = _flightSearchNeedsReturnDate()
      ? calendarDateArrival !== "" && calendarDateLeave !== ""
      : calendarDateArrival !== "";

    const hasDestination =
      Array.isArray(location) ? location.length > 0 : Boolean(location);

    return !(hasDestination && datesOk && !_hasZeroAge(roomInfo.rooms));
  }

  function canLockSearchParams() {
    const hasDestination =
      Array.isArray(location) ? location.length > 0 : Boolean(location);
    if (!hasDestination) return false;

    const hasDates =
      Boolean(String(calendarDateArrival || "").trim()) ||
      Boolean(String(packageMonth || "").trim()) ||
      Boolean(String(tourMonth || "").trim());
    if (!hasDates) return false;

    if (!roomInfo?.rooms?.length) return false;
    if (_hasZeroAge(roomInfo.rooms)) return false;

    return true;
  }

  function getSearchDisableReasons() {
    if (error) {
      return ["אירעה שגיאה."];
    }
    if (loading) {
      return ["טוען..."];
    }
    const reasons = [];

    if (searchTab === ORGANIZED_TOURS) {
      if (!String(tourMonth || "").trim()) {
        reasons.push("יש לבחור חודש יציאה.");
      }
      const rTourGate = resolveLdsSearchCityFromLocation(location);
      if (!rTourGate.isAllDestinations) {
        const p = rTourGate.primary;
        if (
          !p ||
          !(String(p.CITYCODE || "").trim() || String(p.AIRPORTCODE || "").trim())
        ) {
          reasons.push("יש לבחור יעד.");
        }
      }
      if (_hasZeroAge(roomInfo.rooms)) {
        reasons.push("ילד לא יכול להיות בגיל 0.");
      }
      return reasons.length > 0 ? reasons : null;
    }

    if (searchTab === PACKAGES && packageDateMode === "month") {
      if (!String(packageMonth || "").trim()) {
        reasons.push("יש לבחור חודש.");
      }
      const rPkgGate = resolveLdsSearchCityFromLocation(location);
      if (!rPkgGate.isAllDestinations) {
        const p = rPkgGate.primary;
        if (
          !p ||
          !(String(p.CITYCODE || "").trim() || String(p.AIRPORTCODE || "").trim())
        ) {
          reasons.push("יש לבחור יעד.");
        }
      }
      if (_hasZeroAge(roomInfo.rooms)) {
        reasons.push("ילד לא יכול להיות בגיל 0.");
      }
      return reasons.length > 0 ? reasons : null;
    }

    const hasDestination =
      Array.isArray(location) ? location.length > 0 : Boolean(location);
    if (!hasDestination) {
      reasons.push("יש לבחור יעד.");
    }
    if (_flightSearchNeedsReturnDate()) {
      if (!(calendarDateArrival !== "" && calendarDateLeave !== "")) {
        reasons.push("יש לבחור תאריכי יציאה וחזרה.");
      }
    } else if (calendarDateArrival === "") {
      reasons.push("יש לבחור תאריך יציאה.");
    }

    if (_hasZeroAge(roomInfo.rooms)) {
      reasons.push("ילד לא יכול להיות בגיל 0.");

      return reasons;
    }

    return reasons.length > 0 ? reasons : null;
  }

  const calculatePriceWithCommissions = (
    ldsPrice,
    dealCurrencyRate,
    ldsCommission = 0,
    isHotel = false,
    pricingAuditMeta = undefined
  ) => {
    const validLdsPrice = Number(ldsPrice) || 0;
    const validCurrencyRate = Number(dealCurrencyRate) || 0;
    const validLdsCommission = Number(ldsCommission) || 0;

    if (!validCurrencyRate || !Number.isFinite(validCurrencyRate)) {
      return null;
    }

    const entitlementsLoaded =
      agentEntitlementsStatus === "ok" &&
      pricingEntitlements &&
      typeof pricingEntitlements === "object";
    const packageFeatureCommissionPercentage = entitlementsLoaded
      ? Number(
          pricingEntitlements?.features?.[AGENT_FEATURE_KEYS.COMMISSION_PERCENTAGE],
        )
      : null;
    const legacyGlobalCommissionPercentage = Number(getAgentCommissionPercent?.()) || 0;
    const enforceFlightHrCommissionSsot =
      pricingAuditMeta?.enforceFlightHrCommissionSsot === true && isHotel === false;

    const walletCommissionPercent = entitlementsLoaded
      ? resolveWalletCommissionPercentage(pricingEntitlements)
      : null;

    const ldsHrProductType = inferLdsHrProductType({
      isHotelDeal: isHotel,
      enforceFlightHrCommissionSsot,
      dealType: pricingAuditMeta?.dealType ?? pricingAuditMeta?.productType,
      tour: pricingAuditMeta?.tour,
      hotel: pricingAuditMeta?.hotel,
      isOrganizedTour:
        pricingAuditMeta?.isOrganizedTour === true ||
        (typeof pricingAuditMeta?.dealType === "string" &&
          pricingAuditMeta.dealType.toUpperCase().includes("ORGANIZED")),
    });

    const hrMarginResolution = entitlementsLoaded
      ? resolveLdsHrMarginFromFeatures(
          pricingEntitlements.features,
          ldsHrProductType,
        )
      : { percent: null, sourceFeatureKey: null };
    console.log("[HR_MARGIN_SOURCE_TRACE]", "after resolveLdsHrMarginFromFeatures", {
      sourceFeatureKey: hrMarginResolution.sourceFeatureKey,
      percent: hrMarginResolution.percent,
      ldsHrProductType,
    });

    const hrMarginPercentOverride = hrMarginResolution.percent;
    const hrMarginSourceFeatureKey = hrMarginResolution.sourceFeatureKey;

    if (enforceFlightHrCommissionSsot) {
      const effectiveCommissionPercentage = walletCommissionPercent;
      const hasPackageFeatureCommission =
        Number.isFinite(packageFeatureCommissionPercentage) &&
        packageFeatureCommissionPercentage >= 0;
      const resolvedPackageCode =
        pricingAuditMeta?.resolvedPackageCode != null &&
        String(pricingAuditMeta.resolvedPackageCode).trim() !== ""
          ? String(pricingAuditMeta.resolvedPackageCode).trim().toUpperCase()
          : null;
      const mismatchDetected =
        !hasPackageFeatureCommission ||
        !resolvedPackageCode ||
        (Number.isFinite(Number(effectiveCommissionPercentage)) &&
          Number(effectiveCommissionPercentage) !== Number(packageFeatureCommissionPercentage));
      console.log("[FLIGHT_HR_COMMISSION_SSOT_GUARD]", {
        agentId: pricingAuditMeta?.agentId ?? null,
        packageId: pricingAuditMeta?.rawPackageId ?? null,
        resolvedPackageCode,
        packageFeatureCommissionPercentage: hasPackageFeatureCommission
          ? Number(packageFeatureCommissionPercentage)
          : null,
        legacyGlobalCommissionPercentage:
          Number.isFinite(Number(legacyGlobalCommissionPercentage))
            ? Number(legacyGlobalCommissionPercentage)
            : null,
        effectiveCommissionPercentage:
          Number.isFinite(Number(effectiveCommissionPercentage))
            ? Number(effectiveCommissionPercentage)
            : null,
        hrMarginFromFeatures: hrMarginPercentOverride,
        hrMarginSourceFeatureKey,
        ldsHrProductType,
        source: "wallet uses commission_percentage; HR margin uses flight_hr/hotel_hr features",
        mismatchDetected,
        entitlementsLoaded: Boolean(entitlementsLoaded),
      });
      if (process.env.NODE_ENV === "development") {
        console.log("[FLIGHT_HR_PERCENT_PIPELINE_DEBUG]", {
          hrMarginPercentOverride,
          hrMarginSourceFeatureKey,
          hrFlatPercent: hrMarginPercentOverride,
          blockReason: null,
        });
      }
      if (pricingAuditMeta && typeof pricingAuditMeta === "object") {
        pricingAuditMeta.packageFeatureCommissionPercentage = hasPackageFeatureCommission
          ? Number(packageFeatureCommissionPercentage)
          : null;
        pricingAuditMeta.finalHrPercentUsed = hrMarginPercentOverride;
        pricingAuditMeta.walletCommissionPercent = walletCommissionPercent;
        pricingAuditMeta.hrMarginSourceFeatureKey = hrMarginSourceFeatureKey;
        pricingAuditMeta.ldsHrProductType = ldsHrProductType;
      }
    } else if (pricingAuditMeta && typeof pricingAuditMeta === "object") {
      pricingAuditMeta.walletCommissionPercent = walletCommissionPercent;
      pricingAuditMeta.hrMarginSourceFeatureKey = hrMarginSourceFeatureKey;
      pricingAuditMeta.finalHrPercentUsed = hrMarginPercentOverride;
      pricingAuditMeta.ldsHrProductType = ldsHrProductType;
    }

    console.log("[FE_HR_MARGIN_SOURCE]", {
      product_type: ldsHrProductType,
      source_feature_key: hrMarginSourceFeatureKey,
      resolved_hr_percent: hrMarginPercentOverride,
      wallet_commission_percent: walletCommissionPercent,
    });

    console.log("[HR_MARGIN_SOURCE_TRACE]", "before runLdsVacationPackageFareFromEngine", {
      hrMarginSourceFeatureKey,
      hrMarginPercentOverride,
    });

    const res = runLdsVacationPackageFareFromEngine({
      ldsGrossForeign: validLdsPrice,
      currencyRate: validCurrencyRate,
      ldsCommissionPercent: validLdsCommission,
      isHotelDeal: isHotel,
      isB2CMode,
      getHrCommissionPercent,
      getHrCommissionPercentHotels,
      getAgentCommissionPercent,
      getCreditCardFeePercent,
      hrMarginPercentOverride,
      hrMarginSourceFeatureKey,
      walletCommissionPercent,
      channelType: getLdsPackagePricingChannelType(),
      pricingAuditMeta,
    });

    if (res.pricingStatus !== "ok" || !res.ourPrice) {
      return null;
    }

    const ourPrice = { ...res.ourPrice };
    ourPrice.unifiedHrMarginSourceFeatureKey =
      hrMarginSourceFeatureKey != null && String(hrMarginSourceFeatureKey).trim() !== ""
        ? String(hrMarginSourceFeatureKey)
        : ourPrice.unifiedHrMarginSourceFeatureKey ?? null;
    ourPrice.origCurrency = res.origCurrencyVm;
    ourPrice.rawPackageCode = pricingAuditMeta?.rawPackageCode ?? null;
    ourPrice.resolvedPackageCode = pricingAuditMeta?.resolvedPackageCode ?? null;
    ourPrice.isPublicSite = pricingAuditMeta?.isPublicSite === true;
    ourPrice.pricingContext = pricingAuditMeta?.pricingContext || "agent_system";
    ourPrice.currencyRate = Number.isFinite(validCurrencyRate)
      ? validCurrencyRate
      : 0;
    ourPrice.netPrice =
      res.netForeign != null && Number.isFinite(res.netForeign)
        ? res.netForeign
        : 0;
    ourPrice.ldsPrice = round2(validLdsPrice);
    ourPrice.ldsCommission = round2(validLdsCommission);
    console.log("[HR_MARGIN_SOURCE_TRACE]", "before return ourPrice (calculatePriceWithCommissions)", {
      ourPrice_unifiedHrMarginSourceFeatureKey: ourPrice.unifiedHrMarginSourceFeatureKey,
      origCurrency_unifiedHrMarginSourceFeatureKey:
        ourPrice.origCurrency &&
        typeof ourPrice.origCurrency === "object" &&
        "unifiedHrMarginSourceFeatureKey" in ourPrice.origCurrency
          ? ourPrice.origCurrency.unifiedHrMarginSourceFeatureKey
          : undefined,
    });
    if (
      process.env.NODE_ENV === "development" &&
      pricingAuditMeta?.hrPlanAuditEnabled === true
    ) {
      const pax = Math.max(1, Number(pricingAuditMeta?.passengersCount) || 1);
      const apiGrossUsdPerPerson = round2(validLdsPrice / pax);
      const netUsdAfterSupplierCommission = round2((Number(ourPrice.netPrice) || 0) / pax);
      const hrCommissionAmountUsd = round2(
        (Number(ourPrice?.origCurrency?.hrCommission) || 0) / pax
      );
      const finalClientUsdPerPerson = round2(
        (Number(ourPrice?.origCurrency?.priceWithCommissionAndCreditCardFee) || 0) / pax
      );
      const hrCommissionValue =
        Number.isFinite(Number(ourPrice?.origCurrency?.hrCommissionPercent)) &&
        Number(ourPrice?.origCurrency?.hrCommissionPercent) >= 0
          ? Number(ourPrice.origCurrency.hrCommissionPercent)
          : Number.isFinite(Number(ourPrice?.hrCommissionPercent)) &&
              Number(ourPrice?.hrCommissionPercent) >= 0
            ? Number(ourPrice.hrCommissionPercent)
            : null;
      console.log("[FLIGHT_AGENT_PLAN_HR_COMMISSION_DEBUG]", {
        agentId: pricingAuditMeta?.agentId ?? null,
        agentEmail: pricingAuditMeta?.agentEmail ?? null,
        rawPackageId: pricingAuditMeta?.rawPackageId ?? null,
        rawPackageCode: pricingAuditMeta?.rawPackageCode ?? null,
        resolvedPackageCode: pricingAuditMeta?.resolvedPackageCode ?? null,
        packageId: pricingAuditMeta?.packageId ?? null,
        packageCode: pricingAuditMeta?.packageCode ?? null,
        missingPackage: Boolean(pricingAuditMeta?.missingPackage),
        fallbackUsed: Boolean(pricingAuditMeta?.fallbackUsed),
        hrCommissionType: "percent",
        hrCommissionValue,
        apiGrossUsdPerPerson,
        netUsdAfterSupplierCommission,
        hrCommissionAmountUsd,
        finalClientUsdPerPerson,
        sourceFunction:
          pricingAuditMeta?.sourceFunction || "SearchContext.calculatePriceWithCommissions",
      });
    }
    return ourPrice;
  };

  function calculateLDSDealFare(dealInput, options = {}) {
    const ssotBypass = options?.ssotBypass === true;
    if (
      !ssotBypass &&
      isDynamicPackagePricingNormalizedEnabled() &&
      searchTab === DYNAMIC_PACKAGES
    ) {
      return null;
    }
    const {
      dealType,
      dealtype,
      dealId,
      ID,
      id,
      dealFare,
      fareDetails,
      supplierDetails,
      hotel,
      tour,
      dealCategory = null, // Category from deal (e.g., from searchDate or deal metadata)
      dealReferenceDate,
      searchDate,
      departureDate,
      linkedFlights,
      passengersCount: dealPassengersCount,
    } = dealInput || {};
    if (!dealFare || !fareDetails || !supplierDetails) {
      return null;
    }
    const dealTypeFromPayload = dealType ?? dealtype;
    const { adultFare, totalFare, infantFare, childFareExt } = dealFare;
    const { commission, currency } = fareDetails || {};
    let { commissionType, commissionValue, commissionBase } = commission || {};
    const logLdsCommissionType = isLdsCommissionTypeDebugEnabled();
    if (logLdsCommissionType) {
      console.log("[LDS_COMMISSION_TYPE] initial from fareDetails.commission (LDS API)", {
        commissionType,
        commissionValue,
        commissionBase,
      });
    }
    let destination = null;
    const supplierResolved = resolveLdsSupplierCodeForPricing({
      ...(dealInput && typeof dealInput === "object" ? dealInput : {}),
      supplierDetails,
    });
    const supplierIdRaw =
      supplierDetails?.supplierId ??
      supplierDetails?.id ??
      supplierDetails?.supplierCode ??
      supplierDetails?.code ??
      null;
    const supplierId =
      supplierResolved != null && String(supplierResolved).trim() !== ""
        ? String(supplierResolved).trim()
        : supplierIdRaw != null && String(supplierIdRaw).trim() !== ""
          ? String(supplierIdRaw).trim()
          : null;
    const childFare = childFareExt;

    if (hotel) destination = hotel.hotelCity;
    else if (tour) destination = tour.tourCities?.val?.[0];
    if (!destination) {
      destination = extractLdsFlightDealDestination(dealInput);
    }

    // Always use the exchange rate from ADMIN (currencyRate from AppContext)
    // Do not use fallback values - if rate is not loaded, return null
    const dealCurrencyRate = currencyRate?.[currency];

    // If currency rate is not loaded from ADMIN, return null (don't calculate with wrong rate)
    if (!dealCurrencyRate || dealCurrencyRate === 0 || dealCurrencyRate === null || dealCurrencyRate === undefined) {
      console.warn(`Currency rate for ${currency} is not loaded from ADMIN. Cannot calculate flight price.`);
      return null;
    }

    let commissionPercent = 0;
    let cValue = 0;
    let totalFarePercent = 0;
    let adultFarePercent = 0;
    let infantFarePercent = 0;

    // Get category from dealCategory parameter or from current search categories
    // Priority: dealCategory > current categories > null
    const dealCategoryToCheck = dealCategory || (categories && categories.length > 0 && !categories.includes("ALL") ? categories[0] : null);

    // חבילות נופש (FlightsHotel) / טיול מאורגן: אם dealType חסר על האובייקט מה-LDS, ההשוואה
    // supplier.lds_type === dealType נכשלת בשקט (למשל מול undefined) — שורת lds_suppliers לא נבחרת
    // ואז commissionPercent נשאר מה־fareDetails בלבד + עמלת האתר ב-calcBaseCommission.
    // טיסות GDS (OneWay / RoundTrip / Flights) ממופות ל-lds_type "Flights".
    const dealTypeForLdsSupplierRow = normalizeDealTypeForLdsSupplierRow(
      dealTypeFromPayload,
      { hotel, tour }
    );

    const logLdsSupplierMatch = isLdsSupplierMatchDebugEnabled();
    const logLdsRuleMatch = isLdsRuleMatchDebugEnabled();
    const dealYmd =
      coerceToYmd(dealReferenceDate) ??
      extractLdsDealReferenceDate({
        dealReferenceDate,
        searchDate,
        departureDate,
        linkedFlights,
        hotel,
        tour,
      });

    if (logLdsSupplierMatch || logLdsRuleMatch) {
      console.log("[LDS_SUPPLIER_MATCH] deal context (rule engine)", {
        dealType,
        dealtype,
        dealTypeFromPayload,
        dealTypeForLdsSupplierRow,
        supplierId,
        supplierIdType: typeof supplierId,
        hotelCity: destination,
        tour: tour ?? null,
        hotel: hotel ? { hotelCity: hotel.hotelCity, hotelId: hotel.hotelId ?? hotel.id } : null,
        dealCategoryToCheck,
        dealYmd,
        ldsSuppliersCount: Array.isArray(ldsSuppliers) ? ldsSuppliers.length : 0,
      });
    }

    const matchResult = selectLdsSupplierCommissionRow({
      rows: ldsSuppliers,
      supplierId,
      dealTypeForLdsSupplierRow,
      dealDestination: destination,
      dealSubCategory: dealCategoryToCheck,
      dealYmd,
      debug: logLdsRuleMatch,
    });

    if (logLdsRuleMatch && matchResult.debugPayload) {
      console.log("[LDS_RULE_MATCH]", matchResult.debugPayload);
    }
    if (logLdsSupplierMatch && !logLdsRuleMatch) {
      console.log(
        "[LDS_SUPPLIER_MATCH] evaluations (compact; LDS_RULE_MATCH_DEBUG=1 for full payload)",
        matchResult.evaluations,
        "selected",
        matchResult.selected
      );
    }

    let ldsCommissionCondition = matchResult.selected;

    if (ldsCommissionCondition) {
      // Check if commission is fixed per person, NET (מחיר כבר נטו), or percentage
      const normLdsRowCommissionType = (raw) =>
        String(raw == null ? "" : raw).trim().toUpperCase();

      if (
        normLdsRowCommissionType(ldsCommissionCondition.commission_type) ===
        "FIXED_PER_PERSON"
      ) {
        commissionType = "FIXED_PER_PERSON";
        commissionPercent = 0;
        if (logLdsCommissionType) {
          console.log("[LDS_COMMISSION_TYPE] path: FIXED_PER_PERSON (from lds_suppliers)", {
            source: "lds_suppliers.selected_row",
            commission_type: ldsCommissionCondition.commission_type,
            commissionPercentForNetPrice: 0,
          });
        }
      } else {
        const ctRaw = ldsCommissionCondition.commission_type;
        const ctU = normLdsRowCommissionType(ctRaw);
        if (ctU === "NET") {
          commissionType = "NET";
          commissionPercent = 0;
          if (logLdsCommissionType) {
            console.log("[LDS_COMMISSION_TYPE] path: NET (from lds_suppliers)", {
              source: "lds_suppliers.selected_row.commission_type",
              raw: ctRaw,
              note: "אין הפחתת אחוז ממחיר הספק לפני המרה — המחיר מטופל כברוטו=נטו לצורך עמלת ספק",
              commission_percentage_ignored: ldsCommissionCondition.commission_percentage,
              discount_percentage_ignored: ldsCommissionCondition.discount_percentage,
            });
          }
        } else {
          commissionType =
            ctU === "PERCENT" || ctRaw == null || ctRaw === ""
              ? "PERCENT"
              : ctRaw || "PERCENT";
          commissionPercent =
            (ldsCommissionCondition.commission_percentage || 0) +
            (ldsCommissionCondition.discount_percentage || 0);
          if (logLdsCommissionType) {
            console.log("[LDS_COMMISSION_TYPE] path: PERCENT (from lds_suppliers)", {
              source: "lds_suppliers.selected_row",
              commission_type: commissionType,
              commissionPercentForNetPrice: commissionPercent,
            });
          }
        }
      }

      if (logLdsSupplierMatch && ldsCommissionCondition) {
        console.log("[LDS_SUPPLIER_MATCH] selected row", {
          supplier_code: ldsCommissionCondition.supplier_code,
          supplier_name: ldsCommissionCondition.supplier_name,
          destination: ldsCommissionCondition.destination,
          lds_type: ldsCommissionCondition.lds_type,
          category: ldsCommissionCondition.category,
          valid_from: ldsCommissionCondition.valid_from,
          valid_to: ldsCommissionCondition.valid_to,
          commission_type: ldsCommissionCondition.commission_type,
          commission_percentage: ldsCommissionCondition.commission_percentage,
          discount_percentage: ldsCommissionCondition.discount_percentage,
        });
      }
    } else if (logLdsSupplierMatch) {
      console.warn(
        "[LDS_SUPPLIER_MATCH] no supplier row selected — rule engine returned null (LDS_RULE_MATCH_DEBUG=1 for פירוט)."
      );
    }

    if (commissionType === "AMOUNT" && commissionBase !== undefined) {
      cValue = Number(commissionValue);
      if (commissionBase === "TOTAL_FARE")
        totalFarePercent = (cValue / totalFare) * 100;
      else if (commissionBase === "ADULT_FARE")
        adultFarePercent = (cValue / adultFare) * 100;
      else if (commissionBase === "INFANT_FARE")
        infantFarePercent = (cValue / infantFare) * 100;
    } else if (
      commissionType === "PERCENT" ||
      String(commissionType || "").trim().toUpperCase() === "PERCENT"
    ) {
      commissionType = "PERCENT";
      if (!ldsCommissionCondition) commissionPercent = Number(commissionValue);
    } else if (
      commissionType === "NET" ||
      String(commissionType || "").trim().toUpperCase() === "NET"
    ) {
      commissionType = "NET";
      commissionPercent = 0;
      if (logLdsCommissionType) {
        console.log("[LDS_COMMISSION_TYPE] path: NET (fareDetails / merged branch)", {
          source: ldsCommissionCondition
            ? "already set from lds row (idempotent)"
            : "fareDetails or residual",
          commissionPercentForNetPrice: 0,
        });
      }
    } else if (commissionType === "FIXED_PER_PERSON") {
      // For fixed commission per person, we don't subtract anything from the price
      // The fixed amount will be handled separately in calculateLDSDealFareForRoomsComposition
      commissionPercent = 0;
    } else {
      // Default to PERCENT if commissionType is not recognized or is null/undefined
      if (commissionType === null || commissionType === undefined || commissionType === "") {
        console.warn("Commission type is null/undefined, defaulting to PERCENT");
        commissionType = "PERCENT";
        if (ldsCommissionCondition) {
          commissionPercent =
            (ldsCommissionCondition.commission_percentage || 0) +
            (ldsCommissionCondition.discount_percentage || 0);
        } else {
          commissionPercent = Number(commissionValue);
        }
      } else {
        console.warn("Unknown commission type:", commissionType, "Defaulting to PERCENT");
        if (logLdsCommissionType) {
          console.warn("[LDS_COMMISSION_TYPE] unexpected type fell through to PERCENT default", {
            commissionType,
            hadLdsRow: !!ldsCommissionCondition,
          });
        }
        commissionType = "PERCENT";
        if (ldsCommissionCondition) {
          commissionPercent =
            (ldsCommissionCondition.commission_percentage || 0) +
            (ldsCommissionCondition.discount_percentage || 0);
        } else {
          commissionPercent = Number(commissionValue);
        }
      }
    }

    if (logLdsCommissionType) {
      console.log("[LDS_COMMISSION_TYPE] resolved → calculatePriceWithCommissions", {
        commissionType,
        commissionPercentDeductedFromSupplierGross: commissionPercent,
        ldsSupplierRowSelected: !!ldsCommissionCondition,
        currency,
        dealCurrencyRate,
      });
    }

    const userDetails = getUserDetails();
    /** @type {Record<string, unknown>} */
    const ldsDealPricingAuditMeta = {};
    const dealIdForAudit = firstNonEmpty(dealId, ID, id);
    if (dealIdForAudit) {
      ldsDealPricingAuditMeta.dealId = dealIdForAudit;
    }
    if (sessionId != null && sessionId !== "") {
      ldsDealPricingAuditMeta.refKind = "session";
      ldsDealPricingAuditMeta.sessionId = String(sessionId);
    }
    if (supplierId != null && supplierId !== "") {
      ldsDealPricingAuditMeta.supplierCode = String(supplierId);
    }
    if (typeof destination === "string" && destination.trim()) {
      ldsDealPricingAuditMeta.destinationCode = destination.trim();
    } else if (destination && typeof destination === "object") {
      const dn = destination.name ?? destination.city ?? destination.hotelCity;
      if (typeof dn === "string" && dn.trim()) {
        ldsDealPricingAuditMeta.destinationCode = dn.trim();
      }
    }
    if (
      dealTypeForLdsSupplierRow != null &&
      String(dealTypeForLdsSupplierRow).trim() !== ""
    ) {
      ldsDealPricingAuditMeta.dealType = String(dealTypeForLdsSupplierRow);
      ldsDealPricingAuditMeta.productType = String(dealTypeForLdsSupplierRow);
    }
    ldsDealPricingAuditMeta.tour = tour ?? null;
    ldsDealPricingAuditMeta.hotel = hotel ?? null;

    if (
      dealCategoryToCheck != null &&
      dealCategoryToCheck !== "" &&
      dealCategoryToCheck !== "ALL"
    ) {
      ldsDealPricingAuditMeta.subCategoryCode = String(dealCategoryToCheck);
    }
    if (userDetails && typeof userDetails === "object" && userDetails.id != null) {
      ldsDealPricingAuditMeta.agentId = String(userDetails.id);
    }
    if (
      userDetails &&
      typeof userDetails === "object" &&
      userDetails.email != null &&
      String(userDetails.email).trim() !== ""
    ) {
      ldsDealPricingAuditMeta.agentEmail = String(userDetails.email).trim();
    }
    Object.assign(
      ldsDealPricingAuditMeta,
      resolveFlightAgentPlanAuditMeta({ agentEntitlements, userDetails })
    );
    if (
      agentEntitlements?.package != null &&
      String(agentEntitlements.package).trim() !== ""
    ) {
      ldsDealPricingAuditMeta.agentPlan = String(agentEntitlements.package);
    }
    const isPublicSitePricingContext = isPublicFlightsVacationPackagesRoute();
    ldsDealPricingAuditMeta.isPublicSite = isPublicSitePricingContext;
    ldsDealPricingAuditMeta.pricingContext = isPublicSitePricingContext
      ? "public_site"
      : "agent_system";
    ldsDealPricingAuditMeta.searchTab = searchTab;
    ldsDealPricingAuditMeta.enforceFlightHrCommissionSsot =
      searchTab === PLANES && !isPublicSitePricingContext;
    const usdCurrencyRateForFlightHr = Number(currencyRate?.USD);
    if (Number.isFinite(usdCurrencyRateForFlightHr) && usdCurrencyRateForFlightHr > 0) {
      ldsDealPricingAuditMeta.usdCurrencyRate = usdCurrencyRateForFlightHr;
    }
    if (currency != null && String(currency).trim() !== "") {
      ldsDealPricingAuditMeta.dealCurrencyCode = String(currency).trim().toUpperCase();
    }
    ldsDealPricingAuditMeta.channelType = getLdsPackagePricingChannelType();
    ldsDealPricingAuditMeta.pricingModel = "searchContext:calculateLDSDealFare";
    ldsDealPricingAuditMeta.dealId = ldsDealPricingAuditMeta.dealId || dealIdForAudit || null;
    ldsDealPricingAuditMeta.isSelfComposeCombined =
      dealInput?.isSelfComposeCombined === true ||
      String(dealIdForAudit || "").startsWith("SC-");
    ldsDealPricingAuditMeta.selfComposeDirectionCount =
      dealInput?.selfComposeDirectionCount;
    ldsDealPricingAuditMeta.flightHrDirectionCount = resolveFlightHrDirectionCount({
      ...ldsDealPricingAuditMeta,
      dealId: dealIdForAudit,
      isSelfComposeCombined: ldsDealPricingAuditMeta.isSelfComposeCombined,
      selfComposeDirectionCount: dealInput?.selfComposeDirectionCount,
    });

    const isHotelDeal = !!hotel; // Check if this is a hotel deal

    const ourAdultFare = calculatePriceWithCommissions(
      adultFare,
      dealCurrencyRate,
      commissionPercent + adultFarePercent,
      isHotelDeal,
      ldsDealPricingAuditMeta
    );

    const ourInfantFare = calculatePriceWithCommissions(
      infantFare,
      dealCurrencyRate,
      commissionPercent + infantFarePercent,
      isHotelDeal,
      ldsDealPricingAuditMeta
    );

    const ourChildFare = [];

    if (Array.isArray(childFare))
      for (let i = 0; i < childFare.length; i++)
        ourChildFare.push(
          calculatePriceWithCommissions(
            childFare[i]?.fare,
            dealCurrencyRate,
            commissionPercent,
            isHotelDeal,
            ldsDealPricingAuditMeta
          )
        );
    else if (typeof childFare === "object")
      ourChildFare.push(
        calculatePriceWithCommissions(
          childFare?.fare,
          dealCurrencyRate,
          commissionPercent,
          isHotelDeal,
          ldsDealPricingAuditMeta
        )
      );
    else
      ourChildFare.push(
        calculatePriceWithCommissions(
          adultFare,
          dealCurrencyRate,
          commissionPercent,
          isHotelDeal,
          ldsDealPricingAuditMeta
        )
      );

    const finalCommissionPercent = commissionPercent + totalFarePercent;

    const dealPaxOverride = Number(dealPassengersCount);
    const passengersCountForAudit = Math.max(
      1,
      Number.isFinite(dealPaxOverride) && dealPaxOverride > 0
        ? Math.floor(dealPaxOverride)
        : (Array.isArray(roomInfo?.rooms) ? roomInfo.rooms : []).reduce((acc, room) => {
            const adults = Number(room?.adults ?? room?.[ADULTS]) || 0;
            const kids = Number(room?.kids ?? room?.[KIDS]) || 0;
            const infants = Number(room?.infants ?? room?.[INFANTS]) || 0;
            return acc + adults + kids + infants;
          }, 0)
    );
    const totalFareAuditMeta = {
      ...ldsDealPricingAuditMeta,
      hrPlanAuditEnabled: true,
      passengersCount: passengersCountForAudit,
      sourceFunction: "SearchContext.calculateLDSDealFare.totalFare",
    };

    // Validate inputs before calculating
    const validTotalFare = Number(totalFare) || 0;
    const validFinalCommissionPercent = Number(finalCommissionPercent) || 0;

    const ourTotalFare = calculatePriceWithCommissions(
      validTotalFare,
      dealCurrencyRate,
      validFinalCommissionPercent,
      isHotelDeal,
      totalFareAuditMeta
    );

    const faresOk =
      ourAdultFare &&
      ourInfantFare &&
      ourTotalFare &&
      !ourChildFare.some((row) => row == null);
    if (!faresOk) {
      console.warn(
        "[LDS] vacation package pricing: engine returned null for a fare component"
      );
      return null;
    }

    // Save original currency for display
    ourTotalFare.originalCurrency = currency;

    const fxSrc =
      currencyRateSource?.[currency] === FX_SOURCE_ADMIN_CONFIG
        ? FX_SOURCE_ADMIN_CONFIG
        : FX_SOURCE_FALLBACK_DEFAULT;
    ourTotalFare.fx_rate = Number(dealCurrencyRate);
    ourTotalFare.fx_currency = currency;
    ourTotalFare.fx_source = fxSrc;

    // If currency is GBP, calculate final price in GBP instead of ILS
    if (currency === "GBP") {
      // Calculate net price in GBP (after LDS commission)
      const netPriceGBP = ourTotalFare.netPrice;
      
      const unifiedPct =
        ourTotalFare.unifiedHrMarginPercent != null &&
        Number.isFinite(Number(ourTotalFare.unifiedHrMarginPercent))
          ? Number(ourTotalFare.unifiedHrMarginPercent)
          : ourTotalFare.agentCommissionPercent || 0;
      const hrCommissionPercent = ourTotalFare.hrCommissionPercent || 0;
      const creditCardFeePercent = getCreditCardFeePercent();
      
      const origHrGbp = Number(ourTotalFare.origCurrency?.hrCommission);
      const hrCommissionGBP =
        Number.isFinite(origHrGbp) && origHrGbp > 0
          ? origHrGbp
          : round2((hrCommissionPercent / 100) * netPriceGBP);
      const agentCommissionGBP = round2((unifiedPct / 100) * netPriceGBP);
      
      const origPriceWithCommissionGbp = Number(
        ourTotalFare.origCurrency?.priceWithCommission
      );
      const priceWithCommissionGBP =
        Number.isFinite(origPriceWithCommissionGbp) && origPriceWithCommissionGbp > 0
          ? origPriceWithCommissionGbp
          : round2(netPriceGBP + agentCommissionGBP);
      
      // Calculate credit card fee in GBP
      const creditCardFeeGBP = round2((creditCardFeePercent / 100) * priceWithCommissionGBP);
      
      // Final price in GBP
      const finalPriceGBP = round2(priceWithCommissionGBP + creditCardFeeGBP);
      
      // Store GBP prices
      ourTotalFare.priceWithCommissionGBP = priceWithCommissionGBP;
      ourTotalFare.creditCardFeeGBP = creditCardFeeGBP;
      ourTotalFare.finalPriceGBP = finalPriceGBP;
      ourTotalFare.hrCommissionGBP = hrCommissionGBP;
      ourTotalFare.agentCommissionGBP = agentCommissionGBP;
    }


    return {
      ourAdultFare,
      ourInfantFare,
      ourChildFare,
      ourTotalFare,
      ldsCommissionCondition, // Pass commission condition for fixed per person calculation
    };
  }

  /**
   * טבלת מחירים לפי חדר (אותו מיפוי כמו calculateLDSDealFareForRoomsComposition) —
   * לשימוש חיצוני (למשל FlightBookingFlowMockup) לבניית roomsAndPaxInfo עם ourFareDetails.
   */
  function getLdsDealRoomFareRows(
    fullDeal,
    shortenedDeal = null,
    roomsCompositionOverride = null,
    composeOptions = null,
  ) {
    const bypassDpFlightFare =
      composeOptions?.bypassDynamicPackageFlightFareGate === true;
    if (
      !bypassDpFlightFare &&
      isDynamicPackagePricingNormalizedEnabled() &&
      searchTab === DYNAMIC_PACKAGES
    ) {
      return null;
    }
    const {
      dealFare,
      fareDetails,
      dealType: dealTypeCamel,
      dealtype: dealTypeLower,
      hotel,
      tour,
      supplierDetails,
    } = fullDeal;
    const dealTypeFromDeal = dealTypeCamel ?? dealTypeLower;
    if (!dealFare) return null;
    const compositionSource =
      Array.isArray(roomsCompositionOverride) && roomsCompositionOverride.length > 0
        ? roomsCompositionOverride
        : roomInfo?.rooms;
    if (!Array.isArray(compositionSource) || compositionSource.length === 0) {
      return null;
    }
    const normalizedCompositions = compositionSource.map((room) => {
      const rm = room && typeof room === "object" ? room : {};
      const adults = Math.max(0, Math.floor(Number(rm.adults) || 0));
      const kids = Math.max(
        0,
        Math.floor(Number(rm.kids ?? rm.children) || 0),
      );
      const infants = Math.max(
        0,
        Math.floor(Number(rm.infants ?? rm.babies) || 0),
      );
      let kidsAges = Array.isArray(rm.kidsAges) ? [...rm.kidsAges] : null;
      if (!kidsAges && kids > 0) {
        const defAge = Number.isFinite(Number(rm.defaultKidAge))
          ? Number(rm.defaultKidAge)
          : 8;
        kidsAges = Array.from({ length: kids }, () => defAge);
      } else if (!kidsAges) {
        kidsAges = [];
      }
      return { adults, kids, infants, kidsAges };
    });
    return normalizedCompositions.map((roomComposition, index) => {
      let { adultFare, infantFare = 0, childFareExt, totalFare } = dealFare;

      const code1 = `${"A" + roomComposition.adults}${roomComposition.kids > 0 ? "C" + roomComposition.kids : ""}`;

      const code2 = `${"A" + (roomComposition.adults + roomComposition.kids)}`;

      if (shortenedDeal)
        if (Array.isArray(shortenedDeal)) {
          let suitscode = shortenedDeal
            .filter((sd) => sd.roomSet === code1 || sd.roomSet === code2)
            .sort((a, b) => a.totalFare - b.totalFare);
          let sd = suitscode.length > 0 ? suitscode[0] : null;
          if (sd) {
            adultFare = sd.adultFare;
            infantFare = sd.infantFare;
            childFareExt = sd.childFare;
            totalFare = sd.totalFare;
          }
        } else {
          adultFare = shortenedDeal.adultFare;
          infantFare = shortenedDeal.infantFare;
          childFareExt = shortenedDeal.childFare;
          totalFare = shortenedDeal.totalFare;
        }
      let childFare = childFareExt || 0;

      if (!totalFare) {
        let childFareFR = 0;
        if (Array.isArray(childFare)) {
          for (let i = 0; i < childFare.length; i++) {
            let j = 0;
            for (j = 0; j < roomComposition.kidsAges.length; j++) {
              const age = roomComposition.kidsAges[j];
              if (age >= childFare[i].ageFrom && age <= childFare[i].ageTo) {
                childFareFR += Number(childFare[i].fare);
                break;
              }
            }
            if (j === roomComposition.kidsAges.length)
              childFareFR += Number(adultFare);
          }
        } else if (!childFare) {
          childFareFR = roomComposition.kids * Number(adultFare);
        } else {
          for (let j = 0; j < roomComposition.kidsAges.length; j++) {
            const age = roomComposition.kidsAges[j];
            if (age >= childFare.ageFrom && age <= childFare.ageTo)
              childFareFR += Number(childFare.fare);
            else childFareFR += Number(adultFare);
          }
        }
        totalFare =
          Number(adultFare) * roomComposition.adults +
          childFareFR +
          Number(infantFare) * roomComposition.infants;
      } else if (roomComposition.infants > 0 && infantFare) {
        // LDS totalFare excludes infants — same add-on as SelectedResultContext / room SSOT
        totalFare =
          Number(totalFare) + Number(infantFare) * roomComposition.infants;
      }

      // Get category from current search categories or from deal metadata
      const dealCategory = fullDeal?.category || (categories && categories.length > 0 && !categories.includes("ALL") ? categories[0] : null);
      
      const ourFareDetails = calculateLDSDealFare(
        {
          dealReferenceDate: extractLdsDealReferenceDate(fullDeal),
          dealFare: {
            adultFare: parseInt(adultFare) || 0,
            totalFare: parseInt(totalFare) || 0,
            childFareExt: parseInt(childFare) || 0,
            infantFare: parseInt(infantFare) || 0,
          },
          fareDetails,
          dealType: dealTypeFromDeal,
          hotel,
          tour,
          supplierDetails,
          dealCategory: dealCategory, // Pass category to calculateLDSDealFare
          passengersCount:
            (Number(roomComposition.adults) || 0) +
            (Number(roomComposition.kids) || 0) +
            (Number(roomComposition.infants) || 0),
        },
        bypassDpFlightFare ? { ssotBypass: true } : {}
      );

      // Calculate fixed commission per person if applicable
      if (
        ourFareDetails?.ldsCommissionCondition?.commission_type === "FIXED_PER_PERSON" &&
        Number(ourFareDetails?.ourTotalFare?.pricingModelVersion) !== 2 &&
        Number(ourFareDetails?.pricingSnapshot?.pricing_model_version) !== 2
      ) {
        const fixedCommissionPerPersonUSD = Number(ourFareDetails.ldsCommissionCondition.fixed_commission_per_person || 0);
        const hrCommissionFixedUSD = Number(ourFareDetails.ldsCommissionCondition.hr_commission_fixed || 0);
        
        // Count adults and kids (usually infants don't count for fixed commission)
        const numberOfPeople = roomComposition.adults + roomComposition.kids;
        const totalFixedCommissionUSD = fixedCommissionPerPersonUSD * numberOfPeople;
        // hr_commission_fixed is also per person (replaces % HR on organized tours)
        const totalHrCommissionFixedUSD = hrCommissionFixedUSD * numberOfPeople;
        
        // Get currency rate for conversion - always use ADMIN rates
        const { currency } = fareDetails;
        const dealCurrencyRate = currencyRate?.[currency];
        const usdRate = currencyRate?.["USD"];
        
        // If rates are not loaded from ADMIN, cannot calculate
        if (!dealCurrencyRate || !usdRate) {
          console.warn(`Currency rates not loaded from ADMIN. Cannot calculate fixed commission price.`);
          return null;
        }
        
        // Logic: All calculations in USD, convert to ILS only at the end
        // 1. Convert LDS Price to USD (if not already in USD)
        // 2. LDS Price (USD) - (Fixed Commission × Number of People) = Net Price (USD)
        // 3. Net Price (USD) + (HR Fixed × Number of People) = Final Price (USD)
        // 4. Final Price (USD) × USD Rate = Final Price (ILS) for display
        if (ourFareDetails.ourTotalFare) {
          // Step 1: Convert original LDS price to USD
          let originalLdsPriceUSD;
          if (currency === "USD") {
            // Price is already in USD
            originalLdsPriceUSD = round2(parseInt(totalFare) || 0);
          } else {
            // Convert from original currency to ILS first, then to USD
            const originalLdsPriceILS = round2((parseInt(totalFare) || 0) * dealCurrencyRate);
            originalLdsPriceUSD = round2(originalLdsPriceILS / (usdRate || 1));
          }
          
          // Step 2: Calculate net price in USD: Original LDS Price - (Fixed Commission × Number of People)
          const netPriceUSD = round2(originalLdsPriceUSD - totalFixedCommissionUSD);
          
          // Step 3: Calculate final price in USD: Net Price + (HR Fixed × people)
          // NOTE: With FIXED_PER_PERSON commission, we don't add percentage-based commissions!
          const finalPriceUSD = round2(netPriceUSD + totalHrCommissionFixedUSD);
          
          // Step 4: Convert final price from USD to ILS for display
          // With FIXED_PER_PERSON, the final price is just the base price (no percentage commissions)
          const finalPriceILS = round2(finalPriceUSD * usdRate);
          
          // Update all price components in ILS
          // With FIXED_PER_PERSON, there are no percentage-based commissions
          ourFareDetails.ourTotalFare.initialPrice = finalPriceILS;
          ourFareDetails.ourTotalFare.hrCommission = 0; // No percentage-based HR commission
          ourFareDetails.ourTotalFare.agentCommission = 0; // No percentage-based agent commission
          ourFareDetails.ourTotalFare.hrCommissionPercent = 0;
          ourFareDetails.ourTotalFare.agentCommissionPercent = 0;
          ourFareDetails.ourTotalFare.priceWithCommission = finalPriceILS; // Final price is the same as base price
          ourFareDetails.ourTotalFare.hrMarginPricingMode = "fixed_per_person_embedded";
          ourFareDetails.ourTotalFare.supplierFixedCommissionTotalUsd =
            totalFixedCommissionUSD;
          ourFareDetails.ourTotalFare.hrCommissionFixedUsdApplied =
            totalHrCommissionFixedUSD;
          
          // Recalculate credit card fee
          const creditCardFeePercent = getCreditCardFeePercent();
          const priceWithCommission = ourFareDetails.ourTotalFare.priceWithCommission || 0;
          const creditCardFee = round2(
            (priceWithCommission * (creditCardFeePercent || 0)) / 100
          );
          
          ourFareDetails.ourTotalFare.creditCardFee = creditCardFee || 0;
          const finalPriceWithCreditCard = round2(priceWithCommission + (creditCardFee || 0));
          ourFareDetails.ourTotalFare.priceWithCommissionAndCreditCardFee = finalPriceWithCreditCard || priceWithCommission;
          
          // Store the net price for reference (convert from USD to ILS)
          const netPriceILS = round2(netPriceUSD * usdRate);
          ourFareDetails.ourTotalFare.netPrice = netPriceILS;
          
          // Preserve original currency if it was set
          if (!ourFareDetails.ourTotalFare.originalCurrency) {
            ourFareDetails.ourTotalFare.originalCurrency = currency;
          }

          const fxR = currency === "USD" ? usdRate : dealCurrencyRate;
          const fxSrcFixed =
            currencyRateSource?.[currency] === FX_SOURCE_ADMIN_CONFIG
              ? FX_SOURCE_ADMIN_CONFIG
              : FX_SOURCE_FALLBACK_DEFAULT;
          ourFareDetails.ourTotalFare.fx_rate = Number(fxR);
          ourFareDetails.ourTotalFare.fx_currency = currency;
          ourFareDetails.ourTotalFare.fx_source = fxSrcFixed;

          // If currency is GBP, calculate final price in GBP
          if (currency === "GBP" && !ourFareDetails.ourTotalFare.finalPriceGBP) {
            // Calculate net price in GBP (convert from USD back to GBP)
            const gbpToUsdRate = usdRate / dealCurrencyRate;
            const netPriceGBP = round2(netPriceUSD / gbpToUsdRate);
            
            // Calculate commissions in GBP
            const hrCommissionPercent = ourFareDetails.ourTotalFare.hrCommissionPercent || 0;
            const agentCommissionPercent = ourFareDetails.ourTotalFare.agentCommissionPercent || 0;
            
            const hrCommissionGBP = round2((hrCommissionPercent / 100) * netPriceGBP);
            const agentCommissionGBP = round2((agentCommissionPercent / 100) * netPriceGBP);
            
            // Calculate price with commissions in GBP
            const priceWithCommissionGBP = round2(netPriceGBP + hrCommissionGBP + agentCommissionGBP);
            
            // Calculate credit card fee in GBP
            const creditCardFeeGBP = round2((creditCardFeePercent / 100) * priceWithCommissionGBP);
            
            // Final price in GBP
            const finalPriceGBP = round2(priceWithCommissionGBP + creditCardFeeGBP);
            
            // Store GBP prices
            ourFareDetails.ourTotalFare.priceWithCommissionGBP = priceWithCommissionGBP;
            ourFareDetails.ourTotalFare.creditCardFeeGBP = creditCardFeeGBP;
            ourFareDetails.ourTotalFare.finalPriceGBP = finalPriceGBP;
            ourFareDetails.ourTotalFare.hrCommissionGBP = hrCommissionGBP;
            ourFareDetails.ourTotalFare.agentCommissionGBP = agentCommissionGBP;
          }
        }
      }

      return { dealFare, ourFareDetails };
    });
  }

  function calculateLDSDealFareForRoomsComposition(
    fullDeal,
    shortenedDeal = null,
    roomsCompositionOverride = null,
    composeOptions = null,
  ) {
    const bypassDpFlightFare =
      composeOptions?.bypassDynamicPackageFlightFareGate === true;
    if (
      !bypassDpFlightFare &&
      isDynamicPackagePricingNormalizedEnabled() &&
      searchTab === DYNAMIC_PACKAGES
    ) {
      const { normalized } = buildNormalizedDynamicPackagePricing({
        stage: "search_card",
        savedDeal,
        fullDeal: fullDeal || savedDeal,
        hotelsItem: null,
        searchListCurrency:
          selectedHotelCurrency ||
          hotelSearchListDisplayCurrency ||
          searchPhrase?.currency,
        linkedFlightsDealPriceIls: linkedFlightsDealPrice,
      });
      return {
        dynamicPackageNormalizedOnly: true,
        normalized,
        ourFareDetails: null,
        ourTotalFare: null,
      };
    }
    const roomsPriceTable = getLdsDealRoomFareRows(
      fullDeal,
      shortenedDeal,
      roomsCompositionOverride,
      composeOptions,
    );
    if (!roomsPriceTable) {
      return null;
    }
    const validRoomPriceRows = roomsPriceTable.filter(
      (row) => row?.ourFareDetails?.ourTotalFare
    );
    if (validRoomPriceRows.length === 0) {
      if (process.env.NODE_ENV === "development") {
        console.warn(
          "[FLIGHT_ROOMS_PRICING_MISSING_TOTAL_FARE]",
          {
            dealId: firstNonEmpty(fullDeal?.dealId, fullDeal?.ID, fullDeal?.id) || null,
            roomsRequested: Array.isArray(roomsCompositionOverride)
              ? roomsCompositionOverride.length
              : Array.isArray(roomInfo?.rooms)
                ? roomInfo.rooms.length
                : 0,
            rowsCount: Array.isArray(roomsPriceTable) ? roomsPriceTable.length : 0,
          }
        );
      }
      return null;
    }

    const ourSumTotalFare = {};
    let originalCurrency = null;
    let summedOrigCurrency = null;
    let finalPriceGBP = null;
    let priceWithCommissionGBP = null;
    let creditCardFeeGBP = null;
    let hrCommissionGBP = null;
    let agentCommissionGBP = null;
    
    for (const roomPrice of validRoomPriceRows) {
      const ourTotalFare = roomPrice.ourFareDetails.ourTotalFare;
      if (ourTotalFare.origCurrency && typeof ourTotalFare.origCurrency === "object") {
        const roomOrig = ourTotalFare.origCurrency;
        if (!summedOrigCurrency) {
          summedOrigCurrency = { ...roomOrig };
        } else {
          for (const key in roomOrig) {
            if (!Object.prototype.hasOwnProperty.call(roomOrig, key)) continue;
            const val = roomOrig[key];
            if (isNumber(val)) {
              if (!isNumber(summedOrigCurrency[key])) {
                summedOrigCurrency[key] = 0;
              }
              summedOrigCurrency[key] += Number(val);
            } else if (summedOrigCurrency[key] == null) {
              summedOrigCurrency[key] = val;
            }
          }
        }
      }

      
      // Preserve non-numeric fields (like originalCurrency)
      if (ourTotalFare.originalCurrency && !originalCurrency) {
        originalCurrency = ourTotalFare.originalCurrency;
      }
      
      // Sum GBP prices if available
      if (ourTotalFare.finalPriceGBP) {
        finalPriceGBP = (finalPriceGBP || 0) + ourTotalFare.finalPriceGBP;
      }
      if (ourTotalFare.priceWithCommissionGBP) {
        priceWithCommissionGBP = (priceWithCommissionGBP || 0) + ourTotalFare.priceWithCommissionGBP;
      }
      if (ourTotalFare.creditCardFeeGBP) {
        creditCardFeeGBP = (creditCardFeeGBP || 0) + ourTotalFare.creditCardFeeGBP;
      }
      if (ourTotalFare.hrCommissionGBP) {
        hrCommissionGBP = (hrCommissionGBP || 0) + ourTotalFare.hrCommissionGBP;
      }
      if (ourTotalFare.agentCommissionGBP) {
        agentCommissionGBP = (agentCommissionGBP || 0) + ourTotalFare.agentCommissionGBP;
      }
      
      // Sum numeric fields (skip per-room rates — same value each room, must not double)
      for (let key in ourTotalFare) {
        if (key === "fx_rate" || key === "currencyRate") {
          continue;
        }
        if (!isNumber(ourTotalFare[key])) {
          continue;
        }
        if (ourSumTotalFare[key] === undefined) {
          ourSumTotalFare[key] = 0;
        }
        ourSumTotalFare[key] += ourTotalFare[key];
      }
    }

    const firstRoomFare = validRoomPriceRows[0]?.ourFareDetails?.ourTotalFare;
    if (firstRoomFare) {
      if (firstRoomFare.fx_rate != null) {
        ourSumTotalFare.fx_rate = firstRoomFare.fx_rate;
      }
      if (firstRoomFare.fx_source != null) {
        ourSumTotalFare.fx_source = firstRoomFare.fx_source;
      }
      if (firstRoomFare.fx_currency != null) {
        ourSumTotalFare.fx_currency = firstRoomFare.fx_currency;
      }
      if (firstRoomFare.currencyRate != null) {
        ourSumTotalFare.currencyRate = firstRoomFare.currencyRate;
      }
    }

    // Add non-numeric fields to the sum
    if (originalCurrency) {
      ourSumTotalFare.originalCurrency = originalCurrency;
    }
    const fxRateForUsdBuild = Number(ourSumTotalFare.fx_rate || firstRoomFare?.fx_rate) || 0;
    if (summedOrigCurrency && typeof summedOrigCurrency === "object") {
      if (
        !isNumber(summedOrigCurrency.priceWithCommissionAndCreditCardFee) &&
        isNumber(ourSumTotalFare.priceWithCommissionAndCreditCardFee) &&
        fxRateForUsdBuild > 0
      ) {
        summedOrigCurrency.priceWithCommissionAndCreditCardFee = round2(
          Number(ourSumTotalFare.priceWithCommissionAndCreditCardFee) / fxRateForUsdBuild
        );
      }
      // Ensure locked supplier-net USD is present for order-details (not live FX).
      if (!isNumber(summedOrigCurrency.netPrice) && isNumber(summedOrigCurrency.initialPrice)) {
        summedOrigCurrency.netPrice = Number(summedOrigCurrency.initialPrice);
      }
      ourSumTotalFare.origCurrency = summedOrigCurrency;
      if (process.env.NODE_ENV === "development") {
        console.log("[FLIGHT_FINAL_USD_BUILD_DEBUG]", {
          finalClientIls: Number(ourSumTotalFare.priceWithCommissionAndCreditCardFee) || null,
          usdRate: fxRateForUsdBuild || null,
          finalClientUsd:
            Number(summedOrigCurrency.priceWithCommissionAndCreditCardFee) || null,
          source: "rooms_sum_origCurrency",
          dealId: firstNonEmpty(fullDeal?.dealId, fullDeal?.ID, fullDeal?.id) || null,
        });
      }
    } else if (
      isNumber(ourSumTotalFare.priceWithCommissionAndCreditCardFee) &&
      fxRateForUsdBuild > 0
    ) {
      const derivedOrigCurrency = {
        initialPrice: round2((Number(ourSumTotalFare.initialPrice) || 0) / fxRateForUsdBuild),
        netPrice: round2((Number(ourSumTotalFare.netPrice) || 0) / fxRateForUsdBuild),
        hrCommissionPercent: Number(ourSumTotalFare.hrCommissionPercent) || 0,
        agentCommissionPercent: Number(ourSumTotalFare.agentCommissionPercent) || 0,
        hrCommission: round2((Number(ourSumTotalFare.hrCommission) || 0) / fxRateForUsdBuild),
        agentCommission: round2(
          (Number(ourSumTotalFare.agentCommission) || 0) / fxRateForUsdBuild
        ),
        priceWithCommission: round2(
          (Number(ourSumTotalFare.priceWithCommission) || 0) / fxRateForUsdBuild
        ),
        creditCardFee: round2((Number(ourSumTotalFare.creditCardFee) || 0) / fxRateForUsdBuild),
        priceWithCommissionAndCreditCardFee: round2(
          Number(ourSumTotalFare.priceWithCommissionAndCreditCardFee) / fxRateForUsdBuild
        ),
        pricingStatus: "ok",
      };
      ourSumTotalFare.origCurrency = derivedOrigCurrency;
      if (process.env.NODE_ENV === "development") {
        console.log("[FLIGHT_FINAL_USD_BUILD_DEBUG]", {
          finalClientIls: Number(ourSumTotalFare.priceWithCommissionAndCreditCardFee) || null,
          usdRate: fxRateForUsdBuild || null,
          finalClientUsd:
            Number(derivedOrigCurrency.priceWithCommissionAndCreditCardFee) || null,
          source: "rooms_derived_from_ils_rate",
          dealId: firstNonEmpty(fullDeal?.dealId, fullDeal?.ID, fullDeal?.id) || null,
        });
      }
    }
    if (finalPriceGBP) {
      ourSumTotalFare.finalPriceGBP = round2(finalPriceGBP);
    }
    if (priceWithCommissionGBP) {
      ourSumTotalFare.priceWithCommissionGBP = round2(priceWithCommissionGBP);
    }
    if (creditCardFeeGBP) {
      ourSumTotalFare.creditCardFeeGBP = round2(creditCardFeeGBP);
    }
    if (hrCommissionGBP) {
      ourSumTotalFare.hrCommissionGBP = round2(hrCommissionGBP);
    }
    if (agentCommissionGBP) {
      ourSumTotalFare.agentCommissionGBP = round2(agentCommissionGBP);
    }

    const ourPrice = {
      ...validRoomPriceRows[0].ourFareDetails,
      ourTotalFare: ourSumTotalFare,
    };
    return ourPrice;
  }

  const contextValue = useMemo(
    () => ({
      searchPhrase,
      setSearchPhrase,
      setCalendarDateArrival,
      tourMonth,
      setTourMonth,
      packageDateMode,
      setPackageDateMode,
      packageMonth,
      setPackageMonth,
      setCalendarDateLeave,
      calendarDateArrival,
      calendarDateLeave,
      flightTripType,
      setFlightTripType,
      dateRange,
      setDateRange,
      setSearchTab: setSearchTab,
      searchParamsLocked,
      setSearchParamsLocked,
      toggleSearchParamsLock,
      canLockSearchParams,
      switchFromOrganizedToursIfNeeded,
      userTouchedCategory,
      markUserTouchedCategory,
      forceStopLoading,
      selectedHotelCurrency,
      setSelectedHotelCurrency,
      hotelSearchResultsCurrency,
      hotelSearchListDisplayCurrency,
      setLocation: setLocation,
      setOrigin,
      retOrigin,
      setRetOrigin: _setRetOrigin,
      retDest,
      setRetDest: _setRetDest,
      setRoomInfo,
      calculatFlightLDSPrice,
      searchTab,
      dynamicPackagePricingSource: getDynamicPackagePricingSource(),
      isDynamicPackagePricingNormalizedEnabled: isDynamicPackagePricingNormalizedEnabled(),
      DYNAMIC_PACKAGE_PRICING_SOURCE_STORAGE_KEY,
      DYNAMIC_PACKAGE_PRICING_SOURCE_NORMALIZED,
      roomInfo,
      location,
      origin,
      getSearchPhrase,
      submitSearch,
      restoreAgentRecentSearchFromRow,
      setFlightSearchAttributionAgentId,
      searchResults,
      currentSearchRequestId,
      lastSearchPayload,
      targetHotelDedupKey,
      sessionId,
      loading,
      error,
      loadingDates,
      datesError,
      isSearchDisabled,
      getSearchDisableReasons,
      ldsDates,
      isSearched,
      clearPublicEmptySearch,
      organizedTourSearchHandshake,
      shouldSkipOrganizedTourResultsBridgeHandshake,
      markOrganizedTourResultsBridgeHandshakeHandled,
      rangeSearchResults,
      passengerInfo,
      setPassengerInfo,
      timeLeft: 202,
      reRunSearch,
      getInitRoomInfo,
      calculateLDSDealFare,
      getLdsDealRoomFareRows,
      calculateLDSDealFareForRoomsComposition,
      calculatePriceWithCommissions,
      categories,
      setCategories,
      destinations,
      subjects,
      availableFlights,
      roundTripFlightResults,
      outboundOneWayFlightResults,
      inboundOneWayFlightResults,
      parallelRoundTripFlightSearchCompleted,
      planesFlightSearchOutcome,
      flightAlternativeDateSuggestions,
      flightAlternativesProbeLoading,
      applyFlightAlternativeSuggestion,
      flightResultsBrowseMode,
      setFlightResultsBrowseMode,
      selectedSelfComposeOutboundDeal,
      setSelectedSelfComposeOutboundDeal,
      selectedSelfComposeInboundDeal,
      setSelectedSelfComposeInboundDeal,
      selectedDepart,
      selectedReturn,
      setSelectedDepart,
      setSelectedReturn,
      setAvailableFlights,
      setSavedDeal,
      applyDynamicPackageSelfComposeSelection,
      dynamicPackageFlightPickMode,
      setDynamicPackageFlightPickMode,
      setLinkedFlightsDealPrice,
      savedDeal,
      linkedFlights,
      linkedFlightsDealPrice,
      setLinkedFlights,
      supplements,
      initSupplements,
      updateDates,
      searchAllAvailableDates,
      searchShows,
      searchByCategory,
      searchHotelForMonth,
      autocorrectSuggestion,
      autocorrectDismissed,
      applyAutocorrectSuggestion,
      dismissAutocorrectSuggestion,
      attractionsSearchResults,
      attractionsSearchLoading,
      attractionsSearchError,
      attractionsSearchMeta,
      attractionsDestinationRules,
      compositeHotelSearchMeta,
      dynamicPackageFutureCxlOnly,
      setDynamicPackageFutureCxlOnly,
    }),
    [
      // Only include primitive values and state, not functions
      searchPhrase,
      autocorrectSuggestion,
      autocorrectDismissed,
      attractionsSearchResults,
      attractionsSearchLoading,
      attractionsSearchError,
      attractionsSearchMeta,
      attractionsDestinationRules,
      compositeHotelSearchMeta,
      dynamicPackageFutureCxlOnly,
      calendarDateArrival,
      calendarDateLeave,
      flightTripType,
      dateRange,
      tourMonth,
      packageDateMode,
      packageMonth,
      searchTab,
      searchParamsLocked,
      userTouchedCategory,
      selectedHotelCurrency,
      hotelSearchResultsCurrency,
      hotelSearchListDisplayCurrency,
      roomInfo,
      location,
      origin,
      retOrigin,
      retDest,
      searchResults,
      currentSearchRequestId,
      lastSearchPayload,
      targetHotelDedupKey,
      sessionId,
      loading,
      error,
      loadingDates,
      datesError,
      ldsDates,
      isSearched,
      organizedTourSearchHandshake,
      rangeSearchResults,
      passengerInfo,
      categories,
      destinations,
      subjects,
      availableFlights,
      roundTripFlightResults,
      outboundOneWayFlightResults,
      inboundOneWayFlightResults,
      parallelRoundTripFlightSearchCompleted,
      planesFlightSearchOutcome,
      flightAlternativeDateSuggestions,
      flightAlternativesProbeLoading,
      flightResultsBrowseMode,
      selectedSelfComposeOutboundDeal,
      selectedSelfComposeInboundDeal,
      selectedDepart,
      selectedReturn,
      savedDeal,
      dynamicPackageFlightPickMode,
      linkedFlights,
      linkedFlightsDealPrice,
      supplements,
      initSupplements,
      // Refresh pricing closures when DEV package-tier preview (or live entitlements) changes.
      packageTierOverride,
      pricingEntitlements,
      agentEntitlementsStatus,
      // Functions are otherwise recreated each render; deps above force consumer updates.
    ]
  );

  return (
    <SearchContext.Provider value={contextValue}>
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error("useSearch must be used within a SearchProvider");
  }
  return context;
};

export default SearchContext;

export function parsePeopleCount(roomInfo) {
  const rooms = roomInfo?.rooms || [];
  const totalRooms = rooms.length;

  let totalPeople = 0;

  rooms.forEach((personData) => {
    totalPeople += personData.adults || 0;
    totalPeople += personData.kids || 0;
    totalPeople += personData.infants || 0;
  });

  return { totalRooms, totalPeople };
}

// export function getPeoplesSum(roomInfo) {
//   const rooms = roomInfo?.rooms || [];

//   let adults = 0;
//   let kids = 0;

//   rooms.forEach((personData) => {
//     adults += personData.adults || 0;
//     kids += personData.kids || 0;
//   });

//   return { adults, kids };
// }
